import { lazy, startTransition, Suspense, useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { Toaster, toast } from 'sonner'
import { Header } from './components/Header'
import type { FloatingHelpActionKey } from './components/FloatingHelpMenu'
import type { CheckoutStepInfo } from './components/checkout'
import { HomeView } from './components/HomeView'
import { MaintenanceView } from './components/MaintenanceView'
import { QrCodeDataResultView } from './components/QrCodeDataResultView'
import { NfeQrEmailView } from './components/NfeQrEmailView'
import type { CheckoutStep as CheckoutRouteStep } from './components/checkout'
import { CheckoutProvider } from './components/checkout/CheckoutContext'
import type { InstitutionalSection } from './components/InstitutionalView'
import { writeSupportFormPrefill } from '@/components/support/support-prefill'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog'
import { Input } from './components/ui/input'
import { Label } from './components/ui/label'
import { Button } from './components/ui/button'
import { Skeleton } from './components/ui/skeleton'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './components/ui/sheet'
import {
  buildCartFreightQuotePayload,
  type CartFreightSelection,
} from './lib/cart-freight-selection'
import { brands } from './lib/data'
import {
  buildCheckoutAttemptStorageKeys,
  clearPersistedCheckoutAttempt,
  readPersistedCheckoutAttempt,
} from './lib/checkout-attempt-storage'
import {
  buildCheckoutSuccessStorageKeys,
  clearPersistedCheckoutSuccess,
} from './lib/checkout-success-storage'
import { CART_ITEM_QUANTITY_MAX } from '@/lib/cart-quantity'
import {
  checkClienteExists,
  clearCarrinho,
  clearAuthenticatedEntidadeId,
  copyCarrinhoBetweenCompradores,
  consumeBuyerAccessShortCode,
  consumeBuyerAccessTicket,
  createBuyerPriceTableAccessLink,
  expandProductsWithParentContexts,
  getAcabaramDeChegarPage,
  getBackendAvailabilityState,
  getAuthenticatedEntidadeId,
  getBuyerAccountSummary,
  getBuyerAccessStatus,
  getBuyerRotatingSearchTerms,
  getPasswordRecoveryDestinations,
  getPasswordlessDestinations,
  getOrcamentoById,
  recalculateCarrinho,
  replaceOrcamento,
  setAuthenticatedEntidadeId,
  getCarrinhoQuantidades,
  getCarrinhoProducts,
  getCheckoutCustomerProfile,
  getHomeProductShowcases,
  getIndicadorPedidoDetalheAsAccountOrder,
  getLancamentosPage,
  getPedidoById,
  getProductVariations,
  getProducts,
  type CheckoutCustomerProfile,
  type CheckoutOrderResult,
  type PaginatedProductListResult,
  relateClientes,
  searchProductsByImage,
  searchProducts,
  searchFilteredProductsPage,
  searchMarketplaceVitrineProductsPage,
  searchProductsPage,
  startIndicatorClientDelegation,
  getProductById,
  getProductsByIds,
  getMenu,
  getMenuTopLevel,
  login,
  loginLegacyCheckout,
  type PasswordlessLoginChannel,
  requestPasswordRecoveryCode,
  requestPasswordlessLogin,
  requestPasswordlessContact,
  resetPasswordWithRecoveryToken,
  salvarOrcamento,
  subscribeBackendAvailability,
  upsertCarrinhoItemByKey,
  upsertCarrinhoItemsBatch,
  updateCarrinho,
  verifyPasswordRecoveryCode,
  verifyPasswordlessLogin,
} from './lib/api-shell'
import {
  type CheckoutSimulationResult,
  type SavedQuotePdfClient,
} from './lib/api'
import {
  BUYER_SESSION_EXPIRED_EVENT,
  BUYER_SESSION_TOKEN_RESET_EVENT,
  getBuyerSessionToken,
  setBuyerSessionToken,
} from './lib/buyer-session'
import { getLojaVirtualDefaultLojaId } from './lib/storefront-runtime'
import { getStoreNetworkHandoffSession } from './features/store-network/api'
import {
  buildStorefrontPurchaseOptionProduct,
  resolveStorefrontCartBaseProductId,
  resolveStorefrontCartBoxQuantity,
} from './lib/storefront-purchase-option'
import {
  buildAccessPath,
  buildMarketplaceVitrineHref,
  buildMarketplaceVitrineSellerHref,
  buildProductHref,
  buildShortAccessPath,
  buildCheckoutTrackingPath,
  buildAttendanceDetailPathFromToken,
  BUYER_SHORT_ACCESS_PATH_PREFIX,
  PUBLIC_TICKET_PATH_PREFIX,
  buildOrderDetailToken,
  buildBusinessOrderDetailPathFromToken,
  buildBusinessQuoteDetailPathFromToken,
  buildOrderDetailPathFromToken,
  buildQuoteDetailToken,
  buildQuoteDetailPathFromToken,
  buildCategoryHref,
  handleLinkClick,
  LEGACY_INSTITUTIONAL_PATH,
  LEGACY_PRODUCTS_PATH,
  LEGACY_VIEW_PATH_ALIASES,
  MARKETPLACE_ACCOUNT_HOME_PATH,
  normalizeLegacyProductDetailPathname,
  normalizeAccountDetailToken,
  normalizeProductRouteValue,
  preserveMarketplaceVitrineDetailPath,
  preserveMarketplaceVitrineSellerPath,
  PRODUCT_DETAIL_BASE,
  type ProductNavigationContext,
  resolveAccountDetailIdFromPathSegment,
  VIEW_PATHS,
} from './lib/navigation'
import {
  buildStorefrontSearchResultProducts,
  buildStorefrontPageViewEvent,
  createStorefrontSearchId,
  ensureStorefrontAnalyticsHistoryInstrumentation,
  flushStorefrontAnalyticsQueue,
  getCurrentStorefrontUrl,
  getStorefrontAnalyticsLocationKey,
  queueStorefrontConsentGrantedAnalyticsEvent,
  queueStorefrontAnalyticsEvent,
  registerStorefrontSearchResultPage,
  STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT,
  trackStorefrontCartState,
  withStorefrontCartAnalyticsPayload,
  withStorefrontGranularAnalyticsPayload,
  withStorefrontSearchResultAttribution,
  type StorefrontSearchAnalyticsContext,
  type StorefrontSearchResultContext,
} from './lib/storefront-analytics'
import { isMarketplaceVitrinePublicSearchEnabled } from './lib/marketplace-vitrine-search'
import { marketplaceListingEditorIdFromPath } from './lib/marketplace-vitrine-listing-form'
import { preserveAffiliateCheckoutSearch } from './lib/affiliate-checkout-link'
import { preserveSellerRegistrationSearch } from './lib/seller-registration-link'
import { preserveStoreNetworkAccessSearch } from './lib/store-network'
import { slugify } from './lib/slug'
import { useBreadcrumb } from './hooks/use-breadcrumb'
import { useAccountNotifications } from './hooks/use-account-notifications'
import { useBuyerBeneficios } from './hooks/use-buyer-beneficios'
import { useBuyerDistribuicao } from './hooks/use-buyer-distribuicao'
import { useIsMobile } from './hooks/use-mobile'
import { useStorefrontSiteConfig } from './contexts/site-config'
import { useRecommendationTracking } from './hooks/use-recommendation-tracking'
import type {
  BusinessQuoteCheckoutMode,
  CartItem,
  Category,
  CategoryPathNode,
  CategorySelectionData,
  FilterState,
  Product,
  QuoteCartFreightPrefill,
  QuoteCartSnapshotUpdatePayload,
  QuoteSentToCartPayload,
  QuoteStartCheckoutPayload,
  VehicleFilter,
} from './lib/types'
import type { AppView } from './lib/navigation'
import { applySeoMetadata, toAbsoluteUrl, type SeoMetadata } from './lib/seo'
import { resolveDistributionStatus } from './lib/distribution-status'
import { normalizeStorefrontLanguage, resolveStorefrontLanguage } from './lib/language-preference'
import {
  normalizeDocumentDigits,
  type ProductPricingContext,
} from './lib/pricing'
import {
  filterHiddenUnavailableProducts,
  filterUnavailableProductsWhenStockRuleEnabled,
  isProductUnavailable,
  sortProductsByAvailability,
} from './lib/product-stock'
import { buildOrderCheckoutItems } from './lib/order-checkout'
import { mapQuoteDetailToCheckoutItems } from './lib/quote-checkout'
import {
  applyLinkedQuoteCartSnapshotUpdate,
  buildCartQuoteSnapshotSignature,
  doesCartMatchQuoteFinancialSummary,
} from './lib/cart-quote-snapshot'
import {
  productMatchesDetailRoute,
  productSupportsDetailParentContext,
} from './lib/product-detail-route'
import {
  clearQuickCheckoutCartItems,
  loadQuickCheckoutCartItems,
  removeQuickCheckoutCartItem,
  saveQuickCheckoutCartItems,
  setQuickCheckoutCartItemQuantity,
  upsertQuickCheckoutCartItem,
} from './lib/quick-checkout-cart'
import {
  canUseAnonymousQuickCheckout,
  isSimplifiedStorefrontEnabled,
  resolveSimplifiedStorefrontPricingContext,
} from './lib/simplified-storefront'
import { requiresProductVariationSelection, shouldShowPrices } from './lib/utils'
import { PricingProvider } from './components/pricing/PricingContext'
import { useTranslation } from 'react-i18next'
import type { RegisterSuccessContext } from './components/RegisterView'
import { ArrowsClockwise, CaretLeft, CaretRight, CheckCircle, FilePlus, ShoppingCart } from '@phosphor-icons/react'
import {
  DOMESTIC_COMMERCE_CONTEXT,
  formatCommerceCurrency,
  resetBuyerCommerceContext,
  resolveBuyerSessionCommerceContext,
  resolveGuestStorefrontLocale,
  setBuyerCommerceContext,
  type BuyerCommerceContext,
} from './lib/commerce-context'

const LEGACY_QUICK_CHECKOUT_PATH = '/checkout/rapido'

type BuyerAccessConsumePromise =
  | ReturnType<typeof consumeBuyerAccessTicket>
  | ReturnType<typeof consumeBuyerAccessShortCode>

type CartQuoteOrigin = NonNullable<QuoteSentToCartPayload['originQuote']>

type BuyerAccessAttemptState = {
  key: string
  promise: BuyerAccessConsumePromise
}

type View = AppView
const MARKETPLACE_ACCOUNT_VIEWS = new Set<View>([
  'marketplace',
  'marketplace-listings',
  'marketplace-questions',
  'marketplace-performance',
])
type ServerPaginatedProductFeedView = 'launches' | 'just_arrived'
type ServerPaginatedProductFeed =
  | { kind: 'view'; view: ServerPaginatedProductFeedView }
  | { kind: 'catalog'; dataKey: string; onlyAvailable: boolean }
  | { kind: 'catalog_brand'; dataKey: string; manufacturerBrandIds: number[] }
  | {
      kind: 'category'
      dataKey: string
      searchOptions: Record<string, number | number[]>
      onlyAvailable: boolean
    }
  | {
      kind: 'search'
      dataKey: string
      query: string
      manufacturerBrandIds: number[]
      onlyAvailable: boolean
      vehicleFilter: VehicleFilter | null
      analyticsContext: StorefrontSearchAnalyticsContext
    }
type ReturnRequestRouteStep = 'choice' | 'terms' | 'search' | 'items' | 'contact' | 'review' | 'success'
type ReturnRequestRouteFlowType = 'return' | 'repair' | null
type AccountProfile = {
  corporateName?: string
  name?: string
  email?: string
  phone?: string
  cnpj?: string
  document?: string
  activity?: string
  stateRegistration?: string
  street?: string
  number?: string
  complement?: string
  neighborhood?: string
  city?: string
  state?: string
  zipCode?: string
  tipoPessoa?: number | null
  entidadeId?: number
  buyerSessionToken?: string
  commerceContext?: BuyerCommerceContext
  buyerUser?: {
    contatoId?: number | null
    nome?: string
    tipoUsuario: 'admin' | 'vendedor'
    papel: 'admin' | 'vendedor'
    markupPercentual: number
    acessoFinanceiro: boolean
    acessoPedidos: boolean
    acessoUsuarios: boolean
    acessoMarketplaceAnuncios: boolean
    acessoMarketplacePerguntas: boolean
    modoAcesso: 'empresa' | 'contato'
  }
}

type IndicatorDelegationState = {
  principalProfile: AccountProfile
  principalSessionToken: string
  client: SavedQuotePdfClient
  authorized: boolean
  relationshipId: number
}

type RouteResolution = {
  view: View
  productId: string | null
  productParentContextId?: string | null
  quickCheckoutProductId?: string | null
  quickCheckoutProductParentContextId?: string | null
  quickCheckoutQuantity?: number | null
  marketplaceListingId?: number | null
  marketplaceSellerId?: number | null
  accessTicket?: string | null
  accessMode?: 'ticket' | 'short'
  publicTicketCode?: string | null
  buyerAccessCode?: string | null
  legacyCheckoutLink?: string | null
  preCadastroRetomadaCode?: string | null
  categorySlugs?: string[]
  searchQuery?: string
  manufacturerBrands?: ManufacturerBrandFilterState[]
  vehicleFilter?: VehicleFilter | null
  vehicleSearchMode?: VehicleSearchRouteMode | null
  onlyAvailable?: boolean
  checkoutStep?: CheckoutRouteStep
  checkoutSource?: 'cart' | 'quote' | 'order'
  checkoutQuoteId?: number | null
  checkoutQuoteMode?: BusinessQuoteCheckoutMode | null
  checkoutOrderId?: number | null
  checkoutOrderMode?: 'direct' | 'edit' | null
  orderTrackingCode?: string | null
  supportSubjectSlug?: string | null
  orderDetailId?: number | null
  orderDetailToken?: string | null
  quoteDetailId?: number | null
  quoteDetailToken?: string | null
  attendanceDetailId?: number | null
  attendanceDetailToken?: string | null
  returnRequestStep?: ReturnRequestRouteStep
  returnRequestFlowType?: ReturnRequestRouteFlowType
}

type ManufacturerBrandFilterState = {
  id: number
  name: string
  logoUrl?: string | null
}

type VehicleSearchRouteMode = 'plate' | 'manual'

type PostLoginRedirect = {
  view: View
  productId?: string
  parentContextId?: string | null
}

type PostLoginOptions = {
  suppressSuccessToast?: boolean
  suppressErrorToast?: boolean
  postLoginRedirectOverride?: PostLoginRedirect | null
}

type AuthenticatedBuyerLoginResponse = Awaited<ReturnType<typeof login>>

type AuthenticatedAccountSnapshot = {
  identifier: string
  entidadeId?: number
  name?: string
  email?: string
  cnpj?: string
  document?: string
  stateRegistration?: string
  tipoPessoa?: number | null
  buyerSessionToken?: string
  buyerUser?: AccountProfile['buyerUser']
}

type MenuItem = {
  id: number
  descricao: string
  slug?: string
  children?: MenuItem[]
}

type CartAdditionInput = {
  product: Product
  quantity: number
  searchResultContext?: StorefrontSearchResultContext | null
}

type CartSyncOptions = {
  throwOnError?: boolean
}

type CartDrawerSource = 'default' | 'price-table'

type ProductListingSnapshot = {
  originView: 'products' | 'launches' | 'just_arrived'
  restoreKey: string
}

type ImageSearchState = {
  resultKey: string
  confidenceBand: 'high' | 'medium'
  message: string | null
}

type LegacyCheckoutRedirectPayload = {
  uid: number
  auth: string
  compraHash: string
}

const normalizeRouteProductValue = normalizeProductRouteValue

const isPriceTableView = (view: View) => view === 'price-table' || view === 'price-table-margin'
const PRICE_TABLE_PRODUCT_GROUP_NAVIGATION_EVENT = 'price-table:navigate-product-group'

const resolveProductParentContextId = (
  productId?: string | null,
  parentContextId?: string | null,
) => {
  const normalizedProductId = normalizeRouteProductValue(productId)
  const normalizedParentContextId = normalizeRouteProductValue(parentContextId)

  if (!normalizedParentContextId || normalizedParentContextId === normalizedProductId) {
    return null
  }

  return normalizedParentContextId
}

const resolveCheckoutAttemptIdFromLocationSearch = () => {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  return String(params.get('attempt_id') ?? params.get('attemptId') ?? '').trim()
}

const CART_SYNC_DEBOUNCE_MS = 400
const DEFAULT_CHECKOUT_STEP: CheckoutRouteStep = 'cart'
const PRODUCT_CHECKOUT_QUERY_PARAM = 'checkout'
const PRODUCT_CHECKOUT_QUERY_VALUES = new Set(['1', 'true', 'sim', 'yes'])
const PRODUCT_CHECKOUT_REDIRECT_STORAGE_KEY = 'brasiltec:produto-checkout-redirect'
const PRODUCT_CHECKOUT_QUICK_FLOW_STORAGE_KEY = 'brasiltec:produto-checkout-quick-flow'
const PRODUCT_CHECKOUT_QUICK_REQUEST_STORAGE_KEY = 'brasiltec:produto-checkout-quick-request'
const QUICK_CHECKOUT_PRODUCT_QUERY_PARAMS = ['produto_id', 'product_id', 'produto', 'product', 'codigo', 'sku']
const QUICK_CHECKOUT_QUANTITY_QUERY_PARAMS = ['quantidade', 'qtd', 'quantity', 'qty']
const AUTH_LOGIN_FLAG_STORAGE_KEY = 'is-logged-in'
const AUTH_LOGOUT_FLAG_STORAGE_KEY = 'auth-logout-requested'
const ACCOUNT_PROFILE_STORAGE_KEY = 'account-profile'
const INDICATOR_DELEGATION_STORAGE_KEY = 'indicator-client-delegation'
const CART_QUOTE_ORIGIN_STORAGE_KEY = 'cart-quote-origin-v1'
const BUYER_PRICE_TABLE_ACCESS_CODE_STORAGE_KEY_PREFIX = 'buyer-price-table-access-code'
const BUYER_ACCESS_REVALIDATE_INTERVAL_MS = 5 * 60 * 1000
const BUYER_ACCESS_REVALIDATE_MIN_GAP_MS = 60 * 1000
const PRICE_TABLE_CART_PANEL_REVEAL_DELAY_MS = 2200
const WIDE_PRODUCT_CART_PANEL_WIDTH = 416
const CHECKOUT_ROUTE_STEPS = new Set<CheckoutRouteStep>([
  'cart',
  'address',
  'shipping',
  'payment',
  'success',
])
const ACCOUNT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30
const STOREFRONT_PRODUCT_PAGE_SIZE = 12
const DEFAULT_PRODUCT_LIST_LIMIT = STOREFRONT_PRODUCT_PAGE_SIZE
const HOME_INITIAL_PRODUCTS_LIMIT = 28
const HOME_FEATURED_PRODUCTS_LIMIT = 28
const HOME_JUST_ARRIVED_PRODUCTS_LIMIT = 10
const SERVER_PAGINATED_PRODUCTS_PAGE_SIZE = STOREFRONT_PRODUCT_PAGE_SIZE
const TEXT_SEARCH_PRODUCTS_PAGE_SIZE = STOREFRONT_PRODUCT_PAGE_SIZE
const MANUFACTURER_BRAND_PRODUCTS_PAGE_SIZE = STOREFRONT_PRODUCT_PAGE_SIZE
const MANUFACTURER_BRAND_PRODUCTS_PAGE_SCAN_LIMIT = 20
const PRODUCT_PRICE_REFRESH_MAX_ATTEMPTS = 240
const PRODUCT_PRICE_REFRESH_FAST_ATTEMPTS = 40
const PRODUCT_PRICE_REFRESH_FAST_INTERVAL_MS = 750
const PRODUCT_PRICE_REFRESH_WARM_INTERVAL_MS = 1_500
const resolveProductPriceRefreshDelay = (attempts: number) => {
  if (attempts < PRODUCT_PRICE_REFRESH_FAST_ATTEMPTS) {
    return PRODUCT_PRICE_REFRESH_FAST_INTERVAL_MS
  }
  return PRODUCT_PRICE_REFRESH_WARM_INTERVAL_MS
}
const HOME_DEFERRED_CONTENT_DELAY_MS = 160
const HOME_PRODUCTS_REQUEST_TIMEOUT_MS = 5000
const INSTITUTIONAL_SECTIONS: InstitutionalSection[] = ['about', 'privacy', 'terms', 'careers', 'returns-policy']
const loadCartDrawer = () => import('./components/cart/CartDrawer')

const resolveBrowserStorageScopes = () => {
  if (typeof window === 'undefined') return []

  const storages: Storage[] = []
  try {
    if (window.sessionStorage) storages.push(window.sessionStorage)
  } catch {
    // Storage pode estar bloqueado pelo navegador.
  }
  try {
    if (window.localStorage) storages.push(window.localStorage)
  } catch {
    // Storage pode estar bloqueado pelo navegador.
  }

  return storages
}

const readProductCheckoutStorageValue = (key: string) => {
  for (const storage of resolveBrowserStorageScopes()) {
    try {
      const value = storage.getItem(key)
      if (value) return value
    } catch {
      // Tenta o proximo storage disponivel.
    }
  }

  return null
}

const writeProductCheckoutStorageValue = (key: string, value: string) => {
  resolveBrowserStorageScopes().forEach((storage) => {
    try {
      storage.setItem(key, value)
    } catch {
      // O outro storage continua servindo como redundancia.
    }
  })
}

const removeProductCheckoutStorageValue = (key: string) => {
  resolveBrowserStorageScopes().forEach((storage) => {
    try {
      storage.removeItem(key)
    } catch {
      // Ignora bloqueios de storage.
    }
  })
}

const shouldOpenProductCheckoutFromSearch = (search: string | null | undefined) => {
  const params = new URLSearchParams(search ?? '')
  const checkoutParam = String(params.get(PRODUCT_CHECKOUT_QUERY_PARAM) ?? '').trim().toLowerCase()
  return PRODUCT_CHECKOUT_QUERY_VALUES.has(checkoutParam)
}

const getFirstSearchParamValue = (params: URLSearchParams, names: string[]) => {
  for (const name of names) {
    const value = String(params.get(name) ?? '').trim()
    if (value) return value
  }

  return null
}

const resolveQuickCheckoutProductIdsFromSearch = (params: URLSearchParams) => {
  const productIds: string[] = []

  QUICK_CHECKOUT_PRODUCT_QUERY_PARAMS.forEach((name) => {
    const value = normalizeRouteProductValue(params.get(name))
    if (value && !productIds.includes(value)) {
      productIds.push(value)
    }
  })

  return productIds
}

const resolveQuickCheckoutProductIdFromSearch = (params: URLSearchParams) => (
  resolveQuickCheckoutProductIdsFromSearch(params)[0] ?? null
)

const resolveQuickCheckoutQuantityFromSearch = (params: URLSearchParams) => {
  const quantity = parsePositiveIntegerLike(getFirstSearchParamValue(params, QUICK_CHECKOUT_QUANTITY_QUERY_PARAMS))
  if (!quantity) return 1

  return Math.min(CART_ITEM_QUANTITY_MAX, Math.max(1, quantity))
}

type ProductCheckoutQuickRequest = {
  productIds: string[]
  quantity: number
  parentContextId: string | null
}

const normalizeProductCheckoutQuickRequest = (value: unknown): ProductCheckoutQuickRequest | null => {
  if (!value || typeof value !== 'object') return null

  const input = value as {
    productIds?: unknown
    quantity?: unknown
    parentContextId?: unknown
  }
  const productIds = Array.isArray(input.productIds)
    ? input.productIds
      .map((item) => normalizeRouteProductValue(item))
      .filter((item): item is string => Boolean(item))
      .filter((item, index, items) => items.indexOf(item) === index)
    : []

  if (productIds.length === 0) return null

  const quantity = Math.min(
    CART_ITEM_QUANTITY_MAX,
    Math.max(1, Math.trunc(Number(input.quantity) || 1))
  )

  return {
    productIds,
    quantity,
    parentContextId: normalizeRouteProductValue(input.parentContextId) ?? null,
  }
}

const buildProductCheckoutQuickRequestFromSearch = (
  search: string | null | undefined
): ProductCheckoutQuickRequest | null => {
  const params = new URLSearchParams(search ?? '')
  const productIds = resolveQuickCheckoutProductIdsFromSearch(params)
  if (productIds.length === 0) return null

  return {
    productIds,
    quantity: resolveQuickCheckoutQuantityFromSearch(params),
    parentContextId: normalizeRouteProductValue(params.get('pai')) ?? null,
  }
}

const resolveProductCheckoutQuickRequestFromRedirectPath = (
  redirectPath: string | null | undefined
): ProductCheckoutQuickRequest | null => {
  const rawRedirectPath = String(redirectPath ?? '').trim()
  if (!rawRedirectPath) return null

  try {
    const parsedRedirectPath = new URL(rawRedirectPath, 'https://storefront.local')
    const pathname = normalizePathname(parsedRedirectPath.pathname)
    if (pathname !== VIEW_PATHS['quick-checkout'] && pathname !== LEGACY_QUICK_CHECKOUT_PATH) {
      return null
    }

    return buildProductCheckoutQuickRequestFromSearch(parsedRedirectPath.search)
  } catch {
    return null
  }
}

const buildProductCheckoutRequestKey = (pathname: string, search: string) => {
  return `${normalizePathname(pathname)}${search}`
}

const resolveProductCheckoutRequestKeyFromRedirectPath = (redirectPath: string | null | undefined) => {
  const rawRedirectPath = String(redirectPath ?? '').trim()
  if (!rawRedirectPath) return null

  try {
    const parsedRedirectPath = new URL(rawRedirectPath, 'https://storefront.local')
    const pathname = normalizePathname(normalizeLegacyProductDetailPathname(parsedRedirectPath.pathname))
    if (!pathname.startsWith(`${PRODUCT_DETAIL_BASE}/`)) return null
    if (!shouldOpenProductCheckoutFromSearch(parsedRedirectPath.search)) return null
    return buildProductCheckoutRequestKey(pathname, parsedRedirectPath.search)
  } catch {
    return null
  }
}

const persistProductCheckoutRedirectRequestKey = (redirectPath: string | null | undefined) => {
  const requestKey = resolveProductCheckoutRequestKeyFromRedirectPath(redirectPath)
  if (!requestKey || typeof window === 'undefined') return

  writeProductCheckoutStorageValue(PRODUCT_CHECKOUT_REDIRECT_STORAGE_KEY, requestKey)
}

const readProductCheckoutQuickFlowFlag = () => {
  if (typeof window === 'undefined') return false

  return readProductCheckoutStorageValue(PRODUCT_CHECKOUT_QUICK_FLOW_STORAGE_KEY) === '1'
}

const persistProductCheckoutQuickFlowFlag = (active: boolean) => {
  if (typeof window === 'undefined') return

  if (active) {
    writeProductCheckoutStorageValue(PRODUCT_CHECKOUT_QUICK_FLOW_STORAGE_KEY, '1')
  } else {
    removeProductCheckoutStorageValue(PRODUCT_CHECKOUT_QUICK_FLOW_STORAGE_KEY)
  }
}

const readProductCheckoutQuickRequest = () => {
  if (typeof window === 'undefined') return null

  try {
    const raw = readProductCheckoutStorageValue(PRODUCT_CHECKOUT_QUICK_REQUEST_STORAGE_KEY)
    if (!raw) return null
    return normalizeProductCheckoutQuickRequest(JSON.parse(raw))
  } catch {
    return null
  }
}

const persistProductCheckoutQuickRequest = (request: ProductCheckoutQuickRequest | null) => {
  if (typeof window === 'undefined') return

  try {
    const normalizedRequest = normalizeProductCheckoutQuickRequest(request)
    if (!normalizedRequest) {
      removeProductCheckoutStorageValue(PRODUCT_CHECKOUT_QUICK_REQUEST_STORAGE_KEY)
      return
    }

    writeProductCheckoutStorageValue(
      PRODUCT_CHECKOUT_QUICK_REQUEST_STORAGE_KEY,
      JSON.stringify(normalizedRequest)
    )
  } catch {
    // Query string continua sendo o fallback quando o storage estiver indisponivel.
  }
}

const clearProductCheckoutQuickRequest = () => {
  if (typeof window === 'undefined') return

  removeProductCheckoutStorageValue(PRODUCT_CHECKOUT_QUICK_REQUEST_STORAGE_KEY)
}

const persistProductCheckoutIntentFromRedirectPath = (redirectPath: string | null | undefined) => {
  persistProductCheckoutRedirectRequestKey(redirectPath)

  const quickRequest = resolveProductCheckoutQuickRequestFromRedirectPath(redirectPath)
  if (!quickRequest) return

  persistProductCheckoutQuickRequest(quickRequest)
  persistProductCheckoutQuickFlowFlag(true)
}

const consumeStoredProductCheckoutRequestKey = (pathname: string) => {
  if (typeof window === 'undefined') return null

  try {
    const storedRequestKey = readProductCheckoutStorageValue(PRODUCT_CHECKOUT_REDIRECT_STORAGE_KEY)
    if (!storedRequestKey) return null

    const storedPathname = normalizePathname(storedRequestKey.split('?')[0] ?? '')
    const currentPathname = normalizePathname(normalizeLegacyProductDetailPathname(pathname))
    if (storedPathname !== currentPathname) return null

    removeProductCheckoutStorageValue(PRODUCT_CHECKOUT_REDIRECT_STORAGE_KEY)
    return storedRequestKey
  } catch {
    return null
  }
}

const normalizeProductCheckoutIdentity = (value?: string | null) => (
  String(value ?? '').trim().toLowerCase()
)

const productMatchesProductCheckoutIdentity = (
  product: Pick<Product, 'id' | 'code' | 'manufacturerCode' | 'navigationTargetProductId'>,
  normalizedIdentity: string,
) => {
  if (!normalizedIdentity) return false

  return [
    product.id,
    product.code,
    product.manufacturerCode,
    product.navigationTargetProductId,
  ].some((candidate) => normalizeProductCheckoutIdentity(candidate) === normalizedIdentity)
}

const canUseProductForCheckoutLink = (product: Product) => !requiresProductVariationSelection(product)

function resolveHomeRequestWithin<T>(
  request: Promise<T>,
  fallback: T,
  label: string
): Promise<T> {
  if (typeof window === 'undefined') {
    return request.catch((error) => {
      console.error(label, error)
      return fallback
    })
  }

  let timeoutId: number | null = null
  const timeout = new Promise<T>((resolve) => {
    timeoutId = window.setTimeout(() => {
      console.warn(`${label}: limite de ${HOME_PRODUCTS_REQUEST_TIMEOUT_MS}ms atingido.`)
      resolve(fallback)
    }, HOME_PRODUCTS_REQUEST_TIMEOUT_MS)
  })

  return Promise.race([
    request.catch((error) => {
      console.error(label, error)
      return fallback
    }),
    timeout,
  ]).finally(() => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  })
}

const mergeCartItemsByProductId = (items: CartItem[]) => {
  const mergedItems: CartItem[] = []
  const indexByProductId = new Map<string, number>()

  items.forEach((item) => {
    const productId = item.product.id
    if (!productId) return

    const normalizedQuantity = Math.max(0, Math.trunc(item.quantity))
    if (normalizedQuantity <= 0) return

    const existingIndex = indexByProductId.get(productId)
    if (typeof existingIndex === 'number') {
      const existingItem = mergedItems[existingIndex]
      const nextQuantity = Math.min(CART_ITEM_QUANTITY_MAX, existingItem.quantity + normalizedQuantity)
      const existingLockedSubtotal = typeof existingItem.lockedSubtotal === 'number' ? existingItem.lockedSubtotal : 0
      const itemLockedSubtotal = typeof item.lockedSubtotal === 'number' ? item.lockedSubtotal : 0
      const existingLockedDiscount = typeof existingItem.lockedDiscount === 'number' ? existingItem.lockedDiscount : 0
      const itemLockedDiscount = typeof item.lockedDiscount === 'number' ? item.lockedDiscount : 0
      const existingLockedIpi = typeof existingItem.lockedIpi === 'number' ? existingItem.lockedIpi : 0
      const itemLockedIpi = typeof item.lockedIpi === 'number' ? item.lockedIpi : 0
      const existingLockedDifal = typeof existingItem.lockedDifal === 'number' ? existingItem.lockedDifal : 0
      const itemLockedDifal = typeof item.lockedDifal === 'number' ? item.lockedDifal : 0
      const existingLockedTaxes = typeof existingItem.lockedTaxes === 'number' ? existingItem.lockedTaxes : (existingLockedIpi + existingLockedDifal)
      const itemLockedTaxes = typeof item.lockedTaxes === 'number' ? item.lockedTaxes : (itemLockedIpi + itemLockedDifal)
      const mergedLockedSubtotal = existingLockedSubtotal + itemLockedSubtotal
      const mergedLockedDiscount = existingLockedDiscount + itemLockedDiscount
      const mergedLockedIpi = existingLockedIpi + itemLockedIpi
      const mergedLockedDifal = existingLockedDifal + itemLockedDifal
      const mergedLockedTaxes = existingLockedTaxes + itemLockedTaxes
      const mergedLockedTotal = mergedLockedSubtotal + mergedLockedTaxes

      mergedItems[existingIndex] = {
        ...existingItem,
        ...item,
        product: item.product,
        quantity: nextQuantity,
        lockedUnitPrice: item.lockedUnitPrice ?? existingItem.lockedUnitPrice,
        lockedSubtotal: existingItem.checkoutSource === 'quote' || item.checkoutSource === 'quote'
          ? mergedLockedSubtotal
          : item.lockedSubtotal ?? existingItem.lockedSubtotal,
        lockedDiscount: existingItem.checkoutSource === 'quote' || item.checkoutSource === 'quote'
          ? mergedLockedDiscount
          : item.lockedDiscount ?? existingItem.lockedDiscount,
        lockedIpi: existingItem.checkoutSource === 'quote' || item.checkoutSource === 'quote'
          ? mergedLockedIpi
          : item.lockedIpi ?? existingItem.lockedIpi,
        lockedDifal: existingItem.checkoutSource === 'quote' || item.checkoutSource === 'quote'
          ? mergedLockedDifal
          : item.lockedDifal ?? existingItem.lockedDifal,
        lockedTaxes: existingItem.checkoutSource === 'quote' || item.checkoutSource === 'quote'
          ? mergedLockedTaxes
          : item.lockedTaxes ?? existingItem.lockedTaxes,
        lockedTotal: existingItem.checkoutSource === 'quote' || item.checkoutSource === 'quote'
          ? mergedLockedTotal
          : item.lockedTotal ?? existingItem.lockedTotal,
      }
      return
    }

    indexByProductId.set(productId, mergedItems.length)
    mergedItems.push({
      ...item,
      product: item.product,
      quantity: Math.min(CART_ITEM_QUANTITY_MAX, normalizedQuantity),
    })
  })

  return mergedItems
}

const CheckoutHeader = lazy(() =>
  import('./components/checkout').then((module) => ({ default: module.CheckoutHeader }))
)
const FloatingHelpMenu = lazy(() =>
  import('./components/FloatingHelpMenu').then((module) => ({ default: module.FloatingHelpMenu }))
)
const Footer = lazy(() =>
  import('./components/Footer').then((module) => ({ default: module.Footer }))
)
const CookieConsentBanner = lazy(() =>
  import('./components/CookieConsentBanner').then((module) => ({ default: module.CookieConsentBanner }))
)
const OrderTrackingView = lazy(() =>
  import('./components/OrderTrackingView').then((module) => ({ default: module.OrderTrackingView }))
)
const CheckoutOrderTrackingView = lazy(() =>
  import('./components/CheckoutOrderTrackingView').then((module) => ({ default: module.CheckoutOrderTrackingView }))
)
const ProductsView = lazy(() =>
  import('./components/ProductsView').then((module) => ({ default: module.ProductsView }))
)
const SearchResultsView = lazy(() =>
  import('./components/search/SearchResultsView').then((module) => ({ default: module.SearchResultsView }))
)
const ProductDetailView = lazy(() =>
  import('./components/ProductDetailView').then((module) => ({ default: module.ProductDetailView }))
)
const LoginView = lazy(() =>
  import('./components/LoginView').then((module) => ({ default: module.LoginView }))
)
const RegisterView = lazy(() =>
  import('./components/RegisterView').then((module) => ({ default: module.RegisterView }))
)
const CheckoutView = lazy(() =>
  import('./components/checkout').then((module) => ({ default: module.CheckoutView }))
)
const QuickCheckoutView = lazy(() =>
  import('./components/checkout/QuickCheckoutView').then((module) => ({ default: module.QuickCheckoutView }))
)
const InstitutionalView = lazy(() =>
  import('./components/InstitutionalView').then((module) => ({ default: module.InstitutionalView }))
)
const BlogExperience = lazy(() =>
  import('./apps/blog').then((module) => ({ default: module.BlogExperience }))
)
const CompareView = lazy(() =>
  import('./components/compare').then((module) => ({ default: module.CompareView }))
)
const CartDrawer = lazy(() =>
  loadCartDrawer().then((module) => ({ default: module.CartDrawer }))
)
const CategorySidebar = lazy(() =>
  import('./components/CategorySidebar').then((module) => ({ default: module.CategorySidebar }))
)
const AffiliatesPage = lazy(() =>
  import('./pages/account/AffiliatesPage').then((module) => ({ default: module.AffiliatesPage }))
)
const IndicatorPortalPage = lazy(() =>
  import('./pages/account/IndicatorPortalPage').then((module) => ({ default: module.IndicatorPortalPage }))
)
const BenefitsPage = lazy(() =>
  import('./pages/account/BenefitsPage').then((module) => ({ default: module.BenefitsPage }))
)
const CashbackPage = lazy(() =>
  import('./pages/account/CashbackPage').then((module) => ({ default: module.CashbackPage }))
)
const DashboardPage = lazy(() =>
  import('./pages/account/DashboardPage').then((module) => ({ default: module.DashboardPage }))
)
const AttendancesPage = lazy(() =>
  import('./pages/account/AttendancesPage').then((module) => ({ default: module.AttendancesPage }))
)
const AttendanceDetailPage = lazy(() =>
  import('./pages/account/AttendanceDetailPage').then((module) => ({ default: module.AttendanceDetailPage }))
)
const PublicTicketPage = lazy(() =>
  import('./pages/PublicTicketPage').then((module) => ({ default: module.PublicTicketPage }))
)
const ProfilePage = lazy(() =>
  import('./pages/account/ProfilePage').then((module) => ({ default: module.ProfilePage }))
)
const AccountPasswordChangePage = lazy(() =>
  import('./pages/account/AccountPasswordChangePage').then((module) => ({ default: module.AccountPasswordChangePage }))
)
const UsersPage = lazy(() =>
  import('./pages/account/UsersPage').then((module) => ({ default: module.UsersPage }))
)
const BusinessPage = lazy(() =>
  import('./pages/account/BusinessPage').then((module) => ({ default: module.BusinessPage }))
)
const MarketplaceListingsPage = lazy(() =>
  import('./pages/account/MarketplaceListingsPage').then((module) => ({ default: module.MarketplaceListingsPage }))
)
const MarketplaceProfilePage = lazy(() =>
  import('./pages/account/MarketplaceProfilePage').then((module) => ({ default: module.MarketplaceProfilePage }))
)
const MarketplaceQuestionsPage = lazy(() =>
  import('./pages/account/MarketplaceQuestionsPage').then((module) => ({ default: module.MarketplaceQuestionsPage }))
)
const MarketplacePerformancePage = lazy(() =>
  import('./pages/account/MarketplacePerformancePage').then((module) => ({ default: module.MarketplacePerformancePage }))
)
const MarketplaceListingDetailPage = lazy(() =>
  import('./pages/MarketplaceListingDetailPage').then((module) => ({ default: module.MarketplaceListingDetailPage }))
)
const MarketplaceSellerPage = lazy(() =>
  import('./pages/MarketplaceSellerPage').then((module) => ({ default: module.MarketplaceSellerPage }))
)
const AddressesPage = lazy(() =>
  import('./pages/account/AddressesPage').then((module) => ({ default: module.AddressesPage }))
)
const FavoritesPage = lazy(() =>
  import('./pages/account/FavoritesPage').then((module) => ({ default: module.FavoritesPage }))
)
const FinancePage = lazy(() =>
  import('./pages/account/FinancePage').then((module) => ({ default: module.FinancePage }))
)
const OrdersListPage = lazy(() =>
  import('./pages/account/OrdersListPage').then((module) => ({ default: module.OrdersListPage }))
)
const OrderDetailPage = lazy(() =>
  import('./pages/account/OrderDetailPage').then((module) => ({ default: module.OrderDetailPage }))
)
const QuotesListPage = lazy(() =>
  import('./pages/account/QuotesListPage').then((module) => ({ default: module.QuotesListPage }))
)
const QuoteDetailPage = lazy(() =>
  import('./pages/account/QuoteDetailPage').then((module) => ({ default: module.QuoteDetailPage }))
)
const ReturnsPage = lazy(() =>
  import('./pages/account/ReturnsPage').then((module) => ({ default: module.ReturnsPage }))
)
const loadPriceTablePageModule = () => import('./pages/account/PriceTablePage')
const PriceTablePage = lazy(() =>
  loadPriceTablePageModule().then((module) => ({ default: module.PriceTablePage }))
)
const PriceTableMarginPage = lazy(() =>
  loadPriceTablePageModule().then((module) => ({ default: module.PriceTableMarginPage }))
)
const StoreNetworkMarketplacePage = lazy(() => import('./features/store-network/StoreNetworkMarketplacePage'))
const StoreNetworkAccessPage = lazy(() =>
  import('./features/store-network/StoreNetworkMarketplacePage').then((module) => ({ default: module.StoreNetworkAccessPage }))
)
const ReturnRequestView = lazy(() =>
  import('./components/returns/ReturnRequestView').then((module) => ({ default: module.ReturnRequestView }))
)
const SupportView = lazy(() =>
  import('./components/SupportView').then((module) => ({ default: module.SupportView }))
)
const WhatsappSupportView = lazy(() =>
  import('./components/WhatsappSupportView').then((module) => ({ default: module.WhatsappSupportView }))
)
const ServiceEvaluationPage = lazy(() =>
  import('./pages/ServiceEvaluationPage').then((module) => ({ default: module.ServiceEvaluationPage }))
)

const preloadImageAsset = (src: string | null | undefined) => {
  if (typeof document === 'undefined') return

  const normalizedSrc = src?.trim()
  if (!normalizedSrc) return

  const existingPreload = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="preload"][as="image"]')
  ).some((link) => link.getAttribute('href') === normalizedSrc)

  if (existingPreload) return

  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = normalizedSrc
  link.setAttribute('fetchpriority', 'high')
  document.head.appendChild(link)
}

function CheckoutHeaderSkeleton() {
  return (
    <div className="border-b border-border/60 bg-background/95">
      <div className="container mx-auto flex h-[72px] items-center justify-between gap-4 px-4">
        <Skeleton className="h-10 w-40 rounded-full" />
        <Skeleton className="h-4 w-32 rounded-full" />
        <Skeleton className="h-10 w-28 rounded-full" />
      </div>
    </div>
  )
}

function PageGridSkeleton({ cards = 6 }: { cards?: number }) {
  return (
    <div className="flex-1 bg-background">
      <div className="container mx-auto space-y-6 px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Skeleton className="h-5 w-48" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-10 w-44 rounded-xl" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: cards }).map((_, index) => (
            <div
              key={`page-grid-skeleton-${index}`}
              className="overflow-hidden rounded-2xl border border-border/60 bg-card p-4 shadow-sm"
            >
              <Skeleton className="aspect-square w-full rounded-xl" />
              <div className="mt-4 space-y-3">
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-24 w-full rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

type AccountPageSkeletonVariant = 'default' | 'dashboard' | 'list' | 'simple' | 'benefits' | 'favorites' | 'tracking' | 'price-table' | 'support'

function AccountBreadcrumbSkeleton({ segments = 2 }: { segments?: 1 | 2 }) {
  return (
    <div className="bg-muted/30">
      <div className="container mx-auto flex items-center gap-2 px-4 py-3">
        <Skeleton className="h-4 w-16 rounded-full" />
        <Skeleton className="h-3 w-3 rounded-full" />
        <Skeleton className="h-4 w-24 rounded-full" />
        {segments === 2 ? (
          <>
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-4 w-32 rounded-full" />
          </>
        ) : null}
      </div>
    </div>
  )
}

function AccountTitleBlockSkeleton({ titleWidth = 'w-56', subtitleWidth = 'w-80' }: { titleWidth?: string; subtitleWidth?: string }) {
  return (
    <div className="space-y-2">
      <Skeleton className={`h-10 rounded-xl ${titleWidth}`} />
      <Skeleton className={`h-5 rounded-xl ${subtitleWidth}`} />
    </div>
  )
}

function AccountShortcutSectionSkeleton({ items = 4 }: { items?: number }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-7 w-7 rounded-full" />
        <Skeleton className="h-6 w-36 rounded-xl" />
      </div>
      <div className="overflow-hidden rounded-[20px] border border-border/60 bg-card">
        {Array.from({ length: items }).map((_, index) => (
          <div
            key={`account-shortcut-skeleton-${index}`}
            className={`flex items-center gap-3 px-3 py-3 ${index > 0 ? 'border-t border-border/60' : ''}`}
          >
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-4 flex-1 rounded-full" />
            <Skeleton className="h-4 w-4 rounded-full" />
          </div>
        ))}
      </div>
    </section>
  )
}

function AccountListCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
      <div className="border-b border-border/60 px-4 py-3 md:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-5 w-24 rounded-full" />
            <Skeleton className="h-5 w-32 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
      </div>
      <div className="space-y-4 px-4 py-5 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-72 rounded-xl" />
            <Skeleton className="h-5 w-96 max-w-full rounded-xl" />
          </div>
          <Skeleton className="h-11 w-48 rounded-xl" />
        </div>
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    </div>
  )
}

function AccountPageSkeleton({ variant = 'default' }: { variant?: AccountPageSkeletonVariant }) {
  if (variant === 'dashboard') {
    return (
      <>
        <AccountBreadcrumbSkeleton segments={1} />
        <div className="min-h-screen bg-white py-6 sm:py-8">
          <div className="mx-auto max-w-[960px] space-y-7 px-4 sm:px-6">
            <AccountTitleBlockSkeleton titleWidth="w-52" subtitleWidth="w-44" />
            <div className="space-y-7">
              <AccountShortcutSectionSkeleton />
              <AccountShortcutSectionSkeleton items={3} />
              <AccountShortcutSectionSkeleton />
            </div>
          </div>
        </div>
      </>
    )
  }

  if (variant === 'list') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-background py-6">
          <div className="container mx-auto space-y-4 px-4">
            <AccountTitleBlockSkeleton titleWidth="w-40" subtitleWidth="w-72" />
            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-11 w-full rounded-full sm:w-[220px]" />
              <Skeleton className="h-11 w-[210px] rounded-full" />
              <Skeleton className="h-11 w-[210px] rounded-full" />
            </div>
            <div className="space-y-4">
              <AccountListCardSkeleton />
              <AccountListCardSkeleton />
            </div>
          </div>
        </div>
      </>
    )
  }

  if (variant === 'benefits') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-background py-8">
          <div className="container mx-auto space-y-6 px-4">
            <AccountTitleBlockSkeleton titleWidth="w-48" subtitleWidth="w-80" />
            <Skeleton className="h-40 w-full rounded-3xl" />
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`account-benefit-skeleton-${index}`} className="rounded-2xl border border-border/60 bg-card p-5">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-12 w-12 rounded-2xl" />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-5 w-40 rounded-xl" />
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </div>
                      <Skeleton className="h-4 w-full rounded-xl" />
                      <Skeleton className="h-4 w-2/3 rounded-xl" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    )
  }

  if (variant === 'favorites') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-background">
          <div className="border-b bg-card">
            <div className="container mx-auto space-y-4 px-4 py-4 sm:p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <Skeleton className="h-5 w-64 rounded-xl" />
                <div className="flex w-full items-center gap-3 md:w-auto">
                  <Skeleton className="h-10 flex-1 rounded-xl md:w-40" />
                  <Skeleton className="h-10 w-36 rounded-xl" />
                </div>
              </div>
            </div>
          </div>
          <div className="container mx-auto px-4 py-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={`account-favorites-skeleton-${index}`} className="overflow-hidden rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
                  <Skeleton className="aspect-square w-full rounded-xl" />
                  <div className="mt-4 space-y-3">
                    <Skeleton className="h-4 w-11/12 rounded-xl" />
                    <Skeleton className="h-4 w-3/4 rounded-xl" />
                    <Skeleton className="h-3 w-20 rounded-full" />
                    <Skeleton className="h-24 w-full rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    )
  }

  if (variant === 'tracking') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-[linear-gradient(180deg,#FFF7EE_0%,#FFFDF9_22%,#F8FAFC_100%)]">
          <section className="px-4 pb-6 pt-7">
            <div className="mx-auto max-w-5xl space-y-6">
              <div className="rounded-[1.8rem] border border-slate-100 bg-white p-5 shadow-[0_25px_60px_-30px_rgba(15,23,42,0.18)]">
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px]">
                  <Skeleton className="h-12 w-full rounded-[1rem]" />
                  <Skeleton className="h-12 w-full rounded-[1rem]" />
                  <Skeleton className="h-12 w-full rounded-[1rem]" />
                </div>
              </div>
              <div className="grid gap-6 lg:grid-cols-2">
                <Skeleton className="h-56 w-full rounded-[2rem]" />
                <Skeleton className="h-56 w-full rounded-[2rem]" />
              </div>
              <Skeleton className="h-[320px] w-full rounded-[2rem]" />
            </div>
          </section>
        </div>
      </>
    )
  }

  if (variant === 'price-table') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-[#f0f2f5]">
          <div className="hidden xl:fixed xl:inset-y-0 xl:left-0 xl:block xl:w-[276px] xl:bg-[#0d1117]">
            <div className="space-y-4 border-r border-white/10 px-6 py-8">
              <Skeleton className="h-8 w-28 rounded-xl bg-white/10" />
              <Skeleton className="h-4 w-20 rounded-full bg-white/10" />
              <Skeleton className="h-10 w-full rounded-xl bg-white/10" />
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={`price-table-sidebar-skeleton-${index}`} className="h-10 w-full rounded-xl bg-white/10" />
              ))}
            </div>
          </div>
          <main className="min-h-screen xl:pl-[276px]">
            <div className="mx-auto flex min-h-screen max-w-[1540px] flex-col items-center gap-8 px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
              <div className="w-full max-w-[1414px] overflow-hidden bg-white p-8 shadow-[0_30px_80px_-34px_rgba(15,23,42,0.18)]">
                <div className="space-y-5">
                  <Skeleton className="h-5 w-36 rounded-full" />
                  <Skeleton className="h-12 w-80 rounded-2xl" />
                  <Skeleton className="h-5 w-3/4 rounded-full" />
                  <Skeleton className="h-24 w-full rounded-[1.75rem]" />
                </div>
              </div>
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={`price-table-card-skeleton-${index}`}
                  className="w-full max-w-[1414px] overflow-hidden bg-white p-8 shadow-[0_30px_80px_-34px_rgba(15,23,42,0.18)]"
                >
                  <div className="space-y-5">
                    <Skeleton className="h-4 w-28 rounded-full" />
                    <Skeleton className="h-10 w-2/5 rounded-2xl" />
                    <Skeleton className="h-4 w-4/5 rounded-full" />
                  </div>
                  <div className="mt-6 space-y-3">
                    {Array.from({ length: 4 }).map((__, rowIndex) => (
                      <Skeleton key={`price-table-row-skeleton-${index}-${rowIndex}`} className="h-14 w-full rounded-none" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </>
    )
  }

  if (variant === 'support') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-white pb-24">
          <section className="px-4 pb-14 pt-16">
            <div className="mx-auto max-w-5xl space-y-8">
              <div className="space-y-3 text-center">
                <Skeleton className="mx-auto h-10 w-64 rounded-xl" />
                <Skeleton className="mx-auto h-5 w-[32rem] max-w-full rounded-xl" />
              </div>
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-[0_25px_60px_-30px_rgba(15,23,42,0.18)]">
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px]">
                  <Skeleton className="h-12 w-full rounded-[1rem]" />
                  <Skeleton className="h-12 w-full rounded-[1rem]" />
                  <Skeleton className="h-12 w-full rounded-[1rem]" />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={`account-support-skeleton-${index}`} className="h-44 w-full rounded-[1.75rem]" />
                ))}
              </div>
            </div>
          </section>
        </div>
      </>
    )
  }

  if (variant === 'simple') {
    return (
      <>
        <AccountBreadcrumbSkeleton />
        <div className="min-h-screen bg-background py-8">
          <div className="container mx-auto space-y-6 px-4">
            <AccountTitleBlockSkeleton titleWidth="w-48" subtitleWidth="w-80" />
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-56 w-full rounded-2xl" />
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="flex-1 bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-10 w-56" />
          <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="space-y-3 rounded-3xl border border-border/60 bg-card p-5">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={`account-nav-skeleton-${index}`} className="h-11 w-full rounded-xl" />
              ))}
            </div>
            <div className="space-y-4 rounded-3xl border border-border/60 bg-card p-5">
              <Skeleton className="h-12 w-72 rounded-xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CheckoutPageSkeleton() {
  return (
    <div className="flex-1 bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-4">
            <Skeleton className="h-12 w-72 rounded-xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-48 w-full rounded-2xl" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-72 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ProductDetailPageSkeleton() {
  return (
    <div className="flex-1 bg-background">
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="mb-6 h-4 w-56" />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            <div className="space-y-4 lg:col-span-3">
              <Skeleton className="aspect-[20/17] w-full rounded-lg" />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={`product-gallery-skeleton-${index}`} className="aspect-[4/3] w-full rounded-md" />
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <Skeleton className="h-8 w-11/12" />
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-4 w-2/5" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CategorySidebarSkeleton() {
  return (
    <div className="space-y-3 py-2">
      <Skeleton className="h-8 w-48" />
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={`category-sidebar-skeleton-${index}`} className="h-12 w-full rounded-xl" />
      ))}
    </div>
  )
}

const safeDecodeCookieValue = (value: string) => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' ')).trim()
  } catch {
    return value.trim()
  }
}

const getCookieMap = (): Record<string, string> => {
  if (typeof document === 'undefined') return {}
  if (!document.cookie) return {}

  return document.cookie.split(';').reduce<Record<string, string>>((acc, rawPart) => {
    const part = rawPart.trim()
    if (!part) return acc
    const separatorIndex = part.indexOf('=')
    if (separatorIndex <= 0) return acc
    const key = part.slice(0, separatorIndex).trim()
    const value = safeDecodeCookieValue(part.slice(separatorIndex + 1))
    if (key && value) {
      acc[key] = value
    }
    return acc
  }, {})
}

const getCookieValueByKeys = (cookies: Record<string, string>, keys: string[]) => {
  for (const key of keys) {
    const value = cookies[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

const getCookieValueByPattern = (
  cookies: Record<string, string>,
  predicate: (normalizedKey: string) => boolean
) => {
  for (const [key, value] of Object.entries(cookies)) {
    const normalizedKey = key.toLocaleLowerCase('pt-BR')
    if (!predicate(normalizedKey)) continue
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

const parseEntityId = (value: string | undefined) => {
  if (!value) return undefined
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : undefined
}

const parseBuyerPersonType = (value: string | undefined) => {
  if (!value) return undefined
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined
  return Math.trunc(parsed)
}

const normalizeBuyerAccessCode = (value: unknown) => {
  const normalized = String(value ?? '').trim()
  return /^[A-Za-z0-9_-]{6,128}$/.test(normalized) ? normalized : ''
}

const buildBuyerPriceTableAccessCodeStorageKey = (entidadeId: number) => (
  `${BUYER_PRICE_TABLE_ACCESS_CODE_STORAGE_KEY_PREFIX}:${Math.trunc(entidadeId)}`
)

const readPersistedBuyerPriceTableAccessCode = (entidadeId?: number | null) => {
  if (typeof window === 'undefined') return ''
  if (typeof entidadeId !== 'number' || !Number.isFinite(entidadeId) || entidadeId <= 0) return ''

  try {
    return normalizeBuyerAccessCode(
      window.localStorage.getItem(buildBuyerPriceTableAccessCodeStorageKey(entidadeId))
    )
  } catch {
    return ''
  }
}

const persistBuyerPriceTableAccessCode = (entidadeId?: number | null, code?: string | null) => {
  if (typeof window === 'undefined') return
  if (typeof entidadeId !== 'number' || !Number.isFinite(entidadeId) || entidadeId <= 0) return

  const normalizedCode = normalizeBuyerAccessCode(code)
  if (!normalizedCode) return

  try {
    window.localStorage.setItem(buildBuyerPriceTableAccessCodeStorageKey(entidadeId), normalizedCode)
  } catch {
    // Ignore localStorage write errors.
  }
}

const resolveAccountProfileFromCookies = (): Partial<AccountProfile> => {
  const cookies = getCookieMap()
  if (Object.keys(cookies).length === 0) return {}

  const corporateName = getCookieValueByKeys(cookies, [
    'b2b_account_corporate_name',
    'comprador_razao',
    'comprador_razao_social',
    'cliente_razao',
    'cliente_razao_social',
    'razao_social',
    'razao',
  ]) || getCookieValueByPattern(cookies, (key) => {
    const hasCorporateName = key.includes('razao') || key.includes('corporate_name')
    const hasAccountContext = key.includes('comprador')
      || key.includes('cliente')
      || key.includes('account')
      || key.includes('perfil')
      || key.includes('usuario')
      || key === 'razao_social'
      || key === 'razao'
    return hasCorporateName && hasAccountContext
  })

  const name = getCookieValueByKeys(cookies, [
    'b2b_account_name',
    'comprador_nome',
    'compradorName',
    'cliente_nome',
    'clienteName',
    'loja_virtual_comprador_nome',
    'nome_cliente',
  ]) || getCookieValueByPattern(cookies, (key) => {
    const hasName = key.includes('nome') || key.includes('name')
    const hasAccountContext = key.includes('comprador')
      || key.includes('cliente')
      || key.includes('account')
      || key.includes('perfil')
      || key.includes('usuario')
      || key.includes('user')
    return hasName && hasAccountContext
  })

  const email = getCookieValueByKeys(cookies, [
    'b2b_account_email',
    'comprador_email',
    'compradorEmail',
    'cliente_email',
    'clienteEmail',
    'loja_virtual_comprador_email',
    'email_cliente',
  ]) || getCookieValueByPattern(cookies, (key) => {
    const hasEmail = key.includes('email') || key.includes('mail')
    const hasAccountContext = key.includes('comprador')
      || key.includes('cliente')
      || key.includes('account')
      || key.includes('perfil')
      || key.includes('usuario')
      || key.includes('user')
    return hasEmail && hasAccountContext
  })

  const document = normalizeDocumentDigits(
    getCookieValueByKeys(cookies, [
      'b2b_account_document',
      'b2b_account_cnpj',
      'comprador_cnpj',
      'comprador_cpf_cnpj',
      'compradorCpfCnpj',
      'cliente_cnpj',
      'cliente_cpf_cnpj',
      'clienteCpfCnpj',
      'loja_virtual_comprador_cnpj',
      'loja_virtual_comprador_cpf_cnpj',
      'cpf_cnpj',
      'cnpj',
    ]) || getCookieValueByPattern(cookies, (key) => {
      const hasDocument = key.includes('cnpj') || (key.includes('cpf') && key.includes('cnpj'))
      const hasAccountContext = key.includes('comprador')
        || key.includes('cliente')
        || key.includes('account')
        || key.includes('perfil')
        || key.includes('usuario')
        || key.includes('user')
      return hasDocument && hasAccountContext
    })
  )
  const cnpj = document || undefined
  const stateRegistration = getCookieValueByKeys(cookies, [
    'b2b_account_state_registration',
    'comprador_ie',
    'comprador_inscricao_estadual',
    'cliente_ie',
    'cliente_inscricao_estadual',
    'ie',
    'inscricao_estadual',
  ]) || getCookieValueByPattern(cookies, (key) => {
    const hasRegistration = key.includes('inscricao') || key.includes('state_registration') || key === 'ie'
    const hasAccountContext = key.includes('comprador')
      || key.includes('cliente')
      || key.includes('account')
      || key.includes('perfil')
      || key.includes('usuario')
      || key.includes('user')
      || key === 'ie'
      || key.includes('inscricao_estadual')
    return hasRegistration && hasAccountContext
  })
  const tipoPessoa = parseBuyerPersonType(
    getCookieValueByKeys(cookies, [
      'b2b_account_tipo_pessoa',
      'comprador_tipo_pessoa',
      'cliente_tipo_pessoa',
      'tipo_pessoa',
      'tipopessoa',
    ]) || getCookieValueByPattern(cookies, (key) =>
      (key.includes('tipo_pessoa') || key.includes('tipopessoa')) && (
        key.includes('comprador')
        || key.includes('cliente')
        || key.includes('account')
        || key.includes('perfil')
        || key.includes('usuario')
      )
    )
  )

  const entidadeRaw = getCookieValueByKeys(cookies, [
    'b2b_account_entity_id',
    'comprador_entidade_id',
    'compradorEntidadeId',
    'cliente_entidade_id',
    'clienteEntidadeId',
    'loja_virtual_entidade_id',
    'entidade_id',
  ]) || getCookieValueByPattern(cookies, (key) =>
    key.includes('entidade') && key.includes('id')
  )
  const entidadeId = parseEntityId(entidadeRaw)
  const activity = getCookieValueByKeys(cookies, [
    'b2b_account_activity',
    'comprador_atividade',
    'cliente_atividade',
    'atividade',
  ]) || getCookieValueByPattern(cookies, (key) => {
    const hasActivity = key.includes('atividade') || key.includes('activity') || key.includes('segmento')
    const hasAccountContext = key.includes('comprador')
      || key.includes('cliente')
      || key.includes('account')
      || key.includes('perfil')
      || key.includes('usuario')
      || key === 'atividade'
    return hasActivity && hasAccountContext
  })

  return {
    ...(corporateName ? { corporateName } : {}),
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
    ...(cnpj ? { cnpj } : {}),
    ...(document ? { document } : {}),
    ...(activity ? { activity } : {}),
    ...(stateRegistration ? { stateRegistration } : {}),
    ...(typeof tipoPessoa === 'number' ? { tipoPessoa } : {}),
    ...(typeof entidadeId === 'number' ? { entidadeId } : {}),
  }
}

const setCookieValue = (key: string, value: string, maxAgeSeconds: number = ACCOUNT_COOKIE_MAX_AGE_SECONDS) => {
  if (typeof document === 'undefined') return
  const normalized = value.trim()
  if (!normalized) return
  const cookieBase = `${key}=${encodeURIComponent(normalized)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`
  document.cookie = cookieBase
}

const clearCookieValue = (key: string) => {
  if (typeof document === 'undefined') return
  const cookieBase = `${key}=; path=/; max-age=0; SameSite=Lax`
  document.cookie = cookieBase
}

const persistAccountProfileCookies = (profile: AccountProfile | null | undefined) => {
  if (!profile) return
  clearAccountProfileCookies()
  if (profile.name) setCookieValue('b2b_account_name', profile.name)
  if (profile.corporateName) setCookieValue('b2b_account_corporate_name', profile.corporateName)
  if (profile.email) setCookieValue('b2b_account_email', profile.email)
  if (profile.document || profile.cnpj) setCookieValue('b2b_account_document', profile.document || profile.cnpj || '')
  if (profile.cnpj) setCookieValue('b2b_account_cnpj', profile.cnpj)
  if (profile.activity) setCookieValue('b2b_account_activity', profile.activity)
  if (profile.stateRegistration) setCookieValue('b2b_account_state_registration', profile.stateRegistration)
  if (typeof profile.tipoPessoa === 'number') setCookieValue('b2b_account_tipo_pessoa', String(profile.tipoPessoa))
  if (typeof profile.entidadeId === 'number' && profile.entidadeId > 0) {
    setCookieValue('b2b_account_entity_id', String(profile.entidadeId))
  }
}

const stripBuyerSessionTokenFromAccountProfile = (
  profile: AccountProfile | null | undefined
): AccountProfile | null => {
  if (!profile) return null
  if (typeof profile.buyerSessionToken === 'undefined') return profile
  return {
    ...profile,
    buyerSessionToken: undefined,
  }
}

const clearBuyerSessionTokenFromPersistedAccountProfileStorage = () => {
  if (typeof window === 'undefined') return

  try {
    const rawValue = window.localStorage.getItem(ACCOUNT_PROFILE_STORAGE_KEY)
    if (!rawValue) return

    const parsed = JSON.parse(rawValue) as AccountProfile | null
    const sanitizedProfile = stripBuyerSessionTokenFromAccountProfile(parsed)
    if (!sanitizedProfile || sanitizedProfile === parsed) return

    window.localStorage.setItem(ACCOUNT_PROFILE_STORAGE_KEY, JSON.stringify(sanitizedProfile))
  } catch {
    // Ignore localStorage cleanup errors.
  }
}

const readPersistedAccountProfileStorage = (): AccountProfile | null => {
  if (typeof window === 'undefined') return null

  try {
    const rawValue = window.localStorage.getItem(ACCOUNT_PROFILE_STORAGE_KEY)
    if (!rawValue) return null

    const parsed = JSON.parse(rawValue) as AccountProfile | null
    if (!parsed || typeof parsed !== 'object') return null
    const sanitizedProfile = stripBuyerSessionTokenFromAccountProfile(parsed)
    if (sanitizedProfile !== parsed && sanitizedProfile) {
      window.localStorage.setItem(ACCOUNT_PROFILE_STORAGE_KEY, JSON.stringify(sanitizedProfile))
    }
    return sanitizedProfile
  } catch {
    return null
  }
}

const persistAuthenticatedSessionStorage = (profile: AccountProfile | null | undefined) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(AUTH_LOGIN_FLAG_STORAGE_KEY, 'true')
    if (profile) {
      const sanitizedProfile = stripBuyerSessionTokenFromAccountProfile(profile)
      if (sanitizedProfile) {
        window.localStorage.setItem(ACCOUNT_PROFILE_STORAGE_KEY, JSON.stringify(sanitizedProfile))
      }
    }
  } catch {
    // Ignore localStorage write errors.
  }

  // Só grava o token quando o profile realmente carrega um. Os efeitos de
  // re-hidratação reconstroem o accountProfile a partir do storage já stripado
  // (sem buyerSessionToken) e re-chamam esta função; sem este guard,
  // setBuyerSessionToken(undefined) apagaria o token vivo e derrubaria a sessão
  // do comprador. A limpeza legítima do token acontece por
  // clearAuthenticatedSessionStorage / clearBuyerSessionTokenState, que chamam
  // setBuyerSessionToken(null) diretamente.
  const nextBuyerSessionToken = String(profile?.buyerSessionToken ?? '').trim()
  if (nextBuyerSessionToken) {
    setBuyerSessionToken(nextBuyerSessionToken)
  }
}

const clearAuthenticatedSessionStorage = () => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(AUTH_LOGIN_FLAG_STORAGE_KEY)
    window.localStorage.removeItem(ACCOUNT_PROFILE_STORAGE_KEY)
  } catch {
    // Ignore localStorage write errors.
  }

  setBuyerSessionToken(null)
}

const clearAccountProfileCookies = () => {
  clearCookieValue('b2b_account_name')
  clearCookieValue('b2b_account_corporate_name')
  clearCookieValue('b2b_account_email')
  clearCookieValue('b2b_account_document')
  clearCookieValue('b2b_account_cnpj')
  clearCookieValue('b2b_account_activity')
  clearCookieValue('b2b_account_state_registration')
  clearCookieValue('b2b_account_tipo_pessoa')
  clearCookieValue('b2b_account_entity_id')
}

const getDisplayNameFromIdentifier = (rawValue: string) => {
  const normalized = rawValue.trim()
  if (!normalized) return ''

  if (normalized.includes('@')) {
    const [localPart = ''] = normalized.split('@')
    const cleaned = localPart.replace(/[._-]+/g, ' ').trim()
    if (!cleaned) return normalized
    return cleaned.replace(/\p{L}+/gu, (word) => {
      const lower = word.toLocaleLowerCase('pt-BR')
      const [first = '', ...rest] = Array.from(lower)
      return `${first.toLocaleUpperCase('pt-BR')}${rest.join('')}`
    })
  }

  return normalized
}

const CART_SYNC_BROADCAST_CHANNEL = 'b2b-cart-sync'
const CART_EXTERNAL_REFRESH_THROTTLE_MS = 2500

type CartSyncMessage = {
  type: 'cart-updated'
  entidadeId: number
  sourceId: string
  reason: string
  updatedAt: number
}

const createCartSyncSourceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const parseCartSyncMessage = (value: unknown): CartSyncMessage | null => {
  if (!value || typeof value !== 'object') return null
  const record = value as Partial<CartSyncMessage>
  if (record.type !== 'cart-updated') return null
  if (typeof record.entidadeId !== 'number' || !Number.isFinite(record.entidadeId) || record.entidadeId <= 0) return null
  if (typeof record.sourceId !== 'string' || !record.sourceId.trim()) return null
  if (typeof record.reason !== 'string' || !record.reason.trim()) return null
  if (typeof record.updatedAt !== 'number' || !Number.isFinite(record.updatedAt)) return null

  return {
    type: 'cart-updated',
    entidadeId: Math.trunc(record.entidadeId),
    sourceId: record.sourceId,
    reason: record.reason,
    updatedAt: record.updatedAt,
  }
}

const buildAccountProfileFromAuthenticatedSnapshot = (
  snapshot: AuthenticatedAccountSnapshot,
  cookieProfile: Partial<AccountProfile>,
  fallbackIdentifier: string = ''
): AccountProfile => {
  const resolvedIdentifier = (snapshot.identifier || fallbackIdentifier).trim()
  const fallbackDisplayName = getDisplayNameFromIdentifier(resolvedIdentifier)
  const fallbackDocument = resolvedIdentifier.includes('@')
    ? undefined
    : normalizeDocumentDigits(resolvedIdentifier) || undefined
  const snapshotDocument = normalizeDocumentDigits(snapshot.document || snapshot.cnpj || fallbackDocument)
  const cookieDocument = normalizeDocumentDigits(cookieProfile.document || cookieProfile.cnpj)
  const snapshotEntidadeId = typeof snapshot.entidadeId === 'number' && snapshot.entidadeId > 0
    ? snapshot.entidadeId
    : undefined
  const cookieEntidadeId = typeof cookieProfile.entidadeId === 'number' && cookieProfile.entidadeId > 0
    ? cookieProfile.entidadeId
    : undefined
  // Mescla dados do navegador apenas quando eles pertencem ao comprador autenticado.
  const hasEntidadeConflict = Boolean(
    snapshotEntidadeId && cookieEntidadeId && snapshotEntidadeId !== cookieEntidadeId
  )
  const hasDocumentConflict = Boolean(
    snapshotDocument && cookieDocument && snapshotDocument !== cookieDocument
  )
  const canReuseCookieProfile = Boolean(
    !hasEntidadeConflict
    && !hasDocumentConflict
    && (
      (snapshotEntidadeId && cookieEntidadeId && snapshotEntidadeId === cookieEntidadeId)
      || (snapshotDocument && cookieDocument && snapshotDocument === cookieDocument)
    )
  )
  const reusableCookieProfile: Partial<AccountProfile> = canReuseCookieProfile ? cookieProfile : {}

  return {
    corporateName: snapshot.name || reusableCookieProfile.corporateName || fallbackDisplayName || undefined,
    name: snapshot.name || reusableCookieProfile.name || fallbackDisplayName || undefined,
    email: snapshot.email || reusableCookieProfile.email || (resolvedIdentifier.includes('@') ? resolvedIdentifier : undefined),
    cnpj: snapshot.cnpj || reusableCookieProfile.cnpj || fallbackDocument,
    document: snapshot.document || reusableCookieProfile.document || snapshot.cnpj || reusableCookieProfile.cnpj || fallbackDocument,
    activity: reusableCookieProfile.activity,
    stateRegistration: snapshot.stateRegistration || reusableCookieProfile.stateRegistration,
    tipoPessoa: snapshot.tipoPessoa ?? reusableCookieProfile.tipoPessoa ?? null,
    entidadeId: snapshot.entidadeId || reusableCookieProfile.entidadeId,
    buyerSessionToken: snapshot.buyerSessionToken || reusableCookieProfile.buyerSessionToken,
    buyerUser: snapshot.buyerUser || reusableCookieProfile.buyerUser,
  }
}

const normalizeCartProductId = (value: string): number | null => {
  if (!/^\d+$/.test(value)) return null
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Math.trunc(parsed)
}

const resolveCartSyncProductId = (
  productId: string,
  product?: Pick<Product, 'id' | 'cartBaseProductId'> | null,
): number | null => {
  const normalizedBaseId = resolveStorefrontCartBaseProductId(product ?? productId)
  return normalizeCartProductId(normalizedBaseId)
}

const resolveCartSyncBoxQuantity = (
  productId: string,
  product?: Pick<Product, 'id' | 'packagingQuantity'> | null,
): number => resolveStorefrontCartBoxQuantity(product ?? productId)

const normalizeAnalyticsProductId = (value: string | number | null | undefined): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string' && /^\d+$/.test(value)) {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.trunc(parsed)
    }
  }

  return null
}

const parsePositiveIntegerLike = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const parsed = Number.parseInt(value.trim(), 10)
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null
  }

  return null
}

const normalizePathname = (pathname: string) => {
  if (!pathname) return '/'
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

const isCheckoutRouteStep = (value: string | undefined): value is CheckoutRouteStep =>
  Boolean(value && CHECKOUT_ROUTE_STEPS.has(value as CheckoutRouteStep))

const buildCheckoutPath = (step: CheckoutRouteStep = DEFAULT_CHECKOUT_STEP) =>
  `${VIEW_PATHS.checkout}/${step}`

const resolveCheckoutRouteStep = (value: string | undefined): CheckoutRouteStep => {
  if (isCheckoutRouteStep(value)) return value
  if (value === 'review') return 'payment'
  return DEFAULT_CHECKOUT_STEP
}

const RETURN_REQUEST_STEP_SLUGS: Record<ReturnRequestRouteStep, string> = {
  choice: '',
  terms: 'termos',
  search: 'identificacao',
  items: 'produtos',
  contact: 'contato',
  review: 'revisao',
  success: 'conclusao',
}

const RETURN_REQUEST_STEP_BY_SLUG: Record<string, ReturnRequestRouteStep> = {
  inicio: 'choice',
  termos: 'terms',
  identificacao: 'search',
  produtos: 'items',
  contato: 'contact',
  revisao: 'review',
  conclusao: 'success',
}

const RETURN_REQUEST_FLOW_BY_SLUG: Record<string, Exclude<ReturnRequestRouteFlowType, null>> = {
  devolucao: 'return',
  devolucoes: 'return',
  troca: 'return',
  trocas: 'return',
  conserto: 'repair',
  consertos: 'repair',
  reparo: 'repair',
  reparos: 'repair',
  garantia: 'repair',
}

const buildReturnRequestPath = (
  step: ReturnRequestRouteStep = 'choice',
  flowType: ReturnRequestRouteFlowType = null
) => {
  if (step === 'choice') return VIEW_PATHS['return-request']

  const stepSlug = RETURN_REQUEST_STEP_SLUGS[step]
  if (flowType === 'repair') {
    return `${VIEW_PATHS['return-request']}/conserto/${stepSlug}`
  }

  return `${VIEW_PATHS['return-request']}/${stepSlug}`
}

const resolveReturnRequestRouteFromPath = (normalized: string): RouteResolution | null => {
  const segments = normalized.split('/').filter(Boolean).map(safeDecodeURIComponent)
  const returnRequestBase = VIEW_PATHS['return-request'].slice(1)

  if (segments[0] !== returnRequestBase) return null
  if (segments.length === 1) {
    return {
      view: 'return-request',
      productId: null,
      returnRequestStep: 'choice',
      returnRequestFlowType: null,
    }
  }

  const firstSegment = normalizeCategorySlug(segments[1])
  const flowFromPath = RETURN_REQUEST_FLOW_BY_SLUG[firstSegment] ?? null
  const stepSegment = normalizeCategorySlug(flowFromPath ? segments[2] || 'termos' : segments[1])
  const routeStep = RETURN_REQUEST_STEP_BY_SLUG[stepSegment] ?? 'choice'
  const routeFlowType = routeStep === 'choice' ? null : flowFromPath ?? 'return'

  return {
    view: 'return-request',
    productId: null,
    returnRequestStep: routeStep,
    returnRequestFlowType: routeFlowType,
  }
}

const resolveIntlLocale = (language: string | undefined) => {
  const normalizedLanguage = (language || 'pt-BR').toLocaleLowerCase('pt-BR')
  if (normalizedLanguage.startsWith('es')) return 'es-ES'
  if (normalizedLanguage.startsWith('en')) return 'en-US'
  return 'pt-BR'
}

const resolveSeoLocale = (language: string | undefined) => {
  const normalizedLanguage = (language || 'pt-BR').toLocaleLowerCase('pt-BR')
  if (normalizedLanguage.startsWith('es')) return 'es_ES'
  if (normalizedLanguage.startsWith('en')) return 'en_US'
  return 'pt_BR'
}

const isInstitutionalView = (view: View): view is InstitutionalSection =>
  INSTITUTIONAL_SECTIONS.includes(view as InstitutionalSection)

const normalizeCategorySlug = (value: string) => value.trim().toLowerCase()

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const formatCategorySlugLabel = (value: string) => {
  const normalized = safeDecodeURIComponent(value)
    .trim()
    .replace(/[-_]+/g, ' ')

  if (!normalized) return ''

  return normalized.replace(/\p{L}[\p{L}\p{N}]*/gu, (word) => {
    const lower = word.toLocaleLowerCase('pt-BR')
    const [first = '', ...rest] = Array.from(lower)
    return `${first.toLocaleUpperCase('pt-BR')}${rest.join('')}`
  })
}

const getMenuItemSlug = (item: MenuItem) => {
  const explicitSlug = item.slug?.trim()
  return explicitSlug ? explicitSlug : slugify(item.descricao)
}

const mapMenuItemsToCategories = (items: MenuItem[]): Category[] => {
  return items.map((item) => {
    const mappedChildren = mapMenuItemsToCategories(item.children || [])
    return {
      id: item.id.toString(),
      name: item.descricao,
      slug: getMenuItemSlug(item),
      ...(mappedChildren.length > 0 ? { subcategories: mappedChildren } : {}),
    }
  })
}

const chooseMostSpecificCategoryPath = <T,>(paths: T[][]): T[] | null => {
  if (paths.length === 0) return null

  return paths.slice(1).reduce<T[]>((bestPath, currentPath) => (
    currentPath.length > bestPath.length ? currentPath : bestPath
  ), paths[0])
}

const findAllMenuPathsBySlug = (items: MenuItem[], slug: string, path: MenuItem[] = []): MenuItem[][] => {
  const normalizedSlug = normalizeCategorySlug(slug)
  const matches: MenuItem[][] = []

  for (const item of items) {
    const nextPath = [...path, item]

    if (normalizeCategorySlug(getMenuItemSlug(item)) === normalizedSlug) {
      matches.push(nextPath)
    }

    if (item.children?.length) {
      matches.push(...findAllMenuPathsBySlug(item.children, slug, nextPath))
    }
  }

  return matches
}

const findMenuPathBySlugs = (items: MenuItem[], slugs: string[]): MenuItem[] | null => {
  if (slugs.length === 1) {
    return chooseMostSpecificCategoryPath(findAllMenuPathsBySlug(items, slugs[0]))
  }

  let currentItems = items
  const path: MenuItem[] = []

  for (const slug of slugs) {
    const normalizedSlug = normalizeCategorySlug(slug)
    const match = currentItems.find(
      (item) => normalizeCategorySlug(getMenuItemSlug(item)) === normalizedSlug
    )
    if (!match) return null
    path.push(match)
    currentItems = match.children || []
  }

  return path
}

const buildCategoryPathFromMenu = (path: MenuItem[]): CategoryPathNode[] => {
  return path.map((item) => ({
    id: item.id.toString(),
    name: item.descricao,
    slug: getMenuItemSlug(item),
  }))
}

const getLocalCategorySlug = (category: Category) => {
  const explicitSlug = category.slug?.trim()
  return explicitSlug ? explicitSlug : slugify(category.name)
}

const findLocalPathById = (items: Category[], targetId: string, path: Category[] = []): Category[] | null => {
  for (const item of items) {
    const nextPath = [...path, item]
    if (item.id === targetId) return nextPath
    if (item.subcategories && item.subcategories.length > 0) {
      const found = findLocalPathById(item.subcategories, targetId, nextPath)
      if (found) return found
    }
  }
  return null
}

const findLocalNodeById = (items: Category[], targetId: string): Category | null => {
  for (const item of items) {
    if (item.id === targetId) return item
    if (item.subcategories?.length) {
      const found = findLocalNodeById(item.subcategories, targetId)
      if (found) return found
    }
  }
  return null
}

const collectCategoryAndDescendantIds = (category: Category): string[] => {
  const collected = [category.id]

  for (const child of category.subcategories ?? []) {
    collected.push(...collectCategoryAndDescendantIds(child))
  }

  return collected
}

const findAllLocalPathsBySlug = (items: Category[], slug: string, path: Category[] = []): Category[][] => {
  const normalizedSlug = normalizeCategorySlug(slug)
  const matches: Category[][] = []

  for (const item of items) {
    const nextPath = [...path, item]

    if (normalizeCategorySlug(getLocalCategorySlug(item)) === normalizedSlug) {
      matches.push(nextPath)
    }

    if (item.subcategories?.length) {
      matches.push(...findAllLocalPathsBySlug(item.subcategories, slug, nextPath))
    }
  }

  return matches
}

const findLocalPathBySlugs = (items: Category[], slugs: string[]): Category[] | null => {
  if (slugs.length === 1) {
    return chooseMostSpecificCategoryPath(findAllLocalPathsBySlug(items, slugs[0]))
  }

  let currentItems = items
  const path: Category[] = []

  for (const slug of slugs) {
    const normalizedSlug = normalizeCategorySlug(slug)
    const match = currentItems.find(
      (item) => normalizeCategorySlug(getLocalCategorySlug(item)) === normalizedSlug
    )
    if (!match) return null
    path.push(match)
    currentItems = match.subcategories || []
  }

  return path
}

const buildCategoryPathFromLocal = (path: Category[]): CategoryPathNode[] => {
  return path.map((item) => ({
    id: item.id,
    name: item.name,
    slug: getLocalCategorySlug(item),
  }))
}

const normalizeCategoryPathNodes = (path: CategoryPathNode[]): CategoryPathNode[] => {
  return path.map((node) => ({
    id: node.id.toString(),
    name: node.name,
    slug: node.slug || slugify(node.name),
  }))
}

const findCategoryNameById = (items: Category[], targetId: string): string | null => {
  for (const item of items) {
    if (item.id === targetId) return item.name
    if (item.subcategories?.length) {
      const nested = findCategoryNameById(item.subcategories, targetId)
      if (nested) return nested
    }
  }
  return null
}

const resolveCategoryPathFromSelection = (
  categoryId: string,
  categoryData: CategorySelectionData | undefined,
  menuItems: MenuItem[],
  categoryTree: Category[]
): CategoryPathNode[] | null => {
  if (categoryData?.path && categoryData.path.length > 0) {
    return normalizeCategoryPathNodes(categoryData.path)
  }

  const numericId = Number(categoryId)
  if (Number.isFinite(numericId)) {
    const menuPath = findMenuPathById(menuItems, numericId)
    if (menuPath) return buildCategoryPathFromMenu(menuPath)
  }

  const localPath = findLocalPathById(categoryTree, categoryId)
  if (localPath) return buildCategoryPathFromLocal(localPath)

  if (categoryData?.name) {
    return [{
      id: categoryId,
      name: categoryData.name,
      slug: slugify(categoryData.name),
    }]
  }

  return null
}

const resolveCategoryPathFromSlugs = (
  slugs: string[],
  menuItems: MenuItem[],
  categoryTree: Category[]
): CategoryPathNode[] | null => {
  if (menuItems.length > 0) {
    const menuPath = findMenuPathBySlugs(menuItems, slugs)
    if (menuPath) return buildCategoryPathFromMenu(menuPath)
  }

  const localPath = findLocalPathBySlugs(categoryTree, slugs)
  if (localPath) return buildCategoryPathFromLocal(localPath)

  return null
}

const areSlugsEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false
  return left.every((slug, index) => slug === right[index])
}

const getSearchQueryFromUrl = (search: string) => {
  if (!search) return ''
  return (new URLSearchParams(search).get('q') || '').trim()
}

const getOnlyAvailableFromUrl = (search: string) => {
  if (!search) return false

  const params = new URLSearchParams(search)
  const rawValue = String(
    params.get('somente_disponiveis')
    ?? params.get('somente_disponivel')
    ?? params.get('only_available')
    ?? params.get('onlyAvailable')
    ?? ''
  ).trim().toLocaleLowerCase('pt-BR')

  return rawValue === '1' || rawValue === 'true' || rawValue === 'sim' || rawValue === 'yes'
}

const normalizeManufacturerBrandFilters = (
  brands: Array<ManufacturerBrandFilterState | null | undefined> | null | undefined
): ManufacturerBrandFilterState[] => {
  if (!brands?.length) return []

  const normalized = new Map<number, ManufacturerBrandFilterState>()

  for (const brand of brands) {
    const rawId = Number(brand?.id ?? 0)
    if (!Number.isFinite(rawId) || rawId <= 0) continue

    const id = Math.trunc(rawId)
    const existing = normalized.get(id)
    const name = String(brand?.name ?? '').trim()

    normalized.set(id, {
      id,
      name: existing?.name || name,
      logoUrl: brand?.logoUrl ?? existing?.logoUrl ?? null,
    })
  }

  return Array.from(normalized.values())
}

const getManufacturerBrandFilterIds = (
  brands: Array<ManufacturerBrandFilterState | null | undefined> | null | undefined
) => normalizeManufacturerBrandFilters(brands).map((brand) => brand.id)

const getManufacturerBrandFilterKey = (
  brands: Array<ManufacturerBrandFilterState | null | undefined> | null | undefined
) => {
  const ids = getManufacturerBrandFilterIds(brands).sort((left, right) => left - right)
  return ids.join(',')
}

const serializeManufacturerBrandIdsForQuery = (
  brands: Array<ManufacturerBrandFilterState | null | undefined> | null | undefined
) => getManufacturerBrandFilterIds(brands).join(',')

const serializeManufacturerBrandNamesForQuery = (
  brands: Array<ManufacturerBrandFilterState | null | undefined> | null | undefined
) => normalizeManufacturerBrandFilters(brands).map((brand) => brand.name.trim()).join(',')

const toggleManufacturerBrandSelection = (
  currentBrands: Array<ManufacturerBrandFilterState | null | undefined> | null | undefined,
  brand: ManufacturerBrandFilterState
) => {
  const normalizedCurrent = normalizeManufacturerBrandFilters(currentBrands)
  const brandId = Math.trunc(Number(brand.id))
  if (!Number.isFinite(brandId) || brandId <= 0) {
    return normalizedCurrent
  }

  if (normalizedCurrent.some((item) => item.id === brandId)) {
    return normalizedCurrent.filter((item) => item.id !== brandId)
  }

  return normalizeManufacturerBrandFilters([
    ...normalizedCurrent,
    {
      id: brandId,
      name: brand.name,
      logoUrl: brand.logoUrl ?? null,
    },
  ])
}

const normalizeVehicleFilter = (
  vehicle: Partial<VehicleFilter> | null | undefined
): VehicleFilter | null => {
  if (!vehicle) return null

  const placa = String(vehicle.placa ?? '').trim().toLocaleUpperCase('pt-BR').replace(/[^A-Z0-9]/g, '')
  const marca = String(vehicle.marca ?? '').trim()
  const modelo = String(vehicle.modelo ?? '').trim()
  const ano = String(vehicle.ano ?? '').trim()
  const versao = String(vehicle.versao ?? '').trim()
  const peca = String(vehicle.peca ?? '').trim()

  if (!placa && !marca && !modelo && !ano && !versao) return null

  return {
    ...(placa ? { placa } : {}),
    marca,
    modelo,
    ano,
    ...(versao ? { versao } : {}),
    ...(peca ? { peca } : {}),
  }
}

const getVehicleFilterKey = (vehicle: Partial<VehicleFilter> | null | undefined) => {
  const normalized = normalizeVehicleFilter(vehicle)
  if (!normalized) return ''
  return [
    normalized.placa ?? '',
    normalized.marca,
    normalized.modelo,
    normalized.ano,
    normalized.versao ?? '',
  ].join('|')
}

const appendVehicleFilterToQueryParams = (
  params: URLSearchParams,
  vehicle: Partial<VehicleFilter> | null | undefined
) => {
  const normalized = normalizeVehicleFilter(vehicle)
  if (!normalized) return
  if (normalized.placa) params.set('placa', normalized.placa)
  if (normalized.marca) params.set('veiculo_marca', normalized.marca)
  if (normalized.modelo) params.set('veiculo_modelo', normalized.modelo)
  if (normalized.ano) params.set('veiculo_ano', normalized.ano)
  if (normalized.versao) params.set('veiculo_versao', normalized.versao)
  if (normalized.peca) params.set('veiculo_peca', normalized.peca)
}

const getVehicleFilterFromUrl = (search: string): VehicleFilter | null => {
  if (!search) return null
  const params = new URLSearchParams(search)
  return normalizeVehicleFilter({
    placa: params.get('placa') ?? params.get('vehicle_plate') ?? undefined,
    marca: params.get('veiculo_marca') ?? params.get('vehicle_brand') ?? undefined,
    modelo: params.get('veiculo_modelo') ?? params.get('vehicle_model') ?? undefined,
    ano: params.get('veiculo_ano') ?? params.get('vehicle_year') ?? undefined,
    versao: params.get('veiculo_versao') ?? params.get('vehicle_version') ?? undefined,
    peca: params.get('veiculo_peca') ?? params.get('vehicle_part') ?? undefined,
  })
}

const getVehicleSearchModeFromUrl = (search: string): VehicleSearchRouteMode | null => {
  if (!search) return null
  const params = new URLSearchParams(search)
  const raw = String(
    params.get('busca_veiculo')
    ?? params.get('vehicle_search')
    ?? params.get('vehicleSearch')
    ?? ''
  ).trim().toLocaleLowerCase('pt-BR')

  if (raw === 'placa' || raw === 'plate') return 'plate'
  if (raw === 'manual' || raw === 'outros' || raw === 'modelo') return 'manual'
  return null
}

const getManufacturerBrandFiltersFromUrl = (search: string): ManufacturerBrandFilterState[] => {
  if (!search) return []
  const params = new URLSearchParams(search)
  const parseCsvEntries = (values: string[], keepEmpty = false) => values
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter((value) => keepEmpty || Boolean(value))

  const ids = parseCsvEntries([
    ...params.getAll('marca_fabricante_id'),
    ...params.getAll('marca_fabricante_ids'),
    ...params.getAll('marca_fabricante'),
  ])
    .filter((value) => /^\d+$/.test(value))
    .map((value) => Number.parseInt(value, 10))
    .filter((value) => Number.isFinite(value) && value > 0)

  if (ids.length === 0) return []

  const names = parseCsvEntries([
    ...params.getAll('marca_fabricante_nome'),
    ...params.getAll('marca_fabricante_nomes'),
  ], true)

  return normalizeManufacturerBrandFilters(
    ids.map((id, index) => ({
      id: Math.trunc(id),
      name: names[index] ?? '',
      logoUrl: null,
    }))
  )
}

const normalizeLegacyCheckoutRedirectHash = (value: string | null | undefined) => {
  const normalized = String(value ?? '').trim().toLocaleLowerCase('pt-BR')
  return /^[a-f0-9]{32}$/i.test(normalized) ? normalized : ''
}

const decodeBase64QueryValue = (value: string) => {
  const normalized = value
    .trim()
    .replace(/ /g, '+')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  return window.atob(padded)
}

const parseLegacyCheckoutRedirectLink = (
  encodedLink: string | null | undefined
): LegacyCheckoutRedirectPayload | null => {
  const normalizedLink = typeof encodedLink === 'string' ? encodedLink.trim() : ''
  if (!normalizedLink || typeof window === 'undefined') return null

  let decodedQuery = ''
  try {
    decodedQuery = decodeBase64QueryValue(normalizedLink)
  } catch {
    return null
  }

  const params = new URLSearchParams(decodedQuery)
  const uid = Number.parseInt(String(params.get('uid') ?? '').trim(), 10)
  const auth = String(params.get('auth') ?? '').trim().toLocaleLowerCase('pt-BR')
  const compraHash = normalizeLegacyCheckoutRedirectHash(params.get('compra'))

  if (!Number.isFinite(uid) || uid <= 0) return null
  if (!normalizeLegacyCheckoutRedirectHash(auth)) return null
  if (!compraHash) return null

  return {
    uid: Math.trunc(uid),
    auth,
    compraHash,
  }
}

const resolveRoute = (pathname: string, search: string = ''): RouteResolution => {
  const normalized = normalizePathname(pathname)
  const normalizedProductDetailPath = normalizePathname(normalizeLegacyProductDetailPathname(pathname))
  const searchParams = new URLSearchParams(search)
  const pathSegments = normalized.split('/').filter(Boolean)
  const isCheckoutSuccessPath = pathSegments[0] === VIEW_PATHS.checkout.slice(1)
    && resolveCheckoutRouteStep(pathSegments[1]) === 'success'
  const queryFromUrl = getSearchQueryFromUrl(search)
  const onlyAvailableFromUrl = getOnlyAvailableFromUrl(search)
  const manufacturerBrandsFromUrl = getManufacturerBrandFiltersFromUrl(search)
  const vehicleFilterFromUrl = getVehicleFilterFromUrl(search)
  const vehicleSearchModeFromUrl = getVehicleSearchModeFromUrl(search)
  const productParentContextIdFromUrl = normalizeRouteProductValue(searchParams.get('pai'))
  const quickCheckoutProductIdFromUrl = resolveQuickCheckoutProductIdFromSearch(searchParams)
  const quickCheckoutQuantityFromUrl = quickCheckoutProductIdFromUrl
    ? resolveQuickCheckoutQuantityFromSearch(searchParams)
    : null
  const checkoutQuoteIdFromUrl = (
    parsePositiveIntegerLike(searchParams.get('quote_id'))
    ?? parsePositiveIntegerLike(searchParams.get('quoteId'))
    ?? parsePositiveIntegerLike(searchParams.get('orcamento_id'))
    ?? parsePositiveIntegerLike(searchParams.get('orcamentoId'))
  )
  const checkoutOrderIdFromUrl = (
    parsePositiveIntegerLike(searchParams.get('order_id'))
    ?? parsePositiveIntegerLike(searchParams.get('orderId'))
    ?? (
      isCheckoutSuccessPath
        ? null
        : (
            parsePositiveIntegerLike(searchParams.get('pedido_id'))
            ?? parsePositiveIntegerLike(searchParams.get('pedidoId'))
          )
    )
  )
  const checkoutOrderModeFromUrl = (
    searchParams.get('order_mode') === 'direct'
    || searchParams.get('orderMode') === 'direct'
  )
    ? 'direct'
    : (
        searchParams.get('order_mode') === 'edit'
        || searchParams.get('orderMode') === 'edit'
      )
        ? 'edit'
        : null
  const checkoutQuoteModeFromUrl: BusinessQuoteCheckoutMode | null = (
    searchParams.get('quote_mode') === 'partner'
    || searchParams.get('quoteMode') === 'partner'
  )
    ? 'partner'
    : (
        searchParams.get('quote_mode') === 'resale'
        || searchParams.get('quoteMode') === 'resale'
      )
        ? 'resale'
        : null
  const checkoutSourceFromUrl = checkoutOrderIdFromUrl
    ? 'order'
    : checkoutQuoteIdFromUrl
      ? 'quote'
      : 'cart'
  const withQuery = (route: RouteResolution): RouteResolution => (
    route.view === 'products'
      ? {
          ...route,
          searchQuery: queryFromUrl,
          manufacturerBrands: manufacturerBrandsFromUrl,
          vehicleFilter: vehicleFilterFromUrl,
          vehicleSearchMode: vehicleSearchModeFromUrl,
          onlyAvailable: onlyAvailableFromUrl,
        }
      : route
  )
  const accessSegments = pathSegments

  if (
    pathSegments[0] === 'checkout'
    && pathSegments[1] === 'acompanhamento'
    && pathSegments[2]
  ) {
    return {
      view: 'checkout-tracking',
      productId: null,
      orderTrackingCode: safeDecodeURIComponent(pathSegments[2]),
    }
  }

  if (normalized === VIEW_PATHS['redirect-checkout']) {
    return {
      view: 'redirect-checkout',
      productId: null,
      legacyCheckoutLink: new URLSearchParams(search).get('link'),
    }
  }

  if (normalized === VIEW_PATHS.qrcode || normalized === '/qrcode') {
    return {
      view: 'qrcode',
      productId: null,
    }
  }

  if (normalized === VIEW_PATHS['nfe-qr']) {
    return {
      view: 'nfe-qr',
      productId: null,
    }
  }

  if (accessSegments[0] === 'r' && accessSegments[1]) {
    return {
      view: 'register',
      productId: null,
      preCadastroRetomadaCode: safeDecodeURIComponent(accessSegments[1]),
    }
  }

  if (accessSegments[0] === PUBLIC_TICKET_PATH_PREFIX.slice(1)) {
    return {
      view: 'public-ticket',
      productId: null,
      publicTicketCode: accessSegments[1] ? safeDecodeURIComponent(accessSegments[1]) : null,
    }
  }

  if (accessSegments[0] === BUYER_SHORT_ACCESS_PATH_PREFIX.slice(1)) {
    return {
      view: 'access',
      productId: null,
      accessTicket: accessSegments[1] ? safeDecodeURIComponent(accessSegments[1]) : null,
      accessMode: 'short',
    }
  }

  if (accessSegments[0] === VIEW_PATHS.access.slice(1)) {
    return {
      view: 'access',
      productId: null,
      accessTicket: accessSegments[1] ? safeDecodeURIComponent(accessSegments[1]) : null,
      accessMode: 'ticket',
    }
  }

  if (normalized === VIEW_PATHS['quick-checkout'] || normalized === LEGACY_QUICK_CHECKOUT_PATH) {
    return withQuery({
      view: 'quick-checkout',
      productId: null,
      quickCheckoutProductId: quickCheckoutProductIdFromUrl,
      quickCheckoutProductParentContextId: resolveProductParentContextId(
        quickCheckoutProductIdFromUrl,
        productParentContextIdFromUrl
      ),
      quickCheckoutQuantity: quickCheckoutQuantityFromUrl,
    })
  }

  if (normalized === VIEW_PATHS.checkout) {
    return withQuery({
      view: 'checkout',
      productId: null,
      checkoutStep: DEFAULT_CHECKOUT_STEP,
      checkoutSource: checkoutSourceFromUrl,
      checkoutQuoteId: checkoutQuoteIdFromUrl ?? null,
      checkoutQuoteMode: checkoutQuoteIdFromUrl ? checkoutQuoteModeFromUrl : null,
      checkoutOrderId: checkoutOrderIdFromUrl ?? null,
      checkoutOrderMode: checkoutOrderIdFromUrl ? (checkoutOrderModeFromUrl ?? (isCheckoutSuccessPath ? 'direct' : 'edit')) : null,
    })
  }

  const checkoutSegments = pathSegments
  if (checkoutSegments[0] === VIEW_PATHS.checkout.slice(1)) {
    return withQuery({
      view: 'checkout',
      productId: null,
      checkoutStep: resolveCheckoutRouteStep(checkoutSegments[1]),
      checkoutSource: checkoutSourceFromUrl,
      checkoutQuoteId: checkoutQuoteIdFromUrl ?? null,
      checkoutQuoteMode: checkoutQuoteIdFromUrl ? checkoutQuoteModeFromUrl : null,
      checkoutOrderId: checkoutOrderIdFromUrl ?? null,
      checkoutOrderMode: checkoutOrderIdFromUrl ? (checkoutOrderModeFromUrl ?? (isCheckoutSuccessPath ? 'direct' : 'edit')) : null,
    })
  }

  if (normalizedProductDetailPath === PRODUCT_DETAIL_BASE) {
    return withQuery({ view: 'products', productId: null })
  }

  if (pathSegments[0] === VIEW_PATHS['marketplace-listing-detail'].slice(1) && pathSegments[1]) {
    const marketplaceListingId = Number.parseInt(safeDecodeURIComponent(pathSegments[1]), 10)
    if (Number.isFinite(marketplaceListingId) && marketplaceListingId > 0) {
      return {
        view: 'marketplace-listing-detail',
        productId: null,
        marketplaceListingId: Math.trunc(marketplaceListingId),
      }
    }
  }

  if (pathSegments[0] === VIEW_PATHS['marketplace-seller'].slice(1) && pathSegments[1]) {
    const marketplaceSellerId = Number.parseInt(safeDecodeURIComponent(pathSegments[1]), 10)
    if (Number.isFinite(marketplaceSellerId) && marketplaceSellerId > 0) {
      return {
        view: 'marketplace-seller',
        productId: null,
        marketplaceSellerId: Math.trunc(marketplaceSellerId),
      }
    }
  }

  const productSegments = normalizedProductDetailPath.split('/').filter(Boolean)
  if (productSegments[0] === PRODUCT_DETAIL_BASE.slice(1) && productSegments[1]) {
    const routeProductId = normalizeRouteProductValue(safeDecodeURIComponent(productSegments[1]))
    return {
      view: 'product-detail',
      productId: routeProductId,
      productParentContextId: resolveProductParentContextId(routeProductId, productParentContextIdFromUrl),
    }
  }

  if (normalized === VIEW_PATHS.cart) {
    return withQuery({ view: 'checkout', productId: null, checkoutStep: DEFAULT_CHECKOUT_STEP })
  }

  if (normalized === LEGACY_INSTITUTIONAL_PATH) {
    return { view: 'about', productId: null }
  }

  if (
    normalized === VIEW_PATHS.blog
    || normalized.startsWith(`${VIEW_PATHS.blog}/`)
    || normalized === '/blog'
    || normalized.startsWith('/blog/')
  ) {
    return { view: 'blog', productId: null }
  }

  if (
    normalized === MARKETPLACE_ACCOUNT_HOME_PATH
    || marketplaceListingEditorIdFromPath(normalized, VIEW_PATHS['marketplace-listings'])
  ) {
    return { view: 'marketplace-listings', productId: null }
  }

  const returnRequestRoute = resolveReturnRequestRouteFromPath(normalized)
  if (returnRequestRoute) return returnRequestRoute

  const supportRoute = resolveSupportRouteFromPath(normalized)
  if (supportRoute) return supportRoute

  const businessOrderDetailRoute = resolveAccountDetailRouteFromPath(
    normalized,
    `${VIEW_PATHS.business}/pedidos`,
    'business'
  )
  if (businessOrderDetailRoute) return businessOrderDetailRoute

  const businessQuoteDetailRoute = resolveAccountDetailRouteFromPath(
    normalized,
    `${VIEW_PATHS.business}/orcamentos`,
    'business',
    'quote'
  )
  if (businessQuoteDetailRoute) return businessQuoteDetailRoute

  const orderDetailRoute = resolveAccountDetailRouteFromPath(normalized, VIEW_PATHS.orders, 'orders')
  if (orderDetailRoute) return orderDetailRoute

  const quoteDetailRoute = resolveAccountDetailRouteFromPath(normalized, VIEW_PATHS.quotes, 'quotes')
  if (quoteDetailRoute) return quoteDetailRoute

  const attendanceDetailRoute = resolveAccountDetailRouteFromPath(normalized, VIEW_PATHS.attendances, 'attendances')
  if (attendanceDetailRoute) return attendanceDetailRoute

  const buyerAccessCodeRoute = resolveBuyerAccessCodeRoute(normalized)
  if (buyerAccessCodeRoute) return withQuery(buyerAccessCodeRoute)

  const matchedView = resolveExactViewFromPath(normalized)
  if (matchedView) return withQuery({ view: matchedView, productId: null })

  if (normalized === LEGACY_PRODUCTS_PATH) {
    return withQuery({ view: 'products', productId: null })
  }

  const categorySegments = normalized.split('/').filter(Boolean).map(safeDecodeURIComponent)
  if (categorySegments.length > 0 && categorySegments.length <= 3) {
    return withQuery({ view: 'products', productId: null, categorySlugs: categorySegments })
  }

  return { view: 'home', productId: null }
}

const getInitialRoute = (): RouteResolution => {
  if (typeof window === 'undefined') {
    return { view: 'home', productId: null }
  }
  return resolveRoute(window.location.pathname, window.location.search)
}

const BUYER_ACCESS_CODE_VIEWS = new Set<View>(['price-table', 'price-table-margin'])

const resolveBuyerAccessCodeRoute = (normalized: string): RouteResolution | null => {
  const candidateRoutes: Array<[View, string[]]> = [
    ['price-table', [VIEW_PATHS['price-table'], ...(LEGACY_VIEW_PATH_ALIASES['price-table'] ?? [])]],
    ['price-table-margin', [VIEW_PATHS['price-table-margin'], ...(LEGACY_VIEW_PATH_ALIASES['price-table-margin'] ?? [])]],
  ]

  for (const [view, basePaths] of candidateRoutes) {
    for (const basePath of basePaths) {
      const normalizedBasePath = normalizePathname(basePath)
      if (!normalized.startsWith(`${normalizedBasePath}/`)) continue

      const buyerAccessCode = safeDecodeURIComponent(normalized.slice(normalizedBasePath.length + 1))
      if (!buyerAccessCode) continue

      return {
        view,
        productId: null,
        buyerAccessCode,
      }
    }
  }

  return null
}

const buildBuyerAccessCodePath = (view: View, buyerAccessCode?: string | null) => {
  if (!BUYER_ACCESS_CODE_VIEWS.has(view)) return null

  const normalizedCode = String(buyerAccessCode ?? '').trim()
  if (!normalizedCode) return null

  return `${VIEW_PATHS[view]}/${encodeURIComponent(normalizedCode)}`
}

const buildRedirectPathWithBuyerAccessCode = (redirectPath: string | null | undefined, buyerAccessCode: string) => {
  const normalizedCode = buyerAccessCode.trim()
  const fallbackPath = VIEW_PATHS.dashboard
  const rawPath = String(redirectPath ?? '').trim() || fallbackPath
  if (!normalizedCode) return rawPath

  try {
    const parsed = new URL(rawPath, 'https://storefront.local')
    const normalizedPathname = normalizePathname(parsed.pathname)
    const matchedView = resolveExactViewFromPath(normalizedPathname)

    if (!matchedView || !BUYER_ACCESS_CODE_VIEWS.has(matchedView)) return rawPath

    return `${buildBuyerAccessCodePath(matchedView, normalizedCode) ?? normalizedPathname}${parsed.search}${parsed.hash}`
  } catch {
    return rawPath
  }
}

const resolveSameOriginRedirectPath = (redirectPath: string) => {
  if (typeof window === 'undefined') return null

  try {
    const parsed = new URL(redirectPath, window.location.origin)
    if (parsed.origin !== window.location.origin) return null

    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return null
  }
}

const buildPathForView = (
  view: View,
  productId?: string | null,
  accessTicket?: string | null,
  accessMode: 'ticket' | 'short' = 'ticket',
  productName?: string | null,
  productParentContextId?: string | null,
  preCadastroRetomadaCode?: string | null,
  categorySlugs?: string[],
  checkoutStep?: CheckoutRouteStep | null,
  returnRequestStep?: ReturnRequestRouteStep | null,
  returnRequestFlowType?: ReturnRequestRouteFlowType,
  supportSubjectSlug?: string | null,
  orderDetailToken?: string | null,
  quoteDetailToken?: string | null,
  attendanceDetailToken?: string | null,
  buyerAccessCode?: string | null,
  publicTicketCode?: string | null,
  orderTrackingCode?: string | null
) => {
  const buyerAccessCodePath = buildBuyerAccessCodePath(view, buyerAccessCode)
  if (buyerAccessCodePath) return buyerAccessCodePath

  if (view === 'marketplace-listings' && typeof window !== 'undefined') {
    const currentPath = normalizePathname(window.location.pathname)
    if (marketplaceListingEditorIdFromPath(currentPath, VIEW_PATHS['marketplace-listings'])) {
      return `${currentPath}${window.location.search}`
    }
  }

  if (view === 'public-ticket') {
    const normalizedCode = String(publicTicketCode ?? '').trim()
    return normalizedCode
      ? `${VIEW_PATHS['public-ticket']}/${encodeURIComponent(normalizedCode)}`
      : VIEW_PATHS['public-ticket']
  }

  if (view === 'access') {
    if (!accessTicket?.trim()) return VIEW_PATHS.access
    return accessMode === 'short'
      ? buildShortAccessPath(accessTicket)
      : buildAccessPath(accessTicket)
  }
  if (view === 'register') {
    const normalizedRetomadaCode = String(preCadastroRetomadaCode ?? '').trim()
    if (normalizedRetomadaCode) {
      return `/r/${encodeURIComponent(normalizedRetomadaCode)}`
    }
    return VIEW_PATHS.register
  }
  if (view === 'redirect-checkout') {
    return VIEW_PATHS['redirect-checkout']
  }
  if (view === 'qrcode' && typeof window !== 'undefined') {
    const currentPath = normalizePathname(window.location.pathname)
    if (currentPath === VIEW_PATHS.qrcode || currentPath === '/qrcode') {
      return currentPath
    }
  }
  if (view === 'nfe-qr') {
    return VIEW_PATHS['nfe-qr']
  }
  if (view === 'product-detail') {
    if (!productId) return null

    if (typeof window !== 'undefined') {
      const currentPath = normalizePathname(window.location.pathname)
      const currentSegments = currentPath.split('/').filter(Boolean)
      const currentRouteProductId = currentSegments[0] === PRODUCT_DETAIL_BASE.slice(1)
        ? normalizeRouteProductValue(safeDecodeURIComponent(currentSegments[1] ?? ''))
        : null
      const currentRouteParentContextId = resolveProductParentContextId(
        currentRouteProductId,
        new URLSearchParams(window.location.search).get('pai'),
      )
      const nextRouteParentContextId = resolveProductParentContextId(productId, productParentContextId)

      if (
        currentRouteProductId === productId
        && currentRouteParentContextId === nextRouteParentContextId
      ) {
        return `${currentPath}${window.location.search}`
      }
    }

    return buildProductHref(
      {
        id: productId,
        name: productName || '',
      },
      {
        parentContextId: productParentContextId,
      },
    )
  }
  if (view === 'marketplace-listing-detail' && typeof window !== 'undefined') {
    const currentPath = preserveMarketplaceVitrineDetailPath(window.location.pathname)
    if (currentPath) return currentPath
  }
  if (view === 'marketplace-seller' && typeof window !== 'undefined') {
    const currentPath = preserveMarketplaceVitrineSellerPath(window.location.pathname)
    if (currentPath) return currentPath
  }
  if (view === 'checkout') {
    return buildCheckoutPath(checkoutStep ?? DEFAULT_CHECKOUT_STEP)
  }
  if (view === 'checkout-tracking') {
    const normalizedCode = String(orderTrackingCode ?? '').trim()
    return normalizedCode
      ? buildCheckoutTrackingPath(normalizedCode)
      : VIEW_PATHS['checkout-tracking']
  }
  if (view === 'return-request') {
    return buildReturnRequestPath(returnRequestStep ?? 'choice', returnRequestFlowType ?? null)
  }
  if (view === 'products' && categorySlugs && categorySlugs.length > 0) {
    return `/${categorySlugs.join('/')}`
  }
  if (view === 'cart') {
    return buildCheckoutPath(DEFAULT_CHECKOUT_STEP)
  }
  if (view === 'blog') {
    if (typeof window !== 'undefined') {
      const currentPath = normalizePathname(window.location.pathname)
      if (currentPath === VIEW_PATHS.blog || currentPath.startsWith(`${VIEW_PATHS.blog}/`)) {
        return currentPath
      }
    }
    return VIEW_PATHS.blog
  }
  if (view === 'support' && supportSubjectSlug?.trim()) {
    return `${VIEW_PATHS.support}/${encodeURIComponent(supportSubjectSlug.trim())}/`
  }
  if (view === 'orders' && orderDetailToken?.trim()) {
    return buildOrderDetailPathFromToken(orderDetailToken)
  }
  if (view === 'business' && orderDetailToken?.trim()) {
    return buildBusinessOrderDetailPathFromToken(orderDetailToken)
  }
  if (view === 'business' && quoteDetailToken?.trim()) {
    return buildBusinessQuoteDetailPathFromToken(quoteDetailToken)
  }
  if (view === 'quotes' && quoteDetailToken?.trim()) {
    return buildQuoteDetailPathFromToken(quoteDetailToken)
  }
  if (view === 'attendances' && attendanceDetailToken?.trim()) {
    return buildAttendanceDetailPathFromToken(attendanceDetailToken)
  }
  return VIEW_PATHS[view]
}

const findMenuPathById = (items: MenuItem[], targetId: number, path: MenuItem[] = []): MenuItem[] | null => {
  for (const item of items) {
    const nextPath = [...path, item]
    if (item.id === targetId) return nextPath
    if (item.children && item.children.length > 0) {
      const found = findMenuPathById(item.children, targetId, nextPath)
      if (found) return found
    }
  }
  return null
}

const ALL_PRODUCTS_DATA_KEY = 'all'
const LAUNCH_PRODUCTS_DATA_KEY = 'launches'
const JUST_ARRIVED_PRODUCTS_DATA_KEY = 'just_arrived'
const IMAGE_SEARCH_PRODUCTS_DATA_KEY_PREFIX = 'image-search'

const buildSearchProductsDataKey = (
  query: string,
  manufacturerBrands?: Array<ManufacturerBrandFilterState | null | undefined> | null,
  vehicleFilter?: Partial<VehicleFilter> | null,
  onlyAvailable = false
) => {
  const normalizedQuery = query.trim()
  const normalizedBrandKey = getManufacturerBrandFilterKey(manufacturerBrands)
  const vehicleKey = getVehicleFilterKey(vehicleFilter)
  return [
    `search:${normalizedQuery}`,
    normalizedBrandKey ? `brand:${normalizedBrandKey}` : '',
    vehicleKey ? `vehicle:${vehicleKey}` : '',
    onlyAvailable ? 'available:1' : '',
  ].filter(Boolean).join(':')
}
const buildCatalogProductsDataKey = (
  manufacturerBrands?: Array<ManufacturerBrandFilterState | null | undefined> | null,
  onlyAvailable = false
) => {
  const normalizedBrandKey = getManufacturerBrandFilterKey(manufacturerBrands)
  const baseKey = normalizedBrandKey ? `all:brand:${normalizedBrandKey}` : ALL_PRODUCTS_DATA_KEY
  return onlyAvailable ? `${baseKey}:available:1` : baseKey
}
const buildImageSearchProductsDataKey = (resultKey: string) =>
  `${IMAGE_SEARCH_PRODUCTS_DATA_KEY_PREFIX}:${resultKey}`
const buildCategoryProductsDataKey = (
  categoryId: string,
  manufacturerBrands?: Array<ManufacturerBrandFilterState | null | undefined> | null,
  onlyAvailable = false
) => {
  const normalizedBrandKey = getManufacturerBrandFilterKey(manufacturerBrands)
  const baseKey = normalizedBrandKey
    ? `category:${categoryId}:brand:${normalizedBrandKey}`
    : `category:${categoryId}`
  return onlyAvailable ? `${baseKey}:available:1` : baseKey
}
const buildCategoryRouteProductsDataKey = (
  categorySlugs: string[],
  manufacturerBrands?: Array<ManufacturerBrandFilterState | null | undefined> | null,
  onlyAvailable = false
) => {
  const normalizedBrandKey = getManufacturerBrandFilterKey(manufacturerBrands)
  const baseKey = normalizedBrandKey
    ? `category-route:${categorySlugs.join('/')}:brand:${normalizedBrandKey}`
    : `category-route:${categorySlugs.join('/')}`
  return onlyAvailable ? `${baseKey}:available:1` : baseKey
}

const buildProductsViewStateKey = ({
  view,
  searchQuery = '',
  imageSearchKey = '',
  manufacturerBrands,
  vehicleFilter,
  onlyAvailable = false,
  isSearchResultsActive = false,
  categorySlugs = [],
  selectedCategory,
}: {
  view: View
  searchQuery?: string
  imageSearchKey?: string
  manufacturerBrands?: Array<ManufacturerBrandFilterState | null | undefined> | null
  vehicleFilter?: Partial<VehicleFilter> | null
  onlyAvailable?: boolean
  isSearchResultsActive?: boolean
  categorySlugs?: string[]
  selectedCategory?: string
}) => {
  if (view === 'launches') return LAUNCH_PRODUCTS_DATA_KEY
  if (view === 'just_arrived') return JUST_ARRIVED_PRODUCTS_DATA_KEY
  if (view !== 'products') return ''

  if (imageSearchKey.trim()) {
    return buildImageSearchProductsDataKey(imageSearchKey)
  }

  const normalizedQuery = searchQuery.trim()
  if (isSearchResultsActive && normalizedQuery.length >= 2) {
    return buildSearchProductsDataKey(normalizedQuery, manufacturerBrands, vehicleFilter, onlyAvailable)
  }
  if (categorySlugs.length > 0) {
    return buildCategoryRouteProductsDataKey(categorySlugs, manufacturerBrands, onlyAvailable)
  }
  if (selectedCategory) {
    return buildCategoryProductsDataKey(selectedCategory, manufacturerBrands, onlyAvailable)
  }
  return buildCatalogProductsDataKey(manufacturerBrands, onlyAvailable)
}

const resolveSupportRouteFromPath = (normalized: string): RouteResolution | null => {
  const supportBasePaths = [VIEW_PATHS.support, ...(LEGACY_VIEW_PATH_ALIASES.support ?? [])]

  for (const basePath of supportBasePaths) {
    if (normalized === basePath) {
      return { view: 'support', productId: null, supportSubjectSlug: null }
    }

    if (normalized.startsWith(`${basePath}/`)) {
      const subjectSlug = safeDecodeURIComponent(normalized.slice(basePath.length + 1)).trim()
      return {
        view: 'support',
        productId: null,
        supportSubjectSlug: subjectSlug || null,
      }
    }
  }

  return null
}

const resolveAccountDetailRouteFromPath = (
  normalized: string,
  basePath: string,
  view: 'orders' | 'quotes' | 'attendances' | 'business',
  businessDetailKind: 'order' | 'quote' = 'order'
): RouteResolution | null => {
  if (!normalized.startsWith(`${basePath}/`)) return null

  const normalizedSegments = normalized.split('/').filter(Boolean)
  const baseSegments = basePath.split('/').filter(Boolean)
  if (normalizedSegments.length !== baseSegments.length + 2) return null

  const baseMatches = baseSegments.every((segment, index) => normalizedSegments[index] === segment)
  if (!baseMatches) return null
  if (normalizedSegments[normalizedSegments.length - 1] !== 'detalhes') return null

  const detailToken = safeDecodeURIComponent(normalizedSegments[normalizedSegments.length - 2]).trim()
  const detailId = resolveAccountDetailIdFromPathSegment(detailToken)
  if (!detailToken || !detailId) return null
  const normalizedDetailToken = normalizeAccountDetailToken(detailToken, detailId)

  return view === 'business' && businessDetailKind === 'quote'
    ? {
        view,
        productId: null,
        quoteDetailId: detailId,
        quoteDetailToken: normalizedDetailToken,
      }
    : view === 'orders' || view === 'business'
    ? {
        view,
        productId: null,
        orderDetailId: detailId,
        orderDetailToken: normalizedDetailToken,
      }
    : view === 'quotes'
      ? {
        view,
        productId: null,
        quoteDetailId: detailId,
        quoteDetailToken: normalizedDetailToken,
      }
      : {
        view,
        productId: null,
        attendanceDetailId: detailId,
        attendanceDetailToken: normalizedDetailToken,
      }
}

const resolveExactViewFromPath = (normalized: string): View | null => {
  const matchedView = (Object.keys(VIEW_PATHS) as View[]).find(
    (view) => view !== 'product-detail' && VIEW_PATHS[view] === normalized
  )

  if (matchedView) {
    return matchedView === 'cart' ? 'checkout' : matchedView
  }

  const legacyMatch = (Object.entries(LEGACY_VIEW_PATH_ALIASES) as Array<[View, readonly string[]]>).find(
    ([, paths]) => paths.includes(normalized)
  )

  return legacyMatch?.[0] ?? null
}

function AppPrimaryContent({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  if (!enabled) {
    return <>{children}</>
  }

  return (
    <main id="main-content" className="flex-1">
      {children}
    </main>
  )
}

const normalizeLoginFailureMessage = (message: string, fallback: string) => {
  const normalizedMessage = message
    .replace(/^API error:\s*\d+\s*-\s*/i, '')
    .replace(/^API error:\s*\d+\s*/i, '')
    .trim()

  const comparisonMessage = normalizedMessage
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

  if (
    !comparisonMessage
    || comparisonMessage === 'unauthorized'
    || comparisonMessage.includes('unauthorized')
    || comparisonMessage.includes('nao autorizado')
  ) {
    return fallback
  }

  return normalizedMessage
}

function App() {
  const { t, i18n } = useTranslation()
  const catalogLanguage = normalizeStorefrontLanguage(
    i18n.resolvedLanguage ?? i18n.language,
  ) ?? 'pt-BR'
  const isMobileViewport = useIsMobile()
  const {
    siteConfig,
    isLoading: isSiteConfigLoading,
    error: siteConfigError,
    siteName: SEO_SITE_NAME,
    seoTitle: SEO_DEFAULT_TITLE,
    seoDescription: SEO_DEFAULT_DESCRIPTION,
    categoryModel,
    showPricesBeforeLogin,
    showManufacturerBrandFilter,
  } = useStorefrontSiteConfig()
  const institutionalSeoDescriptions = useMemo(() => ({
    privacy: t('institutional.privacy.seo_description'),
    careers: t('institutional.careers.seo_description'),
    returnsPolicy: t('institutional.returnsPolicy.seo_description'),
  }), [t])
  const SEO_DEFAULT_IMAGE = siteConfig?.logo_url?.trim() || '/android-chrome-512x512.png'
  const SEO_DEFAULT_IMAGE_ALT = siteConfig?.empresa_nome?.trim() || SEO_SITE_NAME
  const initialRoute = useMemo(() => getInitialRoute(), [])
  const initialProductCheckoutQuickRequest = useMemo(() => {
    if (typeof window === 'undefined') return null
    if (initialRoute.view !== 'quick-checkout') return null
    return buildProductCheckoutQuickRequestFromSearch(window.location.search)
      ?? readProductCheckoutQuickRequest()
  }, [initialRoute.view])
  const initialRouteQuickCheckoutProductId = initialRoute.view === 'quick-checkout'
    ? initialRoute.quickCheckoutProductId ?? initialProductCheckoutQuickRequest?.productIds[0] ?? null
    : null
  const initialRouteQuickCheckoutParentContextId = initialRoute.view === 'quick-checkout'
    ? initialRoute.quickCheckoutProductParentContextId
      ?? resolveProductParentContextId(
        initialRouteQuickCheckoutProductId,
        initialProductCheckoutQuickRequest?.parentContextId ?? null
      )
    : null
  const initialRouteQuickCheckoutQuantity = initialRoute.view === 'quick-checkout'
    ? initialRoute.quickCheckoutQuantity ?? initialProductCheckoutQuickRequest?.quantity ?? null
    : null
  const [currentView, setCurrentView] = useState<View>(initialRoute.view)
  const [checkoutStepInfo, setCheckoutStepInfo] = useState<CheckoutStepInfo | null>(null)
  const [checkoutRouteStep, setCheckoutRouteStep] = useState<CheckoutRouteStep>(
    initialRoute.checkoutStep ?? DEFAULT_CHECKOUT_STEP
  )
  const [routeCheckoutSource, setRouteCheckoutSource] = useState<'cart' | 'quote' | 'order'>(
    initialRoute.checkoutSource ?? 'cart'
  )
  const [routeCheckoutQuoteId, setRouteCheckoutQuoteId] = useState<number | null>(
    initialRoute.checkoutQuoteId ?? null
  )
  const [routeCheckoutQuoteMode, setRouteCheckoutQuoteMode] = useState<BusinessQuoteCheckoutMode | null>(
    initialRoute.checkoutQuoteMode ?? null
  )
  const [routeCheckoutOrderId, setRouteCheckoutOrderId] = useState<number | null>(
    initialRoute.checkoutOrderId ?? null
  )
  const [routeCheckoutOrderMode, setRouteCheckoutOrderMode] = useState<'direct' | 'edit' | null>(
    initialRoute.checkoutOrderMode ?? null
  )
  const [cartCheckoutShippingPrefill, setCartCheckoutShippingPrefill] = useState<CartFreightSelection | null>(null)
  const [cartCheckoutFinancialPrefill, setCartCheckoutFinancialPrefill] = useState<CheckoutSimulationResult | null>(null)
  const [returnRequestRouteStep, setReturnRequestRouteStep] = useState<ReturnRequestRouteStep>(
    initialRoute.returnRequestStep ?? 'choice'
  )
  const [returnRequestRouteFlowType, setReturnRequestRouteFlowType] = useState<ReturnRequestRouteFlowType>(
    initialRoute.returnRequestFlowType ?? null
  )
  const [routeProductId, setRouteProductId] = useState<string | null>(initialRoute.productId)
  const [routeProductParentContextId, setRouteProductParentContextId] = useState<string | null>(
    initialRoute.productParentContextId ?? null
  )
  const [routeQuickCheckoutProductId, setRouteQuickCheckoutProductId] = useState<string | null>(
    initialRouteQuickCheckoutProductId
  )
  const [routeQuickCheckoutProductParentContextId, setRouteQuickCheckoutProductParentContextId] = useState<string | null>(
    initialRouteQuickCheckoutParentContextId
  )
  const [routeQuickCheckoutQuantity, setRouteQuickCheckoutQuantity] = useState<number | null>(
    initialRouteQuickCheckoutQuantity
  )
  const [pendingProductCheckoutRequestKey, setPendingProductCheckoutRequestKey] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    if (initialRoute.view !== 'product-detail') return null
    const storedCheckoutRequestKey = consumeStoredProductCheckoutRequestKey(window.location.pathname)
    if (storedCheckoutRequestKey) return storedCheckoutRequestKey
    return shouldOpenProductCheckoutFromSearch(window.location.search)
      ? buildProductCheckoutRequestKey(window.location.pathname, window.location.search)
      : null
  })
  const [isProductCheckoutQuickFlowActive, setIsProductCheckoutQuickFlowActive] = useState(() => (
    readProductCheckoutQuickFlowFlag()
    || (initialRoute.view === 'quick-checkout' && Boolean(initialRouteQuickCheckoutProductId))
  ))
  const [routeMarketplaceListingId, setRouteMarketplaceListingId] = useState<number | null>(
    initialRoute.marketplaceListingId ?? null
  )
  const [routeMarketplaceSellerId, setRouteMarketplaceSellerId] = useState<number | null>(
    initialRoute.marketplaceSellerId ?? null
  )
  const [routeAccessTicket, setRouteAccessTicket] = useState<string | null>(initialRoute.accessTicket ?? null)
  const [routeAccessMode, setRouteAccessMode] = useState<'ticket' | 'short'>(initialRoute.accessMode ?? 'ticket')
  const [routeOrderTrackingCode, setRouteOrderTrackingCode] = useState<string | null>(
    initialRoute.orderTrackingCode ?? null
  )
  const [routePublicTicketCode, setRoutePublicTicketCode] = useState<string | null>(
    initialRoute.publicTicketCode ?? null
  )
  const [routeBuyerAccessCode, setRouteBuyerAccessCode] = useState<string | null>(
    initialRoute.buyerAccessCode ?? null
  )
  const [routeLegacyCheckoutLink, setRouteLegacyCheckoutLink] = useState<string | null>(
    initialRoute.legacyCheckoutLink ?? null
  )
  const [routePreCadastroRetomadaCode, setRoutePreCadastroRetomadaCode] = useState<string | null>(
    initialRoute.preCadastroRetomadaCode ?? null
  )
  const [routeSupportSubjectSlug, setRouteSupportSubjectSlug] = useState<string | null>(
    initialRoute.supportSubjectSlug ?? null
  )
  const [routeOrderDetailId, setRouteOrderDetailId] = useState<number | null>(initialRoute.orderDetailId ?? null)
  const [routeOrderDetailToken, setRouteOrderDetailToken] = useState<string | null>(initialRoute.orderDetailToken ?? null)
  const [routeQuoteDetailId, setRouteQuoteDetailId] = useState<number | null>(initialRoute.quoteDetailId ?? null)
  const [routeQuoteDetailToken, setRouteQuoteDetailToken] = useState<string | null>(initialRoute.quoteDetailToken ?? null)
  const [routeAttendanceDetailId, setRouteAttendanceDetailId] = useState<number | null>(initialRoute.attendanceDetailId ?? null)
  const [routeAttendanceDetailToken, setRouteAttendanceDetailToken] = useState<string | null>(initialRoute.attendanceDetailToken ?? null)
  const [categoryRouteSlugs, setCategoryRouteSlugs] = useState<string[]>(initialRoute.categorySlugs ?? [])
  const [searchQuery, setSearchQuery] = useState(initialRoute.searchQuery ?? '')
  const [onlyAvailableFilter, setOnlyAvailableFilter] = useState(Boolean(initialRoute.onlyAvailable))
  const [selectedManufacturerBrands, setSelectedManufacturerBrands] = useState<ManufacturerBrandFilterState[]>(
    () => normalizeManufacturerBrandFilters(initialRoute.manufacturerBrands)
  )
  const [activeVehicleFilter, setActiveVehicleFilter] = useState<VehicleFilter | null>(
    () => normalizeVehicleFilter(initialRoute.vehicleFilter)
  )
  const [vehicleSearchModeFromRoute, setVehicleSearchModeFromRoute] = useState<VehicleSearchRouteMode | null>(
    initialRoute.vehicleSearchMode ?? null
  )
  const [isSearchResultsActive, setIsSearchResultsActive] = useState(
    Boolean((initialRoute.searchQuery ?? '').trim()) || Boolean(initialRoute.vehicleSearchMode)
  )
  const [imageSearchState, setImageSearchState] = useState<ImageSearchState | null>(null)
  const [activeSearchAnalyticsContext, setActiveSearchAnalyticsContext] = useState<StorefrontSearchAnalyticsContext | null>(null)
  const [isImageSearchLoading, setIsImageSearchLoading] = useState(false)
  const shouldApplyRouteSearchRef = useRef(Boolean((initialRoute.searchQuery ?? '').trim()))
  const [isLoggedIn, setIsLoggedIn] = useLocalStorage<boolean>(AUTH_LOGIN_FLAG_STORAGE_KEY, false)
  const [isLogoutRequested, setIsLogoutRequested] = useLocalStorage<boolean>(AUTH_LOGOUT_FLAG_STORAGE_KEY, false)
  const [accountProfile, setAccountProfile] = useLocalStorage<AccountProfile | null>(ACCOUNT_PROFILE_STORAGE_KEY, null)
  const [indicatorDelegation, setIndicatorDelegation] = useLocalStorage<IndicatorDelegationState | null>(
    INDICATOR_DELEGATION_STORAGE_KEY,
    null
  )
  const [postLoginRedirect, setPostLoginRedirect] = useState<PostLoginRedirect | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>()
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [displayedProductDetail, setDisplayedProductDetail] = useState<Product | null>(null)
  const [isLoadingProduct, setIsLoadingProduct] = useState(
    () => initialRoute.view === 'product-detail' && Boolean(initialRoute.productId)
  )
  const [productLoadError, setProductLoadError] = useState<string | null>(null)
  const [showCategorySidebar, setShowCategorySidebar] = useState(false)
  const [showCartDrawer, setShowCartDrawer] = useState(false)
  const [hasWideProductCartSpace, setHasWideProductCartSpace] = useState(false)
  const [showPriceTableCartPanel, setShowPriceTableCartPanel] = useState(false)
  const [isPriceTableCartPanelCollapsed, setIsPriceTableCartPanelCollapsed] = useState(false)
  const [isPriceTableCatalogReady, setIsPriceTableCatalogReady] = useState(false)
  const [cartDrawerSource, setCartDrawerSource] = useState<CartDrawerSource>('default')
  const [cartQuoteOrigin, setCartQuoteOrigin] = useLocalStorage<CartQuoteOrigin | null>(
    CART_QUOTE_ORIGIN_STORAGE_KEY,
    null
  )
  const [showSaveQuoteDestinationDialog, setShowSaveQuoteDestinationDialog] = useState(false)
  const [showSaveQuoteDialog, setShowSaveQuoteDialog] = useState(false)
  const [showCheckoutBusinessRegisterDialog, setShowCheckoutBusinessRegisterDialog] = useState(false)
  const [isSavingQuote, setIsSavingQuote] = useState(false)
  const [quoteName, setQuoteName] = useState('')
  const [cartQuoteFreightSelection, setCartQuoteFreightSelection] = useState<CartFreightSelection | null>(null)
  const [cartQuoteFinancialSummary, setCartQuoteFinancialSummary] = useState<CheckoutSimulationResult | null>(null)
  const [cartQuoteFreightPrefill, setCartQuoteFreightPrefill] = useState<QuoteCartFreightPrefill | null>(null)
  useEffect(() => {
    setCartQuoteFreightPrefill(cartQuoteOrigin?.freightPrefill ?? null)
  }, [cartQuoteOrigin])
  const [backendAvailability, setBackendAvailability] = useState(() => getBackendAvailabilityState())
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [productsDataKey, setProductsDataKey] = useState('')
  const [restoreProductsViewStateKey, setRestoreProductsViewStateKey] = useState<string | null>(null)
  const [homeProducts, setHomeProducts] = useState<Product[]>([])
  const [homeFeaturedProductsView, setHomeFeaturedProductsView] = useState<'launches' | 'products'>('launches')
  const [homeJustArrivedProducts, setHomeJustArrivedProducts] = useState<Product[]>([])
  const [storefrontPriceRefreshVersion, setStorefrontPriceRefreshVersion] = useState(0)
  const [hasLoadedHomeJustArrivedPreview, setHasLoadedHomeJustArrivedPreview] = useState(false)
  const [hasCompletedHomeProductsLoad, setHasCompletedHomeProductsLoad] = useState(false)
  const [isHomeDeferredContentReady, setIsHomeDeferredContentReady] = useState(initialRoute.view !== 'home')
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const [isLoadingMoreProducts, setIsLoadingMoreProducts] = useState(false)
  const [hasMoreProducts, setHasMoreProducts] = useState(false)
  const [serverPaginatedProductsTotal, setServerPaginatedProductsTotal] = useState<number | null>(null)
  const [hasCompletedInitialProductsLoad, setHasCompletedInitialProductsLoad] = useState(false)
  const [homeMenuItems, setHomeMenuItems] = useState<MenuItem[]>([])
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [productBreadcrumbMenuItems, setProductBreadcrumbMenuItems] = useState<MenuItem[]>([])
  const [isHomeMenuLoading, setIsHomeMenuLoading] = useState(false)
  const [isMenuLoading, setIsMenuLoading] = useState(false)
  const [hasLoadedMenuOnce, setHasLoadedMenuOnce] = useState(false)
  const [analyticsLocationKey, setAnalyticsLocationKey] = useState(() => getStorefrontAnalyticsLocationKey())
  const searchRequestIdRef = useRef(0)
  const marketplaceSearchTotalRef = useRef(0)
  const categoryProductsRequestIdRef = useRef(0)
  const categoryProductsInFlightDataKeyRef = useRef<string | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  const inFlightSearchProductsDataKeyRef = useRef<string | null>(null)
  const homeMenuLoadStartedRef = useRef(false)
  // Espelham os tamanhos das listas de menu para que ensureHomeMenuLoaded leia o
  // estado mais recente por ref, sem precisar deles como deps do useCallback.
  // Antes, depender de mainMenuCategories.length / homeMenuItems.length fazia o
  // callback se recriar quando o próprio fetch populava o estado, redisparando o
  // useEffect que o chama (ciclo de auto-disparo).
  const mainMenuCategoriesCountRef = useRef(0)
  const homeMenuItemsCountRef = useRef(0)
  const menuLoadStartedRef = useRef(false)
  const productBreadcrumbMenuLoadStartedRef = useRef(false)
  const menuLoadIdleHandleRef = useRef<number | null>(null)
  const menuLoadTimerHandleRef = useRef<number | null>(null)
  const priceTableCartPanelViewKeyRef = useRef('')
  const currentViewRef = useRef<View>(initialRoute.view)
  const analyticsLastPageViewKeyRef = useRef('')
  const analyticsReferrerUrlRef = useRef<string | null>(
    typeof document !== 'undefined' ? (document.referrer || null) : null
  )
  const productOriginViewRef = useRef<'products' | 'launches' | 'just_arrived'>('products')
  const productListingSnapshotRef = useRef<ProductListingSnapshot | null>(null)
  const productDetailRequestIdRef = useRef(0)
  const productsOffsetRef = useRef(0)
  const serverPaginatedProductFeedRef = useRef<ServerPaginatedProductFeed | null>(null)
  const isLoadingMoreProductsRef = useRef(false)
  const catalogProductsAbortRef = useRef<AbortController | null>(null)
  const homeProductsLoadRequestIdRef = useRef(0)
  const homeProductsLoadAbortRef = useRef<AbortController | null>(null)
  const attemptedAccessTicketRef = useRef<BuyerAccessAttemptState | null>(null)
  const attemptedBuyerAccessCodeRef = useRef<string | null>(null)
  const buyerPriceTableAccessLinkRequestRef = useRef<{
    entidadeId: number | null
    status: 'idle' | 'loading' | 'blocked'
  }>({ entidadeId: null, status: 'idle' })
  const attemptedLegacyCheckoutRedirectRef = useRef<string | null>(null)
  const authBootstrapAttemptedRef = useRef(false)
  const handoffBootstrapInProgressRef = useRef(false)
  const [isAuthBootstrapResolved, setIsAuthBootstrapResolved] = useState(false)
  const isBackendUnavailable = !import.meta.env.DEV && backendAvailability.unavailable && !siteConfig
  const persistedAccountProfile = isLogoutRequested ? null : readPersistedAccountProfileStorage()
  const cookieAccountProfile: Partial<AccountProfile> = isLogoutRequested ? {} : resolveAccountProfileFromCookies()
  const resolvedBuyerSessionToken = isLogoutRequested
    ? ''
    : String(
      accountProfile?.buyerSessionToken
      || persistedAccountProfile?.buyerSessionToken
      || cookieAccountProfile.buyerSessionToken
      || getBuyerSessionToken()
      || ''
    ).trim()
  const hasBuyerSessionToken = resolvedBuyerSessionToken.length > 0
  const authenticatedEntidadeId = isLogoutRequested
    ? undefined
    : (
      accountProfile?.entidadeId
      || persistedAccountProfile?.entidadeId
      || cookieAccountProfile.entidadeId
      || getAuthenticatedEntidadeId()
    )
  const hasAuthenticatedEntidadeId = typeof authenticatedEntidadeId === 'number' && authenticatedEntidadeId > 0
  const effectiveAccountProfile: AccountProfile | null = isLogoutRequested
    ? null
    : (
      accountProfile || persistedAccountProfile || (
        Object.keys(cookieAccountProfile).length > 0
          ? {
              entidadeId: cookieAccountProfile.entidadeId,
              corporateName: cookieAccountProfile.corporateName || cookieAccountProfile.name,
              name: cookieAccountProfile.name,
              email: cookieAccountProfile.email,
              cnpj: cookieAccountProfile.cnpj,
              document: cookieAccountProfile.document,
              activity: cookieAccountProfile.activity,
              stateRegistration: cookieAccountProfile.stateRegistration,
              tipoPessoa: cookieAccountProfile.tipoPessoa ?? null,
            }
          : null
        )
    )
  const effectiveIsLoggedIn = !isLogoutRequested && (
    hasBuyerSessionToken
      ? (isLoggedIn || hasAuthenticatedEntidadeId)
      : (isLoggedIn && hasAuthenticatedEntidadeId)
  )
  const activeCommerceContext = effectiveIsLoggedIn
    ? resolveBuyerSessionCommerceContext(
        effectiveAccountProfile?.commerceContext,
        authenticatedEntidadeId ?? effectiveAccountProfile?.entidadeId,
      )
    : DOMESTIC_COMMERCE_CONTEXT
  const isInternationalCommerce = effectiveIsLoggedIn && activeCommerceContext.international
  const guestCommerceLocale = resolveGuestStorefrontLocale(
    siteConfig?.visitor_country_code,
    import.meta.env.DEV
      ? 'pt-BR'
      : typeof navigator === 'undefined'
        ? null
        : navigator.languages?.[0] || navigator.language,
    typeof Intl === 'undefined'
      ? null
      : Intl.DateTimeFormat().resolvedOptions().timeZone,
  )
  const automaticCommerceLocale = effectiveIsLoggedIn
    ? activeCommerceContext.locale
    : guestCommerceLocale
  const desiredCommerceLocale = resolveStorefrontLanguage(automaticCommerceLocale)
  const automaticCommerceLocaleScope = effectiveIsLoggedIn
    ? `buyer:${authenticatedEntidadeId ?? effectiveAccountProfile?.entidadeId ?? 0}:${desiredCommerceLocale}`
    : isSiteConfigLoading
      ? null
      : `guest:${siteConfig?.visitor_country_code || 'BR'}:${desiredCommerceLocale}`
  const appliedAutomaticCommerceLocaleScopeRef = useRef<string | null>(null)

  useEffect(() => {
    if (!effectiveIsLoggedIn) {
      resetBuyerCommerceContext()
    } else {
      setBuyerCommerceContext(activeCommerceContext)
    }
  }, [activeCommerceContext, effectiveIsLoggedIn])

  useEffect(() => {
    if (
      !automaticCommerceLocaleScope
      || appliedAutomaticCommerceLocaleScopeRef.current === automaticCommerceLocaleScope
    ) {
      return
    }

    appliedAutomaticCommerceLocaleScopeRef.current = automaticCommerceLocaleScope
    const currentCommerceLocale = String(i18n.resolvedLanguage ?? i18n.language).toLowerCase()
    if (!currentCommerceLocale.startsWith(desiredCommerceLocale.toLowerCase())) {
      void i18n.changeLanguage(desiredCommerceLocale)
    }
  }, [automaticCommerceLocaleScope, desiredCommerceLocale, i18n])

  const shouldSuppressGuestSearchHighlights = (() => {
    if (typeof window === 'undefined') return false
    if (isLogoutRequested || effectiveIsLoggedIn || isAuthBootstrapResolved) return false

    let persistedLoginFlag = false
    try {
      persistedLoginFlag = window.localStorage.getItem(AUTH_LOGIN_FLAG_STORAGE_KEY) === 'true'
    } catch {
      persistedLoginFlag = false
    }

    const cookieProfile = resolveAccountProfileFromCookies()
    const persistedProfile = readPersistedAccountProfileStorage()
    const buyerSessionToken = getBuyerSessionToken()
    const entidadeId =
      accountProfile?.entidadeId
      || persistedProfile?.entidadeId
      || cookieProfile.entidadeId
      || getAuthenticatedEntidadeId()

    return Boolean(buyerSessionToken && (persistedLoginFlag || entidadeId))
  })()
  const isSellerAccount = effectiveAccountProfile?.buyerUser?.tipoUsuario === 'vendedor'
  const isStandalonePriceTableView = isPriceTableView(currentView)

  useLayoutEffect(() => {
    if (isLogoutRequested) return
    if (typeof authenticatedEntidadeId !== 'number' || authenticatedEntidadeId <= 0) return
    if (getAuthenticatedEntidadeId() === authenticatedEntidadeId) return

    setAuthenticatedEntidadeId(authenticatedEntidadeId)
  }, [authenticatedEntidadeId, isLogoutRequested])

  useEffect(() => {
    if (isLogoutRequested || hasBuyerSessionToken || isLoggedIn) return
    if (!getAuthenticatedEntidadeId()) return
    clearAuthenticatedEntidadeId()
  }, [hasBuyerSessionToken, isLoggedIn, isLogoutRequested])

  const checkoutStorageScope = currentView === 'checkout'
    ? (
        routeCheckoutSource === 'quote' && routeCheckoutQuoteId
          ? `quote:${routeCheckoutQuoteId}:${routeCheckoutQuoteMode ?? 'default'}`
          : routeCheckoutSource === 'order' && routeCheckoutOrderId
            ? `order:${routeCheckoutOrderId}`
            : 'cart'
      )
    : 'cart'
  const clearScopedCheckoutDraft = useCallback((scope: string | null | undefined) => {
    if (typeof window === 'undefined') return
    const entidadeId = getAuthenticatedEntidadeId()
    const normalizedScope = String(scope ?? '').trim()
    if (!entidadeId || !normalizedScope || normalizedScope === 'cart') return

    try {
      window.localStorage.removeItem(`checkout-draft-v1:${entidadeId}:${normalizedScope}`)
    } catch (error) {
      console.warn('Failed to clear checkout draft from localStorage:', error)
    }
  }, [])
  const clearScopedCheckoutTransientState = useCallback((scope: string | null | undefined) => {
    const entidadeId = getAuthenticatedEntidadeId()
    const normalizedScope = String(scope ?? '').trim()
    if (!entidadeId || !normalizedScope || normalizedScope === 'cart') return

    clearPersistedCheckoutAttempt(buildCheckoutAttemptStorageKeys(entidadeId, normalizedScope))
    clearPersistedCheckoutSuccess(buildCheckoutSuccessStorageKeys(entidadeId, normalizedScope))
  }, [])
  const activeIsolatedCheckoutScope = currentView === 'checkout' && (
    (routeCheckoutSource === 'quote' && routeCheckoutQuoteId)
    || (routeCheckoutSource === 'order' && routeCheckoutOrderId)
  )
    ? checkoutStorageScope
    : null
  const previousIsolatedCheckoutScopeRef = useRef<string | null>(activeIsolatedCheckoutScope)
  const canManageBuyerUsers = Boolean(
    effectiveAccountProfile
    && effectiveAccountProfile.buyerUser?.acessoUsuarios
    && (effectiveAccountProfile.document || effectiveAccountProfile.cnpj || '').replace(/\D+/g, '').length === 14
  )
  const hasPendingCheckoutAttempt = currentView === 'checkout' && (
    Boolean(readPersistedCheckoutAttempt(buildCheckoutAttemptStorageKeys(authenticatedEntidadeId, checkoutStorageScope))?.attemptId)
    || (
      checkoutRouteStep === 'success'
      && Boolean(resolveCheckoutAttemptIdFromLocationSearch())
    )
  )
  const publicPricingClienteId = Number(siteConfig?.cliente_pior_cenario_id ?? 0)
  const publicPricingAudienceKey = showPricesBeforeLogin
    && Number.isFinite(publicPricingClienteId)
    && publicPricingClienteId > 0
    ? `public:${Math.trunc(publicPricingClienteId)}`
    : 'guest'
  const simplifiedStorefrontEnabled = isSimplifiedStorefrontEnabled()
  const productPricingAudienceKey = simplifiedStorefrontEnabled
    ? publicPricingAudienceKey
    : effectiveIsLoggedIn
      ? `authenticated:${authenticatedEntidadeId ?? 0}`
      : publicPricingAudienceKey
  const productDataAudienceKey = `${productPricingAudienceKey}:language:${catalogLanguage}`
  const previousProductDataAudienceKeyRef = useRef(productDataAudienceKey)

  const runViewTransition = useCallback((callback: () => void) => {
    startTransition(() => {
      callback()
    })
  }, [])

  const resolveBuyerAccessCodeForView = useCallback((view: View) => {
    if (!BUYER_ACCESS_CODE_VIEWS.has(view)) return null

    const currentCode = normalizeBuyerAccessCode(routeBuyerAccessCode)
    if (currentCode) return currentCode

    const entidadeId = typeof authenticatedEntidadeId === 'number' && authenticatedEntidadeId > 0
      ? authenticatedEntidadeId
      : effectiveAccountProfile?.entidadeId

    return readPersistedBuyerPriceTableAccessCode(entidadeId) || null
  }, [authenticatedEntidadeId, effectiveAccountProfile?.entidadeId, routeBuyerAccessCode])

  const resolvedPriceTableHref = useMemo(() => {
    const buyerAccessCode = resolveBuyerAccessCodeForView('price-table')
    return buildBuyerAccessCodePath('price-table', buyerAccessCode) ?? VIEW_PATHS['price-table']
  }, [resolveBuyerAccessCodeForView])

  const invalidatePendingProductDetailRequests = useCallback(() => {
    productDetailRequestIdRef.current += 1
  }, [])

  const navigateToView = useCallback((view: View) => {
    if (view !== 'product-detail') {
      invalidatePendingProductDetailRequests()
    }
    if (
      currentView === 'checkout'
      && (
        (routeCheckoutSource === 'quote' && routeCheckoutQuoteId)
        || (routeCheckoutSource === 'order' && routeCheckoutOrderId)
      )
    ) {
      clearScopedCheckoutDraft(checkoutStorageScope)
    }
    runViewTransition(() => {
      if (view !== 'product-detail' && view !== 'products') {
        setImageSearchState(null)
        setIsImageSearchLoading(false)
        setVehicleSearchModeFromRoute(null)
      }
      setRouteOrderDetailId(null)
      setRouteOrderDetailToken(null)
      setRouteQuoteDetailId(null)
      setRouteQuoteDetailToken(null)
      setRouteAttendanceDetailId(null)
      setRouteAttendanceDetailToken(null)
      setRouteAccessTicket(null)
      setRouteAccessMode('ticket')
      setRouteOrderTrackingCode(null)
      setRouteBuyerAccessCode(resolveBuyerAccessCodeForView(view))
      setRoutePreCadastroRetomadaCode(null)
      setReturnRequestRouteStep('choice')
      setReturnRequestRouteFlowType(null)
      setCurrentView(view)
    })
  }, [
    checkoutStorageScope,
    clearScopedCheckoutDraft,
    currentView,
    invalidatePendingProductDetailRequests,
    resolveBuyerAccessCodeForView,
    routeCheckoutOrderId,
    routeCheckoutQuoteId,
    routeCheckoutSource,
    runViewTransition,
  ])

  useEffect(() => {
    const previousScope = previousIsolatedCheckoutScopeRef.current
    if (previousScope && previousScope !== activeIsolatedCheckoutScope) {
      clearScopedCheckoutDraft(previousScope)
    }

    previousIsolatedCheckoutScopeRef.current = activeIsolatedCheckoutScope
  }, [activeIsolatedCheckoutScope, clearScopedCheckoutDraft])

  const navigateToSupport = useCallback((subjectSlug: string | null = null) => {
    const normalizedSubjectSlug = subjectSlug?.trim() || null
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRouteSupportSubjectSlug(normalizedSubjectSlug)
      setRouteAttendanceDetailId(null)
      setRouteAttendanceDetailToken(null)
      setCurrentView('support')
    })
  }, [invalidatePendingProductDetailRequests, runViewTransition])

  const openSupportFromFloatingMenu = useCallback((
    subjectSlug: string | null,
    description?: string
  ) => {
    const normalizedSubjectSlug = subjectSlug?.trim() || null
    const normalizedDescription = description?.trim() || ''

    if (normalizedSubjectSlug && normalizedDescription) {
      writeSupportFormPrefill({
        subjectSlug: normalizedSubjectSlug,
        descricao: normalizedDescription,
      })
    }

    if (currentView === 'support' && routeSupportSubjectSlug === normalizedSubjectSlug) {
      runViewTransition(() => {
        setRouteSupportSubjectSlug(null)
        setCurrentView('support')
      })

      if (typeof window !== 'undefined') {
        window.setTimeout(() => {
          navigateToSupport(normalizedSubjectSlug)
        }, 10)
        return
      }
    }

    navigateToSupport(normalizedSubjectSlug)
  }, [currentView, navigateToSupport, routeSupportSubjectSlug, runViewTransition])

  const handleFloatingHelpAction = useCallback((action: FloatingHelpActionKey) => {
    if (action === 'help_center') {
      openSupportFromFloatingMenu(null)
      return
    }

    if (action === 'suggestion') {
      openSupportFromFloatingMenu('2401', t('floating_help.prefills.suggestion'))
      return
    }

    openSupportFromFloatingMenu('2401', t('floating_help.prefills.bug_report'))
  }, [openSupportFromFloatingMenu, t])

  const navigateToOrderDetails = useCallback((detail: { orderId: number; orderToken: string }) => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRouteOrderDetailId(detail.orderId)
      setRouteOrderDetailToken(detail.orderToken)
      setRouteQuoteDetailId(null)
      setRouteQuoteDetailToken(null)
      setRouteAttendanceDetailId(null)
      setRouteAttendanceDetailToken(null)
      setCurrentView('orders')
    })
  }, [invalidatePendingProductDetailRequests, runViewTransition])

  const navigateToBusinessOrderDetails = useCallback((detail: { orderId: number; orderToken: string }) => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRouteOrderDetailId(detail.orderId)
      setRouteOrderDetailToken(detail.orderToken)
      setRouteQuoteDetailId(null)
      setRouteQuoteDetailToken(null)
      setCurrentView('business')
    })
  }, [invalidatePendingProductDetailRequests, runViewTransition])

  const navigateToBusinessQuoteDetails = useCallback((detail: { quoteId: number; quoteToken: string }) => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRouteQuoteDetailId(detail.quoteId)
      setRouteQuoteDetailToken(detail.quoteToken)
      setRouteOrderDetailId(null)
      setRouteOrderDetailToken(null)
      setCurrentView('business')
    })
  }, [invalidatePendingProductDetailRequests, runViewTransition])

  const navigateToQuoteDetails = useCallback((detail: { quoteId: number; quoteToken: string }) => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRouteQuoteDetailId(detail.quoteId)
      setRouteQuoteDetailToken(detail.quoteToken)
      setRouteOrderDetailId(null)
      setRouteOrderDetailToken(null)
      setRouteAttendanceDetailId(null)
      setRouteAttendanceDetailToken(null)
      setCurrentView('quotes')
    })
  }, [invalidatePendingProductDetailRequests, runViewTransition])

  const navigateToAttendanceDetails = useCallback((detail: { attendanceId: number; attendanceToken: string }) => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRouteAttendanceDetailId(detail.attendanceId)
      setRouteAttendanceDetailToken(detail.attendanceToken)
      setRouteOrderDetailId(null)
      setRouteOrderDetailToken(null)
      setRouteQuoteDetailId(null)
      setRouteQuoteDetailToken(null)
      setCurrentView('attendances')
    })
  }, [invalidatePendingProductDetailRequests, runViewTransition])

  const { buildBreadcrumb, setCategoryBreadcrumb, clearCategoryBreadcrumb, categoryPath } = useBreadcrumb()
  const navigateHome = useCallback(() => {
    // A home não deve manter o contexto da última categoria. Sem essa limpeza,
    // uma navegação SPA podia renderizar os cards anteriores até a próxima
    // busca de categoria terminar.
    categoryProductsRequestIdRef.current += 1
    setSelectedCategory(undefined)
    clearCategoryBreadcrumb()
    setCategoryRouteSlugs([])
    navigateToView('home')
  }, [clearCategoryBreadcrumb, navigateToView])

  const endIndicatorClientSession = useCallback((destination: 'home' | 'business' = 'business') => {
    if (!indicatorDelegation) return
    const restoredProfile: AccountProfile = {
      ...indicatorDelegation.principalProfile,
      buyerSessionToken: indicatorDelegation.principalSessionToken,
    }
    setBuyerSessionToken(indicatorDelegation.principalSessionToken)
    setAuthenticatedEntidadeId(restoredProfile.entidadeId)
    flushSync(() => {
      setAccountProfile(restoredProfile)
      setIndicatorDelegation(null)
      setCartItems([])
      setCartQuoteOrigin(null)
      setShowCartDrawer(false)
    })
    cartItemsRef.current = []
    cartItemKeysByProductIdRef.current = new Map()
    if (destination === 'business') navigateToView('business')
    else navigateHome()
    toast.success('Atendimento encerrado. Você voltou para a conta do indicador.')
  }, [indicatorDelegation, navigateHome, navigateToView, setAccountProfile, setCartQuoteOrigin, setIndicatorDelegation])

  const startIndicatorClientSession = useCallback(async (
    client: Pick<SavedQuotePdfClient, 'id'>,
    options?: { quoteDetail?: { quoteId: number; quoteToken: string } }
  ) => {
    if (indicatorDelegation) {
      throw new Error('Encerre o atendimento atual antes de acessar outro cliente.')
    }
    const principalProfile = effectiveAccountProfile
    const principalSessionToken = getBuyerSessionToken() || resolvedBuyerSessionToken
    if (!principalProfile?.entidadeId || !principalSessionToken) {
      throw new Error('A sessão do indicador não está disponível. Faça login novamente.')
    }

    const result = await startIndicatorClientDelegation(client.id)
    const clientProfile: AccountProfile = {
      corporateName: result.client.name,
      name: result.client.contactName || result.client.name,
      email: result.client.email,
      cnpj: result.client.document,
      document: result.client.document,
      stateRegistration: result.client.registration,
      entidadeId: result.client.id,
      buyerSessionToken: result.buyerSessionToken,
      buyerUser: {
        contatoId: null,
        nome: result.client.contactName || result.client.name,
        tipoUsuario: 'admin',
        papel: 'admin',
        markupPercentual: 0,
        acessoFinanceiro: true,
        acessoPedidos: true,
        acessoUsuarios: true,
        acessoMarketplaceAnuncios: false,
        acessoMarketplacePerguntas: false,
        modoAcesso: 'empresa',
      },
    }

    setBuyerSessionToken(result.buyerSessionToken)
    setAuthenticatedEntidadeId(result.client.id)
    flushSync(() => {
      setIndicatorDelegation({
        principalProfile,
        principalSessionToken,
        client: result.client,
        authorized: result.authorized,
        relationshipId: result.relationshipId,
      })
      setAccountProfile(clientProfile)
      setCartItems([])
      setCartQuoteOrigin(null)
      setShowCartDrawer(false)
    })
    cartItemsRef.current = []
    cartItemKeysByProductIdRef.current = new Map()
    if (options?.quoteDetail) {
      navigateToQuoteDetails(options.quoteDetail)
    } else {
      navigateHome()
    }
    toast.success(`Agora você está atendendo ${result.client.name}.`)
  }, [
    effectiveAccountProfile,
    indicatorDelegation,
    navigateHome,
    navigateToQuoteDetails,
    resolvedBuyerSessionToken,
    setAccountProfile,
    setCartQuoteOrigin,
    setIndicatorDelegation,
  ])
  const navigateToBusinessQuoteTarget = useCallback((detail: {
    quoteId: number
    quoteToken: string
    clientEntityId?: number | null
  }) => {
    const clientEntityId = Number(detail.clientEntityId ?? 0)
    if (Number.isSafeInteger(clientEntityId) && clientEntityId > 0) {
      return startIndicatorClientSession(
        { id: clientEntityId },
        { quoteDetail: { quoteId: detail.quoteId, quoteToken: detail.quoteToken } }
      )
    }
    navigateToBusinessQuoteDetails(detail)
  }, [navigateToBusinessQuoteDetails, startIndicatorClientSession])
  const isLegacyCheckoutSessionBridge = currentView === 'redirect-checkout'
  const [shouldLoadPriceTableAuxiliaryData, setShouldLoadPriceTableAuxiliaryData] = useState(!isStandalonePriceTableView)
  useEffect(() => {
    if (!isStandalonePriceTableView) {
      setShouldLoadPriceTableAuxiliaryData(true)
      return
    }

    setShouldLoadPriceTableAuxiliaryData(false)
    const timeoutId = window.setTimeout(() => {
      setShouldLoadPriceTableAuxiliaryData(true)
    }, 1800)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isStandalonePriceTableView])
  const canLoadBuyerAuxiliaryData = !isStandalonePriceTableView || shouldLoadPriceTableAuxiliaryData
  const {
    beneficios: buyerBeneficios,
    status: buyerBeneficiosStatus,
    revision: buyerBeneficiosRevision,
  } = useBuyerBeneficios(
    effectiveIsLoggedIn && !isBackendUnavailable && !isLegacyCheckoutSessionBridge && canLoadBuyerAuxiliaryData,
    authenticatedEntidadeId ?? null
  )
  const recommendationTracking = useRecommendationTracking()
  const homeRecommendationRefreshKey = effectiveIsLoggedIn ? (authenticatedEntidadeId ?? 'authenticated') : 'guest'
  const canAccessIndicatorPortal = buyerBeneficios?.habilitar_portal_indicador === 1
  const canShowBusiness = canAccessIndicatorPortal && !indicatorDelegation
  const canAccessAffiliates = buyerBeneficios?.habilitar_afiliado === 1
  const hasMarketplaceBenefit = buyerBeneficios?.habilitar_marketplace === 1
  const hasDistributionBenefit = buyerBeneficios?.habilitar_preco_distribuicao === 1
  const { distribuicao: buyerDistribuicao, status: buyerDistribuicaoStatus } = useBuyerDistribuicao(
    effectiveIsLoggedIn && !isBackendUnavailable && hasDistributionBenefit && !isLegacyCheckoutSessionBridge && canLoadBuyerAuxiliaryData,
    authenticatedEntidadeId ?? null
  )

  const distributionNotification = useMemo(() => {
    if (!hasDistributionBenefit) return null
    if (buyerDistribuicao?.tabela_especial === 1) return null

    const locale = resolveIntlLocale(i18n.resolvedLanguage)
    const formatCurrency = (value: number) => formatCommerceCurrency(value)
    const formatDate = (value: Date | null) => {
      if (!value) return t('account.benefits.distribution_date_unavailable')
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(value)
    }

    if (buyerDistribuicaoStatus === 'loading') return null
    if (!buyerDistribuicao) {
      return {
        tone: 'neutral' as const,
        statusLabel: t('account.benefits.distribution_status_unavailable'),
        title: t('header.account.notifications.distribution_unavailable_title'),
        description: t('account.benefits.distribution_goal_missing'),
      }
    }

    const distributionStatus = resolveDistributionStatus({
      registrationEnabled: true,
      distribuicao: buyerDistribuicao,
    })
    const target = formatCurrency(distributionStatus.expected)
    const date = formatDate(distributionStatus.deadline)

    if (distributionStatus.mode === 'active') {
      return {
        tone: 'success' as const,
        statusLabel: t('account.benefits.distribution_current_status_active'),
        title: t('header.account.notifications.distribution_active_title'),
        description: t('account.benefits.distribution_status_confirmed'),
      }
    }

    if (distributionStatus.mode === 'grace' && distributionStatus.urgentRisk) {
      return {
        tone: 'danger' as const,
        statusLabel: t('account.benefits.distribution_current_status_urgent'),
        title: t('header.account.notifications.distribution_urgent_title'),
        description: t('account.benefits.distribution_status_at_risk_urgent', {
          target,
          date,
        }),
      }
    }

    if (distributionStatus.mode === 'grace') {
      return {
        tone: 'warning' as const,
        statusLabel: t('account.benefits.distribution_current_status_grace'),
        title: t('header.account.notifications.distribution_risk_title'),
        description: t('account.benefits.distribution_status_at_risk', {
          target,
          date,
        }),
      }
    }

    return {
      tone: 'danger' as const,
      statusLabel: t('account.benefits.distribution_current_status_inactive'),
      title: t('header.account.notifications.distribution_lost_title'),
      description: distributionStatus.expected > 0
        ? t('account.benefits.distribution_status_lost', {
            target,
          })
        : t('account.benefits.distribution_goal_inactive'),
    }
  }, [
    buyerDistribuicao,
    buyerDistribuicaoStatus,
    hasDistributionBenefit,
    i18n.resolvedLanguage,
    t,
  ])
  const distributionPricingStatusResolved = !hasDistributionBenefit || buyerDistribuicaoStatus !== 'loading'
  const distributionPricingStatus = useMemo(
    () => resolveDistributionStatus({
      registrationEnabled: hasDistributionBenefit,
      distribuicao: buyerDistribuicao,
    }),
    [buyerDistribuicao, hasDistributionBenefit]
  )
  const pricingContextValue = useMemo<ProductPricingContext>(() => resolveSimplifiedStorefrontPricingContext({
    isLoggedIn: effectiveIsLoggedIn,
    showPrices: shouldShowPrices(effectiveIsLoggedIn, showPricesBeforeLogin),
    document: effectiveAccountProfile?.document || effectiveAccountProfile?.cnpj || null,
    stateRegistration: effectiveAccountProfile?.stateRegistration || null,
    tipoPessoa: effectiveAccountProfile?.tipoPessoa ?? null,
    markupPercentual: effectiveAccountProfile?.buyerUser?.tipoUsuario === 'vendedor'
      ? effectiveAccountProfile?.buyerUser?.markupPercentual ?? 0
      : 0,
    distributionEnabled: hasDistributionBenefit,
    distributionSpecial: distributionPricingStatus.special,
    distributionActive: distributionPricingStatusResolved
      ? distributionPricingStatus.masterActive
      : false,
    distributionStatusResolved: distributionPricingStatusResolved,
    distributionInfo: hasDistributionBenefit && !distributionPricingStatus.special
      ? {
          hasData: distributionPricingStatus.hasData,
          expected: distributionPricingStatus.expected,
          currentMonth: distributionPricingStatus.currentMonth,
          average: distributionPricingStatus.average,
          gap: distributionPricingStatus.gap,
          gapToMonth: distributionPricingStatus.gapToMonth,
          gapToAverage: distributionPricingStatus.gapToAverage,
          deadline: distributionPricingStatus.deadline,
          deadlineActive: distributionPricingStatus.deadlineActive,
          daysRemaining: distributionPricingStatus.daysRemaining,
          goalReached: distributionPricingStatus.goalReached,
          monthGoalReached: distributionPricingStatus.monthGoalReached,
          averageGoalReached: distributionPricingStatus.averageGoalReached,
          urgentRisk: distributionPricingStatus.urgentRisk,
          mode: distributionPricingStatus.mode,
        }
      : null,
    showRealStock: buyerBeneficios?.exibir_estoque_real_na_loja === 1,
    stockStatusMode: [0, 1, 2].includes(Number(siteConfig?.produto_sem_estoque_modo))
      ? Number(siteConfig?.produto_sem_estoque_modo)
      : siteConfig?.exibir_apenas_produtos_com_estoque ? 1 : 0,
  }), [
    buyerBeneficios?.exibir_estoque_real_na_loja,
    distributionPricingStatus.average,
    distributionPricingStatus.averageGoalReached,
    distributionPricingStatus.deadline,
    distributionPricingStatus.deadlineActive,
    distributionPricingStatus.daysRemaining,
    distributionPricingStatus.expected,
    distributionPricingStatus.currentMonth,
    distributionPricingStatus.gap,
    distributionPricingStatus.gapToAverage,
    distributionPricingStatus.gapToMonth,
    distributionPricingStatus.goalReached,
    distributionPricingStatus.hasData,
    distributionPricingStatus.masterActive,
    distributionPricingStatus.mode,
    distributionPricingStatus.monthGoalReached,
    distributionPricingStatus.special,
    distributionPricingStatusResolved,
    distributionPricingStatus.urgentRisk,
    effectiveAccountProfile?.cnpj,
    effectiveAccountProfile?.document,
    effectiveAccountProfile?.buyerUser?.markupPercentual,
    effectiveAccountProfile?.buyerUser?.tipoUsuario,
    effectiveAccountProfile?.stateRegistration,
    effectiveAccountProfile?.tipoPessoa,
    effectiveIsLoggedIn,
    hasDistributionBenefit,
    siteConfig?.exibir_apenas_produtos_com_estoque,
    siteConfig?.produto_sem_estoque_modo,
    showPricesBeforeLogin,
  ])

  const mainMenuCategories = useMemo(() => {
    return mapMenuItemsToCategories(menuItems)
  }, [menuItems])
  mainMenuCategoriesCountRef.current = mainMenuCategories.length
  homeMenuItemsCountRef.current = homeMenuItems.length
  const homeMenuCategories = useMemo(() => {
    return mapMenuItemsToCategories(homeMenuItems)
  }, [homeMenuItems])
  const visibleHomeCategories = useMemo(() => {
    return mainMenuCategories.length > 0 ? mainMenuCategories : homeMenuCategories
  }, [homeMenuCategories, mainMenuCategories])
  const productBreadcrumbMenuCategories = useMemo(() => {
    return mapMenuItemsToCategories(productBreadcrumbMenuItems)
  }, [productBreadcrumbMenuItems])

  const normalizedSearchQuery = searchQuery.trim()
  const isImageSearchActive = Boolean(imageSearchState)
  const isAnySearchResultsActive = isSearchResultsActive || isImageSearchActive
  const activeSearchProductsKey = normalizedSearchQuery.length >= 2
    ? buildSearchProductsDataKey(normalizedSearchQuery, selectedManufacturerBrands, activeVehicleFilter, onlyAvailableFilter)
    : ''
  const currentProductsViewStateKey = useMemo(
    () => buildProductsViewStateKey({
      view: currentView,
      searchQuery,
      imageSearchKey: imageSearchState?.resultKey ?? '',
      manufacturerBrands: selectedManufacturerBrands,
      vehicleFilter: activeVehicleFilter,
      onlyAvailable: onlyAvailableFilter,
      isSearchResultsActive,
      categorySlugs: categoryRouteSlugs,
      selectedCategory,
    }),
    [activeVehicleFilter, categoryRouteSlugs, currentView, imageSearchState?.resultKey, isSearchResultsActive, onlyAvailableFilter, searchQuery, selectedCategory, selectedManufacturerBrands]
  )
  const isSearchResultsLoading = isImageSearchActive
    ? isImageSearchLoading
    : (
      isSearchResultsActive
      && normalizedSearchQuery.length >= 2
      && (isLoadingProducts || productsDataKey !== activeSearchProductsKey)
    )
  const isCatalogServerPaginated = (
    currentView === 'products'
    && !isAnySearchResultsActive
    && (
      serverPaginatedProductFeedRef.current?.kind === 'catalog'
      || serverPaginatedProductFeedRef.current?.kind === 'catalog_brand'
      || serverPaginatedProductFeedRef.current?.kind === 'category'
    )
    && serverPaginatedProductFeedRef.current.dataKey === productsDataKey
  )
  const isTextSearchServerPaginated = (
    currentView === 'products'
    && isSearchResultsActive
    && !isImageSearchActive
    && serverPaginatedProductFeedRef.current?.kind === 'search'
    && serverPaginatedProductFeedRef.current.dataKey === activeSearchProductsKey
  )
  const shouldRestoreCurrentProductsViewState = Boolean(
    restoreProductsViewStateKey
    && restoreProductsViewStateKey === currentProductsViewStateKey
    && (currentView === 'products' || currentView === 'launches' || currentView === 'just_arrived')
  )

  const selectedCategoryName = useMemo(() => {
    const fromPath = categoryPath[categoryPath.length - 1]?.name
    if (fromPath) return fromPath
    if (!selectedCategory) return ''
    return findCategoryNameById(mainMenuCategories, selectedCategory) || ''
  }, [categoryPath, selectedCategory, mainMenuCategories])
  const selectedSingleManufacturerBrandName = useMemo(() => {
    if (selectedManufacturerBrands.length !== 1) return ''
    return selectedManufacturerBrands[0]?.name?.trim() || ''
  }, [selectedManufacturerBrands])

  const headerNavigationKey = [
    currentView,
    checkoutRouteStep,
    routeProductId ?? selectedProduct?.id ?? '',
    routeSupportSubjectSlug ?? '',
    routeOrderDetailToken ?? '',
    routeQuoteDetailToken ?? '',
    categoryRouteSlugs.join('/'),
    isSearchResultsActive ? searchQuery.trim() : '',
  ].join('|')
  const accountNotifications = useAccountNotifications({
    isLoggedIn: effectiveIsLoggedIn && !isSellerAccount && !isLegacyCheckoutSessionBridge && !isStandalonePriceTableView,
    distributionNotification: isSellerAccount ? null : distributionNotification,
    refreshKey: headerNavigationKey,
  })
  const scrollResetNavigationKey = [
    currentView,
    checkoutRouteStep,
    currentView === 'product-detail'
      ? routeProductParentContextId ?? selectedProduct?.id ?? routeProductId ?? ''
      : routeProductId ?? selectedProduct?.id ?? '',
    routeSupportSubjectSlug ?? '',
    routeOrderDetailToken ?? '',
    routeQuoteDetailToken ?? '',
    categoryRouteSlugs.join('/'),
    isSearchResultsActive ? searchQuery.trim() : '',
  ].join('|')

  const [cartItems, setCartItems] = useState<CartItem[]>([])
  const [quickCheckoutCartItems, setQuickCheckoutCartItems] = useState<CartItem[]>([])
  const [authenticatedSearchHighlights, setAuthenticatedSearchHighlights] = useState<Array<{ label: string; badge: string }>>([])
  const [buyerRotatingSearchTermsRetryTick, setBuyerRotatingSearchTermsRetryTick] = useState(0)
  const [isCartInitializing, setIsCartInitializing] = useState(false)
  const [hasHydratedCartOnce, setHasHydratedCartOnce] = useState(false)
  const [hasConfirmedCartStateFromApi, setHasConfirmedCartStateFromApi] = useState(false)
  const [checkoutQuoteItems, setCheckoutQuoteItems] = useState<CartItem[]>([])
  const [checkoutQuoteDetail, setCheckoutQuoteDetail] = useState<Record<string, unknown> | null>(null)
  const [checkoutQuoteStatus, setCheckoutQuoteStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const quoteCheckoutFinancialSummaryOverrideRef = useRef<{
    quoteId: number
    quoteMode: BusinessQuoteCheckoutMode
    summary: Record<string, unknown> | null
    freightPrefill: QuoteCartFreightPrefill | null | undefined
    checkoutPrefill: QuoteStartCheckoutPayload['checkoutPrefill']
  } | null>(null)
  const [checkoutOrderItems, setCheckoutOrderItems] = useState<CartItem[]>([])
  const [checkoutOrderStatus, setCheckoutOrderStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [checkoutOrderDetail, setCheckoutOrderDetail] = useState<Record<string, unknown> | null>(null)
  const [checkoutOrderUnavailableCount, setCheckoutOrderUnavailableCount] = useState(0)
  const [recentlyViewed, setRecentlyViewed] = useLocalStorage<Product[]>('recently-viewed', [])
  const [compareProducts, setCompareProducts] = useLocalStorage<Product[]>('compare-products', [])
  const buyerAccountSummaryFetchRef = useRef<{
    entidadeId: number | null
    status: 'idle' | 'loading' | 'loaded' | 'failed'
  }>({ entidadeId: null, status: 'idle' })
  const buyerAccountSummaryBeneficiosRevisionRef = useRef(0)
  const buyerSessionRestoreRef = useRef<{
    entidadeId: number | null
    status: 'idle' | 'loading' | 'loaded' | 'failed'
  }>({ entidadeId: null, status: 'idle' })
  const buyerRotatingSearchTermsFetchRef = useRef<{
    entidadeId: number | null
    status: 'idle' | 'loading' | 'loaded' | 'failed'
  }>({ entidadeId: null, status: 'idle' })
  const buyerRotatingSearchTermsRetryTimerRef = useRef<number | null>(null)
  const buyerAccessRevalidationRef = useRef<{
    entidadeId: number | null
    lastCheckedAt: number
    inFlight: boolean
  }>({ entidadeId: null, lastCheckedAt: 0, inFlight: false })
  const checkoutCustomerProfileFetchRef = useRef<{
    entidadeId: number | null
    status: 'idle' | 'loading' | 'loaded' | 'failed'
  }>({ entidadeId: null, status: 'idle' })
  const buyerSessionExpiryHandledRef = useRef(false)
  const cartItemsRef = useRef<CartItem[]>(cartItems)
  const quickCheckoutCartItemsRef = useRef<CartItem[]>(quickCheckoutCartItems)
  const currentCheckoutItemsRef = useRef<CartItem[]>([])
  const cartItemKeysByProductIdRef = useRef<Map<string, string[]>>(new Map())
  const cartMutationQueueRef = useRef<Promise<void>>(Promise.resolve())
  const cartMutationRevisionRef = useRef(0)
  const cartSyncSourceIdRef = useRef(createCartSyncSourceId())
  const productCheckoutLinkProcessingRef = useRef<string | null>(null)
  const lastProcessedCartSyncMessageRef = useRef<string | null>(null)
  const lastExternalCartRefreshAtRef = useRef(0)
  const lastCartPriceSyncSignatureRef = useRef<string | null>(null)
  const pendingCartSyncRef = useRef<Map<string, number>>(new Map())
  const cartSyncDebounceTimerRef = useRef<number | null>(null)
  const isCartAuthenticated = useMemo(() => {
    return !simplifiedStorefrontEnabled
      && effectiveIsLoggedIn
      && typeof authenticatedEntidadeId === 'number'
      && authenticatedEntidadeId > 0
  }, [authenticatedEntidadeId, effectiveIsLoggedIn, simplifiedStorefrontEnabled])
  const isCheckoutSessionAuthenticated = !simplifiedStorefrontEnabled
    && effectiveIsLoggedIn
    && !isLegacyCheckoutSessionBridge
  const quickCheckoutLojaId = getLojaVirtualDefaultLojaId() ?? null
  const quickCheckoutAffiliateCode = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('afiliado')?.trim() || null
    : null
  const isAnonymousQuickCheckoutAvailable = canUseAnonymousQuickCheckout({
    isLoggedIn: simplifiedStorefrontEnabled ? false : effectiveIsLoggedIn,
    isCartAuthenticated,
    publicPricingEnabled: shouldShowPrices(false, showPricesBeforeLogin),
    simplifiedStorefrontEnabled,
  })
  const isQuickCheckoutFlowAvailable = (
    isAnonymousQuickCheckoutAvailable
    || isProductCheckoutQuickFlowActive
    || Boolean(routeQuickCheckoutProductId)
    || Boolean(quickCheckoutAffiliateCode)
  )
  const updateProductCheckoutQuickFlow = useCallback((active: boolean) => {
    setIsProductCheckoutQuickFlowActive(active)
    persistProductCheckoutQuickFlowFlag(active)
  }, [])
  const visibleCartItems = isCartAuthenticated ? cartItems : []
  const activeCartItems = isQuickCheckoutFlowAvailable ? quickCheckoutCartItems : visibleCartItems
  const isQuoteCheckout = currentView === 'checkout' && routeCheckoutSource === 'quote' && Number(routeCheckoutQuoteId ?? 0) > 0
  const isOrderCheckout = currentView === 'checkout' && routeCheckoutSource === 'order' && Number(routeCheckoutOrderId ?? 0) > 0
  const checkoutItems = isQuoteCheckout
    ? checkoutQuoteItems
    : isOrderCheckout
      ? checkoutOrderItems
      : visibleCartItems
  const cartQuantityByProductId = useMemo(
    () =>
      activeCartItems.reduce<Record<string, number>>((acc, item) => {
        acc[item.product.id] = item.quantity
        return acc
      }, {}),
    [activeCartItems]
  )
  const broadcastCartChange = useCallback((reason: string) => {
    if (!isCartAuthenticated || typeof authenticatedEntidadeId !== 'number' || authenticatedEntidadeId <= 0) return
    if (typeof window === 'undefined') return

    const message: CartSyncMessage = {
      type: 'cart-updated',
      entidadeId: Math.trunc(authenticatedEntidadeId),
      sourceId: cartSyncSourceIdRef.current,
      reason: reason.trim() || 'updated',
      updatedAt: Date.now(),
    }

    try {
      const channel = new BroadcastChannel(CART_SYNC_BROADCAST_CHANNEL)
      channel.postMessage(message)
      channel.close()
    } catch {
      // BroadcastChannel is only an invalidation signal; cart data stays in site_carrinho.
    }
  }, [authenticatedEntidadeId, isCartAuthenticated])

  const clearBuyerRotatingSearchTermsRetryTimer = useCallback(() => {
    if (typeof window === 'undefined') return
    if (buyerRotatingSearchTermsRetryTimerRef.current === null) return
    window.clearTimeout(buyerRotatingSearchTermsRetryTimerRef.current)
    buyerRotatingSearchTermsRetryTimerRef.current = null
  }, [])

  const scheduleBuyerRotatingSearchTermsRetry = useCallback((entidadeId: number) => {
    if (typeof window === 'undefined') return
    if (buyerRotatingSearchTermsRetryTimerRef.current !== null) return

    buyerRotatingSearchTermsRetryTimerRef.current = window.setTimeout(() => {
      buyerRotatingSearchTermsRetryTimerRef.current = null
      const current = buyerRotatingSearchTermsFetchRef.current
      if (current.entidadeId !== entidadeId || current.status !== 'failed') return
      buyerRotatingSearchTermsFetchRef.current = { entidadeId, status: 'idle' }
      setBuyerRotatingSearchTermsRetryTick((value) => value + 1)
    }, 15_000)
  }, [])

  const redirectToLoginForCart = useCallback(() => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setShowCartDrawer(false)
      setCartDrawerSource('default')
      setPostLoginRedirect({ view: 'checkout' })
      setCurrentView('login')
    })
    toast.info(t('cart.login_to_view_cart'))
  }, [invalidatePendingProductDetailRequests, runViewTransition, t])

  const prefetchCartDrawer = useCallback(() => {
    void loadCartDrawer()
  }, [])

  const handleCartDrawerOpen = useCallback(() => {
    if (!isCartAuthenticated && !isAnonymousQuickCheckoutAvailable) {
      redirectToLoginForCart()
      return
    }

    flushSync(() => {
      setCartDrawerSource(isPriceTableView(currentViewRef.current) ? 'price-table' : 'default')
      if (isPriceTableView(currentViewRef.current)) {
        setShowPriceTableCartPanel(true)
        setIsPriceTableCartPanelCollapsed(false)
      }
      setShowCartDrawer(true)
    })
    void loadCartDrawer()
  }, [isAnonymousQuickCheckoutAvailable, isCartAuthenticated, redirectToLoginForCart])

  const navigateToCheckout = useCallback((
    step: CheckoutRouteStep = DEFAULT_CHECKOUT_STEP,
  options?: {
    source?: 'cart' | 'quote' | 'order'
    quoteId?: number | null
    quoteMode?: BusinessQuoteCheckoutMode | null
    orderId?: number | null
    orderMode?: 'direct' | 'edit' | null
    cartShippingPrefill?: CartFreightSelection | null
    cartFinancialPrefill?: CheckoutSimulationResult | null
    allowSellerAccount?: boolean
  }
) => {
  if (isSellerAccount && !options?.allowSellerAccount) {
    toast.info('Usuários vendedores podem apenas montar e salvar orçamentos.')
    return
  }
    const hasOrderSource = options?.source === 'order' && Number(options?.orderId ?? 0) > 0
    const hasQuoteSource = options?.source === 'quote' && Number(options?.quoteId ?? 0) > 0
    const nextSource: 'cart' | 'quote' | 'order' = hasOrderSource ? 'order' : hasQuoteSource ? 'quote' : 'cart'
    const nextQuoteId = nextSource === 'quote' ? Number(options?.quoteId ?? 0) || null : null
    const nextQuoteMode = nextSource === 'quote' ? options?.quoteMode ?? null : null
    const nextOrderId = nextSource === 'order' ? Number(options?.orderId ?? 0) || null : null
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setCheckoutRouteStep(step)
      setRouteCheckoutSource(nextSource)
      setRouteCheckoutQuoteId(nextQuoteId)
      setRouteCheckoutQuoteMode(nextQuoteMode)
      setRouteCheckoutOrderId(nextOrderId)
      setRouteCheckoutOrderMode(nextSource === 'order' ? (options?.orderMode ?? 'edit') : null)
      setCartCheckoutShippingPrefill(nextSource === 'cart' ? options?.cartShippingPrefill ?? null : null)
      setCartCheckoutFinancialPrefill(nextSource === 'cart' ? options?.cartFinancialPrefill ?? null : null)
      setCurrentView('checkout')
    })
  }, [invalidatePendingProductDetailRequests, isSellerAccount, runViewTransition])

  const navigateToPendingOrderPayment = useCallback((orderId: number) => {
    const normalizedOrderId = Math.trunc(Number(orderId) || 0)
    if (normalizedOrderId <= 0) return

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams()
      params.set('pedido_id', String(normalizedOrderId))
      window.history.pushState(
        window.history.state,
        '',
        `${VIEW_PATHS.checkout}/success?${params.toString()}`
      )
    }

    // Retomar pagamento nunca pode usar o checkout de recompra direta: esse
    // modo gera uma nova nota a partir do pedido original.
    navigateToCheckout('success')
  }, [navigateToCheckout])

  useEffect(() => {
    if (
      currentView !== 'quick-checkout'
      || !effectiveIsLoggedIn
      || isProductCheckoutQuickFlowActive
      || routeQuickCheckoutProductId
      || quickCheckoutAffiliateCode
    ) return
    navigateToCheckout('cart')
  }, [
    currentView,
    effectiveIsLoggedIn,
    isProductCheckoutQuickFlowActive,
    navigateToCheckout,
    quickCheckoutAffiliateCode,
    routeQuickCheckoutProductId,
  ])

const navigateToQuoteCheckout = useCallback((quoteId: number, payload?: QuoteStartCheckoutPayload) => {
  const requestedBusinessMode = payload?.businessMode ?? null
  const effectiveBusinessMode: BusinessQuoteCheckoutMode = payload?.selfPurchase
    ? 'partner'
    : requestedBusinessMode ?? 'partner'
  const quoteMode = payload?.selfPurchase ? null : requestedBusinessMode
  const financialSummary = payload?.financialSummary
    && typeof payload.financialSummary === 'object'
    && !Array.isArray(payload.financialSummary)
      ? payload.financialSummary as Record<string, unknown>
      : null
  const hasOriginSnapshot = Boolean(
    financialSummary
    || payload?.freightPrefill !== undefined
    || payload?.checkoutPrefill !== undefined
  )
  if (hasOriginSnapshot) {
    quoteCheckoutFinancialSummaryOverrideRef.current = {
      quoteId,
      quoteMode: effectiveBusinessMode,
      summary: financialSummary,
      freightPrefill: payload?.freightPrefill,
      checkoutPrefill: payload?.checkoutPrefill,
    }
  } else {
    quoteCheckoutFinancialSummaryOverrideRef.current = null
  }
  const scope = `quote:${quoteId}:${quoteMode ?? 'default'}`
  clearScopedCheckoutDraft(scope)
  clearScopedCheckoutTransientState(scope)
  navigateToCheckout('cart', { source: 'quote', quoteId, quoteMode })
}, [clearScopedCheckoutDraft, clearScopedCheckoutTransientState, navigateToCheckout])

const navigateToBusinessQuoteCheckout = useCallback((quoteId: number, payload?: QuoteStartCheckoutPayload) => {
  const requestedBusinessMode = payload?.businessMode ?? null
  const effectiveBusinessMode: BusinessQuoteCheckoutMode = payload?.selfPurchase
    ? 'partner'
    : requestedBusinessMode ?? 'partner'
  const quoteMode = payload?.selfPurchase ? null : requestedBusinessMode
  const financialSummary = payload?.financialSummary
    && typeof payload.financialSummary === 'object'
    && !Array.isArray(payload.financialSummary)
      ? payload.financialSummary as Record<string, unknown>
      : null
  const hasOriginSnapshot = Boolean(
    financialSummary
    || payload?.freightPrefill !== undefined
    || payload?.checkoutPrefill !== undefined
  )
  if (hasOriginSnapshot) {
    quoteCheckoutFinancialSummaryOverrideRef.current = {
      quoteId,
      quoteMode: effectiveBusinessMode,
      summary: financialSummary,
      freightPrefill: payload?.freightPrefill,
      checkoutPrefill: payload?.checkoutPrefill,
    }
  } else {
    quoteCheckoutFinancialSummaryOverrideRef.current = null
  }
  const scope = `quote:${quoteId}:${quoteMode ?? 'default'}`
  if (payload?.selfPurchase) {
    clearScopedCheckoutDraft(scope)
  }
  clearScopedCheckoutTransientState(scope)
  navigateToCheckout('cart', {
    source: 'quote',
    quoteId,
    quoteMode,
    allowSellerAccount: true,
  })
}, [clearScopedCheckoutDraft, clearScopedCheckoutTransientState, navigateToCheckout])

  const navigateToOrderCheckout = useCallback((
    orderId: number,
    orderMode: 'direct' | 'edit' = 'edit'
  ) => {
    clearScopedCheckoutDraft(`order:${orderId}`)
    clearScopedCheckoutTransientState(`order:${orderId}`)
    navigateToCheckout(orderMode === 'direct' ? 'success' : 'cart', { source: 'order', orderId, orderMode })
  }, [clearScopedCheckoutDraft, clearScopedCheckoutTransientState, navigateToCheckout])

  useEffect(() => {
    if (!effectiveIsLoggedIn || currentView !== 'checkout' || routeCheckoutSource !== 'quote' || !routeCheckoutQuoteId) {
      setCheckoutQuoteItems([])
      setCheckoutQuoteDetail(null)
      setCheckoutQuoteStatus('idle')
      return
    }

    let cancelled = false
    setCheckoutQuoteStatus('loading')

    void getOrcamentoById(routeCheckoutQuoteId)
      .then((quote) => {
        if (cancelled) return
        const mappedItems = mapQuoteDetailToCheckoutItems(quote)
        if (mappedItems.length <= 0) {
          throw new Error('Este orçamento não possui itens válidos para checkout.')
        }
        const quoteRecord = quote && typeof quote === 'object' && !Array.isArray(quote)
          ? quote as Record<string, unknown>
          : null
        const summaryOverride = quoteCheckoutFinancialSummaryOverrideRef.current
        const normalizedRouteMode = routeCheckoutQuoteMode ?? 'partner'
        const shouldApplyOriginSnapshot = Boolean(
          quoteRecord
          && summaryOverride
          && summaryOverride.quoteId === routeCheckoutQuoteId
          && summaryOverride.quoteMode === normalizedRouteMode
        )
        const checkoutPrefillKey = normalizedRouteMode === 'resale'
          ? 'checkout_prefill_revenda'
          : 'checkout_prefill'
        const checkoutPrefillCamelKey = normalizedRouteMode === 'resale'
          ? 'checkoutPrefillRevenda'
          : 'checkoutPrefill'
        const rawCheckoutPrefill = shouldApplyOriginSnapshot
          ? quoteRecord?.[checkoutPrefillKey] ?? quoteRecord?.[checkoutPrefillCamelKey]
          : null
        const checkoutPrefill = rawCheckoutPrefill
          && typeof rawCheckoutPrefill === 'object'
          && !Array.isArray(rawCheckoutPrefill)
            ? rawCheckoutPrefill as Record<string, unknown>
            : {}
        const originPrefill = summaryOverride?.checkoutPrefill
        const freightPrefill = summaryOverride?.freightPrefill
        const hasFreightOverride = shouldApplyOriginSnapshot && freightPrefill !== undefined
        const freightSelectionOverride = hasFreightOverride && freightPrefill
          ? {
              tipo_frete: freightPrefill.freightType,
              frete_por_conta: freightPrefill.freightType === 'fob' ? 1 : 0,
              transportadora_id: freightPrefill.carrierId ?? null,
              transportadora_nome: freightPrefill.carrierName ?? '',
              cotacao_id: freightPrefill.carrierQuoteId ?? null,
              numero_cotacao: freightPrefill.quoteNumber ?? null,
              prazo: freightPrefill.eta ?? '',
              valor: Math.max(0, freightPrefill.freightValue ?? 0),
              usar_redespacho: freightPrefill.usarRedespacho ?? 0,
              transportadora_redespacho_id: freightPrefill.transportadoraRedespachoId ?? null,
              redespacho: freightPrefill.usarRedespacho === 1
                ? {
                    transportadora_id: freightPrefill.transportadoraRedespachoId ?? null,
                    transportadora_nome: freightPrefill.transportadoraRedespachoNome ?? '',
                    endereco_resumo: freightPrefill.transportadoraRedespachoEnderecoResumo ?? '',
                  }
                : null,
            }
          : null
        const originPrefillFields = originPrefill
          ? {
              endereco_id: originPrefill.addressId ?? null,
              endereco_padrao: originPrefill.useDefaultAddress === true,
              contato_nome: originPrefill.contactName ?? '',
              contato_telefone: originPrefill.contactPhone ?? '',
              forma_pagamento_id: originPrefill.formaPagamentoId ?? null,
              condicao_pagamento_id: originPrefill.condicaoPagamentoId ?? null,
              finalidade_padrao: originPrefill.finalidade ?? null,
            }
          : {}
        const originFreightPrefillFields = hasFreightOverride
          ? freightPrefill
            ? {
                modalidade_frete: freightPrefill.freightType,
                frete_por_conta: freightPrefill.freightType === 'fob' ? 1 : 0,
                transportadora_id: freightPrefill.carrierId ?? null,
                transportadora_nome: freightPrefill.carrierName ?? '',
                cotacao_id: freightPrefill.carrierQuoteId ?? null,
                transportadora_cotacao_frete_numero: freightPrefill.quoteNumber ?? null,
                transportadora_cotacao_frete_valor: Math.max(0, freightPrefill.freightValue ?? 0),
                prazo: freightPrefill.eta ?? '',
              }
            : {
                transportadora_id: null,
                transportadora_nome: '',
                cotacao_id: null,
                transportadora_cotacao_frete_numero: null,
                transportadora_cotacao_frete_valor: null,
              }
          : {}
        const effectiveCheckoutPrefill = shouldApplyOriginSnapshot && summaryOverride
          ? {
              ...checkoutPrefill,
              ...originPrefillFields,
              ...originFreightPrefillFields,
              ...(summaryOverride.summary
                ? {
                    resumo_financeiro: summaryOverride.summary,
                    resumo_financeiro_exato: true,
                  }
                : {}),
            }
          : checkoutPrefill
        const freightSelectionKey = normalizedRouteMode === 'resale'
          ? 'frete_selecionado_revenda'
          : 'frete_selecionado'
        const freightSelectionCamelKey = normalizedRouteMode === 'resale'
          ? 'freteSelecionadoRevenda'
          : 'freteSelecionado'
        const effectiveQuoteRecord = shouldApplyOriginSnapshot && quoteRecord && summaryOverride
          ? {
              ...quoteRecord,
              [checkoutPrefillKey]: effectiveCheckoutPrefill,
              [checkoutPrefillCamelKey]: effectiveCheckoutPrefill,
              ...(hasFreightOverride
                ? {
                    [freightSelectionKey]: freightSelectionOverride,
                    [freightSelectionCamelKey]: freightSelectionOverride,
                  }
                : {}),
            }
          : quoteRecord
        if (shouldApplyOriginSnapshot) {
          quoteCheckoutFinancialSummaryOverrideRef.current = null
        }
        setCheckoutQuoteDetail(effectiveQuoteRecord)
        setCheckoutQuoteItems(mappedItems)
        setCheckoutQuoteStatus('ready')
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load quote checkout items:', error)
        setCheckoutQuoteItems([])
        setCheckoutQuoteDetail(null)
        setCheckoutQuoteStatus('error')
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : 'Não foi possível abrir este orçamento no checkout.'
        )
        navigateToView('quotes')
      })

    return () => {
      cancelled = true
    }
  }, [
    currentView,
    effectiveIsLoggedIn,
    navigateToView,
    routeCheckoutQuoteId,
    routeCheckoutQuoteMode,
    routeCheckoutSource,
  ])

  useEffect(() => {
    if (!effectiveIsLoggedIn || currentView !== 'checkout' || routeCheckoutSource !== 'order' || !routeCheckoutOrderId) {
      setCheckoutOrderItems([])
      setCheckoutOrderStatus('idle')
      setCheckoutOrderDetail(null)
      setCheckoutOrderUnavailableCount(0)
      return
    }

    if (routeCheckoutOrderMode === 'direct') {
      setCheckoutOrderItems([])
      setCheckoutOrderStatus('ready')
      setCheckoutOrderDetail(null)
      setCheckoutOrderUnavailableCount(0)
      return
    }

    let cancelled = false
    setCheckoutOrderStatus('loading')

    void getPedidoById(routeCheckoutOrderId)
      .then(async (order) => {
        if (cancelled || !order || typeof order !== 'object') return

        const normalizedOrder = order as Record<string, unknown>
        const { availableEntries, unavailableCount } = await buildOrderCheckoutItems(normalizedOrder)
        if (cancelled) return

        if (availableEntries.length <= 0) {
          throw new Error('Este pedido não possui itens válidos para recompra.')
        }

        setCheckoutOrderDetail(normalizedOrder)
        setCheckoutOrderItems(availableEntries)
        setCheckoutOrderUnavailableCount(unavailableCount)
        setCheckoutOrderStatus('ready')

        if (unavailableCount > 0) {
          toast.warning(
            unavailableCount === 1
              ? '1 item do pedido original não está mais disponível para recompra.'
              : `${unavailableCount} itens do pedido original não estão mais disponíveis para recompra.`
          )
        }
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load order checkout items:', error)
        setCheckoutOrderItems([])
        setCheckoutOrderStatus('error')
        setCheckoutOrderDetail(null)
        setCheckoutOrderUnavailableCount(0)
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : 'Não foi possível abrir este pedido na recompra.'
        )
        navigateToView('orders')
      })

    return () => {
      cancelled = true
    }
  }, [currentView, effectiveIsLoggedIn, navigateToView, routeCheckoutOrderId, routeCheckoutOrderMode, routeCheckoutSource])

  const applyCartItemsSnapshot = useCallback((nextItems: CartItem[]) => {
    const mergedItems = mergeCartItemsByProductId(nextItems)
    setCartItems(mergedItems)
    cartItemsRef.current = mergedItems
  }, [])

  const handleBuyerAccessRevoked = useCallback((reason?: string) => {
    buyerSessionExpiryHandledRef.current = true
    clearAuthenticatedSessionStorage()
    clearAuthenticatedEntidadeId()
    buyerAccessRevalidationRef.current = { entidadeId: null, lastCheckedAt: 0, inFlight: false }
    buyerRotatingSearchTermsFetchRef.current = { entidadeId: null, status: 'idle' }
    clearBuyerRotatingSearchTermsRetryTimer()
    flushSync(() => {
      setIsLogoutRequested(true)
      setIsLoggedIn(false)
      setAccountProfile(null)
      setIndicatorDelegation(null)
      setAuthenticatedSearchHighlights([])
      setCartItems([])
      setShowCartDrawer(false)
      setCartDrawerSource('default')
    })
    cartItemsRef.current = []
    cartItemKeysByProductIdRef.current = new Map()
    pendingCartSyncRef.current = new Map()
    if (cartSyncDebounceTimerRef.current !== null) {
      window.clearTimeout(cartSyncDebounceTimerRef.current)
      cartSyncDebounceTimerRef.current = null
    }
    clearAccountProfileCookies()
    navigateHome()
    toast.info(reason?.trim() || 'Seu acesso à loja foi desativado. Faça contato com a equipe para liberar novamente.')
  }, [navigateHome, setAccountProfile, setIsLoggedIn, setIsLogoutRequested])

  useEffect(() => {
    return subscribeBackendAvailability(setBackendAvailability)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleBuyerSessionTokenReset = () => {
      clearBuyerSessionTokenFromPersistedAccountProfileStorage()
      setAccountProfile((current) => stripBuyerSessionTokenFromAccountProfile(current))
    }

    const handleBuyerSessionExpired = (event: Event) => {
      if (buyerSessionExpiryHandledRef.current) return
      if (!effectiveIsLoggedIn && !isLoggedIn && typeof authenticatedEntidadeId !== 'number') return

      buyerSessionExpiryHandledRef.current = true
      clearBuyerSessionTokenFromPersistedAccountProfileStorage()
      setAccountProfile((current) => stripBuyerSessionTokenFromAccountProfile(current))

      const detail = event instanceof CustomEvent
        ? event.detail as { reason?: unknown } | null | undefined
        : undefined
      const reason = typeof detail?.reason === 'string' && detail.reason.trim()
        ? detail.reason.trim()
        : 'Sua sessão expirou. Faça login novamente.'

      handleBuyerAccessRevoked(reason)
    }

    window.addEventListener(BUYER_SESSION_TOKEN_RESET_EVENT, handleBuyerSessionTokenReset)
    window.addEventListener(BUYER_SESSION_EXPIRED_EVENT, handleBuyerSessionExpired)

    return () => {
      window.removeEventListener(BUYER_SESSION_TOKEN_RESET_EVENT, handleBuyerSessionTokenReset)
      window.removeEventListener(BUYER_SESSION_EXPIRED_EVENT, handleBuyerSessionExpired)
    }
  }, [authenticatedEntidadeId, effectiveIsLoggedIn, handleBuyerAccessRevoked, isLoggedIn, setAccountProfile])

  useEffect(() => {
    if (authBootstrapAttemptedRef.current) return
    authBootstrapAttemptedRef.current = true
    if (typeof window === 'undefined') {
      setIsAuthBootstrapResolved(true)
      return
    }
    if (new URLSearchParams(window.location.search).get('store_network') === 'handoff') {
      handoffBootstrapInProgressRef.current = true
      void getStoreNetworkHandoffSession()
        .then(async (handoff) => {
          const entidadeId = Number(handoff.entidade_id)
          const token = String(handoff.buyer_session_token ?? '').trim()
          if (!Number.isInteger(entidadeId) || entidadeId <= 0 || !token) {
            throw new Error('invalid_store_network_handoff_session')
          }
          setAuthenticatedEntidadeId(entidadeId)
          setBuyerSessionToken(token)
          const summary = await getBuyerAccountSummary()
          if (!summary || summary.entidadeId !== entidadeId) {
            throw new Error('store_network_handoff_profile_unavailable')
          }
          const document = normalizeDocumentDigits(summary.document)
          const profile: AccountProfile = {
            entidadeId,
            corporateName: summary.corporateName || summary.name,
            name: summary.name || summary.corporateName,
            email: summary.email,
            phone: summary.phone,
            cnpj: document,
            document,
            activity: summary.activity,
            stateRegistration: summary.stateRegistration,
            street: summary.street,
            number: summary.number,
            complement: summary.complement,
            neighborhood: summary.neighborhood,
            city: summary.city,
            state: summary.state,
            zipCode: summary.zipCode,
            buyerSessionToken: summary.buyerSessionToken || token,
            commerceContext: summary.commerceContext,
            buyerUser: summary.buyerUser,
          }
          setIsLogoutRequested(false)
          setAccountProfile(profile)
          persistAuthenticatedSessionStorage(profile)
          persistAccountProfileCookies(profile)
          setIsLoggedIn(true)
          const url = new URL(window.location.href)
          url.searchParams.delete('store_network')
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
          handoffBootstrapInProgressRef.current = false
          setIsAuthBootstrapResolved(true)
        })
        .catch(() => {
          handoffBootstrapInProgressRef.current = false
          clearAuthenticatedSessionStorage()
          clearAccountProfileCookies()
          setAccountProfile(null)
          setIsLoggedIn(false)
          setIsAuthBootstrapResolved(true)
          toast.error('Não foi possível iniciar o acesso temporário à Source. Tente novamente pela Brasiltec.')
        })
      return
    }
    if (isLogoutRequested) {
      setIsAuthBootstrapResolved(true)
      return
    }
    if (effectiveIsLoggedIn) {
      setIsAuthBootstrapResolved(true)
      return
    }

    let persistedLoginFlag = false
    try {
      persistedLoginFlag = window.localStorage.getItem(AUTH_LOGIN_FLAG_STORAGE_KEY) === 'true'
    } catch {
      persistedLoginFlag = false
    }

    const cookieProfile = resolveAccountProfileFromCookies()
    const persistedProfile = readPersistedAccountProfileStorage()
    const buyerSessionToken = resolvedBuyerSessionToken
    const entidadeId =
      accountProfile?.entidadeId
      || persistedProfile?.entidadeId
      || cookieProfile.entidadeId
      || getAuthenticatedEntidadeId()

    if (!persistedLoginFlag && !entidadeId) {
      setIsAuthBootstrapResolved(true)
      return
    }
    if (!entidadeId) {
      setIsAuthBootstrapResolved(true)
      return
    }
    if (!buyerSessionToken) {
      setAuthenticatedEntidadeId(entidadeId)
      let active = true

      void getBuyerAccountSummary()
        .then((summary) => {
          if (!active) return
          const restoredBuyerSessionToken = String(summary?.buyerSessionToken ?? '').trim()
          if (!summary || summary.entidadeId !== entidadeId || !restoredBuyerSessionToken) {
            setIsAuthBootstrapResolved(true)
            return
          }

          const normalizedDocument = normalizeDocumentDigits(summary.document)
          const recoveredProfile: AccountProfile = {
            entidadeId,
            corporateName: summary.corporateName || accountProfile?.corporateName || persistedProfile?.corporateName || cookieProfile.corporateName || summary.name,
            name: summary.name || accountProfile?.name || persistedProfile?.name || cookieProfile.name || summary.corporateName,
            email: summary.email || accountProfile?.email || persistedProfile?.email || cookieProfile.email,
            phone: summary.phone || accountProfile?.phone || persistedProfile?.phone || cookieProfile.phone,
            cnpj: normalizedDocument || accountProfile?.cnpj || persistedProfile?.cnpj || cookieProfile.cnpj,
            document: normalizedDocument || accountProfile?.document || persistedProfile?.document || cookieProfile.document || accountProfile?.cnpj || persistedProfile?.cnpj || cookieProfile.cnpj,
            activity: summary.activity || accountProfile?.activity || persistedProfile?.activity || cookieProfile.activity,
            stateRegistration: summary.stateRegistration || accountProfile?.stateRegistration || persistedProfile?.stateRegistration || cookieProfile.stateRegistration,
            street: summary.street || accountProfile?.street || persistedProfile?.street,
            number: summary.number || accountProfile?.number || persistedProfile?.number,
            complement: summary.complement || accountProfile?.complement || persistedProfile?.complement,
            neighborhood: summary.neighborhood || accountProfile?.neighborhood || persistedProfile?.neighborhood,
            city: summary.city || accountProfile?.city || persistedProfile?.city,
            state: summary.state || accountProfile?.state || persistedProfile?.state,
            zipCode: summary.zipCode || accountProfile?.zipCode || persistedProfile?.zipCode,
            tipoPessoa: accountProfile?.tipoPessoa ?? persistedProfile?.tipoPessoa ?? cookieProfile.tipoPessoa ?? null,
            buyerSessionToken: restoredBuyerSessionToken,
            commerceContext: summary.commerceContext || accountProfile?.commerceContext || persistedProfile?.commerceContext,
            buyerUser: summary.buyerUser || accountProfile?.buyerUser || persistedProfile?.buyerUser,
          }

          setAccountProfile(recoveredProfile)
          persistAuthenticatedSessionStorage(recoveredProfile)
          persistAccountProfileCookies(recoveredProfile)
          setIsLoggedIn(true)
          setIsAuthBootstrapResolved(true)
        })
        .catch(() => {
          if (active) setIsAuthBootstrapResolved(true)
        })

      return () => {
        active = false
      }
    }

    const recoveredProfile: AccountProfile = {
      entidadeId,
      corporateName: accountProfile?.corporateName || persistedProfile?.corporateName || cookieProfile.corporateName || accountProfile?.name || persistedProfile?.name || cookieProfile.name,
      name: accountProfile?.name || persistedProfile?.name || cookieProfile.name,
      email: accountProfile?.email || persistedProfile?.email || cookieProfile.email,
      phone: accountProfile?.phone || persistedProfile?.phone || cookieProfile.phone,
      cnpj: accountProfile?.cnpj || persistedProfile?.cnpj || cookieProfile.cnpj,
      document: accountProfile?.document || persistedProfile?.document || cookieProfile.document || accountProfile?.cnpj || persistedProfile?.cnpj || cookieProfile.cnpj,
      activity: accountProfile?.activity || persistedProfile?.activity || cookieProfile.activity,
      stateRegistration: accountProfile?.stateRegistration || persistedProfile?.stateRegistration || cookieProfile.stateRegistration,
      street: accountProfile?.street || persistedProfile?.street,
      number: accountProfile?.number || persistedProfile?.number,
      complement: accountProfile?.complement || persistedProfile?.complement,
      neighborhood: accountProfile?.neighborhood || persistedProfile?.neighborhood,
      city: accountProfile?.city || persistedProfile?.city,
      state: accountProfile?.state || persistedProfile?.state,
      zipCode: accountProfile?.zipCode || persistedProfile?.zipCode,
      tipoPessoa: accountProfile?.tipoPessoa ?? persistedProfile?.tipoPessoa ?? cookieProfile.tipoPessoa ?? null,
      buyerSessionToken,
      commerceContext: accountProfile?.commerceContext || persistedProfile?.commerceContext,
      buyerUser: accountProfile?.buyerUser || persistedProfile?.buyerUser,
    }

    setAuthenticatedEntidadeId(entidadeId)
    setAccountProfile((current) => current ?? recoveredProfile)
    persistAuthenticatedSessionStorage(recoveredProfile)
    persistAccountProfileCookies(recoveredProfile)
    setIsLoggedIn(true)
    setIsAuthBootstrapResolved(true)
  }, [accountProfile, effectiveIsLoggedIn, isLoggedIn, isLogoutRequested, resolvedBuyerSessionToken, setAccountProfile, setIsLoggedIn, setIsLogoutRequested])

  useEffect(() => {
    if (isLoggedIn || !effectiveIsLoggedIn) return

    setIsLoggedIn(true)
    if (!accountProfile && effectiveAccountProfile) {
      setAccountProfile(effectiveAccountProfile)
    }
  }, [accountProfile, effectiveAccountProfile, effectiveIsLoggedIn, isLoggedIn, setAccountProfile, setIsLoggedIn])

  useEffect(() => {
    if (effectiveIsLoggedIn && !isAuthBootstrapResolved && !handoffBootstrapInProgressRef.current) {
      setIsAuthBootstrapResolved(true)
    }
  }, [effectiveIsLoggedIn, isAuthBootstrapResolved])

  useEffect(() => {
    if (
      isLogoutRequested
      || isLegacyCheckoutSessionBridge
      || !isLoggedIn
      || hasBuyerSessionToken
      || !hasAuthenticatedEntidadeId
    ) {
      if (hasBuyerSessionToken || !isLoggedIn || isLogoutRequested) {
        buyerSessionRestoreRef.current = { entidadeId: null, status: 'idle' }
      }
      return
    }

    const entidadeId = authenticatedEntidadeId
    if (typeof entidadeId !== 'number' || entidadeId <= 0) return

    if (buyerSessionRestoreRef.current.entidadeId !== entidadeId) {
      buyerSessionRestoreRef.current = { entidadeId, status: 'idle' }
    }

    if (buyerSessionRestoreRef.current.status !== 'idle') return

    let active = true
    buyerSessionRestoreRef.current = { entidadeId, status: 'loading' }

    void getBuyerAccountSummary()
      .then((summary) => {
        if (!active) return

        const restoredBuyerSessionToken = String(summary?.buyerSessionToken ?? '').trim()
        if (!summary || summary.entidadeId !== entidadeId || !restoredBuyerSessionToken) {
          buyerSessionRestoreRef.current = { entidadeId, status: 'failed' }
          return
        }

        const baseProfile = effectiveAccountProfile
        const normalizedDocument = normalizeDocumentDigits(summary.document)
        const recoveredProfile: AccountProfile = {
          entidadeId,
          corporateName: summary.corporateName || baseProfile?.corporateName || baseProfile?.name || summary.name,
          name: summary.name || baseProfile?.name || summary.corporateName,
          email: summary.email || baseProfile?.email,
          phone: summary.phone || baseProfile?.phone,
          cnpj: normalizedDocument || baseProfile?.cnpj,
          document: normalizedDocument || baseProfile?.document || baseProfile?.cnpj,
          activity: summary.activity || baseProfile?.activity,
          stateRegistration: summary.stateRegistration || baseProfile?.stateRegistration,
          street: summary.street || baseProfile?.street,
          number: summary.number || baseProfile?.number,
          complement: summary.complement || baseProfile?.complement,
          neighborhood: summary.neighborhood || baseProfile?.neighborhood,
          city: summary.city || baseProfile?.city,
          state: summary.state || baseProfile?.state,
          zipCode: summary.zipCode || baseProfile?.zipCode,
          tipoPessoa: baseProfile?.tipoPessoa ?? null,
          buyerSessionToken: restoredBuyerSessionToken,
          commerceContext: summary.commerceContext || baseProfile?.commerceContext,
          buyerUser: summary.buyerUser || baseProfile?.buyerUser,
        }

        setAuthenticatedEntidadeId(entidadeId)
        setAccountProfile(recoveredProfile)
        persistAuthenticatedSessionStorage(recoveredProfile)
        persistAccountProfileCookies(recoveredProfile)
        setIsLoggedIn(true)
        buyerSessionRestoreRef.current = { entidadeId, status: 'loaded' }
      })
      .catch(() => {
        if (!active) return
        buyerSessionRestoreRef.current = { entidadeId, status: 'failed' }
      })

    return () => {
      active = false
    }
  }, [
    authenticatedEntidadeId,
    effectiveAccountProfile,
    hasAuthenticatedEntidadeId,
    hasBuyerSessionToken,
    isLegacyCheckoutSessionBridge,
    isLoggedIn,
    isLogoutRequested,
    setAccountProfile,
    setIsLoggedIn,
  ])

  useEffect(() => {
    if (!effectiveIsLoggedIn) return
    const entidadeId =
      authenticatedEntidadeId

    if (!entidadeId) {
      clearAuthenticatedSessionStorage()
      setIsLoggedIn(false)
      setAccountProfile(null)
      clearAccountProfileCookies()
      return
    }

    setAuthenticatedEntidadeId(entidadeId)
    const mergedProfile: AccountProfile = {
      entidadeId,
      corporateName: effectiveAccountProfile?.corporateName || effectiveAccountProfile?.name,
      name: effectiveAccountProfile?.name,
      email: effectiveAccountProfile?.email,
      phone: effectiveAccountProfile?.phone,
      cnpj: effectiveAccountProfile?.cnpj,
      document: effectiveAccountProfile?.document || effectiveAccountProfile?.cnpj,
      activity: effectiveAccountProfile?.activity,
      stateRegistration: effectiveAccountProfile?.stateRegistration,
      street: effectiveAccountProfile?.street,
      number: effectiveAccountProfile?.number,
      complement: effectiveAccountProfile?.complement,
      neighborhood: effectiveAccountProfile?.neighborhood,
      city: effectiveAccountProfile?.city,
      state: effectiveAccountProfile?.state,
      zipCode: effectiveAccountProfile?.zipCode,
      tipoPessoa: effectiveAccountProfile?.tipoPessoa ?? null,
      buyerSessionToken: effectiveAccountProfile?.buyerSessionToken,
      commerceContext: effectiveAccountProfile?.commerceContext,
      buyerUser: effectiveAccountProfile?.buyerUser,
    }
    const hasProfileChanged =
      accountProfile?.entidadeId !== mergedProfile.entidadeId
      || accountProfile?.corporateName !== mergedProfile.corporateName
      || accountProfile?.name !== mergedProfile.name
      || accountProfile?.email !== mergedProfile.email
      || accountProfile?.phone !== mergedProfile.phone
      || accountProfile?.cnpj !== mergedProfile.cnpj
      || accountProfile?.document !== mergedProfile.document
      || accountProfile?.activity !== mergedProfile.activity
      || accountProfile?.stateRegistration !== mergedProfile.stateRegistration
      || accountProfile?.street !== mergedProfile.street
      || accountProfile?.number !== mergedProfile.number
      || accountProfile?.complement !== mergedProfile.complement
      || accountProfile?.neighborhood !== mergedProfile.neighborhood
      || accountProfile?.city !== mergedProfile.city
      || accountProfile?.state !== mergedProfile.state
      || accountProfile?.zipCode !== mergedProfile.zipCode
      || (accountProfile?.tipoPessoa ?? null) !== (mergedProfile.tipoPessoa ?? null)
      || accountProfile?.buyerSessionToken !== mergedProfile.buyerSessionToken
      || JSON.stringify(accountProfile?.commerceContext ?? null) !== JSON.stringify(mergedProfile.commerceContext ?? null)
      || JSON.stringify(accountProfile?.buyerUser ?? null) !== JSON.stringify(mergedProfile.buyerUser ?? null)

    if (hasProfileChanged) {
      setAccountProfile(mergedProfile)
      persistAuthenticatedSessionStorage(mergedProfile)
      persistAccountProfileCookies(mergedProfile)
    }
  }, [accountProfile, authenticatedEntidadeId, effectiveAccountProfile, effectiveIsLoggedIn, setAccountProfile, setIsLoggedIn])

  useEffect(() => {
    if (!accountProfile) return
    persistAuthenticatedSessionStorage(accountProfile)
    persistAccountProfileCookies(accountProfile)
  }, [accountProfile])

  useEffect(() => {
    if (
      isLegacyCheckoutSessionBridge
      || !effectiveIsLoggedIn
      || typeof authenticatedEntidadeId !== 'number'
      || authenticatedEntidadeId <= 0
      || typeof window === 'undefined'
    ) {
      buyerAccessRevalidationRef.current = { entidadeId: null, lastCheckedAt: 0, inFlight: false }
      buyerPriceTableAccessLinkRequestRef.current = { entidadeId: null, status: 'idle' }
      return
    }

    const entidadeId = Math.trunc(authenticatedEntidadeId)
    if (buyerAccessRevalidationRef.current.entidadeId !== entidadeId) {
      buyerAccessRevalidationRef.current = { entidadeId, lastCheckedAt: 0, inFlight: false }
      buyerPriceTableAccessLinkRequestRef.current = { entidadeId, status: 'idle' }
    }

    let active = true
    const validateAccess = (force = false) => {
      const current = buyerAccessRevalidationRef.current
      const now = Date.now()

      if (!active || current.inFlight) return
      if (!force && now - current.lastCheckedAt < BUYER_ACCESS_REVALIDATE_MIN_GAP_MS) return

      buyerAccessRevalidationRef.current = {
        entidadeId,
        lastCheckedAt: now,
        inFlight: true,
      }

      void getBuyerAccessStatus(entidadeId)
        .then((status) => {
          const latest = buyerAccessRevalidationRef.current
          if (!active || latest.entidadeId !== entidadeId) return

          buyerAccessRevalidationRef.current = {
            entidadeId,
            lastCheckedAt: latest.lastCheckedAt,
            inFlight: false,
          }

          if (status && status.entidadeId === entidadeId && !status.active) {
            handleBuyerAccessRevoked(status.reason)
            return
          }

          if (status && status.entidadeId === entidadeId && status.active) {
            const statusAccessCode = normalizeBuyerAccessCode(status.priceTableAccessCode)
            if (statusAccessCode) {
              persistBuyerPriceTableAccessCode(entidadeId, statusAccessCode)
              if (BUYER_ACCESS_CODE_VIEWS.has(currentView) && !normalizeBuyerAccessCode(routeBuyerAccessCode)) {
                setRouteBuyerAccessCode(statusAccessCode)
              }
              return
            }

            const linkRequest = buyerPriceTableAccessLinkRequestRef.current
            const shouldRequestLink = (
              linkRequest.entidadeId !== entidadeId
              || linkRequest.status === 'idle'
            )

            if (shouldRequestLink) {
              buyerPriceTableAccessLinkRequestRef.current = { entidadeId, status: 'loading' }
              void createBuyerPriceTableAccessLink(entidadeId)
                .then((link) => {
                  if (!active || buyerAccessRevalidationRef.current.entidadeId !== entidadeId) return

                  const generatedAccessCode = normalizeBuyerAccessCode(link?.accessCode)
                  if (!generatedAccessCode) {
                    buyerPriceTableAccessLinkRequestRef.current = { entidadeId, status: 'blocked' }
                    return
                  }

                  buyerPriceTableAccessLinkRequestRef.current = { entidadeId, status: 'idle' }
                  persistBuyerPriceTableAccessCode(entidadeId, generatedAccessCode)
                  if (BUYER_ACCESS_CODE_VIEWS.has(currentView) && !normalizeBuyerAccessCode(routeBuyerAccessCode)) {
                    setRouteBuyerAccessCode(generatedAccessCode)
                  }
                })
                .catch((error) => {
                  if (!active || buyerAccessRevalidationRef.current.entidadeId !== entidadeId) return
                  buyerPriceTableAccessLinkRequestRef.current = { entidadeId, status: 'blocked' }
                  console.warn('Failed to generate buyer price table access link:', error)
                })
            }
          }
        })
        .catch((error) => {
          if (!active) return
          console.warn('Failed to validate buyer access status:', error)
          buyerAccessRevalidationRef.current = {
            entidadeId,
            lastCheckedAt: Date.now(),
            inFlight: false,
          }
        })
    }

    let initialValidationTimerId: number | null = null
    if (isStandalonePriceTableView && typeof window !== 'undefined') {
      initialValidationTimerId = window.setTimeout(() => validateAccess(true), 900)
    } else {
      validateAccess(true)
    }

    const handleWindowFocus = () => validateAccess(false)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateAccess(false)
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    const intervalId = window.setInterval(() => validateAccess(false), BUYER_ACCESS_REVALIDATE_INTERVAL_MS)

    return () => {
      active = false
      if (initialValidationTimerId !== null) {
        window.clearTimeout(initialValidationTimerId)
      }
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.clearInterval(intervalId)
      if (buyerAccessRevalidationRef.current.entidadeId === entidadeId) {
        buyerAccessRevalidationRef.current = {
          entidadeId,
          lastCheckedAt: buyerAccessRevalidationRef.current.lastCheckedAt,
          inFlight: false,
        }
      }
    }
  }, [
    authenticatedEntidadeId,
    currentView,
    effectiveIsLoggedIn,
    handleBuyerAccessRevoked,
    isLegacyCheckoutSessionBridge,
    isStandalonePriceTableView,
    routeBuyerAccessCode,
  ])

  useEffect(() => {
    if (
      !effectiveIsLoggedIn
      || isLegacyCheckoutSessionBridge
      || typeof authenticatedEntidadeId !== 'number'
      || authenticatedEntidadeId <= 0
    ) {
      buyerRotatingSearchTermsFetchRef.current = { entidadeId: null, status: 'idle' }
      setAuthenticatedSearchHighlights((current) => (current.length > 0 ? [] : current))
      return
    }

    const entidadeId = Math.trunc(authenticatedEntidadeId)
    if (buyerRotatingSearchTermsFetchRef.current.entidadeId !== entidadeId) {
      buyerRotatingSearchTermsFetchRef.current = { entidadeId, status: 'idle' }
      clearBuyerRotatingSearchTermsRetryTimer()
    }

    if (buyerRotatingSearchTermsFetchRef.current.status !== 'idle') return

    let active = true
    buyerRotatingSearchTermsFetchRef.current = { entidadeId, status: 'loading' }
    setAuthenticatedSearchHighlights([])

    void getBuyerRotatingSearchTerms(entidadeId)
      .then((response) => {
        if (!active) return

        setAuthenticatedSearchHighlights(
          (response?.termos ?? []).map((term) => ({
            label: term.label,
            badge: term.origem === 'activity' ? 'No seu ramo' : 'Em alta',
          }))
        )

        const nextStatus = response ? 'loaded' : 'failed'
        buyerRotatingSearchTermsFetchRef.current = {
          entidadeId,
          status: nextStatus,
        }
        if (nextStatus === 'loaded') {
          clearBuyerRotatingSearchTermsRetryTimer()
        } else {
          scheduleBuyerRotatingSearchTermsRetry(entidadeId)
        }
      })
      .catch((error) => {
        if (!active) return
        setAuthenticatedSearchHighlights([])
        buyerRotatingSearchTermsFetchRef.current = { entidadeId, status: 'failed' }
        scheduleBuyerRotatingSearchTermsRetry(entidadeId)
        console.error('Failed to load buyer rotating search terms:', error)
      })

    return () => {
      active = false
    }
  }, [
    authenticatedEntidadeId,
    buyerRotatingSearchTermsRetryTick,
    clearBuyerRotatingSearchTermsRetryTimer,
    effectiveIsLoggedIn,
    isLegacyCheckoutSessionBridge,
    scheduleBuyerRotatingSearchTermsRetry,
  ])

  useEffect(() => {
    return () => {
      clearBuyerRotatingSearchTermsRetryTimer()
    }
  }, [clearBuyerRotatingSearchTermsRetryTimer])

  useEffect(() => {
    if (!BUYER_ACCESS_CODE_VIEWS.has(currentView)) return
    if (!effectiveIsLoggedIn) return
    if (typeof authenticatedEntidadeId !== 'number' || authenticatedEntidadeId <= 0) return

    const currentCode = normalizeBuyerAccessCode(routeBuyerAccessCode)
    if (currentCode) return

    const persistedCode = readPersistedBuyerPriceTableAccessCode(authenticatedEntidadeId)
    if (persistedCode) {
      setRouteBuyerAccessCode(persistedCode)
    }
  }, [authenticatedEntidadeId, currentView, effectiveIsLoggedIn, routeBuyerAccessCode])

  useEffect(() => {
    if (
      !effectiveIsLoggedIn
      || isLegacyCheckoutSessionBridge
    ) {
      buyerAccountSummaryFetchRef.current = { entidadeId: null, status: 'idle' }
      return
    }

    const entidadeId = accountProfile?.entidadeId
    if (!entidadeId) {
      buyerAccountSummaryFetchRef.current = { entidadeId: null, status: 'idle' }
      return
    }

    if (buyerAccountSummaryFetchRef.current.entidadeId !== entidadeId) {
      buyerAccountSummaryFetchRef.current = { entidadeId, status: 'idle' }
    }

    if (
      buyerAccountSummaryBeneficiosRevisionRef.current !== buyerBeneficiosRevision
    ) {
      buyerAccountSummaryBeneficiosRevisionRef.current = buyerBeneficiosRevision
      buyerAccountSummaryFetchRef.current = { entidadeId, status: 'idle' }
    }

    if (buyerAccountSummaryFetchRef.current.status !== 'idle') return

    let active = true
    let timeoutId: number | null = null
    buyerAccountSummaryFetchRef.current = { entidadeId, status: 'loading' }

    const loadBuyerSummary = () => {
      void getBuyerAccountSummary()
        .then((summary) => {
          if (!active) return
          if (!summary || summary.entidadeId !== entidadeId) {
            buyerAccountSummaryFetchRef.current = { entidadeId, status: 'failed' }
            return
          }

          setAccountProfile((current) => {
            if (!current || current.entidadeId !== entidadeId) return current

            const normalizedDocument = normalizeDocumentDigits(summary.document)
            const nextProfile: AccountProfile = {
              ...current,
              corporateName: summary.corporateName || current.corporateName || current.name || undefined,
              name: current.name || summary.name || summary.corporateName || undefined,
              email: current.email || summary.email || undefined,
              phone: current.phone || summary.phone || undefined,
              cnpj: normalizedDocument || current.cnpj,
              document: normalizedDocument || current.document || current.cnpj,
              activity: summary.activity || current.activity || undefined,
              stateRegistration: summary.stateRegistration || current.stateRegistration || undefined,
              street: summary.street || current.street || undefined,
              number: summary.number || current.number || undefined,
              complement: summary.complement || current.complement || undefined,
              neighborhood: summary.neighborhood || current.neighborhood || undefined,
              city: summary.city || current.city || undefined,
              state: summary.state || current.state || undefined,
              zipCode: summary.zipCode || current.zipCode || undefined,
              buyerSessionToken: summary.buyerSessionToken || current.buyerSessionToken,
              commerceContext: summary.commerceContext || current.commerceContext,
              buyerUser: summary.buyerUser || current.buyerUser,
            }

            const didChange =
              current.corporateName !== nextProfile.corporateName
              || current.name !== nextProfile.name
              || current.email !== nextProfile.email
              || current.phone !== nextProfile.phone
              || current.cnpj !== nextProfile.cnpj
              || current.document !== nextProfile.document
              || current.activity !== nextProfile.activity
              || current.stateRegistration !== nextProfile.stateRegistration
              || current.street !== nextProfile.street
              || current.number !== nextProfile.number
              || current.complement !== nextProfile.complement
              || current.neighborhood !== nextProfile.neighborhood
              || current.city !== nextProfile.city
              || current.state !== nextProfile.state
              || current.zipCode !== nextProfile.zipCode
              || current.buyerSessionToken !== nextProfile.buyerSessionToken
              || JSON.stringify(current.commerceContext ?? null) !== JSON.stringify(nextProfile.commerceContext ?? null)
              || JSON.stringify(current.buyerUser ?? null) !== JSON.stringify(nextProfile.buyerUser ?? null)

            return didChange ? nextProfile : current
          })

          buyerAccountSummaryFetchRef.current = { entidadeId, status: 'loaded' }
        })
        .catch((error) => {
          buyerAccountSummaryFetchRef.current = { entidadeId, status: 'failed' }
          console.error('Failed to load buyer account summary:', error)
        })
    }

    if (isStandalonePriceTableView && typeof window !== 'undefined') {
      timeoutId = window.setTimeout(loadBuyerSummary, 1200)
    } else {
      loadBuyerSummary()
    }

    return () => {
      active = false
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [
    accountProfile?.entidadeId,
    buyerBeneficiosRevision,
    effectiveIsLoggedIn,
    setAccountProfile,
    isLegacyCheckoutSessionBridge,
    isStandalonePriceTableView,
  ])

  useEffect(() => {
    if (
      !effectiveIsLoggedIn
      || currentView !== 'checkout'
      || isLegacyCheckoutSessionBridge
    ) {
      checkoutCustomerProfileFetchRef.current = { entidadeId: null, status: 'idle' }
      return
    }

    const entidadeId = accountProfile?.entidadeId
    if (!entidadeId) {
      checkoutCustomerProfileFetchRef.current = { entidadeId: null, status: 'idle' }
      return
    }

    const hasCheckoutProfileData = Boolean(accountProfile?.document || accountProfile?.cnpj)
      && Boolean(accountProfile?.stateRegistration)
      && Boolean(accountProfile?.street)
      && Boolean(accountProfile?.city)
      && Boolean(accountProfile?.state)
      && Boolean(accountProfile?.zipCode)
      && (accountProfile?.tipoPessoa ?? null) !== null

    if (checkoutCustomerProfileFetchRef.current.entidadeId !== entidadeId) {
      checkoutCustomerProfileFetchRef.current = { entidadeId, status: 'idle' }
    }

    if (hasCheckoutProfileData) {
      checkoutCustomerProfileFetchRef.current = { entidadeId, status: 'loaded' }
      return
    }

    if (checkoutCustomerProfileFetchRef.current.status !== 'idle') return

    let active = true
    checkoutCustomerProfileFetchRef.current = { entidadeId, status: 'loading' }

    void getCheckoutCustomerProfile()
      .then((profile) => {
        if (!active) return
        if (!profile) {
          checkoutCustomerProfileFetchRef.current = { entidadeId, status: 'failed' }
          return
        }

        setAccountProfile((current) => {
          if (!current || current.entidadeId !== entidadeId) return current

          const document = normalizeDocumentDigits(profile.document) || current.document || current.cnpj
          const nextProfile: AccountProfile = {
            ...current,
            corporateName: current.corporateName || profile.corporateName || profile.name || undefined,
            cnpj: document || current.cnpj,
            document: document || current.document,
            activity: current.activity || profile.activity || undefined,
            stateRegistration: profile.stateRegistration || current.stateRegistration,
            street: current.street || profile.street || undefined,
            number: current.number || profile.number || undefined,
            complement: current.complement || profile.complement || undefined,
            neighborhood: current.neighborhood || profile.neighborhood || undefined,
            city: current.city || profile.city || undefined,
            state: current.state || profile.state || undefined,
            zipCode: current.zipCode || profile.zipCode || undefined,
            tipoPessoa: profile.tipoPessoa ?? current.tipoPessoa ?? null,
            email: current.email || profile.email || undefined,
            phone: current.phone || profile.phone || undefined,
            name: current.name || profile.name || profile.corporateName || undefined,
          }

          const didChange =
            current.corporateName !== nextProfile.corporateName
            || current.cnpj !== nextProfile.cnpj
            || current.document !== nextProfile.document
            || current.activity !== nextProfile.activity
            || current.stateRegistration !== nextProfile.stateRegistration
            || current.street !== nextProfile.street
            || current.number !== nextProfile.number
            || current.complement !== nextProfile.complement
            || current.neighborhood !== nextProfile.neighborhood
            || current.city !== nextProfile.city
            || current.state !== nextProfile.state
            || current.zipCode !== nextProfile.zipCode
            || (current.tipoPessoa ?? null) !== (nextProfile.tipoPessoa ?? null)
            || current.email !== nextProfile.email
            || current.phone !== nextProfile.phone
            || current.name !== nextProfile.name

          return didChange ? nextProfile : current
        })
        checkoutCustomerProfileFetchRef.current = { entidadeId, status: 'loaded' }
      })
      .catch((error) => {
        checkoutCustomerProfileFetchRef.current = { entidadeId, status: 'failed' }
        console.error('Failed to load checkout customer profile:', error)
      })

    return () => {
      active = false
    }
  }, [
    accountProfile?.entidadeId,
    currentView,
    effectiveIsLoggedIn,
    setAccountProfile,
    isLegacyCheckoutSessionBridge,
  ])

  useEffect(() => {
    currentViewRef.current = currentView
  }, [currentView])

  useEffect(() => {
    if (typeof window === 'undefined') return

    ensureStorefrontAnalyticsHistoryInstrumentation()

    const syncAnalyticsLocation = () => {
      setAnalyticsLocationKey(getStorefrontAnalyticsLocationKey())
    }
    const flushAnalytics = () => {
      void flushStorefrontAnalyticsQueue({ keepalive: true })
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushAnalytics()
      }
    }

    syncAnalyticsLocation()
    window.addEventListener(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT, syncAnalyticsLocation)
    window.addEventListener('popstate', syncAnalyticsLocation)
    window.addEventListener('pagehide', flushAnalytics)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT, syncAnalyticsLocation)
      window.removeEventListener('popstate', syncAnalyticsLocation)
      window.removeEventListener('pagehide', flushAnalytics)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      flushAnalytics()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (currentView !== 'home') {
      setIsHomeDeferredContentReady(false)
      return
    }

    setIsHomeDeferredContentReady(false)

    let timeoutId: number | null = null
    const frameId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        startTransition(() => {
          setIsHomeDeferredContentReady(true)
        })
      }, HOME_DEFERRED_CONTENT_DELAY_MS)
    })

    return () => {
      window.cancelAnimationFrame(frameId)
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [currentView])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!('scrollRestoration' in window.history)) return

    const previousScrollRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'

    return () => {
      window.history.scrollRestoration = previousScrollRestoration
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (shouldRestoreCurrentProductsViewState) return

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })
  }, [scrollResetNavigationKey, shouldRestoreCurrentProductsViewState])

  useEffect(() => {
    cartItemsRef.current = cartItems
  }, [cartItems])

  useEffect(() => {
    quickCheckoutCartItemsRef.current = quickCheckoutCartItems
  }, [quickCheckoutCartItems])

  useEffect(() => {
    const storedItems = loadQuickCheckoutCartItems(quickCheckoutLojaId)
    const defaultStoredItems = isProductCheckoutQuickFlowActive && quickCheckoutLojaId
      ? loadQuickCheckoutCartItems(null)
      : []

    setQuickCheckoutCartItems((currentItems) => {
      if (isProductCheckoutQuickFlowActive && currentItems.length > 0 && storedItems.length === 0) {
        const migratedItems = currentItems.reduce<CartItem[]>((items, item) => (
          upsertQuickCheckoutCartItem(quickCheckoutLojaId, items, item.product, item.quantity)
        ), [])

        if (quickCheckoutLojaId) {
          clearQuickCheckoutCartItems(null)
        }
        quickCheckoutCartItemsRef.current = migratedItems
        return migratedItems
      }

      if (isProductCheckoutQuickFlowActive && storedItems.length === 0 && defaultStoredItems.length > 0) {
        const migratedItems = defaultStoredItems.reduce<CartItem[]>((items, item) => (
          upsertQuickCheckoutCartItem(quickCheckoutLojaId, items, item.product, item.quantity)
        ), [])

        clearQuickCheckoutCartItems(null)
        quickCheckoutCartItemsRef.current = migratedItems
        return migratedItems
      }

      quickCheckoutCartItemsRef.current = storedItems
      return storedItems
    })
  }, [isProductCheckoutQuickFlowActive, quickCheckoutLojaId])

  useEffect(() => {
    currentCheckoutItemsRef.current = checkoutItems
  }, [checkoutItems])

  useEffect(() => {
    if (hasConfirmedCartStateFromApi && cartItems.length === 0) {
      setCartQuoteOrigin(null)
      void loadCartDrawer()
        .then(({ clearCartDrawerFreightSelections }) => {
          clearCartDrawerFreightSelections()
        })
        .catch(() => undefined)
    }
  }, [cartItems.length, hasConfirmedCartStateFromApi, setCartQuoteOrigin])

  useEffect(() => {
    if (isCartAuthenticated) return
    if (cartItems.length === 0) return
    setCartItems([])
    cartItemsRef.current = []
    cartItemKeysByProductIdRef.current = new Map()
  }, [cartItems, isCartAuthenticated])

  useEffect(() => {
    const normalizedPath = normalizePathname(window.location.pathname)
    const canonicalPath = normalizePathname(normalizeLegacyProductDetailPathname(window.location.pathname))

    if (canonicalPath !== normalizedPath) {
      window.history.replaceState(window.history.state, '', `${canonicalPath}${window.location.search}`)
    }
  }, [])

  useEffect(() => {
    const syncRouteStateFromLocation = () => {
      const normalizedPath = normalizePathname(window.location.pathname)
      const canonicalPath = normalizePathname(normalizeLegacyProductDetailPathname(window.location.pathname))

      if (canonicalPath !== normalizedPath) {
        window.history.replaceState(window.history.state, '', `${canonicalPath}${window.location.search}`)
      }

      const route = resolveRoute(canonicalPath, window.location.search)
      if (route.view !== 'product-detail') {
        invalidatePendingProductDetailRequests()
      }
      const storedCheckoutRequestKey = route.view === 'product-detail'
        ? consumeStoredProductCheckoutRequestKey(canonicalPath)
        : null
      setPendingProductCheckoutRequestKey((currentKey) => {
        if (route.view !== 'product-detail') return null
        if (storedCheckoutRequestKey) return storedCheckoutRequestKey
        if (shouldOpenProductCheckoutFromSearch(window.location.search)) {
          return buildProductCheckoutRequestKey(canonicalPath, window.location.search)
        }

        const currentPath = currentKey?.split('?')[0] ?? ''
        return currentPath === canonicalPath ? currentKey : null
      })
      const normalizedRouteQuery = (route.searchQuery ?? '').trim()
      const normalizedRouteManufacturerBrands = normalizeManufacturerBrandFilters(route.manufacturerBrands)
      const normalizedRouteVehicleFilter = normalizeVehicleFilter(route.vehicleFilter)
      const routeVehicleSearchMode = route.vehicleSearchMode ?? null
      const routeOnlyAvailable = Boolean(route.onlyAvailable)
      const routeProductsViewStateKey = buildProductsViewStateKey({
        view: route.view,
        searchQuery: route.searchQuery ?? '',
        manufacturerBrands: normalizedRouteManufacturerBrands,
        vehicleFilter: normalizedRouteVehicleFilter,
        onlyAvailable: routeOnlyAvailable,
        isSearchResultsActive: normalizedRouteQuery.length >= 2,
        categorySlugs: route.categorySlugs ?? [],
      })
      const routeSearchProductsDataKey = normalizedRouteQuery.length >= 2
        ? buildSearchProductsDataKey(normalizedRouteQuery, normalizedRouteManufacturerBrands, normalizedRouteVehicleFilter, routeOnlyAvailable)
        : ''
      const hasMatchingCachedSearchResults = normalizedRouteQuery.length >= 2
        && productsDataKey === routeSearchProductsDataKey
        && allProducts.length > 0
      const hasMatchingInFlightSearchResults = normalizedRouteQuery.length >= 2
        && inFlightSearchProductsDataKeyRef.current === routeSearchProductsDataKey
      const shouldRestoreProductsViewState = (
        route.view === 'products'
        || route.view === 'launches'
        || route.view === 'just_arrived'
      ) && productListingSnapshotRef.current?.restoreKey === routeProductsViewStateKey

      setRestoreProductsViewStateKey(shouldRestoreProductsViewState ? routeProductsViewStateKey : null)
      setCurrentView(route.view)
      setCheckoutRouteStep(route.checkoutStep ?? DEFAULT_CHECKOUT_STEP)
      setRouteCheckoutSource(route.checkoutSource ?? 'cart')
      setRouteCheckoutQuoteId(route.checkoutQuoteId ?? null)
      setRouteCheckoutQuoteMode(route.checkoutQuoteMode ?? null)
      setRouteCheckoutOrderId(route.checkoutOrderId ?? null)
      setRouteCheckoutOrderMode(route.checkoutOrderMode ?? null)
      setRouteProductId(route.productId)
      setRouteProductParentContextId(route.view === 'product-detail' ? route.productParentContextId ?? null : null)
      const storedQuickCheckoutRequest = route.view === 'quick-checkout'
        ? readProductCheckoutQuickRequest()
        : null
      const nextQuickCheckoutProductId = route.view === 'quick-checkout'
        ? route.quickCheckoutProductId ?? storedQuickCheckoutRequest?.productIds[0] ?? null
        : null
      setRouteQuickCheckoutProductId(nextQuickCheckoutProductId)
      setRouteQuickCheckoutProductParentContextId(
        route.view === 'quick-checkout'
          ? route.quickCheckoutProductParentContextId
            ?? resolveProductParentContextId(
              nextQuickCheckoutProductId,
              storedQuickCheckoutRequest?.parentContextId ?? null
            )
          : null
      )
      setRouteQuickCheckoutQuantity(
        route.view === 'quick-checkout'
          ? route.quickCheckoutQuantity ?? storedQuickCheckoutRequest?.quantity ?? null
          : null
      )
      if (route.view === 'quick-checkout' && nextQuickCheckoutProductId) {
        updateProductCheckoutQuickFlow(true)
      }
      setRouteMarketplaceListingId(route.view === 'marketplace-listing-detail' ? route.marketplaceListingId ?? null : null)
      setRouteMarketplaceSellerId(route.view === 'marketplace-seller' ? route.marketplaceSellerId ?? null : null)
      setRouteAccessTicket(route.view === 'access' ? route.accessTicket ?? null : null)
      setRouteAccessMode(route.view === 'access' ? route.accessMode ?? 'ticket' : 'ticket')
      setRouteOrderTrackingCode(route.view === 'checkout-tracking' ? route.orderTrackingCode ?? null : null)
      setRoutePublicTicketCode(route.view === 'public-ticket' ? route.publicTicketCode ?? null : null)
      setRouteBuyerAccessCode(BUYER_ACCESS_CODE_VIEWS.has(route.view) ? route.buyerAccessCode ?? null : null)
      setRouteLegacyCheckoutLink(route.view === 'redirect-checkout' ? route.legacyCheckoutLink ?? null : null)
      setRoutePreCadastroRetomadaCode(route.view === 'register' ? route.preCadastroRetomadaCode ?? null : null)
      setRouteSupportSubjectSlug(route.supportSubjectSlug ?? null)
      setRouteOrderDetailId(route.view === 'orders' || route.view === 'business' ? route.orderDetailId ?? null : null)
      setRouteOrderDetailToken(route.view === 'orders' || route.view === 'business' ? route.orderDetailToken ?? null : null)
      setRouteQuoteDetailId(route.view === 'quotes' || route.view === 'business' ? route.quoteDetailId ?? null : null)
      setRouteQuoteDetailToken(route.view === 'quotes' || route.view === 'business' ? route.quoteDetailToken ?? null : null)
      setRouteAttendanceDetailId(route.view === 'attendances' ? route.attendanceDetailId ?? null : null)
      setRouteAttendanceDetailToken(route.view === 'attendances' ? route.attendanceDetailToken ?? null : null)
      setReturnRequestRouteStep(route.view === 'return-request' ? route.returnRequestStep ?? 'choice' : 'choice')
      setReturnRequestRouteFlowType(route.view === 'return-request' ? route.returnRequestFlowType ?? null : null)
      if (route.view !== 'products' && route.view !== 'product-detail') {
        setImageSearchState(null)
        setIsImageSearchLoading(false)
      }
      if (route.view === 'products') {
        setCategoryRouteSlugs(route.categorySlugs ?? [])
        setSearchQuery(route.searchQuery ?? '')
        setOnlyAvailableFilter(routeOnlyAvailable)
        setSelectedManufacturerBrands(normalizedRouteManufacturerBrands)
        setActiveVehicleFilter(normalizedRouteVehicleFilter)
        setVehicleSearchModeFromRoute(routeVehicleSearchMode)
        setIsSearchResultsActive(normalizedRouteQuery.length >= 2 || Boolean(routeVehicleSearchMode))
        shouldApplyRouteSearchRef.current = (
          normalizedRouteQuery.length >= 2
          && !hasMatchingCachedSearchResults
          && !hasMatchingInFlightSearchResults
        )
        return
      }

      setSearchQuery('')
      setOnlyAvailableFilter(false)
      setSelectedManufacturerBrands([])
      setActiveVehicleFilter(null)
      setVehicleSearchModeFromRoute(null)
      setIsSearchResultsActive(false)
      shouldApplyRouteSearchRef.current = false

      if (route.view === 'launches' || route.view === 'just_arrived') {
        setSelectedCategory(undefined)
        clearCategoryBreadcrumb()
        setCategoryRouteSlugs([])
      }
    }

    window.addEventListener(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT, syncRouteStateFromLocation)
    window.addEventListener('popstate', syncRouteStateFromLocation)
    return () => {
      window.removeEventListener(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT, syncRouteStateFromLocation)
      window.removeEventListener('popstate', syncRouteStateFromLocation)
    }
  }, [
    allProducts.length,
    clearCategoryBreadcrumb,
    invalidatePendingProductDetailRequests,
    productsDataKey,
    updateProductCheckoutQuickFlow,
  ])

  useEffect(() => {
    if (currentView === 'product-detail' && !selectedProduct) return

    const normalizedSearchQuery = searchQuery.trim()
    const effectiveCategoryRouteSlugs = currentView === 'products' && isSearchResultsActive && normalizedSearchQuery.length >= 2
      ? []
      : categoryRouteSlugs
    const effectiveBuyerAccessCode = resolveBuyerAccessCodeForView(currentView)
    const path = buildPathForView(
      currentView,
      currentView === 'product-detail'
        ? (routeProductId ?? selectedProduct?.id ?? null)
        : (selectedProduct?.id ?? routeProductId),
      routeAccessTicket,
      routeAccessMode,
      selectedProduct?.name,
      routeProductParentContextId,
      routePreCadastroRetomadaCode,
      effectiveCategoryRouteSlugs,
      checkoutRouteStep,
      returnRequestRouteStep,
      returnRequestRouteFlowType,
      routeSupportSubjectSlug,
      routeOrderDetailToken,
      routeQuoteDetailToken,
      routeAttendanceDetailToken,
      effectiveBuyerAccessCode,
      routePublicTicketCode,
      routeOrderTrackingCode
    )
    if (!path) return
    const queryString = currentView === 'products'
      ? (() => {
          const params = new URLSearchParams()

          if (isSearchResultsActive && normalizedSearchQuery.length >= 2) {
            params.set('q', normalizedSearchQuery)
          }

          if (onlyAvailableFilter) {
            params.set('somente_disponiveis', '1')
          }

          if (isSearchResultsActive && vehicleSearchModeFromRoute) {
            params.set('busca_veiculo', vehicleSearchModeFromRoute === 'plate' ? 'placa' : 'manual')
          }

          const serializedManufacturerBrandIds = serializeManufacturerBrandIdsForQuery(selectedManufacturerBrands)
          if (serializedManufacturerBrandIds) {
            params.set('marca_fabricante_id', serializedManufacturerBrandIds)
            const serializedManufacturerBrandNames = serializeManufacturerBrandNamesForQuery(selectedManufacturerBrands)
            if (serializedManufacturerBrandNames) {
              params.set('marca_fabricante_nome', serializedManufacturerBrandNames)
            }
          }

          appendVehicleFilterToQueryParams(params, activeVehicleFilter)

          const serialized = params.toString()
          return serialized ? `?${serialized}` : ''
        })()
      : currentView === 'register'
        ? preserveSellerRegistrationSearch(window.location.search)
      : currentView === 'checkout' && checkoutRouteStep === 'success'
        ? (() => {
            const params = new URLSearchParams(window.location.search || '')
            const finalizedPedidoId = (
              parsePositiveIntegerLike(params.get('pedido_id'))
              ?? parsePositiveIntegerLike(params.get('pedidoId'))
            )
            if (finalizedPedidoId) {
              params.delete('source')
              params.delete('quote_id')
              params.delete('quoteId')
              params.delete('orcamento_id')
              params.delete('orcamentoId')
              params.delete('quote_mode')
              params.delete('quoteMode')
              params.delete('order_id')
              params.delete('orderId')
              params.delete('order_mode')
              params.delete('orderMode')
              params.set('pedido_id', String(finalizedPedidoId))
            } else if (routeCheckoutSource === 'quote' && routeCheckoutQuoteId) {
              params.set('source', 'quote')
              params.set('quote_id', String(routeCheckoutQuoteId))
              if (routeCheckoutQuoteMode) {
                params.set('quote_mode', routeCheckoutQuoteMode)
              } else {
                params.delete('quote_mode')
                params.delete('quoteMode')
              }
              params.delete('order_id')
              params.delete('orderId')
            } else {
              params.delete('quote_id')
              params.delete('quoteId')
              params.delete('orcamento_id')
              params.delete('orcamentoId')
              params.delete('quote_mode')
              params.delete('quoteMode')
              if (routeCheckoutSource === 'order' && routeCheckoutOrderId) {
                if (routeCheckoutOrderMode === 'direct') {
                  params.delete('source')
                  params.delete('order_id')
                  params.delete('orderId')
                  params.delete('order_mode')
                  params.delete('orderMode')
                  params.set('pedido_id', String(routeCheckoutOrderId))
                } else {
                  params.set('source', 'order')
                  params.set('order_id', String(routeCheckoutOrderId))
                  if (routeCheckoutOrderMode) {
                    params.set('order_mode', routeCheckoutOrderMode)
                  } else {
                    params.delete('order_mode')
                    params.delete('orderMode')
                  }
                }
              } else {
                params.delete('source')
                params.delete('order_id')
                params.delete('orderId')
                params.delete('order_mode')
                params.delete('orderMode')
              }
            }
            const serialized = params.toString()
            return serialized ? `?${serialized}` : ''
          })()
      : currentView === 'checkout'
          ? (() => {
              const params = new URLSearchParams()
              if (routeCheckoutSource === 'quote' && routeCheckoutQuoteId) {
                params.set('source', 'quote')
                params.set('quote_id', String(routeCheckoutQuoteId))
                if (routeCheckoutQuoteMode) {
                  params.set('quote_mode', routeCheckoutQuoteMode)
                }
              } else if (routeCheckoutSource === 'order' && routeCheckoutOrderId) {
                params.set('source', 'order')
                params.set('order_id', String(routeCheckoutOrderId))
                if (routeCheckoutOrderMode) {
                  params.set('order_mode', routeCheckoutOrderMode)
                }
              }
              const serialized = params.toString()
              return serialized ? `?${serialized}` : ''
            })()
        : currentView === 'quick-checkout' && preserveAffiliateCheckoutSearch(window.location.search)
          ? preserveAffiliateCheckoutSearch(window.location.search)
        : currentView === 'store-network-access'
          ? preserveStoreNetworkAccessSearch(window.location.search)
        : currentView === 'redirect-checkout' && routeLegacyCheckoutLink?.trim()
          ? `?link=${encodeURIComponent(routeLegacyCheckoutLink.trim())}`
          : currentView === 'qrcode' || currentView === 'nfe-qr'
            ? window.location.search
          : ''
    const nextUrl = `${path}${queryString}`

    const currentUrl = `${window.location.pathname}${window.location.search}`
    const normalizedCurrentUrl = `${normalizePathname(window.location.pathname)}${window.location.search}`
    const normalizedNextUrl = `${normalizePathname(path)}${queryString}`
    if (currentUrl !== nextUrl) {
      const shouldReplaceQuickCheckoutAlias = (
        currentView === 'quick-checkout'
        && normalizePathname(window.location.pathname) === LEGACY_QUICK_CHECKOUT_PATH
      )
      const shouldReplaceProductCanonicalUrl = (
        currentView === 'product-detail'
        && Boolean(routeProductParentContextId)
        && Boolean(routeProductId)
        && routeProductId !== routeProductParentContextId
        && selectedProduct?.id === routeProductParentContextId
      )

      if (shouldReplaceQuickCheckoutAlias || shouldReplaceProductCanonicalUrl) {
        window.history.replaceState(window.history.state, '', nextUrl)
      } else if (normalizedCurrentUrl === normalizedNextUrl) {
        window.history.replaceState(window.history.state, '', nextUrl)
      } else {
        window.history.pushState({}, '', nextUrl)
      }
    }
  }, [
    currentView,
    selectedProduct?.id,
    selectedProduct?.name,
    routeProductId,
    routeProductParentContextId,
    routeAccessTicket,
    routeAccessMode,
    routeOrderTrackingCode,
    routePublicTicketCode,
    routeBuyerAccessCode,
    resolveBuyerAccessCodeForView,
    routePreCadastroRetomadaCode,
    returnRequestRouteStep,
    returnRequestRouteFlowType,
    routeSupportSubjectSlug,
    routeOrderDetailToken,
    routeQuoteDetailToken,
    routeAttendanceDetailToken,
    routeCheckoutSource,
    routeCheckoutQuoteId,
    routeCheckoutQuoteMode,
    routeCheckoutOrderId,
    routeCheckoutOrderMode,
    categoryRouteSlugs,
    checkoutRouteStep,
    searchQuery,
    isSearchResultsActive,
    routeLegacyCheckoutLink,
    selectedManufacturerBrands,
    activeVehicleFilter,
    vehicleSearchModeFromRoute,
    onlyAvailableFilter,
  ])

  useEffect(() => {
    if (typeof window === 'undefined' || isBackendUnavailable) return
    if (currentView === 'blog') return

    const currentLocationKey = getStorefrontAnalyticsLocationKey()
    const pageViewEvent = buildStorefrontPageViewEvent({
      currentView,
      pathname: window.location.pathname,
      search: window.location.search,
      routeProductId,
      routeSupportSubjectSlug,
      checkoutStep: checkoutRouteStep,
      categoryRouteSlugs,
      isSearchResultsActive,
      searchQuery,
      selectedProduct,
    })
    const dedupeKey = [
      currentLocationKey,
      pageViewEvent.pageKind || '',
      pageViewEvent.pageKey || '',
      String(pageViewEvent.produtoId ?? ''),
    ].join('|')

    if (analyticsLastPageViewKeyRef.current === dedupeKey) {
      return
    }

    analyticsLastPageViewKeyRef.current = dedupeKey
    queueStorefrontAnalyticsEvent({
      ...pageViewEvent,
      eventType: 'page_view',
      referrerUrl: analyticsReferrerUrlRef.current,
    })
    analyticsReferrerUrlRef.current = getCurrentStorefrontUrl() || null
  }, [
    analyticsLocationKey,
    categoryRouteSlugs,
    checkoutRouteStep,
    currentView,
    isBackendUnavailable,
    isSearchResultsActive,
    routeOrderDetailToken,
    routeProductId,
    routeQuoteDetailToken,
    routeAttendanceDetailToken,
    routeSupportSubjectSlug,
    searchQuery,
    selectedProduct,
  ])

  useEffect(() => {
    if (!effectiveIsLoggedIn) return
    if (currentView !== 'affiliates') return
    if (buyerBeneficiosStatus === 'loading') return
    if (canAccessAffiliates) return

    invalidatePendingProductDetailRequests()
    setCurrentView('dashboard')
    toast.error(t('account.affiliates.unavailable_redirect'))
  }, [buyerBeneficiosStatus, canAccessAffiliates, currentView, effectiveIsLoggedIn, invalidatePendingProductDetailRequests, t])

  useEffect(() => {
    if (!indicatorDelegation || (currentView !== 'business' && currentView !== 'clients')) return
    invalidatePendingProductDetailRequests()
    navigateHome()
  }, [
    currentView,
    indicatorDelegation,
    invalidatePendingProductDetailRequests,
    navigateHome,
  ])

  useEffect(() => {
    if (indicatorDelegation || (currentView !== 'business' && currentView !== 'clients')) return
    if (!effectiveIsLoggedIn || buyerBeneficiosStatus === 'loading') return
    if (canAccessIndicatorPortal) return

    invalidatePendingProductDetailRequests()
    setCurrentView('dashboard')
  }, [
    buyerBeneficiosStatus,
    canAccessIndicatorPortal,
    currentView,
    effectiveIsLoggedIn,
    indicatorDelegation,
    invalidatePendingProductDetailRequests,
  ])

  useEffect(() => {
    if (currentView !== 'indicator') return
    if (!effectiveIsLoggedIn) return
    if (buyerBeneficiosStatus === 'loading') return
    if (canAccessIndicatorPortal) return

    invalidatePendingProductDetailRequests()
    setCurrentView('dashboard')
    toast.error(t('account.indicator.unavailable_redirect'))
  }, [buyerBeneficiosStatus, canAccessIndicatorPortal, currentView, effectiveIsLoggedIn, invalidatePendingProductDetailRequests, t])

  useEffect(() => {
    if (!effectiveIsLoggedIn) return
    if (!MARKETPLACE_ACCOUNT_VIEWS.has(currentView)) return
    if (buyerBeneficiosStatus === 'idle' || buyerBeneficiosStatus === 'loading') return
    if (hasMarketplaceBenefit) return

    navigateToView('dashboard')
    toast.error('O benefício Marketplace não está habilitado para esta conta.')
  }, [
    buyerBeneficiosStatus,
    currentView,
    effectiveIsLoggedIn,
    hasMarketplaceBenefit,
    navigateToView,
  ])

  const buildCategorySearchFilters = (
    categoryId?: string,
    routeSlugs: string[] = categoryRouteSlugs,
  ) => {
    if (!categoryId) return {}

    const numericId = Number(categoryId)
    if (!Number.isFinite(numericId)) return {}

    if (categoryModel === 'classes') {
      return { classe_id: numericId }
    }

    // A rota e o contexto canonico da categoria selecionada. O breadcrumb e
    // atualizado separadamente e pode ainda refletir a categoria anterior no
    // primeiro carregamento SPA, fazendo um menu ser consultado como grupo.
    let level = routeSlugs.length
    if (level === 0) {
      const resolvedPath = resolveCategoryPathFromSelection(categoryId, undefined, menuItems, mainMenuCategories)
      level = resolvedPath?.length ?? 0
    }

    if (level <= 1) return { site_menu_id: numericId }
    if (level === 2) return { departamento_id: numericId }
    return { produto_grupo_id: numericId }
  }

  const buildImageSearchContextFilters = useCallback(() => {
    const filters: Record<string, number | number[]> = {}

    if (currentViewRef.current === 'products' && selectedCategory) {
      if (categoryModel === 'classes') {
        const selectedCategoryNode = findLocalNodeById(mainMenuCategories, selectedCategory)
        const classIds = selectedCategoryNode
          ? collectCategoryAndDescendantIds(selectedCategoryNode)
          : [selectedCategory]
        const normalizedClassIds = classIds
          .map((classId) => Number(classId))
          .filter((classId) => Number.isFinite(classId) && classId > 0)
          .map((classId) => Math.trunc(classId))

        if (normalizedClassIds.length > 0) {
          filters.classe_ids = normalizedClassIds
        }
      } else {
        Object.assign(filters, buildCategorySearchFilters(selectedCategory))
      }
    }

    const manufacturerBrandIds = getManufacturerBrandFilterIds(selectedManufacturerBrands)
    if (manufacturerBrandIds.length > 0) {
      filters.marca_fabricante_ids = manufacturerBrandIds
    }

    return filters
  }, [buildCategorySearchFilters, categoryModel, mainMenuCategories, selectedCategory, selectedManufacturerBrands])

  const resetServerPaginatedProducts = useCallback(() => {
    if (catalogProductsAbortRef.current) {
      catalogProductsAbortRef.current.abort()
      catalogProductsAbortRef.current = null
    }
    categoryProductsInFlightDataKeyRef.current = null
    productsOffsetRef.current = 0
    marketplaceSearchTotalRef.current = 0
    serverPaginatedProductFeedRef.current = null
    isLoadingMoreProductsRef.current = false
    setHasMoreProducts(false)
    setIsLoadingMoreProducts(false)
    setServerPaginatedProductsTotal(null)
  }, [])

  useEffect(() => {
    if (previousProductDataAudienceKeyRef.current === productDataAudienceKey) return

    previousProductDataAudienceKeyRef.current = productDataAudienceKey
    homeProductsLoadRequestIdRef.current += 1
    homeProductsLoadAbortRef.current?.abort()
    homeProductsLoadAbortRef.current = null

    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }

    searchRequestIdRef.current += 1
    inFlightSearchProductsDataKeyRef.current = null
    productListingSnapshotRef.current = null
    setRestoreProductsViewStateKey(null)
    shouldApplyRouteSearchRef.current = (
      currentViewRef.current === 'products'
      && searchQuery.trim().length >= 2
      && !imageSearchState
    )
    resetServerPaginatedProducts()
    setAllProducts([])
    setProductsDataKey('')
    // Mantem os cards grandes da home visiveis durante logout/login; a recarga
    // abaixo atualiza os dados sem deixar a vitrine piscar ou sumir.
    setHasLoadedHomeJustArrivedPreview(false)
    setHasCompletedHomeProductsLoad(false)
    setHasCompletedInitialProductsLoad(false)

    if (currentViewRef.current === 'product-detail' && routeProductId) {
      productDetailRequestIdRef.current += 1
      setSelectedProduct(null)
      setDisplayedProductDetail(null)
      setProductLoadError(null)
      setIsLoadingProduct(true)
    }
  }, [imageSearchState, productDataAudienceKey, resetServerPaginatedProducts, routeProductId, searchQuery])

  const syncServerPaginatedProducts = useCallback((
    page: PaginatedProductListResult,
    options: { trustTotal?: boolean } = {}
  ) => {
    const trustTotal = options.trustTotal !== false
    const previousOffset = productsOffsetRef.current
    const nextOffset = page.offset + page.items.length
    const pageLimit = Math.max(1, page.limit)
    const stalled = page.items.length > 0 && nextOffset <= previousOffset
    productsOffsetRef.current = nextOffset
    setHasMoreProducts(
      stalled
        ? false
        : (trustTotal && page.total !== null ? nextOffset < page.total : page.items.length >= pageLimit)
    )
    setServerPaginatedProductsTotal(trustTotal ? page.total : null)
  }, [])

  const fetchServerPaginatedProducts = useCallback(async (
    view: ServerPaginatedProductFeedView,
    offset: number
  ) => {
    if (view === 'launches') {
      return getLancamentosPage(SERVER_PAGINATED_PRODUCTS_PAGE_SIZE, offset)
    }

    return getAcabaramDeChegarPage(SERVER_PAGINATED_PRODUCTS_PAGE_SIZE, offset)
  }, [])

  const appendUniqueProducts = useCallback((current: Product[], incoming: Product[]) => {
    if (incoming.length === 0) return current

    const existingIds = new Set(current.map((product) => product.id))
    const uniqueIncoming = incoming.filter((product) => !existingIds.has(product.id))
    return uniqueIncoming.length > 0 ? [...current, ...uniqueIncoming] : current
  }, [])

  const appendSearchProductsWithMarketplaceLast = useCallback((current: Product[], incoming: Product[]) => {
    const merged = appendUniqueProducts(current, incoming)
    if (merged === current) return current

    return [
      ...merged.filter((product) => product.origem !== 'marketplace_vitrine'),
      ...merged.filter((product) => product.origem === 'marketplace_vitrine'),
    ]
  }, [appendUniqueProducts])

  const resolveDisplayedSearchProductsForAnalytics = useCallback((products: Product[]) => (
    sortProductsByAvailability(
      filterHiddenUnavailableProducts(products, pricingContextValue.stockStatusMode),
      pricingContextValue.stockStatusMode,
    )
  ), [pricingContextValue.stockStatusMode])

  const mergeProductPriceUpdates = useCallback((current: Product[], updatesById: Map<string, Product>) => {
    if (updatesById.size === 0) return current

    let changed = false
    const next = current.map((product) => {
      const update = updatesById.get(product.id)
      if (!update) return product

      const priceFields: Array<keyof Product> = [
        'pricingPending',
        'priceVisible',
        'needsQuote',
        'unitPrice',
        'price',
        'originalPrice',
        'discountedPrice',
        'wholesalePrice',
        'cnpjIePrice',
        'distributionPrice',
      ]
      if (priceFields.every((field) => Object.is(product[field], update[field]))) {
        return product
      }

      changed = true
      const merged = { ...product }
      for (const field of priceFields) {
        ;(merged as unknown as Record<string, unknown>)[field] = update[field]
      }
      return merged
    })

    return changed ? next : current
  }, [])

  const pendingProductPricesKey = Array.from(
    new Set(
      [...allProducts, ...homeProducts, ...homeJustArrivedProducts]
        .filter((product) => product.pricingPending)
        .map((product) => product.id)
        .filter((productId) => /^\d+$/.test(productId))
    )
  ).sort((left, right) => Number(left) - Number(right)).join(',')

  useEffect(() => {
    if (isBackendUnavailable || !pendingProductPricesKey) return

    const pendingIds = pendingProductPricesKey
      .split(',')
      .map((productId) => Number.parseInt(productId, 10))
      .filter((productId) => Number.isFinite(productId) && productId > 0)
      .slice(0, 100)
    if (pendingIds.length === 0) return

    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0

    const refreshPendingPrices = async () => {
      attempts += 1
      try {
        const page = await searchProductsPage('', {
          produto_ids: pendingIds,
          limit: pendingIds.length,
          offset: 0,
          pricingMode: 'cached',
          pricingRefresh: `${Date.now()}-${attempts}`,
          signal: controller.signal,
        })
        if (controller.signal.aborted) return

        const updatesById = new Map(page.items.map((product) => [product.id, product]))
        setAllProducts((current) => mergeProductPriceUpdates(current, updatesById))
        setHomeProducts((current) => mergeProductPriceUpdates(current, updatesById))
        setHomeJustArrivedProducts((current) => mergeProductPriceUpdates(current, updatesById))

        const hasUnresolvedPendingPrices = pendingIds.some((productId) => {
          const update = updatesById.get(String(productId))
          return !update || update.pricingPending === true
        })

        if (
          page.pricingPending
          && hasUnresolvedPendingPrices
          && attempts < PRODUCT_PRICE_REFRESH_MAX_ATTEMPTS
        ) {
          timer = setTimeout(refreshPendingPrices, resolveProductPriceRefreshDelay(attempts))
        }
      } catch (error) {
        if (controller.signal.aborted || (error as { name?: string } | null)?.name === 'AbortError') return
        if (attempts < PRODUCT_PRICE_REFRESH_MAX_ATTEMPTS) {
          timer = setTimeout(refreshPendingPrices, resolveProductPriceRefreshDelay(attempts))
        }
      }
    }

    timer = setTimeout(refreshPendingPrices, 400)
    return () => {
      controller.abort()
      if (timer) clearTimeout(timer)
    }
  }, [
    isBackendUnavailable,
    mergeProductPriceUpdates,
    pendingProductPricesKey,
  ])

  const fetchManufacturerBrandProductsChunk = useCallback(async (
    manufacturerBrandIds: number[],
    offset: number,
    signal?: AbortSignal,
    onlyAvailable = onlyAvailableFilter
  ) => {
    let nextOffset = offset
    let hasMore = false
    let collected: Product[] = []

    for (let pageCount = 0; pageCount < MANUFACTURER_BRAND_PRODUCTS_PAGE_SCAN_LIMIT; pageCount += 1) {
      const page = await searchProductsPage('', {
        limit: MANUFACTURER_BRAND_PRODUCTS_PAGE_SIZE,
        offset: nextOffset,
        marca_fabricante_ids: manufacturerBrandIds,
        onlyAvailable,
        pricingMode: 'cached',
        signal,
      })
      const pageLimit = Math.max(1, page.limit)
      const rawNextOffset = page.offset + page.items.length
      const visibleItems = onlyAvailable || pricingContextValue.stockStatusMode === 1
        ? page.items.filter((product) => !isProductUnavailable(product, pricingContextValue.stockStatusMode))
        : page.items

      collected = appendUniqueProducts(collected, visibleItems)

      const reachedTotal = page.total !== null && rawNextOffset >= page.total
      const exhaustedByShortPage = page.items.length < pageLimit
      const stalled = rawNextOffset <= nextOffset
      hasMore = page.items.length > 0 && !reachedTotal && !exhaustedByShortPage && !stalled
      nextOffset = rawNextOffset

      if (collected.length >= MANUFACTURER_BRAND_PRODUCTS_PAGE_SIZE || !hasMore) {
        break
      }
    }

    return {
      items: collected,
      nextOffset,
      hasMore,
    }
  }, [appendUniqueProducts, onlyAvailableFilter, pricingContextValue.stockStatusMode])

  const fetchCatalogProductsChunk = useCallback(async (
    offset: number,
    onlyAvailable = onlyAvailableFilter,
    targetLimit = SERVER_PAGINATED_PRODUCTS_PAGE_SIZE,
    signal?: AbortSignal
  ) => {
    const safeTargetLimit = Math.max(1, Math.trunc(targetLimit))
    let nextOffset = offset
    let hasMore = false
    let total: number | null = null
    let collected: Product[] = []

    for (let pageCount = 0; pageCount < 10; pageCount += 1) {
      signal?.throwIfAborted()
      const page = await searchProductsPage('', {
        limit: safeTargetLimit - collected.length,
        offset: nextOffset,
        onlyAvailable,
        pricingMode: 'cached',
        signal,
      })
      total = page.total
      const rawNextOffset = page.offset + page.items.length
      const visibleItems = onlyAvailable || pricingContextValue.stockStatusMode === 1
        ? page.items.filter((product) => !isProductUnavailable(product, pricingContextValue.stockStatusMode))
        : page.items
      collected = appendUniqueProducts(collected, visibleItems)

      const reachedTotal = page.total !== null && rawNextOffset >= page.total
      const exhaustedByShortPage = page.items.length < Math.max(1, page.limit)
      const stalled = rawNextOffset <= nextOffset
      hasMore = page.items.length > 0 && !reachedTotal && !exhaustedByShortPage && !stalled
      nextOffset = rawNextOffset

      if (collected.length >= safeTargetLimit || !hasMore) break
    }

    return {
      items: collected.slice(0, safeTargetLimit),
      nextOffset,
      hasMore,
      total,
    }
  }, [appendUniqueProducts, onlyAvailableFilter, pricingContextValue.stockStatusMode])

  const loadProducts = async (limit: number = DEFAULT_PRODUCT_LIST_LIMIT, onlyAvailable = onlyAvailableFilter) => {
    if (isBackendUnavailable) return

    resetServerPaginatedProducts()
    const controller = new AbortController()
    catalogProductsAbortRef.current = controller

    try {
      setImageSearchState((current) => (current ? null : current))
      setIsSearchResultsActive((current) => (vehicleSearchModeFromRoute ? current : false))
      setIsLoadingProducts(true)
      const manufacturerBrandIds = getManufacturerBrandFilterIds(selectedManufacturerBrands)
      const productsDataKey = buildCatalogProductsDataKey(selectedManufacturerBrands, onlyAvailable)

      if (manufacturerBrandIds.length > 0) {
        const page = await fetchManufacturerBrandProductsChunk(
          manufacturerBrandIds,
          0,
          controller.signal,
          onlyAvailable
        )
        controller.signal.throwIfAborted()
        serverPaginatedProductFeedRef.current = {
          kind: 'catalog_brand',
          dataKey: productsDataKey,
          manufacturerBrandIds,
        }
        productsOffsetRef.current = page.nextOffset
        setHasMoreProducts(page.hasMore)
        setServerPaginatedProductsTotal(null)
        setAllProducts(page.items)
        setProductsDataKey(productsDataKey)
        setHomeProducts(page.items)
        return
      }

      const page = await fetchCatalogProductsChunk(0, onlyAvailable, limit, controller.signal)
      controller.signal.throwIfAborted()
      serverPaginatedProductFeedRef.current = {
        kind: 'catalog',
        dataKey: productsDataKey,
        onlyAvailable,
      }
      productsOffsetRef.current = page.nextOffset
      setHasMoreProducts(page.hasMore)
      setServerPaginatedProductsTotal(page.total)
      setAllProducts(page.items)
      setProductsDataKey(productsDataKey)
      setHomeProducts(page.items)
    } catch (error) {
      if ((error as { name?: string } | null)?.name === 'AbortError') return
      console.error('Failed to load products:', error)
      toast.error(t('toasts.load_products_error'))
    } finally {
      if (catalogProductsAbortRef.current === controller) {
        catalogProductsAbortRef.current = null
        setIsLoadingProducts(false)
        setHasCompletedInitialProductsLoad(true)
      }
    }
  }

  const loadHomeProducts = async (limit: number = HOME_INITIAL_PRODUCTS_LIMIT) => {
    if (isBackendUnavailable) return
    if (homeProductsLoadAbortRef.current && !homeProductsLoadAbortRef.current.signal.aborted) return

    const shouldShowLoadingState = homeProducts.length === 0 && homeJustArrivedProducts.length === 0
    resetServerPaginatedProducts()
    const controller = new AbortController()
    const requestId = ++homeProductsLoadRequestIdRef.current
    homeProductsLoadAbortRef.current = controller
    catalogProductsAbortRef.current = controller

    try {
      setImageSearchState((current) => (current ? null : current))
      setSelectedManufacturerBrands((current) => (current.length > 0 ? [] : current))
      setVehicleSearchModeFromRoute(null)
      setIsSearchResultsActive((current) => (current ? false : current))
      if (shouldShowLoadingState) {
        setIsLoadingProducts(true)
      }

      const homeShowcases = await resolveHomeRequestWithin(
        getHomeProductShowcases(
          HOME_INITIAL_PRODUCTS_LIMIT,
          HOME_JUST_ARRIVED_PRODUCTS_LIMIT,
          { signal: controller.signal }
        ),
        {
          launches: {
            items: [],
            limit: HOME_INITIAL_PRODUCTS_LIMIT,
            offset: 0,
            total: null,
          },
          justArrived: {
            items: [],
            limit: HOME_JUST_ARRIVED_PRODUCTS_LIMIT,
            offset: 0,
            total: null,
          },
        },
        'Failed to load home product showcases'
      )
      const launchesPage = homeShowcases.launches
      const justArrivedPage = homeShowcases.justArrived
      controller.signal.throwIfAborted()
      if (requestId !== homeProductsLoadRequestIdRef.current) return

      let catalogProductsPrefetched: Product[] | null = null
      let featuredPreviewProducts = sortProductsByAvailability(
        filterUnavailableProductsWhenStockRuleEnabled(launchesPage.items, pricingContextValue.stockStatusMode),
        pricingContextValue.stockStatusMode
      )

      if (featuredPreviewProducts.length > 0) {
        setHomeFeaturedProductsView('launches')
      } else {
        const catalogFallbackLimit = Math.max(limit, HOME_FEATURED_PRODUCTS_LIMIT)
        catalogProductsPrefetched = await resolveHomeRequestWithin(
          getProducts(catalogFallbackLimit, 0, { signal: controller.signal }),
          [],
          'Failed to load home launches fallback from catalog'
        )
        controller.signal.throwIfAborted()
        if (requestId !== homeProductsLoadRequestIdRef.current) return

        featuredPreviewProducts = sortProductsByAvailability(
          filterUnavailableProductsWhenStockRuleEnabled(catalogProductsPrefetched, pricingContextValue.stockStatusMode),
          pricingContextValue.stockStatusMode
        ).slice(0, HOME_FEATURED_PRODUCTS_LIMIT)
        setHomeFeaturedProductsView(featuredPreviewProducts.length > 0 ? 'products' : 'launches')

        if (catalogProductsPrefetched.length > 0) {
          setAllProducts(catalogProductsPrefetched)
          setProductsDataKey(ALL_PRODUCTS_DATA_KEY)
        }
      }

      preloadImageAsset(
        featuredPreviewProducts.find((item) => typeof item.image === 'string' && item.image.trim())?.image
      )
      setHomeProducts(featuredPreviewProducts)
      setHomeJustArrivedProducts(sortProductsByAvailability(
        filterUnavailableProductsWhenStockRuleEnabled(justArrivedPage.items, pricingContextValue.stockStatusMode),
        pricingContextValue.stockStatusMode
      ))
      setHasLoadedHomeJustArrivedPreview(true)

    } catch (error) {
      if ((error as { name?: string } | null)?.name === 'AbortError') return
      console.error('Failed to load home products:', error)
      toast.error(t('toasts.load_products_error'))
    } finally {
      if (homeProductsLoadAbortRef.current === controller) {
        homeProductsLoadAbortRef.current = null
        const stillOwnsProductsLoading = catalogProductsAbortRef.current === controller
        if (stillOwnsProductsLoading) {
          catalogProductsAbortRef.current = null
          if (shouldShowLoadingState) {
            setIsLoadingProducts(false)
          }
        }
        if (!controller.signal.aborted && requestId === homeProductsLoadRequestIdRef.current) {
          setHasCompletedHomeProductsLoad(true)
          setHasCompletedInitialProductsLoad(true)
        }
      }
    }
  }

  const loadLaunchProducts = async () => {
    if (isBackendUnavailable) return

    try {
      resetServerPaginatedProducts()
      setImageSearchState((current) => (current ? null : current))
      setSelectedManufacturerBrands((current) => (current.length > 0 ? [] : current))
      setVehicleSearchModeFromRoute(null)
      setIsSearchResultsActive((current) => (current ? false : current))
      setIsLoadingProducts(true)
      const page = await getLancamentosPage(SERVER_PAGINATED_PRODUCTS_PAGE_SIZE, 0)
      serverPaginatedProductFeedRef.current = { kind: 'view', view: 'launches' }
      setAllProducts(page.items)
      syncServerPaginatedProducts(page)
      setProductsDataKey(LAUNCH_PRODUCTS_DATA_KEY)
    } catch (error) {
      console.error('Failed to load launch products:', error)
      toast.error(t('toasts.load_products_error'))
    } finally {
      setIsLoadingProducts(false)
      setHasCompletedInitialProductsLoad(true)
    }
  }

  const loadJustArrivedProducts = async () => {
    if (isBackendUnavailable) return

    try {
      resetServerPaginatedProducts()
      setImageSearchState((current) => (current ? null : current))
      setSelectedManufacturerBrands((current) => (current.length > 0 ? [] : current))
      setVehicleSearchModeFromRoute(null)
      setIsSearchResultsActive((current) => (current ? false : current))
      setIsLoadingProducts(true)
      const page = await getAcabaramDeChegarPage(SERVER_PAGINATED_PRODUCTS_PAGE_SIZE, 0)
      serverPaginatedProductFeedRef.current = { kind: 'view', view: 'just_arrived' }
      setAllProducts(page.items)
      syncServerPaginatedProducts(page)
      setProductsDataKey(JUST_ARRIVED_PRODUCTS_DATA_KEY)
    } catch (error) {
      console.error('Failed to load just-arrived products:', error)
      toast.error(t('toasts.load_products_error'))
    } finally {
      setIsLoadingProducts(false)
      setHasCompletedInitialProductsLoad(true)
    }
  }

  const loadMoreServerPaginatedProducts = useCallback(async () => {
    if (isBackendUnavailable || isLoadingProducts || isLoadingMoreProductsRef.current || !hasMoreProducts) {
      return
    }

    const feed = serverPaginatedProductFeedRef.current
    if (!feed) {
      return
    }

    isLoadingMoreProductsRef.current = true
    setIsLoadingMoreProducts(true)
    const controller = new AbortController()
    catalogProductsAbortRef.current = controller

    try {
      let page: PaginatedProductListResult

      if (feed.kind === 'view') {
        const view = feed.view
        if (currentViewRef.current !== view) return
        page = await fetchServerPaginatedProducts(view, productsOffsetRef.current)
        if (currentViewRef.current !== view || serverPaginatedProductFeedRef.current !== feed) return
      } else if (feed.kind === 'catalog_brand') {
        if (currentViewRef.current !== 'products') return
        const manufacturerBrandPage = await fetchManufacturerBrandProductsChunk(
          feed.manufacturerBrandIds,
          productsOffsetRef.current,
          controller.signal
        )
        controller.signal.throwIfAborted()
        if (currentViewRef.current !== 'products' || serverPaginatedProductFeedRef.current !== feed) return
        setAllProducts((current) => appendUniqueProducts(current, manufacturerBrandPage.items))
        productsOffsetRef.current = manufacturerBrandPage.nextOffset
        setHasMoreProducts(manufacturerBrandPage.hasMore)
        setServerPaginatedProductsTotal(null)
        return
      } else if (feed.kind === 'search') {
        if (currentViewRef.current !== 'products') return
        const searchOffset = productsOffsetRef.current
        const searchPage = await searchFilteredProductsPage(feed.query, {
          limit: TEXT_SEARCH_PRODUCTS_PAGE_SIZE,
          offset: searchOffset,
          marca_fabricante_ids: feed.manufacturerBrandIds.length > 0
            ? feed.manufacturerBrandIds
            : undefined,
          onlyAvailable: feed.onlyAvailable,
          vehicleFilter: feed.vehicleFilter,
          pricingMode: 'cached',
          signal: controller.signal,
        })
        const expandedSearchProducts = await expandProductsWithParentContexts(feed.query, searchPage.items, {
          signal: controller.signal,
        })
        controller.signal.throwIfAborted()
        if (currentViewRef.current !== 'products' || serverPaginatedProductFeedRef.current !== feed) return
        const searchPageNumber = Math.floor(searchOffset / TEXT_SEARCH_PRODUCTS_PAGE_SIZE) + 1
        const previousProductIds = new Set(allProducts.map((product) => product.id))
        const incomingProductIds = new Set(expandedSearchProducts.map((product) => product.id))
        const mergedSearchProducts = appendSearchProductsWithMarketplaceLast(allProducts, expandedSearchProducts)
        const displayedSearchProducts = resolveDisplayedSearchProductsForAnalytics(mergedSearchProducts)
        const displayedIncomingProducts = displayedSearchProducts.filter((product) => (
          incomingProductIds.has(product.id) && !previousProductIds.has(product.id)
        ))
        const positionByProductId = new Map(
          displayedSearchProducts.map((product, index) => [product.id, index + 1]),
        )
        registerStorefrontSearchResultPage(feed.analyticsContext, displayedIncomingProducts, searchPageNumber)
        setAllProducts(mergedSearchProducts)
        productsOffsetRef.current = searchPage.nextOffset
        setHasMoreProducts(searchPage.hasMore)
        setServerPaginatedProductsTotal(
          feed.vehicleFilter || searchPage.total === null
            ? null
            : searchPage.total + marketplaceSearchTotalRef.current
        )
        queueStorefrontConsentGrantedAnalyticsEvent({
          eventType: 'search',
          pageKind: 'search',
          pageKey: `search:${feed.query}`,
          pagePath: `${VIEW_PATHS.products}?q=${encodeURIComponent(feed.query)}`,
          payload: {
            search_id: feed.analyticsContext.searchId,
            search_term: feed.query,
            search_mode: feed.analyticsContext.searchMode,
            search_status: 'success',
            page: searchPageNumber,
            page_results_count: displayedIncomingProducts.length,
            result_products: buildStorefrontSearchResultProducts(displayedIncomingProducts, {
              positionByProductId,
            }),
            source_view: feed.analyticsContext.sourceView || undefined,
          },
        })
        return
      } else if (feed.kind === 'category') {
        if (currentViewRef.current !== 'products') return
        const categoryPage = await searchProductsPage('', {
          ...feed.searchOptions,
          limit: STOREFRONT_PRODUCT_PAGE_SIZE,
          offset: productsOffsetRef.current,
          onlyAvailable: feed.onlyAvailable,
          pricingMode: 'cached',
          signal: controller.signal,
        })
        controller.signal.throwIfAborted()
        if (currentViewRef.current !== 'products' || serverPaginatedProductFeedRef.current !== feed) return
        setAllProducts((current) => appendUniqueProducts(current, categoryPage.items))
        syncServerPaginatedProducts(categoryPage)
        return
      } else {
        if (currentViewRef.current !== 'products') return
        const catalogPage = await fetchCatalogProductsChunk(
          productsOffsetRef.current,
          feed.onlyAvailable,
          SERVER_PAGINATED_PRODUCTS_PAGE_SIZE,
          controller.signal
        )
        controller.signal.throwIfAborted()
        if (currentViewRef.current !== 'products' || serverPaginatedProductFeedRef.current !== feed) return
        setAllProducts((current) => appendUniqueProducts(current, catalogPage.items))
        productsOffsetRef.current = catalogPage.nextOffset
        setHasMoreProducts(catalogPage.hasMore)
        setServerPaginatedProductsTotal(catalogPage.total)
        return
      }

      setAllProducts((current) => appendUniqueProducts(current, page.items))
      syncServerPaginatedProducts(page)
    } catch (error) {
      if ((error as { name?: string } | null)?.name === 'AbortError') return
      console.error('Failed to load more products:', error)
      toast.error(t('toasts.load_products_error'))
      if (serverPaginatedProductFeedRef.current === feed) {
        setHasMoreProducts(false)
      }
    } finally {
      if (
        catalogProductsAbortRef.current === controller
        && serverPaginatedProductFeedRef.current === feed
      ) {
        catalogProductsAbortRef.current = null
        setIsLoadingMoreProducts(false)
        isLoadingMoreProductsRef.current = false
      }
    }
  }, [
    appendUniqueProducts,
    appendSearchProductsWithMarketplaceLast,
    allProducts,
    fetchManufacturerBrandProductsChunk,
    fetchCatalogProductsChunk,
    fetchServerPaginatedProducts,
    hasMoreProducts,
    isBackendUnavailable,
    isLoadingProducts,
    resolveDisplayedSearchProductsForAnalytics,
    syncServerPaginatedProducts,
    t,
  ])

  useEffect(() => {
    if (isBackendUnavailable) return
    if (currentView !== 'home') return

    resetServerPaginatedProducts()
    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }

    searchRequestIdRef.current += 1
    inFlightSearchProductsDataKeyRef.current = null
    shouldApplyRouteSearchRef.current = false
    setImageSearchState((current) => (current ? null : current))
    setIsImageSearchLoading(false)
    setIsSearchResultsActive((current) => (current ? false : current))
    setSelectedManufacturerBrands((current) => (current.length > 0 ? [] : current))
    setIsLoadingProducts((current) => (current ? false : current))

    if (!hasCompletedHomeProductsLoad && (homeProducts.length === 0 || !hasLoadedHomeJustArrivedPreview)) {
      void loadHomeProducts(HOME_INITIAL_PRODUCTS_LIMIT)
    }
  }, [
    currentView,
    hasCompletedHomeProductsLoad,
    hasLoadedHomeJustArrivedPreview,
    homeProducts.length,
    isBackendUnavailable,
    resetServerPaginatedProducts,
  ])

  useEffect(() => {
    if (isBackendUnavailable) return

    if (currentView === 'launches') {
      if (productsDataKey === LAUNCH_PRODUCTS_DATA_KEY && allProducts.length > 0) {
        return
      }
      void loadLaunchProducts()
      return
    }

    if (currentView !== 'just_arrived') return

    if (productsDataKey === JUST_ARRIVED_PRODUCTS_DATA_KEY && allProducts.length > 0) {
      return
    }

    void loadJustArrivedProducts()
  }, [
    allProducts.length,
    currentView,
    isBackendUnavailable,
    productsDataKey,
  ])

  useEffect(() => {
    if (isBackendUnavailable) return
    if (currentView !== 'products') return

    const normalizedProductsRouteQuery = searchQuery.trim()
    const hasPendingCategoryRoute = categoryRouteSlugs.length > 0 && !selectedCategory
    if (hasPendingCategoryRoute) return
    if (imageSearchState) return

    // A categoria passa a ser a dona da listagem assim que é selecionada.
    // Antes, uma busca anterior ainda registrada em searchAbortRef fazia este
    // efeito sair sem carregar a categoria. Quando a busca terminava, somente
    // a ref mudava (sem alterar uma dependência do efeito), deixando a tela
    // vazia até o refresh completo da página.
    if (selectedCategory && !normalizedProductsRouteQuery && searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
      searchRequestIdRef.current += 1
      inFlightSearchProductsDataKeyRef.current = null
    }

    if (searchAbortRef.current) return

    if (selectedCategory && !normalizedProductsRouteQuery) {
      const categoryProductsDataKey = categoryRouteSlugs.length > 0
        ? buildCategoryRouteProductsDataKey(categoryRouteSlugs, selectedManufacturerBrands, onlyAvailableFilter)
        : buildCategoryProductsDataKey(selectedCategory, selectedManufacturerBrands, onlyAvailableFilter)
      if (productsDataKey === categoryProductsDataKey && allProducts.length > 0) {
        return
      }
      void loadProductsByCategory()
      return
    }

    if (!selectedCategory && !normalizedProductsRouteQuery) {
      const catalogProductsDataKey = buildCatalogProductsDataKey(selectedManufacturerBrands, onlyAvailableFilter)
      if (productsDataKey === catalogProductsDataKey && allProducts.length > 0) {
        return
      }
      void loadProducts(DEFAULT_PRODUCT_LIST_LIMIT)
    }
  }, [
    allProducts.length,
    categoryRouteSlugs.length,
    currentView,
    isBackendUnavailable,
    imageSearchState,
    onlyAvailableFilter,
    productDataAudienceKey,
    productsDataKey,
    searchQuery,
    selectedCategory,
    selectedManufacturerBrands,
  ])

  const loadProductsByCategory = async (
    onlyAvailable = onlyAvailableFilter,
    target?: {
      categoryId: string
      routeSlugs: string[]
      manufacturerBrands: ManufacturerBrandFilterState[]
    },
  ) => {
    const categoryId = target?.categoryId ?? selectedCategory
    if (!categoryId || isBackendUnavailable) return

    const requestRouteSlugs = target?.routeSlugs ?? categoryRouteSlugs
    const requestManufacturerBrands = target?.manufacturerBrands ?? selectedManufacturerBrands
    const categoryProductsDataKey = requestRouteSlugs.length > 0
      ? buildCategoryRouteProductsDataKey(requestRouteSlugs, requestManufacturerBrands, onlyAvailable)
      : buildCategoryProductsDataKey(categoryId, requestManufacturerBrands, onlyAvailable)
    if (categoryProductsInFlightDataKeyRef.current === categoryProductsDataKey) return

    resetServerPaginatedProducts()
    categoryProductsInFlightDataKeyRef.current = categoryProductsDataKey
    const categoryProductsRequestId = ++categoryProductsRequestIdRef.current
    const controller = new AbortController()
    catalogProductsAbortRef.current = controller
    const manufacturerBrandIds = getManufacturerBrandFilterIds(requestManufacturerBrands)

    try {
      setImageSearchState((current) => (current ? null : current))
      setVehicleSearchModeFromRoute(null)
      setIsSearchResultsActive(false)
      setIsLoadingProducts(true)
      let searchOptions: Record<string, number | number[]> = {}

      if (categoryModel === 'classes') {
        const selectedCategoryNode = findLocalNodeById(mainMenuCategories, categoryId)
        const classIds = selectedCategoryNode
          ? collectCategoryAndDescendantIds(selectedCategoryNode)
          : [categoryId]
        const normalizedClassIds = classIds
          .map((classId) => Number(classId))
          .filter((classId) => Number.isFinite(classId) && classId > 0)
          .map((classId) => Math.trunc(classId))

        if (normalizedClassIds.length > 0) {
          searchOptions = {
            classe_ids: normalizedClassIds,
          }

          if (manufacturerBrandIds.length > 0) {
            searchOptions.marca_fabricante_ids = manufacturerBrandIds
          }
        }
      } else {
        Object.assign(searchOptions, buildCategorySearchFilters(categoryId, requestRouteSlugs))
        if (manufacturerBrandIds.length > 0) {
          searchOptions.marca_fabricante_ids = manufacturerBrandIds
        }
      }

      const page = Object.keys(searchOptions).length > 0
        ? await searchProductsPage('', {
            ...searchOptions,
            limit: STOREFRONT_PRODUCT_PAGE_SIZE,
            offset: 0,
            onlyAvailable,
            pricingMode: 'cached',
            signal: controller.signal,
          })
        : {
            items: [],
            limit: STOREFRONT_PRODUCT_PAGE_SIZE,
            offset: 0,
            total: 0,
          } satisfies PaginatedProductListResult

      controller.signal.throwIfAborted()
      if (categoryProductsRequestId !== categoryProductsRequestIdRef.current) return
      serverPaginatedProductFeedRef.current = {
        kind: 'category',
        dataKey: categoryProductsDataKey,
        searchOptions,
        onlyAvailable,
      }
      setAllProducts(page.items)
      syncServerPaginatedProducts(page)
      setProductsDataKey(categoryProductsDataKey)
    } catch (error) {
      if ((error as { name?: string } | null)?.name === 'AbortError') return
      console.error('Failed to load category products:', error)
      toast.error(t('toasts.load_category_products_error'))
    } finally {
      if (categoryProductsInFlightDataKeyRef.current === categoryProductsDataKey) {
        categoryProductsInFlightDataKeyRef.current = null
      }
      if (catalogProductsAbortRef.current === controller) {
        catalogProductsAbortRef.current = null
        setIsLoadingProducts(false)
      }
    }
  }

  // O servidor é a fonte de verdade para o preço de um carrinho regular.
  // Enviar o valor calculado no navegador pode persistir um preço que não é o
  // aplicável ao item. Apenas a fotografia travada de um orçamento mantém um
  // preço explícito.
  const resolveCartApiPrice = useCallback((item?: CartItem | null) => {
    if (item && typeof item.lockedUnitPrice === 'number' && Number.isFinite(item.lockedUnitPrice) && item.lockedUnitPrice > 0) {
      return item.lockedUnitPrice
    }

    return undefined
  }, [])

  // Assinatura de preço explícito (somente itens de orçamento travados), sem quantidade.
  const computeCartApiPriceSignature = useCallback((items: CartItem[]) => (
    items.map((item) => {
      const price = resolveCartApiPrice(item)
      return `${item.product.id}:${typeof price === 'number' ? price.toFixed(4) : ''}`
    }).sort().join('|')
  ), [resolveCartApiPrice])
  const computeCartApiPriceSignatureRef = useRef(computeCartApiPriceSignature)

  useEffect(() => {
    computeCartApiPriceSignatureRef.current = computeCartApiPriceSignature
  }, [computeCartApiPriceSignature])

  // Após sincronizar o carrinho pelos caminhos normais (add / quantidade /
  // remoção), adota a assinatura atual como baseline para não reenviar uma
  // rodada de sincronização que já foi persistida.
  const markCartPriceSyncBaseline = useCallback(() => {
    lastCartPriceSyncSignatureRef.current = computeCartApiPriceSignatureRef.current(cartItemsRef.current)
  }, [])

  // Reconstrói a item-key (`${produtoId}_${qtdeCaixa}`) a partir da resposta do
  // upsert/update, para atualizar o mapa de item-keys SEM precisar re-hidratar o
  // carrinho inteiro (que faria um GET getProductById por produto).
  const resolveItemKeyFromCartResponse = useCallback((
    response: { produto_id?: number | null; qtde_caixa?: number | null } | null | undefined,
    fallbackProductId: number
  ): string => {
    const produtoId = Math.trunc(Number(response?.produto_id ?? fallbackProductId)) || fallbackProductId
    const qtdeCaixa = Math.max(1, Math.trunc(Number(response?.qtde_caixa ?? 1)) || 1)
    return `${produtoId}_${qtdeCaixa}`
  }, [])

  const syncCartItemToApi = useCallback(async (
    productId: string,
    quantity: number,
    product?: Product | null,
    options?: CartSyncOptions
  ) => {
    if (!isCartAuthenticated) return

    const currentCartItem = cartItemsRef.current.find((item) => item.product.id === productId)
    const resolvedProduct = product ?? currentCartItem?.product ?? null
    const numericProductId = resolveCartSyncProductId(productId, resolvedProduct)
    if (!numericProductId) return

    const qtdeCaixa = resolveCartSyncBoxQuantity(productId, resolvedProduct)

    try {
      const mappedItemKeys = cartItemKeysByProductIdRef.current.get(productId) ?? []
      const cartApiPrice = resolveCartApiPrice(currentCartItem)

      if (mappedItemKeys.length > 1) {
        await Promise.all(
          mappedItemKeys.map((itemKey) => upsertCarrinhoItemByKey(itemKey, 0))
        )

        if (quantity <= 0) {
          cartItemKeysByProductIdRef.current.delete(productId)
          return
        }

        const response = await updateCarrinho(numericProductId, quantity, cartApiPrice, undefined, undefined, qtdeCaixa)
        cartItemKeysByProductIdRef.current.set(productId, [
          resolveItemKeyFromCartResponse(response, numericProductId),
        ])
        return
      }

      const mappedItemKey = mappedItemKeys[0]
      if (mappedItemKey) {
        await upsertCarrinhoItemByKey(mappedItemKey, Math.max(0, quantity), cartApiPrice)
        if (quantity <= 0) {
          cartItemKeysByProductIdRef.current.delete(productId)
        }
        return
      }

      if (quantity <= 0) {
        await updateCarrinho(numericProductId, 0, undefined, undefined, undefined, qtdeCaixa)
      } else {
        const response = await updateCarrinho(numericProductId, quantity, cartApiPrice, undefined, undefined, qtdeCaixa)
        cartItemKeysByProductIdRef.current.set(productId, [
          resolveItemKeyFromCartResponse(response, numericProductId),
        ])
      }
    } catch (error) {
      console.error(`Failed to sync cart item ${productId}:`, error)
      if (options?.throwOnError) {
        throw error instanceof Error ? error : new Error(String(error))
      }
    }
  }, [isCartAuthenticated, resolveCartApiPrice, resolveItemKeyFromCartResponse])

  const syncCartItemsToApi = useCallback(async (
    targets: Array<{ productId: string; quantity: number; product: Product }>,
    options?: { useServerPricing?: boolean; deferTaxes?: boolean },
  ) => {
    if (!isCartAuthenticated || targets.length === 0) return

    const response = await upsertCarrinhoItemsBatch(targets.map(({ productId, quantity, product }) => {
      const numericProductId = resolveCartSyncProductId(productId, product)
      if (!numericProductId) {
        throw new Error(`Produto inválido para sincronização em lote: ${productId}`)
      }
      const currentCartItem = cartItemsRef.current.find((item) => item.product.id === productId)
      return {
        requestId: productId,
        produtoId: numericProductId,
        qtde: Math.max(0, quantity),
        qtdeCaixa: resolveCartSyncBoxQuantity(productId, product),
        preco: options?.useServerPricing
          ? undefined
          : resolveCartApiPrice(currentCartItem),
      }
    }), undefined, undefined, { deferTaxes: options?.deferTaxes ?? true })

    if (response.items.length !== targets.length) {
      throw new Error('Resposta incompleta ao sincronizar itens do carrinho.')
    }

    const resultByRequestId = new Map(response.items.map((item) => [item.requestId, item]))
    for (const target of targets) {
      const result = resultByRequestId.get(target.productId)
      if (!result || result.produtoId <= 0) {
        throw new Error(`Item ${target.productId} ausente na resposta do lote.`)
      }
      if (target.quantity <= 0) {
        cartItemKeysByProductIdRef.current.delete(target.productId)
        continue
      }
      cartItemKeysByProductIdRef.current.set(target.productId, [
        `${result.produtoId}_${Math.max(1, result.qtdeCaixa)}`,
      ])
    }
  }, [isCartAuthenticated, resolveCartApiPrice])

  const hydrateCartFromApi = useCallback(async (options?: { markInitialized?: boolean }) => {
    if (!isCartAuthenticated || isLegacyCheckoutSessionBridge) return
    const shouldMarkInitialized = options?.markInitialized === true
    const mutationRevisionAtStart = cartMutationRevisionRef.current

    try {
      const response = await getCarrinhoQuantidades(undefined, undefined, {
        suppressBuyerSessionExpiry: true,
      })
      const cartEntries = new Map<string, {
        baseProductId: string
        qtdeCaixa: number
        quantity: number
        itemKeys: string[]
      }>()

      Object.entries(response.itens).forEach(([itemKey, rawQuantity]) => {
        const [rawProductId = '', rawQtdeCaixa = '1'] = itemKey.split('_')
        const numericProductId = normalizeCartProductId(rawProductId.trim())
        if (!numericProductId) return
        const qtdeCaixa = Math.max(1, Math.trunc(Number(rawQtdeCaixa) || 1))

        const normalizedQuantity = Math.max(0, Math.trunc(rawQuantity))
        if (normalizedQuantity <= 0) return

        const entryKey = `${numericProductId}_${qtdeCaixa}`
        const currentEntry = cartEntries.get(entryKey)
        if (currentEntry) {
          currentEntry.quantity += normalizedQuantity
          currentEntry.itemKeys.push(itemKey)
          return
        }

        cartEntries.set(entryKey, {
          baseProductId: String(numericProductId),
          qtdeCaixa,
          quantity: normalizedQuantity,
          itemKeys: [itemKey],
        })
      })

      if (cartEntries.size === 0) {
        if (cartMutationRevisionRef.current !== mutationRevisionAtStart) return
        cartItemKeysByProductIdRef.current = new Map()
        applyCartItemsSnapshot([])
        trackStorefrontCartState([], 'cart_hydrated')
        setHasConfirmedCartStateFromApi(true)
        return
      }

      const productMap = new Map<string, Product>()
      const currentProductByCartEntry = new Map<string, Product>()
      cartItemsRef.current.forEach((item) => {
        const baseProductId = resolveCartSyncProductId(item.product.id, item.product)
        if (!baseProductId) return
        const qtdeCaixa = resolveCartSyncBoxQuantity(item.product.id, item.product)
        currentProductByCartEntry.set(`${baseProductId}_${qtdeCaixa}`, item.product)
      })
      const baseProductIds = Array.from(
        new Set(Array.from(cartEntries.values()).map((entry) => entry.baseProductId))
      )
      const baseProductIdsNeedingBoxOptions = Array.from(
        new Set(
          Array.from(cartEntries.values())
            .filter((entry) => entry.qtdeCaixa > 1)
            .map((entry) => entry.baseProductId)
        )
      )
      const fetchedProducts = await getProductsByIds(baseProductIds, {
        fallbackMissing: false,
        fallbackMissingBoxOptionsForIds: baseProductIdsNeedingBoxOptions,
        // Uma indisponibilidade transitória do catálogo não significa que o
        // carrinho persistido esteja vazio. Propagamos o erro para preservar o
        // snapshot atual em vez de converter um HTTP 503 em uma lista vazia.
        throwOnBatchError: true,
        // A busca por produto_ids pode devolver somente o card do pai quando o
        // carrinho contém uma variação. Resolve todas as variações ausentes em
        // uma única chamada batch, sem N+1.
        resolveRequestedVariations: true,
      })

      fetchedProducts.forEach((product) => {
        if (product) {
          productMap.set(product.id, product)
        }
      })

      const persistedProductByCartEntry = new Map<string, Product>()
      const hasMissingCartProduct = Array.from(cartEntries.values()).some((entry) => (
        !productMap.has(entry.baseProductId)
        && !currentProductByCartEntry.has(`${entry.baseProductId}_${entry.qtdeCaixa}`)
      ))
      if (hasMissingCartProduct) {
        const persistedProducts = await getCarrinhoProducts(undefined, undefined, {
          suppressBuyerSessionExpiry: true,
        })
        persistedProducts.forEach((product) => {
          const baseProductId = resolveCartSyncProductId(product.id, product)
          const qtdeCaixa = resolveCartSyncBoxQuantity(product.id, product)
          persistedProductByCartEntry.set(`${baseProductId}_${qtdeCaixa}`, product)
        })
      }

      const hydratedItems: CartItem[] = []
      const hydratedItemKeysByProduct = new Map<string, string[]>()

      cartEntries.forEach((entry) => {
        const baseProduct = productMap.get(entry.baseProductId)
          ?? currentProductByCartEntry.get(`${entry.baseProductId}_${entry.qtdeCaixa}`)
        const product = baseProduct
          ? buildStorefrontPurchaseOptionProduct(baseProduct, entry.qtdeCaixa)
          : persistedProductByCartEntry.get(`${entry.baseProductId}_${entry.qtdeCaixa}`)
        if (!product) {
          throw new Error(`Produto ${entry.baseProductId} ausente na consulta do carrinho salvo.`)
        }

        const minOrder = product.minOrder ?? 1
        hydratedItems.push({
          product,
          quantity: Math.max(minOrder, Math.min(CART_ITEM_QUANTITY_MAX, entry.quantity)),
        })
        hydratedItemKeysByProduct.set(product.id, entry.itemKeys)
      })

      // Uma hidratação iniciada antes de uma inclusão/alteração não pode
      // sobrescrever a mutação mais recente com a fotografia antiga do servidor.
      if (cartMutationRevisionRef.current !== mutationRevisionAtStart) return

      applyCartItemsSnapshot(hydratedItems)
      trackStorefrontCartState(hydratedItems, 'cart_hydrated')
      cartItemKeysByProductIdRef.current = hydratedItemKeysByProduct
      markCartPriceSyncBaseline()
      setHasConfirmedCartStateFromApi(true)
    } catch (error) {
      console.error('Failed to hydrate cart from API:', error)
    } finally {
      if (shouldMarkInitialized) {
        setHasHydratedCartOnce(true)
        setIsCartInitializing(false)
      }
    }
  }, [applyCartItemsSnapshot, isCartAuthenticated, isLegacyCheckoutSessionBridge, markCartPriceSyncBaseline])

  const refreshStorefrontPricesAfterTaxModeChange = useCallback(async () => {
    // O carrinho e a vitrine leem o mesmo contexto de preço. Reidratar o
    // carrinho sozinho deixava os cards já renderizados no valor anterior.
    await hydrateCartFromApi()

    const productsOnScreen = [
      ...allProducts,
      ...homeProducts,
      ...homeJustArrivedProducts,
      ...(selectedProduct ? [selectedProduct] : []),
      ...(displayedProductDetail ? [displayedProductDetail] : []),
      ...(compareProducts ?? []),
    ]
    const productIds = Array.from(new Set(
      productsOnScreen
        .map((product) => String(product.id ?? '').trim())
        .filter((productId) => /^\d+$/.test(productId))
    ))

    try {
      if (productIds.length > 0) {
        // Uma busca consolidada atualiza todos os produtos conhecidos da tela.
        // A função interna respeita o limite do endpoint sem fazer N+1.
        const refreshedProducts = await getProductsByIds(productIds, {
          fallbackMissing: false,
          throwOnBatchError: true,
          resolveRequestedVariations: true,
        })
        const updatesById = new Map(
          refreshedProducts
            .filter((product): product is Product => Boolean(product?.id))
            .map((product) => [product.id, product])
        )

        setAllProducts((current) => mergeProductPriceUpdates(current, updatesById))
        setHomeProducts((current) => mergeProductPriceUpdates(current, updatesById))
        setHomeJustArrivedProducts((current) => mergeProductPriceUpdates(current, updatesById))
        setCompareProducts((current) => mergeProductPriceUpdates(current ?? [], updatesById))
        setSelectedProduct((current) => {
          if (!current) return current
          return mergeProductPriceUpdates([current], updatesById)[0] ?? current
        })
        setDisplayedProductDetail((current) => {
          if (!current) return current
          return mergeProductPriceUpdates([current], updatesById)[0] ?? current
        })
      }
    } catch (error) {
      // A troca já foi persistida no backend. Uma falha transitória na vitrine
      // não deve revertê-la nem exibir o valor anterior como confirmação.
      console.error('Failed to refresh storefront prices after tax mode change:', error)
    } finally {
      // O detalhe tem listas locais (relacionados/compre junto) que também são
      // atualizadas em lote quando esta versão muda.
      setStorefrontPriceRefreshVersion((current) => current + 1)
    }
  }, [
    allProducts,
    compareProducts,
    displayedProductDetail,
    homeJustArrivedProducts,
    homeProducts,
    hydrateCartFromApi,
    mergeProductPriceUpdates,
    selectedProduct,
    setCompareProducts,
  ])

  const handleQuoteSentToCart = useCallback(async (payload?: QuoteSentToCartPayload) => {
    if (!isCartAuthenticated) {
      redirectToLoginForCart()
      return
    }

    const nextCartItems = Array.isArray(payload?.cartItems) ? payload.cartItems : []
    let snapshotItems = nextCartItems
    if (nextCartItems.length > 0) {
      if (payload?.mode === 'replace') {
        cartItemKeysByProductIdRef.current = new Map()
      }
      applyCartItemsSnapshot(nextCartItems)
      if (payload?.cartAlreadyPersisted) {
        markCartPriceSyncBaseline()
      } else {
        try {
          await syncCartItemsToApi(
            nextCartItems.map((item) => ({
              productId: item.product.id,
              quantity: item.quantity,
              product: item.product,
            })),
            payload?.pricingMode === 'catalog'
              ? { useServerPricing: true, deferTaxes: false }
              : undefined
          )
          if (payload?.pricingMode === 'catalog') {
            await hydrateCartFromApi()
            snapshotItems = cartItemsRef.current
          }
          markCartPriceSyncBaseline()
        } catch (error) {
          await hydrateCartFromApi()
          throw (
            error instanceof Error
              ? error
              : new Error('Não foi possível confirmar os itens do orçamento no carrinho.')
          )
        }
      }
    } else {
      await hydrateCartFromApi()
    }
    const nextOriginQuote = payload?.originQuote
      ? {
          ...payload.originQuote,
          destination: 'self' as const,
          invoiceRecipientId: effectiveAccountProfile?.entidadeId ?? authenticatedEntidadeId ?? null,
          invoiceRecipientName: effectiveAccountProfile?.corporateName || effectiveAccountProfile?.name || '',
          invoiceRecipientDocument: effectiveAccountProfile?.document || effectiveAccountProfile?.cnpj || '',
          cartSnapshotSignature: buildCartQuoteSnapshotSignature(snapshotItems),
          freightPrefill: payload.freightPrefill ?? null,
        }
      : null
    setCartQuoteOrigin(nextOriginQuote)
    setCartQuoteFreightPrefill(nextOriginQuote?.freightPrefill ?? null)
    broadcastCartChange('quote-sent-to-cart')
    trackStorefrontCartState(snapshotItems, 'quote_sent_to_cart')
    flushSync(() => {
      setCartDrawerSource('default')
      setShowPriceTableCartPanel(false)
      setIsPriceTableCartPanelCollapsed(true)
      setShowCartDrawer(true)
    })
    void loadCartDrawer()
  }, [
    applyCartItemsSnapshot,
    authenticatedEntidadeId,
    broadcastCartChange,
    hydrateCartFromApi,
    effectiveAccountProfile?.cnpj,
    effectiveAccountProfile?.corporateName,
    effectiveAccountProfile?.document,
    effectiveAccountProfile?.entidadeId,
    effectiveAccountProfile?.name,
    isCartAuthenticated,
    markCartPriceSyncBaseline,
    redirectToLoginForCart,
    syncCartItemsToApi,
  ])

  const handleQuoteCartSnapshotUpdated = useCallback((payload: QuoteCartSnapshotUpdatePayload) => {
    setCartQuoteOrigin((currentOrigin) => applyLinkedQuoteCartSnapshotUpdate(
      currentOrigin,
      cartItemsRef.current,
      payload
    ))
  }, [setCartQuoteOrigin])

  const runCartMutation = useCallback(async (operation: () => Promise<void>) => {
    // A revisão muda de forma síncrona, antes de a operação entrar na fila, para
    // invalidar hidratações que tenham começado com um estado anterior.
    cartMutationRevisionRef.current += 1
    const nextRun = cartMutationQueueRef.current
      .catch(() => undefined)
      .then(operation)

    cartMutationQueueRef.current = nextRun.then(() => undefined, () => undefined)
    await nextRun
  }, [])

  useEffect(() => {
    if (!isCartAuthenticated) {
      cartItemKeysByProductIdRef.current = new Map()
      lastCartPriceSyncSignatureRef.current = null
      pendingCartSyncRef.current = new Map()
      if (cartSyncDebounceTimerRef.current !== null) {
        window.clearTimeout(cartSyncDebounceTimerRef.current)
        cartSyncDebounceTimerRef.current = null
      }
      setIsCartInitializing(false)
      setHasHydratedCartOnce(false)
      setHasConfirmedCartStateFromApi(false)
      return
    }

    if (isLegacyCheckoutSessionBridge) {
      setIsCartInitializing(false)
      return
    }

    cartItemKeysByProductIdRef.current = new Map()
    setHasHydratedCartOnce(false)
    setHasConfirmedCartStateFromApi(false)
    setIsCartInitializing(true)

    if (isStandalonePriceTableView && typeof window !== 'undefined') {
      let cancelled = false
      const timeoutId = window.setTimeout(() => {
        if (!cancelled) {
          void hydrateCartFromApi({ markInitialized: true })
        }
      }, 1400)

      return () => {
        cancelled = true
        window.clearTimeout(timeoutId)
      }
    }

    void hydrateCartFromApi({ markInitialized: true })
  }, [
    applyCartItemsSnapshot,
    authenticatedEntidadeId,
    hydrateCartFromApi,
    isCartAuthenticated,
    isLegacyCheckoutSessionBridge,
    isStandalonePriceTableView,
  ])

  const prefetchCartCalculations = useCallback((items: CartItem[]) => {
    // Os fluxos de inclusão/alteração chamam este helper somente depois de o
    // carrinho ser persistido. A primeira inclusão pode terminar antes da
    // hidratação inicial; nesse caso também devemos aquecer impostos e frete.
    if (!isCartAuthenticated || items.length === 0) return

    const currentItems = mergeCartItemsByProductId(items)
    void loadCartDrawer()
      .then(({ prefetchCartDrawerCalculations }) => prefetchCartDrawerCalculations({
        items: currentItems,
        pricingContext: pricingContextValue,
        freightPrefill: cartQuoteFreightPrefill ?? cartQuoteOrigin?.freightPrefill ?? null,
      }))
      .catch((error) => {
        console.warn('Failed to pre-calculate cart taxes and shipping:', error)
      })
  }, [
    cartQuoteFreightPrefill,
    cartQuoteOrigin?.freightPrefill,
    isCartAuthenticated,
    pricingContextValue,
  ])

  // Itens regulares não participam desta assinatura: seu preço vem do servidor.
  const cartApiPriceSignature = useMemo(
    () => computeCartApiPriceSignature(cartItems),
    [cartItems, computeCartApiPriceSignature]
  )

  useEffect(() => {
    if (!isCartAuthenticated || !hasHydratedCartOnce || cartItems.length === 0) return
    if (pricingContextValue.distributionEnabled && !pricingContextValue.distributionStatusResolved) return
    if (!cartApiPriceSignature || lastCartPriceSyncSignatureRef.current === cartApiPriceSignature) return

    lastCartPriceSyncSignatureRef.current = cartApiPriceSignature
    void runCartMutation(async () => {
      await syncCartItemsToApi(cartItems.map((item) => ({
        productId: item.product.id,
        quantity: item.quantity,
        product: item.product,
      })))
    })
  }, [
    cartApiPriceSignature,
    cartItems,
    hasHydratedCartOnce,
    isCartAuthenticated,
    pricingContextValue.distributionEnabled,
    pricingContextValue.distributionStatusResolved,
    runCartMutation,
    syncCartItemsToApi,
  ])

  const addItemsToCart = useCallback(async (
    entries: CartAdditionInput[],
    options?: {
      successTitle?: string
      successDescription?: string
      skipSuccessToast?: boolean
    }
  ) => {
    if (!Array.isArray(entries) || entries.length === 0) return false

    const trackSuccessfulCartAdditions = (nextItems: CartItem[]) => {
      entries.forEach(({ product, quantity, searchResultContext }) => {
        const analyticsProductId = normalizeAnalyticsProductId(product.id)
        queueStorefrontAnalyticsEvent({
          eventType: 'add_to_cart',
          pagePath: getStorefrontAnalyticsLocationKey(),
          produtoId: analyticsProductId,
          payload: withStorefrontSearchResultAttribution({
            product_name: product.name,
            product_brand: product.brand || undefined,
            product_category: product.category || undefined,
            origin: product.origem === 'marketplace_vitrine' ? 'marketplace_vitrine' : 'flexi',
            quantity,
            source_view: currentViewRef.current,
          }, searchResultContext),
        })
      })
      trackStorefrontCartState(nextItems, 'items_added')
    }

    const variationBlocked = entries.find(({ product }) => requiresProductVariationSelection(product))
    if (variationBlocked) {
      toast.info(t('toasts.select_variation_to_add'), {
        description: variationBlocked.product.name,
      })
      void handleProductClick(variationBlocked.product)
      return false
    }

    const unavailableBlocked = entries.find(({ product }) => isProductUnavailable(product, pricingContextValue.stockStatusMode))
    if (unavailableBlocked) {
      toast.warning('Produto indisponível', {
        description: unavailableBlocked.product.name,
      })
      return false
    }

    if (!isCartAuthenticated) {
      if (!isAnonymousQuickCheckoutAvailable) {
        redirectToLoginForCart()
        return false
      }

      let nextItems = quickCheckoutCartItemsRef.current
      entries.forEach(({ product, quantity }) => {
        nextItems = upsertQuickCheckoutCartItem(quickCheckoutLojaId, nextItems, product, quantity)
        recommendationTracking.trackInteraction(product.id, 'cart', product.category, product.brand)
      })
      setQuickCheckoutCartItems(nextItems)
      quickCheckoutCartItemsRef.current = nextItems
      trackSuccessfulCartAdditions(nextItems)
      if (!options?.skipSuccessToast) {
        toast.success(options?.successTitle ?? t('toasts.added_to_cart'), {
          description: options?.successDescription
            ?? (entries.length === 1 ? entries[0]?.product.name : `${entries.length} item(ns) adicionados`),
        })
      }
      return true
    }

    const currentItems = mergeCartItemsByProductId(cartItemsRef.current)
    const currentItemKeysByProductId = new Map(
      Array.from(cartItemKeysByProductIdRef.current.entries(), ([productId, itemKeys]) => [
        productId,
        [...itemKeys],
      ])
    )
    const currentQuantityByProductId = new Map<string, number>()
    currentItems.forEach((item) => {
      currentQuantityByProductId.set(
        item.product.id,
        (currentQuantityByProductId.get(item.product.id) ?? 0) + item.quantity
      )
    })
    const targetQuantities = new Map<string, number>()

    for (const entry of entries) {
      const currentQuantity = targetQuantities.get(entry.product.id)
        ?? currentQuantityByProductId.get(entry.product.id)
        ?? 0
      const minOrder = entry.product.minOrder ?? 1
      const normalizedQuantity = Math.max(minOrder, entry.quantity)
      targetQuantities.set(
        entry.product.id,
        Math.min(CART_ITEM_QUANTITY_MAX, currentQuantity + normalizedQuantity)
      )
      recommendationTracking.trackInteraction(entry.product.id, 'cart', entry.product.category, entry.product.brand)
    }

    const optimisticItems = [...currentItems]
    for (const entry of entries) {
      const targetQuantity = targetQuantities.get(entry.product.id)
      if (!targetQuantity) continue

      const existingIndex = optimisticItems.findIndex((item) => item.product.id === entry.product.id)
      if (existingIndex >= 0) {
        optimisticItems[existingIndex] = {
          ...optimisticItems[existingIndex],
          quantity: targetQuantity,
        }
        continue
      }

      optimisticItems.push({
        product: entry.product,
        quantity: targetQuantity,
      })
    }

    applyCartItemsSnapshot(optimisticItems)
    // O código do drawer pode ainda não ter sido baixado na primeira inclusão.
    // Começamos a carregá-lo enquanto o carrinho é salvo; a cotação fiscal e de
    // frete parte logo após a confirmação do servidor, com a composição correta.
    void loadCartDrawer()

    try {
      await runCartMutation(async () => {
        // syncCartItemsToApi já atualiza o mapa de item-keys a partir da resposta do
        // servidor, então NÃO refazemos o hydrate completo aqui (que dispararia um
        // GET getProductById por produto do carrinho a cada item adicionado).
        await syncCartItemsToApi(
          Array.from(targetQuantities.entries()).map(([productId, quantity]) => {
            const product = optimisticItems.find((item) => item.product.id === productId)?.product
            if (!product) {
              throw new Error(`Produto ${productId} ausente no carrinho otimista.`)
            }
            return { productId, quantity, product }
          })
        )
        // Já sincronizamos a composição atual: evita que o efeito de re-sync de
        // preço dispare uma segunda rodada de syncs por essa mudança.
        markCartPriceSyncBaseline()
      })
    } catch (error) {
      console.error('Failed to add items to cart:', error)
      applyCartItemsSnapshot(currentItems)
      cartItemKeysByProductIdRef.current = currentItemKeysByProductId
      markCartPriceSyncBaseline()
      void hydrateCartFromApi()
      toast.error('Não foi possível adicionar ao carrinho', {
        description: error instanceof Error && /sem estoque/i.test(error.message)
          ? 'Produto sem estoque disponível para esta loja.'
          : undefined,
      })
      return false
    }

    broadcastCartChange('items-upserted')
    prefetchCartCalculations(optimisticItems)
    trackSuccessfulCartAdditions(optimisticItems)

    if (options?.skipSuccessToast) return true

    toast.success(options?.successTitle ?? t('toasts.added_to_cart'), {
      description: options?.successDescription
        ?? (entries.length === 1 ? entries[0]?.product.name : `${entries.length} item(ns) adicionados`),
    })
    return true
  }, [
    handleProductClick,
    broadcastCartChange,
    hydrateCartFromApi,
    isAnonymousQuickCheckoutAvailable,
    isCartAuthenticated,
    quickCheckoutLojaId,
    recommendationTracking,
    redirectToLoginForCart,
    runCartMutation,
    markCartPriceSyncBaseline,
    prefetchCartCalculations,
    syncCartItemsToApi,
    t,
  ])

  const handleAddToCart = async (
    product: Product,
    quantity: number = 1,
    searchResultContext?: StorefrontSearchResultContext | null
  ) => {
    await addItemsToCart([{ product, quantity, searchResultContext }])
  }

  const showPriceTableMobileCartToast = () => {
    toast.success('Adicionado ao carrinho', {
      duration: 1600,
    })
  }

  const handlePriceTableAddToCart = async (product: Product, quantity: number = 1) => {
    if (isMobileViewport) {
      flushSync(() => {
        setCartDrawerSource('price-table')
        setShowCartDrawer(false)
        setShowPriceTableCartPanel(false)
        setIsPriceTableCartPanelCollapsed(true)
      })

      const didAdd = await addItemsToCart(
        [{ product, quantity }],
        { skipSuccessToast: true },
      )
      if (didAdd) {
        showPriceTableMobileCartToast()
      }
      return
    }

    flushSync(() => {
      setCartDrawerSource('price-table')
      setShowPriceTableCartPanel(true)
      setIsPriceTableCartPanelCollapsed(false)
      setShowCartDrawer(false)
    })
    void loadCartDrawer()

    const didAdd = await addItemsToCart([{ product, quantity }])
    if (!didAdd && cartItemsRef.current.length === 0 && !isMobileViewport) {
      setIsPriceTableCartPanelCollapsed(true)
    }
  }

  const handlePriceTableSetCartQuantity = async (product: Product, quantity: number = 0) => {
    const normalizedQuantity = Math.max(0, Math.min(CART_ITEM_QUANTITY_MAX, Math.trunc(quantity || 0)))
    const currentItem = cartItemsRef.current.find((item) => item.product.id === product.id)
    const currentQuantity = currentItem?.quantity ?? 0

    if (normalizedQuantity === currentQuantity) return

    flushSync(() => {
      setCartDrawerSource('price-table')
      if (isMobileViewport) {
        setShowCartDrawer(false)
        setShowPriceTableCartPanel(false)
        setIsPriceTableCartPanelCollapsed(true)
      } else {
        setShowPriceTableCartPanel(true)
        setIsPriceTableCartPanelCollapsed(false)
        setShowCartDrawer(false)
      }
    })
    if (!isMobileViewport) {
      void loadCartDrawer()
    }

    if (normalizedQuantity <= 0) {
      if (currentQuantity > 0) {
        await handleRemoveFromCart(product.id)
      }

      if (cartItemsRef.current.length === 0 && !isMobileViewport) {
        setIsPriceTableCartPanelCollapsed(true)
      }
      return
    }

    if (currentQuantity > 0) {
      await handleUpdateQuantity(product.id, normalizedQuantity)
      if (isMobileViewport && normalizedQuantity > currentQuantity) {
        showPriceTableMobileCartToast()
      }
      return
    }

    const didAdd = await addItemsToCart(
      [{ product, quantity: normalizedQuantity }],
      { skipSuccessToast: true }
    )

    if (!didAdd && cartItemsRef.current.length === 0 && !isMobileViewport) {
      setIsPriceTableCartPanelCollapsed(true)
    }
    if (didAdd && isMobileViewport) {
      showPriceTableMobileCartToast()
    }
  }

  const handleAddMultipleToCart = async (entries: CartAdditionInput[]) => {
    await addItemsToCart(entries, {
      successTitle: 'Itens adicionados ao carrinho',
      successDescription: `${entries.length} item(ns) enviados para o carrinho`,
    })
  }

  const resolveCheckoutCartProductForInput = useCallback(async ({
    productId,
    parentContextId,
    primaryProducts = [],
  }: {
    productId?: string | null
    parentContextId?: string | null
    primaryProducts?: Array<Product | null | undefined>
  }): Promise<Product | null> => {
    const routeProductValue = normalizeRouteProductValue(productId)

    const normalizedRouteIdentity = normalizeProductCheckoutIdentity(routeProductValue)
    const candidates: Product[] = []
    const seenCandidateIds = new Set<string>()

    const appendCandidate = (product?: Product | null) => {
      if (!product || seenCandidateIds.has(product.id)) return
      seenCandidateIds.add(product.id)
      candidates.push(product)
    }

    const appendMatchingLocalProducts = () => {
      primaryProducts.forEach(appendCandidate)

      if (!normalizedRouteIdentity) return

      allProducts.forEach((product) => {
        if (productMatchesProductCheckoutIdentity(product, normalizedRouteIdentity)) {
          appendCandidate(product)
        }
      })
    }

    const findDirectAddableCandidate = () => candidates.find((product) => (
      canUseProductForCheckoutLink(product)
      && (!normalizedRouteIdentity || productMatchesProductCheckoutIdentity(product, normalizedRouteIdentity))
    ))

    const resolveVariationFromProductContext = async (product: Product) => {
      const normalizedTargetIdentity = normalizeProductCheckoutIdentity(product.navigationTargetProductId)
      const parentIds = [
        product.navigationParentContextId,
        product.parentProductId,
        product.id,
        parentContextId,
      ]
        .map((value) => normalizeRouteProductValue(value))
        .filter((value): value is string => Boolean(value))

      const uniqueParentIds = Array.from(new Set(parentIds))

      for (const parentId of uniqueParentIds) {
        let variations: Product[] = []
        try {
          variations = await getProductVariations(parentId, { includeManuals: false })
        } catch (error) {
          console.warn(`Falha ao buscar variações do produto ${parentId} para checkout direto.`, error)
          continue
        }

        const matchedVariation = variations.find((variation) => (
          (
            normalizedTargetIdentity
            && productMatchesProductCheckoutIdentity(variation, normalizedTargetIdentity)
          )
          || (
            normalizedRouteIdentity
            && productMatchesProductCheckoutIdentity(variation, normalizedRouteIdentity)
          )
        ))

        if (matchedVariation && canUseProductForCheckoutLink(matchedVariation)) {
          return matchedVariation
        }

        const addableVariations = variations.filter(canUseProductForCheckoutLink)
        if (addableVariations.length === 1) {
          return addableVariations[0]
        }
      }

      return null
    }

    appendMatchingLocalProducts()

    const directCandidate = findDirectAddableCandidate()
    if (directCandidate) return directCandidate

    for (const candidate of candidates) {
      const variationCandidate = await resolveVariationFromProductContext(candidate)
      if (variationCandidate) return variationCandidate
    }

    if (!routeProductValue) return null

    try {
      const searchedProducts = await searchProducts(routeProductValue, {
        limit: 20,
        onlyAvailable: false,
      })
      const expandedSearchedProducts = await expandProductsWithParentContexts(routeProductValue, searchedProducts)
      expandedSearchedProducts.forEach(appendCandidate)

      const searchedDirectCandidate = findDirectAddableCandidate()
      if (searchedDirectCandidate) return searchedDirectCandidate

      for (const candidate of expandedSearchedProducts) {
        const variationCandidate = await resolveVariationFromProductContext(candidate)
        if (variationCandidate) return variationCandidate
      }
    } catch (error) {
      console.warn(`Falha ao buscar produto ${routeProductValue} para checkout direto.`, error)
    }

    try {
      const detailedProduct = await getProductById(routeProductValue)
      appendCandidate(detailedProduct)

      if (
        canUseProductForCheckoutLink(detailedProduct)
        && (!normalizedRouteIdentity || productMatchesProductCheckoutIdentity(detailedProduct, normalizedRouteIdentity))
      ) {
        return detailedProduct
      }

      return resolveVariationFromProductContext(detailedProduct)
    } catch (error) {
      console.warn(`Falha ao carregar produto ${routeProductValue} para checkout direto.`, error)
    }

    return null
  }, [allProducts])

  const resolveProductCheckoutCartProduct = useCallback(async (): Promise<Product | null> => {
    const routeProductValue = normalizeRouteProductValue(routeProductId)
      ?? normalizeRouteProductValue(displayedProductDetail?.navigationTargetProductId)
      ?? normalizeRouteProductValue(selectedProduct?.navigationTargetProductId)
      ?? normalizeRouteProductValue(selectedProduct?.id)

    return resolveCheckoutCartProductForInput({
      productId: routeProductValue,
      parentContextId: routeProductParentContextId,
      primaryProducts: [selectedProduct, displayedProductDetail],
    })
  }, [
    displayedProductDetail,
    resolveCheckoutCartProductForInput,
    routeProductId,
    routeProductParentContextId,
    selectedProduct,
  ])

  const replaceQuickCheckoutCartWithProduct = useCallback((
    product: Product,
    quantity = 1,
    options?: { persistDirectCheckoutFallback?: boolean },
  ) => {
    if (requiresProductVariationSelection(product)) {
      toast.info(t('toasts.select_variation_to_add'), {
        description: product.name,
      })
      void handleProductClick(product)
      return false
    }

    if (isProductUnavailable(product, pricingContextValue.stockStatusMode)) {
      toast.warning('Produto indisponível', {
        description: product.name,
      })
      return false
    }

    const normalizedQuantity = Math.min(
      CART_ITEM_QUANTITY_MAX,
      Math.max(1, Math.trunc(Number(quantity) || 1))
    )
    const nextItems = upsertQuickCheckoutCartItem(quickCheckoutLojaId, [], product, normalizedQuantity)
    if (options?.persistDirectCheckoutFallback) {
      saveQuickCheckoutCartItems(null, nextItems)
      if (quickCheckoutLojaId) {
        saveQuickCheckoutCartItems(quickCheckoutLojaId, nextItems)
      }
    }
    setQuickCheckoutCartItems(nextItems)
    quickCheckoutCartItemsRef.current = nextItems
    trackStorefrontCartState(nextItems, 'direct_checkout_product')
    recommendationTracking.trackInteraction(product.id, 'cart', product.category, product.brand)
    return true
  }, [
    handleProductClick,
    pricingContextValue.stockStatusMode,
    quickCheckoutLojaId,
    recommendationTracking,
    t,
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (currentView !== 'quick-checkout') return
    if (preserveAffiliateCheckoutSearch(window.location.search)) return

    const storedQuickCheckoutRequest = readProductCheckoutQuickRequest()
    const quickCheckoutProductId = normalizeRouteProductValue(routeQuickCheckoutProductId)
      ?? storedQuickCheckoutRequest?.productIds[0]
    if (!quickCheckoutProductId) return

    const quantity = Math.min(
      CART_ITEM_QUANTITY_MAX,
      Math.max(1, Math.trunc(Number(routeQuickCheckoutQuantity ?? storedQuickCheckoutRequest?.quantity) || 1))
    )
    const quickCheckoutParentContextId = routeQuickCheckoutProductParentContextId
      ?? resolveProductParentContextId(
        quickCheckoutProductId,
        storedQuickCheckoutRequest?.parentContextId ?? null
      )
    const quickCheckoutProductIds = (() => {
      if (typeof window === 'undefined') return [quickCheckoutProductId]

      const ids = resolveQuickCheckoutProductIdsFromSearch(new URLSearchParams(window.location.search))
      storedQuickCheckoutRequest?.productIds.forEach((storedProductId) => {
        if (!ids.includes(storedProductId)) {
          ids.push(storedProductId)
        }
      })
      if (!ids.includes(quickCheckoutProductId)) {
        ids.unshift(quickCheckoutProductId)
      }
      return ids
    })()
    const processKey = [
      'direct-quick-checkout',
      quickCheckoutProductIds.join(','),
      quickCheckoutParentContextId ?? '',
      quantity,
    ].join('|')

    if (productCheckoutLinkProcessingRef.current === processKey) return
    productCheckoutLinkProcessingRef.current = processKey
    updateProductCheckoutQuickFlow(true)

    let cancelled = false

    void (async () => {
      let checkoutProduct: Product | null = null
      for (const candidateProductId of quickCheckoutProductIds) {
        checkoutProduct = await resolveCheckoutCartProductForInput({
          productId: candidateProductId,
          parentContextId: quickCheckoutParentContextId,
        })

        if (checkoutProduct) break
      }
      if (cancelled) return

      if (!checkoutProduct) {
        updateProductCheckoutQuickFlow(false)
        clearProductCheckoutQuickRequest()
        setRouteQuickCheckoutProductId(null)
        setRouteQuickCheckoutProductParentContextId(null)
        setRouteQuickCheckoutQuantity(null)
        productCheckoutLinkProcessingRef.current = null
        toast.error('Não foi possível abrir o checkout do produto', {
          description: 'Não encontramos uma variação comprável para o código informado.',
        })
        return
      }

      const didAdd = replaceQuickCheckoutCartWithProduct(checkoutProduct, quantity, {
        persistDirectCheckoutFallback: true,
      })
      if (cancelled) return

      if (!didAdd) {
        updateProductCheckoutQuickFlow(false)
        clearProductCheckoutQuickRequest()
        setRouteQuickCheckoutProductId(null)
        setRouteQuickCheckoutProductParentContextId(null)
        setRouteQuickCheckoutQuantity(null)
        productCheckoutLinkProcessingRef.current = null
        return
      }

      clearProductCheckoutQuickRequest()
      setRouteQuickCheckoutProductId(null)
      setRouteQuickCheckoutProductParentContextId(null)
      setRouteQuickCheckoutQuantity(null)

      if (normalizePathname(window.location.pathname) === VIEW_PATHS['quick-checkout'] && window.location.search) {
        window.history.replaceState(window.history.state, '', VIEW_PATHS['quick-checkout'])
      }

      toast.success('Produto adicionado ao checkout', {
        description: `${quantity} unidade(s) pronta(s) para finalizar.`,
      })
    })()

    return () => {
      cancelled = true
    }
  }, [
    currentView,
    replaceQuickCheckoutCartWithProduct,
    resolveCheckoutCartProductForInput,
    routeQuickCheckoutProductId,
    routeQuickCheckoutProductParentContextId,
    routeQuickCheckoutQuantity,
    updateProductCheckoutQuickFlow,
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (currentView !== 'product-detail' || !selectedProduct) return

    const checkoutRequestKey = pendingProductCheckoutRequestKey
      ?? (
        shouldOpenProductCheckoutFromSearch(window.location.search)
          ? buildProductCheckoutRequestKey(window.location.pathname, window.location.search)
          : null
      )
    if (!checkoutRequestKey) return

    const processKey = [
      checkoutRequestKey,
      'quick-checkout',
      routeProductId ?? selectedProduct.id,
      routeProductParentContextId ?? '',
      selectedProduct.id,
      displayedProductDetail?.id ?? '',
    ].join('|')

    if (productCheckoutLinkProcessingRef.current === processKey) return
    productCheckoutLinkProcessingRef.current = processKey
    updateProductCheckoutQuickFlow(true)

    let cancelled = false

    void (async () => {
      const checkoutProduct = await resolveProductCheckoutCartProduct()
      if (cancelled) return

      if (!checkoutProduct) {
        setPendingProductCheckoutRequestKey(null)
        updateProductCheckoutQuickFlow(false)
        productCheckoutLinkProcessingRef.current = null
        toast.error('Não foi possível abrir o checkout do produto', {
          description: 'Não encontramos uma variação comprável para o código informado.',
        })
        return
      }

      const didAdd = replaceQuickCheckoutCartWithProduct(checkoutProduct)

      if (cancelled) return

      if (!didAdd) {
        setPendingProductCheckoutRequestKey(null)
        updateProductCheckoutQuickFlow(false)
        productCheckoutLinkProcessingRef.current = null
        return
      }

      setPendingProductCheckoutRequestKey(null)
      toast.success('Produto adicionado ao carrinho', {
        description: 'Abrindo o checkout.',
      })
      navigateToView('quick-checkout')
    })()

    return () => {
      cancelled = true
    }
  }, [
    currentView,
    displayedProductDetail,
    navigateToView,
    pendingProductCheckoutRequestKey,
    replaceQuickCheckoutCartWithProduct,
    resolveProductCheckoutCartProduct,
    routeProductParentContextId,
    routeProductId,
    selectedProduct,
    updateProductCheckoutQuickFlow,
  ])

  useEffect(() => {
    if (!isProductCheckoutQuickFlowActive) return
    if (currentView === 'quick-checkout' || currentView === 'product-detail') return
    if (pendingProductCheckoutRequestKey) return

    updateProductCheckoutQuickFlow(false)
  }, [
    currentView,
    isProductCheckoutQuickFlowActive,
    pendingProductCheckoutRequestKey,
    updateProductCheckoutQuickFlow,
  ])

  const flushPendingCartSync = useCallback(async () => {
    if (cartSyncDebounceTimerRef.current !== null) {
      window.clearTimeout(cartSyncDebounceTimerRef.current)
      cartSyncDebounceTimerRef.current = null
    }

    if (pendingCartSyncRef.current.size === 0) {
      // Pode haver uma atualização que o debounce já retirou da fila, mas cuja
      // gravação ainda está em execução. A simulação deve aguardar essa mutação
      // antes de consultar o carrinho no servidor.
      await cartMutationQueueRef.current
      return
    }

    const pending = Array.from(pendingCartSyncRef.current.entries())
    pendingCartSyncRef.current = new Map()

    await runCartMutation(async () => {
      await Promise.all(
        pending.map(([productId, quantity]) => {
          const product = cartItemsRef.current.find((item) => item.product.id === productId)?.product
          return syncCartItemToApi(productId, quantity, product)
        })
      )
    })
    broadcastCartChange('quantity-updated')
    prefetchCartCalculations(cartItemsRef.current)
    trackStorefrontCartState(cartItemsRef.current, 'quantity_updated')
  }, [broadcastCartChange, prefetchCartCalculations, runCartMutation, syncCartItemToApi])

  const scheduleCartSyncFlush = useCallback(() => {
    if (cartSyncDebounceTimerRef.current !== null) {
      window.clearTimeout(cartSyncDebounceTimerRef.current)
    }
    cartSyncDebounceTimerRef.current = window.setTimeout(() => {
      cartSyncDebounceTimerRef.current = null
      void flushPendingCartSync()
    }, CART_SYNC_DEBOUNCE_MS)
  }, [flushPendingCartSync])

  const backgroundCartCalculationSignature = useMemo(
    () => buildCartQuoteSnapshotSignature(cartItems),
    [cartItems]
  )

  useEffect(() => {
    if (
      !isCartAuthenticated
      || !hasHydratedCartOnce
      || !backgroundCartCalculationSignature
      || showCartDrawer
      || showPriceTableCartPanel
    ) return

    let cancelled = false
    const timerId = window.setTimeout(() => {
      void flushPendingCartSync()
        .then(() => cartMutationQueueRef.current)
        .then(async () => {
          if (cancelled) return
          prefetchCartCalculations(cartItemsRef.current)
        })
        .catch((error) => {
          if (!cancelled) {
            console.warn('Failed to pre-calculate cart taxes and shipping:', error)
          }
        })
    }, CART_SYNC_DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timerId)
    }
  }, [
    backgroundCartCalculationSignature,
    flushPendingCartSync,
    hasHydratedCartOnce,
    isCartAuthenticated,
    prefetchCartCalculations,
    showCartDrawer,
    showPriceTableCartPanel,
  ])

  const syncCartForQuoteSnapshot = useCallback(async (items: CartItem[]) => {
    await flushPendingCartSync()

    const currentItems = items.length > 0 ? items : cartItemsRef.current
    if (currentItems.length === 0) return

    await runCartMutation(async () => {
      await syncCartItemsToApi(currentItems.map((item) => ({
        productId: item.product.id,
        quantity: item.quantity,
        product: item.product,
      })))
      markCartPriceSyncBaseline()
    })
  }, [flushPendingCartSync, markCartPriceSyncBaseline, runCartMutation, syncCartItemsToApi])

  useEffect(() => {
    if (!isCartAuthenticated || isLegacyCheckoutSessionBridge) return
    if (typeof authenticatedEntidadeId !== 'number' || authenticatedEntidadeId <= 0) return
    if (typeof window === 'undefined') return

    const currentEntidadeId = Math.trunc(authenticatedEntidadeId)
    const handleCartSyncMessage = (value: unknown) => {
      const message = parseCartSyncMessage(value)
      if (!message) return
      if (message.entidadeId !== currentEntidadeId) return
      if (message.sourceId === cartSyncSourceIdRef.current) return

      const messageKey = `${message.sourceId}:${message.updatedAt}:${message.reason}`
      if (lastProcessedCartSyncMessageRef.current === messageKey) return
      lastProcessedCartSyncMessageRef.current = messageKey

      void flushPendingCartSync()
        .catch(() => undefined)
        .then(() => hydrateCartFromApi())
    }

    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(CART_SYNC_BROADCAST_CHANNEL)
      channel.addEventListener('message', (event) => handleCartSyncMessage(event.data))
    } catch {
      channel = null
    }

    return () => {
      channel?.close()
    }
  }, [
    authenticatedEntidadeId,
    flushPendingCartSync,
    hydrateCartFromApi,
    isCartAuthenticated,
    isLegacyCheckoutSessionBridge,
  ])

  useEffect(() => {
    if (!isCartAuthenticated || isLegacyCheckoutSessionBridge) return
    if (typeof authenticatedEntidadeId !== 'number' || authenticatedEntidadeId <= 0) return
    if (typeof window === 'undefined') return

    const refreshCartFromServer = () => {
      const now = Date.now()
      if (now - lastExternalCartRefreshAtRef.current < CART_EXTERNAL_REFRESH_THROTTLE_MS) return
      lastExternalCartRefreshAtRef.current = now

      void flushPendingCartSync()
        .catch(() => undefined)
        .then(() => hydrateCartFromApi())
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCartFromServer()
      }
    }

    window.addEventListener('focus', refreshCartFromServer)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', refreshCartFromServer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    authenticatedEntidadeId,
    flushPendingCartSync,
    hydrateCartFromApi,
    isCartAuthenticated,
    isLegacyCheckoutSessionBridge,
  ])

  useEffect(() => {
    if (!showCartDrawer || !isCartAuthenticated || isLegacyCheckoutSessionBridge) return

    const hasCurrentQuoteSnapshot = Boolean(
      cartQuoteOrigin?.cartSnapshotSignature
      && (
        cartQuoteOrigin.cartSnapshotSignature === buildCartQuoteSnapshotSignature(cartItemsRef.current)
        || doesCartMatchQuoteFinancialSummary(
          cartItemsRef.current,
          cartQuoteOrigin.financialSummary,
        )
      )
    )
    if (hasCurrentQuoteSnapshot) return

    void flushPendingCartSync()
      .catch(() => undefined)
      .then(() => cartMutationQueueRef.current)
      .then(() => hydrateCartFromApi())
  }, [
    flushPendingCartSync,
    hydrateCartFromApi,
    isCartAuthenticated,
    isLegacyCheckoutSessionBridge,
    cartQuoteOrigin?.cartSnapshotSignature,
    showCartDrawer,
  ])

  const handleUpdateQuantity = async (productId: string, quantity: number) => {
    if (!isCartAuthenticated) {
      if (isAnonymousQuickCheckoutAvailable) {
        const nextItems = setQuickCheckoutCartItemQuantity(
          quickCheckoutLojaId,
          quickCheckoutCartItemsRef.current,
          productId,
          quantity
        )
        setQuickCheckoutCartItems(nextItems)
        quickCheckoutCartItemsRef.current = nextItems
        trackStorefrontCartState(nextItems, 'quantity_updated')
        return
      }
      redirectToLoginForCart()
      return
    }

    const currentItem = cartItemsRef.current.find((item) => item.product.id === productId)
    if (!currentItem) return

    const normalizedQuantity = Math.max(
      currentItem.product.minOrder ?? 1,
      Math.min(CART_ITEM_QUANTITY_MAX, quantity)
    )

    // Atualiza a UI imediatamente (otimista) e coalesce o sync com a API num
    // único request por produto após o usuário parar de clicar (debounce).
    applyCartItemsSnapshot(
      cartItemsRef.current.map((item) =>
        item.product.id === productId
          ? { ...item, quantity: normalizedQuantity }
          : item
      )
    )

    pendingCartSyncRef.current.set(productId, normalizedQuantity)
    scheduleCartSyncFlush()
  }

  const handleQuickCheckoutUpdateQuantity = (productId: string, quantity: number) => {
    const nextItems = setQuickCheckoutCartItemQuantity(
      quickCheckoutLojaId,
      quickCheckoutCartItemsRef.current,
      productId,
      quantity
    )
    setQuickCheckoutCartItems(nextItems)
    quickCheckoutCartItemsRef.current = nextItems
    trackStorefrontCartState(nextItems, 'quantity_updated')
  }

  const handleRemoveFromCart = async (productId: string) => {
    if (!isCartAuthenticated) {
      if (isAnonymousQuickCheckoutAvailable) {
        const nextItems = removeQuickCheckoutCartItem(
          quickCheckoutLojaId,
          quickCheckoutCartItemsRef.current,
          productId
        )
        setQuickCheckoutCartItems(nextItems)
        quickCheckoutCartItemsRef.current = nextItems
        trackStorefrontCartState(nextItems, 'item_removed')
        toast.info(t('toasts.removed_from_cart'))
        return
      }
      redirectToLoginForCart()
      return
    }

    // Cancela qualquer sync de quantidade pendente para este produto para que
    // ele não ressuscite o item recém-removido.
    pendingCartSyncRef.current.delete(productId)

    await runCartMutation(async () => {
      const removedProduct = cartItemsRef.current.find((item) => item.product.id === productId)?.product
      applyCartItemsSnapshot(
        cartItemsRef.current.filter((item) => item.product.id !== productId)
      )
      await syncCartItemToApi(productId, 0, removedProduct)
      // Composição já sincronizada: evita re-sync de todos pelo efeito de preço.
      markCartPriceSyncBaseline()
    })
    broadcastCartChange('item-removed')
    prefetchCartCalculations(cartItemsRef.current)
    trackStorefrontCartState(cartItemsRef.current, 'item_removed')
    toast.info(t('toasts.removed_from_cart'))
  }

  const handleOpenSaveQuoteDialog = useCallback((
    freightSelection: CartFreightSelection | null = null,
    financialSummary: CheckoutSimulationResult | null = null
  ) => {
    setCartQuoteFreightSelection(freightSelection)
    setCartQuoteFinancialSummary(financialSummary)
    if (cartQuoteOrigin) {
      setShowSaveQuoteDestinationDialog(true)
      return
    }
    setShowSaveQuoteDialog(true)
  }, [cartQuoteOrigin])

  const handleChooseNewQuote = useCallback(() => {
    setShowSaveQuoteDestinationDialog(false)
    setShowSaveQuoteDialog(true)
  }, [])

  const handleUpdateOriginQuote = async () => {
    if (!cartQuoteOrigin || isSavingQuote) return

    if (!isCartAuthenticated) {
      redirectToLoginForCart()
      return
    }

    const items = visibleCartItems
    if (items.length === 0) {
      toast.error(t('toasts.cart_empty'))
      return
    }

    try {
      setIsSavingQuote(true)
      await syncCartForQuoteSnapshot(items)
      // A substituição usa exclusivamente a composição já sincronizada do carrinho.
      // Uma cotação de frete pertence à composição anterior e pode ficar inválida
      // quando itens ou quantidades mudam; o orçamento recalcula o frete depois.
      const response = await replaceOrcamento(cartQuoteOrigin.quoteId)
      const updatedQuote = cartQuoteOrigin
      await clearCartContents({ skipSuccessToast: true })
      setCartQuoteFreightSelection(null)
      setCartQuoteFinancialSummary(null)
      setShowSaveQuoteDestinationDialog(false)
      setCartQuoteOrigin(null)
      const navigateToUpdatedQuote = updatedQuote.context === 'business'
        ? navigateToBusinessQuoteDetails
        : navigateToQuoteDetails
      navigateToUpdatedQuote({
        quoteId: updatedQuote.quoteId,
        quoteToken: buildQuoteDetailToken(updatedQuote.quoteId),
      })
      toast.success(
        response?.message || `Orçamento #${updatedQuote.quoteNumber} atualizado com os itens do carrinho.`
      )
    } catch (error) {
      console.error('Failed to update origin quote:', error)
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Não foi possível atualizar o orçamento atual.'
      )
    } finally {
      setIsSavingQuote(false)
    }
  }

  const handleSaveQuote = async () => {
    if (!isCartAuthenticated) {
      redirectToLoginForCart()
      return
    }
    if (isSavingQuote) return

    if (!quoteName.trim()) {
      toast.error(t('toasts.quote_name_required'))
      return
    }
    const items = visibleCartItems
    if (items.length === 0) {
      toast.error(t('toasts.cart_empty'))
      return
    }

    try {
      setIsSavingQuote(true)
      await syncCartForQuoteSnapshot(items)
      const selectedFreightPayload = buildCartFreightQuotePayload(cartQuoteFreightSelection)
      const response = await salvarOrcamento({
        nome: quoteName.trim(),
        ...(selectedFreightPayload ? { frete_selecionado: selectedFreightPayload } : {}),
        ...(cartQuoteFinancialSummary ? { resumo_financeiro: cartQuoteFinancialSummary } : {}),
      })
      await clearCartContents({ skipSuccessToast: true })
      setQuoteName('')
      setCartQuoteFreightSelection(null)
      setCartQuoteFinancialSummary(null)
      setShowSaveQuoteDialog(false)
      setCartQuoteOrigin(null)
      navigateToView('quotes')
      toast.success(response?.message || t('toasts.quote_saved'))
    } catch (error) {
      console.error('Failed to save quote:', error)
      toast.error(t('account.quotes.load_error'))
    } finally {
      setIsSavingQuote(false)
    }
  }

  const handleSaveCurrentCartAsQuoteBeforeQuoteSend = useCallback(async (suggestedName: string) => {
    if (!isCartAuthenticated) {
      redirectToLoginForCart()
      throw new Error('Faça login para salvar o carrinho como orçamento.')
    }

    if (isSavingQuote) {
      throw new Error('Já existe um salvamento de orçamento em andamento.')
    }

    const items = visibleCartItems
    if (items.length === 0) {
      throw new Error('O carrinho está vazio.')
    }

    const normalizedName = suggestedName.trim() || `Carrinho salvo em ${new Date().toLocaleString('pt-BR')}`

    try {
      setIsSavingQuote(true)
      await syncCartForQuoteSnapshot(items)
      const response = await salvarOrcamento({
        nome: normalizedName,
      })
      toast.success(response?.message || 'Carrinho salvo como orçamento.')
    } catch (error) {
      console.error('Failed to save current cart before sending quote:', error)
      throw (
        error instanceof Error
          ? error
          : new Error('Não foi possível salvar o carrinho atual como orçamento.')
      )
    } finally {
      setIsSavingQuote(false)
    }
  }, [isCartAuthenticated, isSavingQuote, redirectToLoginForCart, syncCartForQuoteSnapshot, visibleCartItems])

  const handleSearchChange = async (
    query: string,
    options?: {
      scrollToTop?: boolean
      manufacturerBrands?: ManufacturerBrandFilterState[] | null
      vehicleFilter?: VehicleFilter | null
      vehicleSearchMode?: VehicleSearchRouteMode | null
      onlyAvailable?: boolean
    }
  ) => {
    if (isBackendUnavailable) return

    const normalizedQuery = query.trim()
    const searchSourceView = currentViewRef.current
    const shouldScrollToTop = options?.scrollToTop ?? true
    const hasTextSearch = normalizedQuery.length >= 2
    const textSearchAnalyticsContext: StorefrontSearchAnalyticsContext | null = hasTextSearch
      ? {
          searchId: createStorefrontSearchId(),
          searchMode: 'text',
          searchTerm: normalizedQuery,
          sourceView: searchSourceView,
          resultPageByProductId: new Map(),
        }
      : null
    invalidatePendingProductDetailRequests()
    setRestoreProductsViewStateKey(null)
    resetServerPaginatedProducts()
    setImageSearchState(null)
    setActiveSearchAnalyticsContext(textSearchAnalyticsContext)
    setIsImageSearchLoading(false)
    setSearchQuery(normalizedQuery)
    const nextManufacturerBrands = options && 'manufacturerBrands' in options
      ? normalizeManufacturerBrandFilters(options.manufacturerBrands)
      : selectedManufacturerBrands
    const nextVehicleFilter = options && 'vehicleFilter' in options
      ? normalizeVehicleFilter(options.vehicleFilter)
      : (
          normalizedQuery && normalizedQuery === searchQuery.trim()
            ? activeVehicleFilter
            : null
        )
    const nextVehicleSearchMode = options && 'vehicleSearchMode' in options
      ? (options.vehicleSearchMode ?? null)
      : (
          nextVehicleFilter
            ? (
                normalizedQuery && normalizedQuery === searchQuery.trim()
                  ? (vehicleSearchModeFromRoute ?? (nextVehicleFilter.placa ? 'plate' : 'manual'))
                  : (nextVehicleFilter.placa ? 'plate' : 'manual')
              )
            : null
        )
    const nextOnlyAvailable = options && 'onlyAvailable' in options
      ? Boolean(options.onlyAvailable)
      : onlyAvailableFilter
    const nextManufacturerBrandIds = getManufacturerBrandFilterIds(nextManufacturerBrands)
    const hasManufacturerBrandSearch = nextManufacturerBrandIds.length > 0
    const hasCatalogBrandOnlySearch = hasManufacturerBrandSearch && !hasTextSearch && !nextVehicleFilter
    const nextProductsDataKey = hasTextSearch
      ? buildSearchProductsDataKey(normalizedQuery, nextManufacturerBrands, nextVehicleFilter, nextOnlyAvailable)
      : buildCatalogProductsDataKey(nextManufacturerBrands, nextOnlyAvailable)
    const trackTextSearchResults = (resultsCount: number, resultProducts: Product[]) => {
      if (!textSearchAnalyticsContext) return
      queueStorefrontAnalyticsEvent({
        eventType: 'search',
        pageKind: 'search',
        pageKey: `search:${normalizedQuery}`,
        pagePath: `${VIEW_PATHS.products}?q=${encodeURIComponent(normalizedQuery)}`,
        payload: withStorefrontGranularAnalyticsPayload({
          search_term: normalizedQuery,
          results_count: resultsCount,
          manufacturer_brand_ids: nextManufacturerBrandIds.length > 0 ? nextManufacturerBrandIds : undefined,
          manufacturer_brand_names: nextManufacturerBrands
            .map((brand) => brand.name.trim())
            .filter(Boolean),
          vehicle_filter: nextVehicleFilter
            ? {
                has_plate: Boolean(nextVehicleFilter.placa),
                marca: nextVehicleFilter.marca || undefined,
                modelo: nextVehicleFilter.modelo || undefined,
                ano: nextVehicleFilter.ano || undefined,
              }
            : undefined,
          source_view: searchSourceView,
        }, {
          search_id: textSearchAnalyticsContext.searchId,
          search_mode: textSearchAnalyticsContext.searchMode,
          search_status: 'success',
          page: 1,
          page_results_count: resultProducts.length,
          result_products: buildStorefrontSearchResultProducts(resultProducts),
        }),
      })
    }
    setSelectedManufacturerBrands(nextManufacturerBrands)
    setOnlyAvailableFilter(nextOnlyAvailable)
    setActiveVehicleFilter(nextVehicleFilter)
    setVehicleSearchModeFromRoute(nextVehicleSearchMode)

    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }
    inFlightSearchProductsDataKeyRef.current = null

    if (hasTextSearch || hasManufacturerBrandSearch) {
      inFlightSearchProductsDataKeyRef.current = nextProductsDataKey
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams()
        if (hasTextSearch && textSearchAnalyticsContext) {
          params.set('q', normalizedQuery)
        }
        if (nextOnlyAvailable) {
          params.set('somente_disponiveis', '1')
        }
        if (nextVehicleSearchMode) {
          params.set('busca_veiculo', nextVehicleSearchMode === 'plate' ? 'placa' : 'manual')
        }
        appendVehicleFilterToQueryParams(params, nextVehicleFilter)
        const serializedManufacturerBrandIds = serializeManufacturerBrandIdsForQuery(nextManufacturerBrands)
        if (serializedManufacturerBrandIds) {
          params.set('marca_fabricante_id', serializedManufacturerBrandIds)
          const serializedManufacturerBrandNames = serializeManufacturerBrandNamesForQuery(nextManufacturerBrands)
          if (serializedManufacturerBrandNames) {
            params.set('marca_fabricante_nome', serializedManufacturerBrandNames)
          }
        }
        const nextUrl = `${VIEW_PATHS.products}?${params.toString()}`
        const currentUrl = `${normalizePathname(window.location.pathname)}${window.location.search}`
        if (currentUrl !== nextUrl) {
          window.history.pushState({}, '', nextUrl)
        }
      }

      if (shouldScrollToTop && typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        })
      }

      setSelectedCategory(undefined)
      clearCategoryBreadcrumb()
      setCategoryRouteSlugs([])
      setIsSearchResultsActive(hasTextSearch)
      currentViewRef.current = 'products'
      runViewTransition(() => {
        setCurrentView('products')
      })
      const requestId = ++searchRequestIdRef.current
      const controller = new AbortController()
      searchAbortRef.current = controller
      try {
        setIsLoadingProducts(true)

        if (hasCatalogBrandOnlySearch) {
          const page = await fetchManufacturerBrandProductsChunk(nextManufacturerBrandIds, 0, controller.signal, nextOnlyAvailable)
          if (requestId !== searchRequestIdRef.current) return
          serverPaginatedProductFeedRef.current = {
            kind: 'catalog_brand',
            dataKey: nextProductsDataKey,
            manufacturerBrandIds: nextManufacturerBrandIds,
          }
          productsOffsetRef.current = page.nextOffset
          setHasMoreProducts(page.hasMore)
          setServerPaginatedProductsTotal(null)
          setAllProducts(page.items)
          setProductsDataKey(nextProductsDataKey)
          return
        }

        if (hasTextSearch && textSearchAnalyticsContext) {
          const page = await searchFilteredProductsPage(normalizedQuery, {
            limit: TEXT_SEARCH_PRODUCTS_PAGE_SIZE,
            offset: 0,
            marca_fabricante_ids: nextManufacturerBrandIds.length > 0
              ? nextManufacturerBrandIds
              : undefined,
            onlyAvailable: nextOnlyAvailable,
            vehicleFilter: nextVehicleFilter,
            pricingMode: 'cached',
            signal: controller.signal,
          })
          const results = await expandProductsWithParentContexts(normalizedQuery, page.items, {
            signal: controller.signal,
          })
          if (requestId !== searchRequestIdRef.current) return
          const displayedResults = resolveDisplayedSearchProductsForAnalytics(results)
          registerStorefrontSearchResultPage(textSearchAnalyticsContext, displayedResults, 1)
          serverPaginatedProductFeedRef.current = {
            kind: 'search',
            dataKey: nextProductsDataKey,
            query: normalizedQuery,
            manufacturerBrandIds: nextManufacturerBrandIds,
            onlyAvailable: nextOnlyAvailable,
            vehicleFilter: nextVehicleFilter,
            analyticsContext: textSearchAnalyticsContext,
          }
          setAllProducts(results)
          productsOffsetRef.current = page.nextOffset
          setHasMoreProducts(page.hasMore)
          setServerPaginatedProductsTotal(nextVehicleFilter ? null : page.total)
          setProductsDataKey(nextProductsDataKey)
          trackTextSearchResults(nextVehicleFilter ? results.length : (page.total ?? results.length), displayedResults)

          if (requestId === searchRequestIdRef.current) {
            setIsLoadingProducts(false)
          }

          const shouldLoadMarketplaceResults = (
            isMarketplaceVitrinePublicSearchEnabled()
            && nextManufacturerBrandIds.length === 0
            && !nextVehicleFilter
          )
          if (!shouldLoadMarketplaceResults) return

          try {
            const marketplacePage = await searchMarketplaceVitrineProductsPage(normalizedQuery, {
              limit: 60,
              offset: 0,
              onlyAvailable: nextOnlyAvailable,
              signal: controller.signal,
            })
            controller.signal.throwIfAborted()
            if (requestId !== searchRequestIdRef.current) return

            const mergedMarketplaceResults = appendSearchProductsWithMarketplaceLast(results, marketplacePage.items)
            const displayedMarketplaceResults = resolveDisplayedSearchProductsForAnalytics(mergedMarketplaceResults)
            const marketplaceIds = new Set(marketplacePage.items.map((product) => product.id))
            const displayedMarketplaceItems = displayedMarketplaceResults.filter((product) => marketplaceIds.has(product.id))
            const marketplacePositionByProductId = new Map(
              displayedMarketplaceResults.map((product, index) => [product.id, index + 1]),
            )
            registerStorefrontSearchResultPage(textSearchAnalyticsContext, displayedMarketplaceItems, 1)
            marketplaceSearchTotalRef.current = marketplacePage.total
            setAllProducts(mergedMarketplaceResults)
            setServerPaginatedProductsTotal(
              page.total === null
                ? null
                : page.total + marketplacePage.total
            )
            queueStorefrontConsentGrantedAnalyticsEvent({
              eventType: 'search',
              pageKind: 'search',
              pageKey: `search:${normalizedQuery}`,
              pagePath: `${VIEW_PATHS.products}?q=${encodeURIComponent(normalizedQuery)}`,
              payload: {
                search_id: textSearchAnalyticsContext.searchId,
                search_term: normalizedQuery,
                search_mode: textSearchAnalyticsContext.searchMode,
                search_status: 'success',
                page: 1,
                page_results_count: displayedMarketplaceItems.length,
                result_batch_origin: 'marketplace_vitrine',
                result_products: buildStorefrontSearchResultProducts(displayedMarketplaceItems, {
                  positionByProductId: marketplacePositionByProductId,
                }),
                source_view: searchSourceView,
              },
            })
          } catch (marketplaceError: any) {
            if (marketplaceError?.name === 'AbortError') return
            console.warn('Marketplace background search failed:', marketplaceError)
          }
          return
        }
      } catch (error: any) {
        if (error?.name === 'AbortError') return
        console.error('Search failed:', error)
        if (textSearchAnalyticsContext) {
          queueStorefrontConsentGrantedAnalyticsEvent({
            eventType: 'search',
            pageKind: 'search',
            pageKey: `search:${normalizedQuery}`,
            pagePath: `${VIEW_PATHS.products}?q=${encodeURIComponent(normalizedQuery)}`,
            payload: {
              search_id: textSearchAnalyticsContext.searchId,
              search_term: normalizedQuery,
              search_mode: textSearchAnalyticsContext.searchMode,
              search_status: 'error',
              page: 1,
              source_view: searchSourceView,
            },
          })
        }
        toast.error(t('toasts.search_error'))
        if (requestId === searchRequestIdRef.current) {
          setAllProducts([])
          setProductsDataKey(nextProductsDataKey)
        }
      } finally {
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null
        }
        if (requestId === searchRequestIdRef.current) {
          if (inFlightSearchProductsDataKeyRef.current === nextProductsDataKey) {
            inFlightSearchProductsDataKeyRef.current = null
          }
          setIsLoadingProducts(false)
        }
      }
    } else {
      searchRequestIdRef.current += 1
      inFlightSearchProductsDataKeyRef.current = null
      setIsSearchResultsActive(false)
      setIsLoadingProducts(false)
      if (currentViewRef.current === 'products') {
        if (selectedCategory) {
          loadProductsByCategory(nextOnlyAvailable)
        } else {
          loadProducts(DEFAULT_PRODUCT_LIST_LIMIT, nextOnlyAvailable)
        }
      }
    }
  }

  const handleVehicleSearch = (params: { query: string; vehicle: VehicleFilter }) => {
    void handleSearchChange(params.query, {
      vehicleFilter: params.vehicle,
      vehicleSearchMode: params.vehicle.placa ? 'plate' : 'manual',
    })
  }

  const handleVehicleFilterClear = () => {
    const fallbackQuery = activeVehicleFilter?.peca?.trim() || ''
    void handleSearchChange(fallbackQuery, {
      vehicleFilter: null,
      vehicleSearchMode: null,
    })
  }

  const handleOnlyAvailableFilterChange = (nextOnlyAvailable: boolean) => {
    if (isImageSearchActive) return

    void handleSearchChange(searchQuery.trim(), {
      scrollToTop: false,
      manufacturerBrands: selectedManufacturerBrands,
      vehicleFilter: activeVehicleFilter,
      vehicleSearchMode: vehicleSearchModeFromRoute,
      onlyAvailable: nextOnlyAvailable,
    })
  }

  const handleImageSearch = useCallback(async (file: File) => {
    if (isBackendUnavailable || isImageSearchLoading) return

    const contextFilters = buildImageSearchContextFilters()
    const searchSourceView = currentViewRef.current
    const imageSearchAnalyticsContext: StorefrontSearchAnalyticsContext = {
      searchId: createStorefrontSearchId(),
      searchMode: 'image',
      sourceView: searchSourceView,
      resultPageByProductId: new Map(),
    }

    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }

    invalidatePendingProductDetailRequests()
    setRestoreProductsViewStateKey(null)
    resetServerPaginatedProducts()
    setActiveSearchAnalyticsContext(null)
    setOnlyAvailableFilter(false)
    setIsImageSearchLoading(true)
    inFlightSearchProductsDataKeyRef.current = null

    const requestId = ++searchRequestIdRef.current
    const controller = new AbortController()
    searchAbortRef.current = controller

    try {
      const imageSearchResult = await searchProductsByImage(file, {
        ...contextFilters,
        limit: 20,
        signal: controller.signal,
      })
      const expandedResults = imageSearchResult.items.length > 0
        ? await expandProductsWithParentContexts('', imageSearchResult.items, {
          signal: controller.signal,
        })
        : []

      if (requestId !== searchRequestIdRef.current) return

      if (imageSearchResult.matchStatus !== 'ok' || expandedResults.length === 0) {
        queueStorefrontConsentGrantedAnalyticsEvent({
          eventType: 'search',
          pageKind: 'search',
          pageKey: 'image-search',
          pagePath: VIEW_PATHS.products,
          payload: {
            search_id: imageSearchAnalyticsContext.searchId,
            search_mode: imageSearchAnalyticsContext.searchMode,
            search_status: 'success',
            results_count: 0,
            page: 1,
            page_results_count: 0,
            result_products: [],
            match_status: imageSearchResult.matchStatus,
            source_view: searchSourceView,
          },
        })
        toast.info(t('toasts.image_search_out_of_catalog'))
        return
      }

      const displayedImageResults = resolveDisplayedSearchProductsForAnalytics(expandedResults)
      registerStorefrontSearchResultPage(imageSearchAnalyticsContext, displayedImageResults, 1)

      if (typeof window !== 'undefined') {
        const currentUrl = `${normalizePathname(window.location.pathname)}${window.location.search}`
        if (currentViewRef.current !== 'products' && currentUrl !== VIEW_PATHS.products) {
          window.history.pushState({}, '', VIEW_PATHS.products)
        }
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        })
      }

      const resultKey = `${Date.now()}-${requestId}`
      setSearchQuery('')
      setActiveVehicleFilter(null)
      setIsSearchResultsActive(false)
      setAllProducts(expandedResults)
      setProductsDataKey(buildImageSearchProductsDataKey(resultKey))
      setImageSearchState({
        resultKey,
        confidenceBand: imageSearchResult.confidenceBand === 'high' ? 'high' : 'medium',
        message: imageSearchResult.confidenceBand === 'medium'
          ? t('header.search.image_medium_message')
          : null,
      })
      setActiveSearchAnalyticsContext(imageSearchAnalyticsContext)
      currentViewRef.current = 'products'
      runViewTransition(() => {
        setCurrentView('products')
      })

      queueStorefrontAnalyticsEvent({
        eventType: 'search',
        pageKind: 'search',
        pageKey: 'image-search',
        pagePath: VIEW_PATHS.products,
        payload: withStorefrontGranularAnalyticsPayload({
          results_count: expandedResults.length,
          confidence_band: imageSearchResult.confidenceBand,
          source_view: searchSourceView,
        }, {
          search_id: imageSearchAnalyticsContext.searchId,
          search_mode: imageSearchAnalyticsContext.searchMode,
          search_status: 'success',
          page: 1,
          page_results_count: displayedImageResults.length,
          result_products: buildStorefrontSearchResultProducts(displayedImageResults),
        }),
      })
    } catch (error: any) {
      if (error?.name === 'AbortError') return
      console.error('Image search failed:', error)
      queueStorefrontConsentGrantedAnalyticsEvent({
        eventType: 'search',
        pageKind: 'search',
        pageKey: 'image-search',
        pagePath: VIEW_PATHS.products,
        payload: {
          search_id: imageSearchAnalyticsContext.searchId,
          search_mode: imageSearchAnalyticsContext.searchMode,
          search_status: 'error',
          page: 1,
          source_view: searchSourceView,
        },
      })
      toast.error(t('toasts.image_search_error'))
    } finally {
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null
      }
      if (requestId === searchRequestIdRef.current) {
        setIsImageSearchLoading(false)
      }
    }
  }, [
    buildImageSearchContextFilters,
    invalidatePendingProductDetailRequests,
    isBackendUnavailable,
    isImageSearchLoading,
    resetServerPaginatedProducts,
    resolveDisplayedSearchProductsForAnalytics,
    runViewTransition,
    t,
  ])

  const handleManufacturerBrandFilterSelect = useCallback((brand: ManufacturerBrandFilterState) => {
    const nextManufacturerBrands = toggleManufacturerBrandSelection(selectedManufacturerBrands, brand)
    const isBrowsingCategoryContext = (
      currentView === 'products'
      && searchQuery.trim().length === 0
      && (Boolean(selectedCategory) || categoryRouteSlugs.length > 0)
    )

    if (isBrowsingCategoryContext) {
      invalidatePendingProductDetailRequests()
      setRestoreProductsViewStateKey(null)
      resetServerPaginatedProducts()
      setSelectedManufacturerBrands(nextManufacturerBrands)
      setIsSearchResultsActive(false)
      return
    }

    if (currentView === 'products' && searchQuery.trim().length === 0) {
      void handleSearchChange('', {
        scrollToTop: false,
        manufacturerBrands: nextManufacturerBrands,
      })
      return
    }

    if (searchQuery.trim().length === 0) {
      invalidatePendingProductDetailRequests()
      setRestoreProductsViewStateKey(null)
      resetServerPaginatedProducts()
      setSelectedCategory(undefined)
      clearCategoryBreadcrumb()
      setCategoryRouteSlugs([])
      setSearchQuery('')
      setActiveVehicleFilter(null)
      setVehicleSearchModeFromRoute(null)
      setSelectedManufacturerBrands(nextManufacturerBrands)
      setIsSearchResultsActive(false)
      currentViewRef.current = 'products'
      runViewTransition(() => {
        setCurrentView('products')
      })
      return
    }

    void handleSearchChange(searchQuery.trim(), { manufacturerBrands: nextManufacturerBrands })
  }, [
    categoryRouteSlugs.length,
    clearCategoryBreadcrumb,
    currentView,
    handleSearchChange,
    invalidatePendingProductDetailRequests,
    resetServerPaginatedProducts,
    searchQuery,
    selectedCategory,
    selectedManufacturerBrands,
  ])

  const handleHomeManufacturerBrandSelect = useCallback((brand: ManufacturerBrandFilterState) => {
    void handleSearchChange('', { manufacturerBrands: [brand] })
  }, [handleSearchChange])

  const handleManufacturerBrandFilterClear = useCallback((brand?: ManufacturerBrandFilterState) => {
    const nextManufacturerBrands = brand
      ? selectedManufacturerBrands.filter((item) => item.id !== brand.id)
      : []
    const isBrowsingCategoryContext = (
      currentView === 'products'
      && searchQuery.trim().length === 0
      && (Boolean(selectedCategory) || categoryRouteSlugs.length > 0)
    )

    if (isBrowsingCategoryContext) {
      invalidatePendingProductDetailRequests()
      setRestoreProductsViewStateKey(null)
      resetServerPaginatedProducts()
      setSelectedManufacturerBrands(nextManufacturerBrands)
      setIsSearchResultsActive(false)
      return
    }

    if (currentView === 'products' && searchQuery.trim().length === 0) {
      void handleSearchChange('', {
        scrollToTop: false,
        manufacturerBrands: nextManufacturerBrands,
      })
      return
    }

    void handleSearchChange(searchQuery.trim(), { manufacturerBrands: nextManufacturerBrands })
  }, [
    categoryRouteSlugs.length,
    currentView,
    handleSearchChange,
    invalidatePendingProductDetailRequests,
    resetServerPaginatedProducts,
    searchQuery,
    selectedCategory,
    selectedManufacturerBrands,
  ])

  useEffect(() => {
    if (isBackendUnavailable) return
    if (!shouldApplyRouteSearchRef.current) return
    if (currentView !== 'products') return
    if (imageSearchState) return

    const routeQuery = searchQuery.trim()
    shouldApplyRouteSearchRef.current = false

    if (routeQuery.length >= 2) {
      void handleSearchChange(routeQuery, {
        scrollToTop: false,
        manufacturerBrands: selectedManufacturerBrands,
        onlyAvailable: onlyAvailableFilter,
      })
    }
  }, [currentView, imageSearchState, isBackendUnavailable, onlyAvailableFilter, productDataAudienceKey, searchQuery, selectedManufacturerBrands])

  const resetProductsNavigation = () => {
    setRestoreProductsViewStateKey(null)
    setImageSearchState(null)
    setIsImageSearchLoading(false)
    setActiveVehicleFilter(null)
    setVehicleSearchModeFromRoute(null)
    setOnlyAvailableFilter(false)
    setSelectedCategory(undefined)
    clearCategoryBreadcrumb()
    setCategoryRouteSlugs([])
  }

  const buildDefaultProductsBreadcrumbSegments = () =>
    buildBreadcrumb('products', {
      onCategoryClick: handleCategorySelect,
      onProductsClick: resetProductsNavigation,
    })

  const navigateToLaunches = () => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRestoreProductsViewStateKey(null)
      setSelectedCategory(undefined)
      clearCategoryBreadcrumb()
      setCategoryRouteSlugs([])
      setImageSearchState(null)
      setIsImageSearchLoading(false)
      setSearchQuery('')
      setActiveVehicleFilter(null)
      setVehicleSearchModeFromRoute(null)
      setSelectedManufacturerBrands([])
      setIsSearchResultsActive(false)
      shouldApplyRouteSearchRef.current = false
      setCurrentView('launches')
    })
  }

  const buildLaunchesBreadcrumbSegments = () => [
    {
      label: t('header.quick_links.launches'),
      href: VIEW_PATHS.launches,
      onClick: navigateToLaunches,
    },
  ]

  const navigateToJustArrived = () => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setRestoreProductsViewStateKey(null)
      setSelectedCategory(undefined)
      clearCategoryBreadcrumb()
      setCategoryRouteSlugs([])
      setImageSearchState(null)
      setIsImageSearchLoading(false)
      setSearchQuery('')
      setActiveVehicleFilter(null)
      setVehicleSearchModeFromRoute(null)
      setSelectedManufacturerBrands([])
      setIsSearchResultsActive(false)
      shouldApplyRouteSearchRef.current = false
      setCurrentView('just_arrived')
    })
  }

  const navigateToBlog = () => {
    if (typeof window !== 'undefined') {
      const currentPath = normalizePathname(window.location.pathname)
      if (!(currentPath === VIEW_PATHS.blog || currentPath.startsWith(`${VIEW_PATHS.blog}/`))) {
        window.history.pushState({}, '', VIEW_PATHS.blog)
      }
    }

    navigateToView('blog')
  }

  const handleRequestedViewChange = useCallback((view: AppView) => {
    if (view === 'cart' || view === 'checkout') {
      navigateToCheckout()
      return
    }

    if (view === 'launches') {
      navigateToLaunches()
      return
    }

    if (view === 'just_arrived') {
      navigateToJustArrived()
      return
    }

    if (view === 'blog') {
      navigateToBlog()
      return
    }

    if (view === 'support') {
      navigateToSupport()
      return
    }

    navigateToView(view as View)
  }, [navigateToBlog, navigateToCheckout, navigateToJustArrived, navigateToLaunches, navigateToSupport, navigateToView])

  const handleReturnRequestRouteStepChange = useCallback((
    step: ReturnRequestRouteStep,
    flowType: ReturnRequestRouteFlowType
  ) => {
    setReturnRequestRouteStep(step)
    setReturnRequestRouteFlowType(flowType)
  }, [])

  const buildJustArrivedBreadcrumbSegments = () => [
    {
      label: t('header.quick_links.just_arrived'),
      href: VIEW_PATHS.just_arrived,
      onClick: navigateToJustArrived,
    },
  ]

  const resolveQuickSearchBreadcrumbSegment = () => {
    if (!isSearchResultsActive || isImageSearchActive || categoryPath.length > 0) return null

    const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase('pt-BR')
    if (!normalizedSearchQuery) return null

    const quickSearchLabels = [
      t('header.quick_links.launches'),
      t('header.quick_links.just_arrived'),
    ]

    const matchedLabel = quickSearchLabels.find(
      (label) => label.toLocaleLowerCase('pt-BR') === normalizedSearchQuery
    )

    if (!matchedLabel) return null

    return {
      label: matchedLabel,
      href: `${VIEW_PATHS.products}?q=${encodeURIComponent(matchedLabel)}`,
      onClick: () => {
        resetProductsNavigation()
        void handleSearchChange(matchedLabel)
      },
    }
  }

  const buildProductsBreadcrumbSegments = () => {
    const quickSearchBreadcrumbSegment = resolveQuickSearchBreadcrumbSegment()
    if (quickSearchBreadcrumbSegment) {
      return [quickSearchBreadcrumbSegment]
    }

    if (categoryPath.length === 0 && categoryRouteSlugs.length > 0) {
      const fallbackRouteSlugs = categoryRouteSlugs
        .map((slug) => safeDecodeURIComponent(slug).trim())
        .filter(Boolean)

      if (fallbackRouteSlugs.length > 0) {
        return fallbackRouteSlugs.map((slug, index) => ({
          label: formatCategorySlugLabel(slug),
          href: buildCategoryHref(fallbackRouteSlugs.slice(0, index + 1)),
        }))
      }
    }

    return buildDefaultProductsBreadcrumbSegments()
  }

  const handleCategorySelect = (categoryId: string, categoryData?: CategorySelectionData) => {
    const resolvedPath = resolveCategoryPathFromSelection(categoryId, categoryData, menuItems, mainMenuCategories)
    const targetCategoryId = resolvedPath?.[resolvedPath.length - 1]?.id ?? categoryId
    const targetRouteSlugs = resolvedPath?.map((node) => node.slug) ?? []
    invalidatePendingProductDetailRequests()
    // Cancela a listagem anterior antes de alterar a rota. A troca de
    // categoria é uma navegação crítica e não deve ficar numa transition,
    // pois o usuário poderia ver os itens da categoria anterior enquanto a
    // atualização de baixa prioridade aguardava.
    categoryProductsRequestIdRef.current += 1
    // A home mantém um controle próprio da requisição além da ref compartilhada
    // do catálogo. Na primeira navegação home -> categoria, invalidar somente a
    // ref compartilhada ainda permitia que a conclusão tardia da home alterasse
    // o estado da listagem que já pertencia à categoria.
    homeProductsLoadRequestIdRef.current += 1
    homeProductsLoadAbortRef.current?.abort()
    homeProductsLoadAbortRef.current = null
    resetServerPaginatedProducts()
    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }
    searchRequestIdRef.current += 1
    inFlightSearchProductsDataKeyRef.current = null
    shouldApplyRouteSearchRef.current = false
    setAllProducts([])
    setProductsDataKey('')
    setIsLoadingProducts(true)
    setRestoreProductsViewStateKey(null)
    currentViewRef.current = 'products'
    setCurrentView('products')
    setShowCategorySidebar(false)
    setImageSearchState(null)
    setIsImageSearchLoading(false)
    setSearchQuery('')
    setActiveVehicleFilter(null)
    setVehicleSearchModeFromRoute(null)
    setSelectedManufacturerBrands([])
    setIsSearchResultsActive(false)

    if (resolvedPath && resolvedPath.length > 0) {
      setSelectedCategory(targetCategoryId)
      setCategoryBreadcrumb(resolvedPath.map((node) => ({
        id: node.id,
        name: node.name,
        slug: node.slug,
      })))
      setCategoryRouteSlugs(targetRouteSlugs)
    } else {
      setSelectedCategory(categoryId)
      setCategoryRouteSlugs([])
      clearCategoryBreadcrumb()
    }

    void loadProductsByCategory(onlyAvailableFilter, {
      categoryId: targetCategoryId,
      routeSlugs: targetRouteSlugs,
      manufacturerBrands: [],
    })
  }

  const ensureHomeMenuLoaded = useCallback(async () => {
    if (isBackendUnavailable) return
    if (isSiteConfigLoading) return
    if (mainMenuCategoriesCountRef.current > 0 || homeMenuItemsCountRef.current > 0 || homeMenuLoadStartedRef.current) return

    homeMenuLoadStartedRef.current = true

    try {
      setIsHomeMenuLoading(true)
      const data = await resolveHomeRequestWithin(
        getMenuTopLevel({ categoryModel }),
        [],
        'Failed to load home menu preview'
      )
      const normalizedData = Array.isArray(data) ? (data as MenuItem[]) : []
      setHomeMenuItems(normalizedData)
    } catch (error) {
      console.error('Failed to load home menu preview:', error)
    } finally {
      setIsHomeMenuLoading(false)
    }
  }, [categoryModel, isBackendUnavailable, isSiteConfigLoading])

  const ensureMenuLoaded = useCallback(async () => {
    if (isBackendUnavailable) return
    if (isSiteConfigLoading) return
    if (menuItems.length > 0 || menuLoadStartedRef.current) return

    menuLoadStartedRef.current = true
    let loadedSuccessfully = false

    try {
      setIsMenuLoading(true)
      const data = await getMenu({ includeProductGroups: false, categoryModel })
      const normalizedData = Array.isArray(data) ? (data as MenuItem[]) : []
      setMenuItems(normalizedData)
      loadedSuccessfully = normalizedData.length > 0
    } catch (error) {
      console.error('Failed to load menu:', error)
    } finally {
      setHasLoadedMenuOnce(true)
      setIsMenuLoading(false)
      if (!loadedSuccessfully) {
        menuLoadStartedRef.current = false
      }
    }
  }, [categoryModel, isBackendUnavailable, isSiteConfigLoading, menuItems.length])

  useEffect(() => {
    setHomeMenuItems([])
    homeMenuLoadStartedRef.current = false
    setProductBreadcrumbMenuItems([])
    productBreadcrumbMenuLoadStartedRef.current = false
  }, [categoryModel])

  useEffect(() => {
    if (currentView !== 'home') return
    if (visibleHomeCategories.length > 0) return
    void ensureHomeMenuLoaded()
  }, [currentView, ensureHomeMenuLoaded, visibleHomeCategories.length])

  useEffect(() => {
    if (categoryModel !== 'grupos') return
    if (isBackendUnavailable) return
    if (isSiteConfigLoading) return
    if (currentView !== 'product-detail') return
    if (!selectedProduct?.groupId?.trim()) return
    if (productBreadcrumbMenuItems.length > 0 || productBreadcrumbMenuLoadStartedRef.current) return

    let isCancelled = false
    productBreadcrumbMenuLoadStartedRef.current = true

    getMenu({ includeProductGroups: true, categoryModel })
      .then((data) => {
        if (isCancelled) return
        const normalizedData = Array.isArray(data) ? (data as MenuItem[]) : []
        setProductBreadcrumbMenuItems(normalizedData)
        if (normalizedData.length === 0) {
          productBreadcrumbMenuLoadStartedRef.current = false
        }
      })
      .catch((error) => {
        if (isCancelled) return
        console.warn('Failed to load product breadcrumb menu:', error)
        productBreadcrumbMenuLoadStartedRef.current = false
      })

    return () => {
      isCancelled = true
      productBreadcrumbMenuLoadStartedRef.current = false
    }
  }, [
    categoryModel,
    currentView,
    isBackendUnavailable,
    isSiteConfigLoading,
    productBreadcrumbMenuItems.length,
    selectedProduct?.groupId,
  ])

  useEffect(() => {
    if (categoryRouteSlugs.length === 0) {
      if (currentView === 'products') {
        setSelectedCategory(undefined)
        clearCategoryBreadcrumb()
      }
      return
    }

    if (menuItems.length === 0) {
      void ensureMenuLoaded()
      return
    }

    const resolvedPath = resolveCategoryPathFromSlugs(categoryRouteSlugs, menuItems, mainMenuCategories)
    if (!resolvedPath) return

    const canonicalSlugs = resolvedPath.map((node) => node.slug)
    if (!areSlugsEqual(categoryRouteSlugs, canonicalSlugs)) {
      setCategoryRouteSlugs(canonicalSlugs)
      return
    }

    setSelectedCategory(resolvedPath[resolvedPath.length - 1].id)
    setCategoryBreadcrumb(resolvedPath.map((node) => ({
      id: node.id,
      name: node.name,
      slug: node.slug,
    })))
  }, [
    categoryRouteSlugs,
    menuItems,
    mainMenuCategories,
    currentView,
    clearCategoryBreadcrumb,
    ensureMenuLoaded,
    setCategoryBreadcrumb,
  ])

  useEffect(() => {
    if (isBackendUnavailable) return
    if (isSiteConfigLoading) return
    if (menuItems.length > 0) return
    const shouldPrefetchMenuForHome = currentView === 'home'
    if (!shouldPrefetchMenuForHome && !hasCompletedInitialProductsLoad && currentView !== 'product-detail') return
    if (menuLoadIdleHandleRef.current !== null || menuLoadTimerHandleRef.current !== null) return

    const schedulePrefetch = () => {
      void ensureMenuLoaded()
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      menuLoadIdleHandleRef.current = (window as Window & {
        requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
      }).requestIdleCallback(() => {
        menuLoadIdleHandleRef.current = null
        schedulePrefetch()
      }, { timeout: shouldPrefetchMenuForHome ? 240 : 1200 })
      return
    }

    menuLoadTimerHandleRef.current = globalThis.setTimeout(() => {
      menuLoadTimerHandleRef.current = null
      schedulePrefetch()
    }, shouldPrefetchMenuForHome ? 120 : 450) as unknown as number
  }, [currentView, isBackendUnavailable, isSiteConfigLoading, menuItems.length, hasCompletedInitialProductsLoad, ensureMenuLoaded])

  useEffect(() => {
    return () => {
      if (
        typeof window !== 'undefined'
        && menuLoadIdleHandleRef.current !== null
        && 'cancelIdleCallback' in window
      ) {
        (window as Window & { cancelIdleCallback: (handle: number) => void })
          .cancelIdleCallback(menuLoadIdleHandleRef.current)
        menuLoadIdleHandleRef.current = null
      }
      if (menuLoadTimerHandleRef.current !== null) {
        window.clearTimeout(menuLoadTimerHandleRef.current)
        menuLoadTimerHandleRef.current = null
      }
    }
  }, [])

  const showProductDetail = (
    product: Product,
    options?: {
      track?: boolean
      routeProductId?: string | null
      parentContextId?: string | null
    },
  ) => {
    const shouldTrack = options?.track !== false
    const resolvedRouteProductId = normalizeRouteProductValue(options?.routeProductId) || product.id
    const resolvedParentContextId = resolveProductParentContextId(
      resolvedRouteProductId,
      options?.parentContextId,
    )
    const originView = currentView === 'launches'
      ? 'launches'
      : currentView === 'just_arrived'
        ? 'just_arrived'
        : 'products'
    productOriginViewRef.current = originView
    if (currentViewRef.current === 'products' || currentViewRef.current === 'launches' || currentViewRef.current === 'just_arrived') {
      productListingSnapshotRef.current = {
        originView,
        restoreKey: buildProductsViewStateKey({
          view: currentViewRef.current,
          searchQuery,
          manufacturerBrands: selectedManufacturerBrands,
          vehicleFilter: activeVehicleFilter,
          onlyAvailable: onlyAvailableFilter,
          isSearchResultsActive,
          categorySlugs: categoryRouteSlugs,
          selectedCategory,
        }),
      }
    }
    setRestoreProductsViewStateKey(null)
    currentViewRef.current = 'product-detail'
    runViewTransition(() => {
      setSelectedProduct(product)
      setDisplayedProductDetail(product)
      setCurrentView('product-detail')
      setRouteProductId(resolvedRouteProductId)
      setRouteProductParentContextId(resolvedParentContextId)
      setIsLoadingProduct(false)
      setProductLoadError(null)
    })
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      })
    }
    if (shouldTrack) {
      recommendationTracking.trackView(product)
    }

    if (shouldTrack) {
      setRecentlyViewed((current) => {
        const existing = current || []
        const filtered = existing.filter(p => p.id !== product.id)
        return [product, ...filtered].slice(0, 10)
      })
    }
  }

  const pushProductDetailHistory = (
    product: Product,
    routeProductId: string,
    parentContextId: string | null,
  ) => {
    if (typeof window === 'undefined') return

    const nextUrl = buildProductHref(
      {
        id: routeProductId,
        name: product.navigationTargetProductName || product.name,
      },
      { parentContextId },
    )
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`

    if (currentUrl === nextUrl) return

    // A URL do detalhe precisa entrar no historico durante o clique. Deixar
    // essa transicao para o effect de sincronizacao pode substituir a busca
    // anterior durante a normalizacao de variacoes.
    window.history.pushState(window.history.state, '', nextUrl)
  }

  const mergeProductClassificationForActiveCategoryModel = useCallback((baseProduct: Product | null | undefined, detailedProduct: Product) => {
    if (categoryModel !== 'classes' || !baseProduct) {
      return detailedProduct
    }

    const baseCategory = baseProduct.category.trim()
    const baseSubcategory = baseProduct.subcategory.trim()
    const baseClassId = baseProduct.classId?.trim()

    if (!baseCategory && !baseSubcategory && !baseClassId) {
      return detailedProduct
    }

    return {
      ...detailedProduct,
      category: baseCategory || detailedProduct.category,
      subcategory: baseSubcategory || detailedProduct.subcategory,
      classId: baseClassId || detailedProduct.classId,
    }
  }, [categoryModel])

  const resolveInitialVariationRouteContext = useCallback(async (
    product: Product,
    routeProductId?: string | null,
    parentContextId?: string | null,
  ) => {
    const normalizedRouteProductId = normalizeRouteProductValue(routeProductId) || product.id
    const normalizedParentContextId = resolveProductParentContextId(normalizedRouteProductId, parentContextId)

    if (
      normalizedParentContextId
      || product.parentProductId
      || normalizedRouteProductId !== product.id
      || (!product.hasVariations && product.usarSeletorVariacao !== true)
    ) {
      return {
        routeProductId: normalizedRouteProductId,
        parentContextId: normalizedParentContextId,
      }
    }

    try {
      const variations = await getProductVariations(product.id, { includeManuals: false })
      const initialVariation = variations.find((variation) => variation.parentProductId === product.id) ?? variations[0]

      if (!initialVariation) {
        return {
          routeProductId: normalizedRouteProductId,
          parentContextId: normalizedParentContextId,
        }
      }

      return {
        routeProductId: initialVariation.id,
        parentContextId: product.id,
      }
    } catch (error) {
      console.warn(`Failed to resolve initial variation route context for product ${product.id}.`, error)
      return {
        routeProductId: normalizedRouteProductId,
        parentContextId: normalizedParentContextId,
      }
    }
  }, [])

  const loadProductFromRoute = async (
    productId: string,
    parentContextId?: string | null,
  ) => {
    if (isBackendUnavailable) return
    const normalizedProductId = normalizeRouteProductValue(productId)
    if (!normalizedProductId) return

    const normalizedParentContextId = resolveProductParentContextId(normalizedProductId, parentContextId)
    const routeBaseProductId = normalizedParentContextId ?? normalizedProductId

    if (
      selectedProduct?.id === routeBaseProductId
      || productSupportsDetailParentContext(selectedProduct, normalizedParentContextId)
    ) return
    const requestId = ++productDetailRequestIdRef.current
    const routeProduct = allProducts.find((item) => item.id === normalizedProductId)
    const baseProduct = allProducts.find((item) => item.id === routeBaseProductId) ?? routeProduct

    try {
      setIsLoadingProduct(true)
      setProductLoadError(null)
      const detailedProduct = await getProductById(routeBaseProductId)
      if (productDetailRequestIdRef.current !== requestId) return
      const initialRouteContext = await resolveInitialVariationRouteContext(
        detailedProduct,
        normalizedProductId,
        normalizedParentContextId,
      )
      if (productDetailRequestIdRef.current !== requestId) return
      showProductDetail(
        mergeProductClassificationForActiveCategoryModel(baseProduct, detailedProduct),
        {
          routeProductId: initialRouteContext.routeProductId,
          parentContextId: initialRouteContext.parentContextId,
        },
      )
      return
    } catch (error) {
      if (productDetailRequestIdRef.current !== requestId) return
      console.error('Failed to load product details:', error)
    }

    const fallbackProduct = baseProduct
    if (fallbackProduct) {
      if (productDetailRequestIdRef.current !== requestId) return
      showProductDetail(fallbackProduct, {
        routeProductId: normalizedProductId,
        parentContextId: normalizedParentContextId,
      })
      return
    }

    if (productDetailRequestIdRef.current !== requestId) return
    setIsLoadingProduct(false)
    setProductLoadError('product.not_found')
    toast.error(t('toasts.product_load_error'))
  }

  const handleMarketplaceSellerClick = useCallback((sellerId: number, sellerName: string) => {
    const normalizedSellerId = Math.trunc(sellerId)
    if (!Number.isFinite(normalizedSellerId) || normalizedSellerId <= 0 || typeof window === 'undefined') return

    window.history.pushState({}, '', buildMarketplaceVitrineSellerHref(normalizedSellerId, sellerName))
    setRouteMarketplaceListingId(null)
    setRouteMarketplaceSellerId(normalizedSellerId)
    currentViewRef.current = 'marketplace-seller'
    runViewTransition(() => setCurrentView('marketplace-seller'))
    window.scrollTo({ top: 0, left: 0 })
  }, [runViewTransition])

  async function handleProductClick(
    product: Product,
    context?: ProductNavigationContext,
  ) {
    if (product.origem === 'marketplace_vitrine' && product.marketplaceAnuncioId) {
      const anuncioId = Math.trunc(product.marketplaceAnuncioId)
      window.history.pushState({}, '', buildMarketplaceVitrineHref(anuncioId, product.name))
      setRouteMarketplaceListingId(anuncioId)
      currentViewRef.current = 'marketplace-listing-detail'
      runViewTransition(() => setCurrentView('marketplace-listing-detail'))
      window.scrollTo({ top: 0, left: 0 })
      return
    }

    if (isPriceTableView(currentViewRef.current)) {
      const productGroupId = categoryModel === 'classes' ? product.classId : product.groupId
      const productGroupNumericId = Number(productGroupId)
      if (Number.isFinite(productGroupNumericId) && productGroupNumericId > 0) {
        window.dispatchEvent(new CustomEvent(PRICE_TABLE_PRODUCT_GROUP_NAVIGATION_EVENT, {
          detail: { product },
        }))
        return
      }
    }

    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
      searchAbortRef.current = null
    }
    searchRequestIdRef.current += 1
    inFlightSearchProductsDataKeyRef.current = null
    const requestId = ++productDetailRequestIdRef.current
    const navigableProductId = product.navigationTargetProductId?.trim() || product.id
    const normalizedRouteProductId = normalizeRouteProductValue(context?.routeProductId) || navigableProductId
    const normalizedParentContextId = resolveProductParentContextId(
      normalizedRouteProductId,
      context?.parentContextId,
    )
    const detailProductId = normalizedParentContextId ?? navigableProductId

    showProductDetail(product, {
      routeProductId: normalizedRouteProductId,
      parentContextId: normalizedParentContextId,
    })
    pushProductDetailHistory(product, normalizedRouteProductId, normalizedParentContextId)

    if (!/^\d+$/.test(detailProductId)) {
      return
    }

    try {
      const detailedProduct = await getProductById(detailProductId)
      if (productDetailRequestIdRef.current !== requestId) return
      showProductDetail(
        mergeProductClassificationForActiveCategoryModel(product, detailedProduct),
        {
          track: false,
          routeProductId: normalizedRouteProductId,
          parentContextId: normalizedParentContextId,
        },
      )
    } catch (error) {
      if (productDetailRequestIdRef.current !== requestId) return
      console.warn('Failed to enrich product details; keeping listing payload.', error)
    }
  }

  useEffect(() => {
    if (isBackendUnavailable) return
    if (!isAuthBootstrapResolved) return
    if (currentView !== 'product-detail' || !routeProductId) return
    const routeBaseProductId = resolveProductParentContextId(routeProductId, routeProductParentContextId) ?? routeProductId
    if (
      selectedProduct?.id === routeBaseProductId
      || productSupportsDetailParentContext(selectedProduct, routeProductParentContextId)
    ) return
    loadProductFromRoute(routeProductId, routeProductParentContextId)
  }, [
    currentView,
    isAuthBootstrapResolved,
    isBackendUnavailable,
    routeProductId,
    routeProductParentContextId,
    selectedProduct?.id,
    selectedProduct?.parentProductId,
    allProducts.length,
  ])

  const handleBackFromProduct = () => {
    if (typeof window !== 'undefined' && productListingSnapshotRef.current && window.history.length > 1) {
      window.history.back()
      return
    }

    if (productOriginViewRef.current === 'launches') {
      navigateToLaunches()
      return
    }

    if (productOriginViewRef.current === 'just_arrived') {
      navigateToJustArrived()
      return
    }

    navigateToView('products')
  }

  const resolveProductClassificationPathForActiveCategoryModel = useCallback((product: Product): CategoryPathNode[] | null => {
    const breadcrumbMenuItems = productBreadcrumbMenuItems.length > 0 ? productBreadcrumbMenuItems : menuItems
    const breadcrumbCategoryTree = productBreadcrumbMenuItems.length > 0
      ? productBreadcrumbMenuCategories
      : mainMenuCategories

    if (categoryModel === 'classes') {
      const normalizedClassId = product.classId?.trim()

      if (normalizedClassId) {
        const classPath = resolveCategoryPathFromSelection(
          normalizedClassId,
          undefined,
          breadcrumbMenuItems,
          breadcrumbCategoryTree
        )

        if (classPath && classPath.length > 0) {
          return classPath
        }
      }
    } else {
      const normalizedGroupId = product.groupId?.trim()

      if (normalizedGroupId) {
        const groupPath = resolveCategoryPathFromSelection(
          normalizedGroupId,
          undefined,
          breadcrumbMenuItems,
          breadcrumbCategoryTree
        )

        if (groupPath && groupPath.length > 0) {
          return groupPath
        }
      }
    }

    const normalizedCategory = product.category.trim()
    const normalizedSubcategory = product.subcategory.trim()
    const pathLabels = categoryModel === 'classes'
      ? (normalizedCategory || normalizedSubcategory)
          .split('>')
          .map((token) => token.trim())
          .filter(Boolean)
      : (() => {
          const categoryTokens = normalizedCategory
            .split('>')
            .map((token) => token.trim())
            .filter(Boolean)

          const labels = categoryTokens.length > 0 ? [...categoryTokens] : []

          if (labels.length === 0 && normalizedCategory) {
            labels.push(normalizedCategory)
          }

          if (
            normalizedSubcategory
            && normalizeCategorySlug(labels[labels.length - 1] || '') !== normalizeCategorySlug(normalizedSubcategory)
          ) {
            labels.push(normalizedSubcategory)
          }

          return labels
        })()

    if (pathLabels.length === 0) {
      return null
    }

    const resolvedPath = resolveCategoryPathFromSlugs(
      pathLabels.map((label) => slugify(label)),
      breadcrumbMenuItems,
      breadcrumbCategoryTree
    )

    if (resolvedPath && resolvedPath.length > 0) {
      return resolvedPath
    }

    return pathLabels.map((label, index) => ({
      id: `classification:${index}:${slugify(label)}`,
      name: label,
      slug: slugify(label),
    }))
  }, [
    categoryModel,
    mainMenuCategories,
    menuItems,
    productBreadcrumbMenuCategories,
    productBreadcrumbMenuItems,
  ])

  const resolveProductClassIdForRelatedProducts = useCallback((product: Product): number | null => {
    const directClassId = Number(product.classId)
    if (Number.isFinite(directClassId) && directClassId > 0) {
      return Math.trunc(directClassId)
    }

    if (categoryModel !== 'classes') {
      return null
    }

    const resolvedPath = resolveProductClassificationPathForActiveCategoryModel(product)
    const lastNodeId = resolvedPath?.[resolvedPath.length - 1]?.id
    const derivedClassId = Number(lastNodeId)
    return Number.isFinite(derivedClassId) && derivedClassId > 0
      ? Math.trunc(derivedClassId)
      : null
  }, [categoryModel, resolveProductClassificationPathForActiveCategoryModel])

  const buildProductBreadcrumbSegments = (product: Product) => {
    const segments: { label: string; categoryId?: string; href?: string; onClick?: () => void }[] = []
    const breadcrumbMenuItems = productBreadcrumbMenuItems.length > 0 ? productBreadcrumbMenuItems : menuItems
    const breadcrumbCategoryTree = productBreadcrumbMenuItems.length > 0
      ? productBreadcrumbMenuCategories
      : mainMenuCategories
    const addPathSegments = (pathNodes: CategoryPathNode[]) => {
      const slugs: string[] = []
      pathNodes.forEach((node, index) => {
        const pathSlice = pathNodes.slice(0, index + 1)
        const slug = node.slug || slugify(node.name)
        const isSyntheticPathNode = node.id.startsWith('classification:')
        slugs.push(slug)
        segments.push({
          label: node.name,
          categoryId: isSyntheticPathNode ? undefined : node.id,
          href: isSyntheticPathNode ? undefined : buildCategoryHref(slugs),
          onClick: isSyntheticPathNode
            ? undefined
            : () => handleCategorySelect(node.id, { name: node.name, path: pathSlice }),
        })
      })
    }

    const quickSearchBreadcrumbSegment = resolveQuickSearchBreadcrumbSegment()
    if (quickSearchBreadcrumbSegment) {
      segments.push(quickSearchBreadcrumbSegment)
      return segments
    }

    if (productOriginViewRef.current === 'launches') {
      segments.push({
        label: t('header.quick_links.launches'),
        href: VIEW_PATHS.launches,
        onClick: navigateToLaunches,
      })
      return segments
    }

    if (productOriginViewRef.current === 'just_arrived') {
      segments.push({
        label: t('header.quick_links.just_arrived'),
        href: VIEW_PATHS.just_arrived,
        onClick: navigateToJustArrived,
      })
      return segments
    }

    let resolvedPath = resolveProductClassificationPathForActiveCategoryModel(product)
    if (
      categoryModel !== 'classes'
      && (!resolvedPath || resolvedPath.length === 0)
      && product.groupId
      && Number.isFinite(Number(product.groupId))
    ) {
      resolvedPath = resolveCategoryPathFromSelection(
        product.groupId,
        undefined,
        breadcrumbMenuItems,
        breadcrumbCategoryTree
      )
    }

    if (resolvedPath && resolvedPath.length > 0) {
      addPathSegments(resolvedPath)
    } else if (categoryPath.length > 0) {
      const fallbackPath = categoryPath.map((node) => ({
        id: node.id,
        name: node.name,
        slug: slugify(node.name),
      }))
      addPathSegments(fallbackPath)
    } else {
      segments.push({
        label: t('nav.products'),
        href: VIEW_PATHS.products,
        onClick: handleBackFromProduct,
      })
    }

    return segments
  }

  const handleProductsViewStateRestoreComplete = useCallback(() => {
    setRestoreProductsViewStateKey((current) => (
      current === currentProductsViewStateKey ? null : current
    ))
  }, [currentProductsViewStateKey])

  const openLogin = (redirect?: PostLoginRedirect) => {
    invalidatePendingProductDetailRequests()
    runViewTransition(() => {
      setPostLoginRedirect(redirect ?? null)
      setCurrentView('login')
    })
  }

  const persistAuthenticatedBuyerProfile = useCallback((profile: AccountProfile) => {
    buyerSessionExpiryHandledRef.current = false
    persistAuthenticatedSessionStorage(profile)
    flushSync(() => {
      setIsLogoutRequested(false)
      if (profile.entidadeId) {
        setAuthenticatedEntidadeId(profile.entidadeId)
      }
      setIsLoggedIn(true)
      setAccountProfile(profile)
    })
    persistAccountProfileCookies(profile)
    return profile
  }, [setAccountProfile, setIsLoggedIn, setIsLogoutRequested])

  const applyAuthenticatedBuyerSession = useCallback((
    snapshot: AuthenticatedAccountSnapshot,
    fallbackIdentifier: string = ''
  ) => {
    const cookieProfile = resolveAccountProfileFromCookies()
    const resolvedProfile = buildAccountProfileFromAuthenticatedSnapshot(
      snapshot,
      cookieProfile,
      fallbackIdentifier
    )

    return persistAuthenticatedBuyerProfile(resolvedProfile)
  }, [persistAuthenticatedBuyerProfile])

  const handleLoginClick = () => {
    if (effectiveIsLoggedIn) {
      navigateToView('dashboard')
    } else {
      openLogin()
    }
  }

  const completeAuthenticatedBuyerLogin = useCallback((
    response: AuthenticatedBuyerLoginResponse,
    normalizedLogin: string,
    options?: PostLoginOptions
  ) => {
    if (!response?.authenticated) {
      if (!options?.suppressErrorToast) {
        toast.error(response?.reason || t('toasts.invalid_credentials'))
      }
      return false
    }

    applyAuthenticatedBuyerSession(response, normalizedLogin)
    if (!options?.suppressSuccessToast) {
      toast.success(t('toasts.login_success'))
    }
    const redirectTarget = options?.postLoginRedirectOverride ?? postLoginRedirect

    if (redirectTarget) {
      if (redirectTarget.view === 'product-detail') {
        const targetProductId = redirectTarget.productId || routeProductId || selectedProduct?.id
        runViewTransition(() => {
          if (targetProductId) {
            setRouteProductId(targetProductId)
            setRouteProductParentContextId(
              resolveProductParentContextId(
                targetProductId,
                redirectTarget.parentContextId ?? routeProductParentContextId,
              ),
            )
            setSelectedProduct(null)
            setDisplayedProductDetail(null)
            setIsLoadingProduct(true)
            setProductLoadError(null)
          }
          setCurrentView('product-detail')
        })
      } else {
        const redirectView = redirectTarget.view === 'cart' ? 'checkout' : redirectTarget.view
        if (redirectView === 'checkout') {
          navigateToCheckout()
        } else if (redirectView === 'support') {
          navigateToSupport()
        } else {
          navigateToView(redirectView)
        }
      }
      if (postLoginRedirect) {
        setPostLoginRedirect(null)
      }
      return true
    }
    navigateHome()
    return true
  }, [applyAuthenticatedBuyerSession, navigateHome, navigateToCheckout, navigateToSupport, navigateToView, postLoginRedirect, routeProductId, routeProductParentContextId, runViewTransition, selectedProduct?.id, t])

  const requestBuyerLogin = useCallback(async (
    loginValue: string,
    senha: string,
    documento?: string
  ) => {
    try {
      return await login(loginValue, senha, documento)
    } catch (error) {
      console.error('Failed to login:', error)
      const message = error instanceof Error
        ? normalizeLoginFailureMessage(error.message, t('toasts.invalid_credentials'))
        : ''
      return {
        identifier: loginValue.trim(),
        authenticated: false,
        reason: message || t('toasts.login_failed'),
      } satisfies AuthenticatedBuyerLoginResponse
    }
  }, [t])

  const performLogin = useCallback(async (
    loginValue: string,
    senha: string,
    documento?: string,
    options?: PostLoginOptions
  ) => {
    const normalizedLogin = loginValue.trim()
    const response = await requestBuyerLogin(loginValue, senha, documento)
    if (response?.authenticated) {
      return completeAuthenticatedBuyerLogin(response, normalizedLogin, options)
    }
    if (!response?.requiresDocument && !options?.suppressErrorToast) {
      toast.error(response?.reason || t('toasts.login_failed'))
    }
    return false
  }, [completeAuthenticatedBuyerLogin, requestBuyerLogin, t])

  const handleLogin = async (loginValue: string, senha: string, documento?: string) => {
    const response = await requestBuyerLogin(loginValue, senha, documento)
    if (response?.authenticated) {
      completeAuthenticatedBuyerLogin(response, loginValue.trim())
      return response
    }
    if (!response?.requiresDocument) {
      toast.error(response?.reason || t('toasts.invalid_credentials'))
    }
    return response
  }

  const handlePasswordlessRequest = useCallback(async (
    identifier: string,
    channel: PasswordlessLoginChannel,
    selection?: {
      destinationType?: 'entidade' | 'contato'
      contactId?: number | null
    },
    documento?: string
  ) => {
    let alreadyNotified = false
    try {
      const response = await requestPasswordlessLogin(identifier, channel, selection, documento)
      if (response.requiresDocument) {
        return response
      }
      if (!response.sent) {
        alreadyNotified = true
        toast.error(response.reason || 'Não foi possível enviar o código.')
        throw new Error(response.reason || 'passwordless_request_failed')
      }
      toast.success(response.reason || (response.channel === 'email'
        ? 'Código enviado por e-mail.'
        : 'Código enviado pelo WhatsApp.'))
      return response
    } catch (error) {
      console.error('Failed to request passwordless login:', error)
      if (!alreadyNotified) {
        const message = error instanceof Error
          ? error.message
            .replace(/^API error:\s*\d+\s*-\s*/i, '')
            .replace(/^API error:\s*\d+\s*/i, '')
            .trim()
          : ''
        toast.error(message || 'Não foi possível enviar o código.')
      }
      throw error
    }
  }, [])

  const handlePasswordlessDestinations = useCallback(async (
    identifier: string,
    options?: { suppressErrorToast?: boolean; documento?: string }
  ) => {
    const shouldToast = !options?.suppressErrorToast
    let alreadyNotified = false
    try {
      const response = await getPasswordlessDestinations(identifier, options?.documento)
      if (response.requiresDocument) {
        return response
      }
      if (!response.loaded) {
        alreadyNotified = shouldToast
        if (shouldToast) {
          toast.error(response.reason || 'Não foi possível consultar os números disponíveis.')
        }
        throw new Error(response.reason || 'passwordless_destinations_failed')
      }
      return response
    } catch (error) {
      console.error('Failed to load passwordless destinations:', error)
      if (!alreadyNotified && shouldToast) {
        const message = error instanceof Error
          ? error.message
            .replace(/^API error:\s*\d+\s*-\s*/i, '')
            .replace(/^API error:\s*\d+\s*/i, '')
            .trim()
          : ''
        toast.error(message || 'Não foi possível consultar os números disponíveis.')
      }
      throw error
    }
  }, [])

  const handlePasswordlessContactRequest = useCallback(async (input: {
    identifier: string
    documento?: string
    nome: string
    whatsapp: string
    cargo: string
  }) => {
    let alreadyNotified = false
    try {
      const response = await requestPasswordlessContact(input)
      if (response.requiresDocument) {
        return response
      }
      if (!response.requested) {
        alreadyNotified = true
        toast.error(response.reason || 'Não foi possível solicitar o cadastro do WhatsApp.')
        throw new Error(response.reason || 'passwordless_contact_request_failed')
      }
      return response
    } catch (error) {
      console.error('Failed to request passwordless contact:', error)
      if (!alreadyNotified) {
        const message = error instanceof Error
          ? error.message
            .replace(/^API error:\s*\d+\s*-\s*/i, '')
            .replace(/^API error:\s*\d+\s*/i, '')
            .trim()
          : ''
        toast.error(message || 'Não foi possível solicitar o cadastro do WhatsApp.')
      }
      throw error
    }
  }, [])

  const handlePasswordlessVerify = useCallback(async (input: {
    identifier: string
    documento?: string
    code: string
    channel: PasswordlessLoginChannel
    requestId?: string | number | null
  }) => {
    try {
      const response = await verifyPasswordlessLogin(input)
      if (response.requiresDocument) return false
      return completeAuthenticatedBuyerLogin(response, input.identifier)
    } catch (error) {
      console.error('Failed to verify passwordless login:', error)
      const message = error instanceof Error
        ? error.message
          .replace(/^API error:\s*\d+\s*-\s*/i, '')
          .replace(/^API error:\s*\d+\s*/i, '')
          .trim()
        : ''
      toast.error(message || 'Não foi possível validar o código.')
      return false
    }
  }, [completeAuthenticatedBuyerLogin])

  useEffect(() => {
    if (currentView !== 'access') {
      attemptedAccessTicketRef.current = null
      return
    }

    const ticket = routeAccessTicket?.trim() || ''
    if (!ticket) {
      toast.error('Link de acesso inválido ou expirado.')
      navigateToView('login')
      return
    }

    const accessAttemptKey = `${routeAccessMode}:${ticket}`
    const existingAttempt = attemptedAccessTicketRef.current?.key === accessAttemptKey
      ? attemptedAccessTicketRef.current
      : null

    let cancelled = false

    const consumeAccess = existingAttempt?.promise ?? (
      routeAccessMode === 'short'
        ? consumeBuyerAccessShortCode(ticket)
        : consumeBuyerAccessTicket(ticket)
    )

    if (!existingAttempt) {
      attemptedAccessTicketRef.current = {
        key: accessAttemptKey,
        promise: consumeAccess,
      }
    }

    void consumeAccess
      .then((response) => {
        if (cancelled && attemptedAccessTicketRef.current?.key !== accessAttemptKey) return
        if (attemptedAccessTicketRef.current?.key !== accessAttemptKey) return

        attemptedAccessTicketRef.current = null

        if (response.accessScope === 'order_tracking' && response.redirectPath) {
          const trackingPath = resolveSameOriginRedirectPath(response.redirectPath)
          if (trackingPath) {
            window.history.replaceState(window.history.state, '', trackingPath)
            window.dispatchEvent(new Event(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT))
            return
          }
        }

        if (!response.authenticated || !response.entidadeId) {
          toast.error(response.reason || 'Link de acesso inválido ou expirado.')
          navigateToView('login')
          return
        }

        applyAuthenticatedBuyerSession(response)
        const redirectRoute = response.redirectPath
          ? resolveRoute(response.redirectPath).view
          : null
        if (routeAccessMode === 'short' && redirectRoute && BUYER_ACCESS_CODE_VIEWS.has(redirectRoute)) {
          persistBuyerPriceTableAccessCode(response.entidadeId, ticket)
        }
        if (typeof window !== 'undefined') {
          const rawRedirectPath = response.redirectPath || VIEW_PATHS.dashboard
          persistProductCheckoutIntentFromRedirectPath(rawRedirectPath)
          const redirectPath = routeAccessMode === 'short'
            ? buildRedirectPathWithBuyerAccessCode(rawRedirectPath, ticket)
            : rawRedirectPath
          if (redirectPath !== rawRedirectPath) {
            persistProductCheckoutIntentFromRedirectPath(redirectPath)
          }
          const sameOriginRedirectPath = resolveSameOriginRedirectPath(redirectPath)
          if (sameOriginRedirectPath) {
            const sameOriginRedirectUrl = new URL(sameOriginRedirectPath, window.location.origin)
            const sameOriginRoute = resolveRoute(sameOriginRedirectUrl.pathname, sameOriginRedirectUrl.search)
            if (
              sameOriginRoute.view === 'quick-checkout'
              && (sameOriginRoute.quickCheckoutProductId || readProductCheckoutQuickRequest())
            ) {
              updateProductCheckoutQuickFlow(true)
            }
            window.history.replaceState(window.history.state, '', sameOriginRedirectPath)
            window.dispatchEvent(new Event(STOREFRONT_ANALYTICS_LOCATION_CHANGE_EVENT))
            return
          }

          window.location.replace(redirectPath)
        }
      })
      .catch((error) => {
        if (cancelled && attemptedAccessTicketRef.current?.key !== accessAttemptKey) return
        if (attemptedAccessTicketRef.current?.key !== accessAttemptKey) return

        attemptedAccessTicketRef.current = null
        console.error('Failed to consume buyer access ticket:', error)
        toast.error('Não foi possível validar o link de acesso.')
        navigateToView('login')
      })

    return () => {
      cancelled = true
    }
  }, [
    applyAuthenticatedBuyerSession,
    currentView,
    navigateToView,
    routeAccessMode,
    routeAccessTicket,
    updateProductCheckoutQuickFlow,
  ])

  useEffect(() => {
    if (!BUYER_ACCESS_CODE_VIEWS.has(currentView)) {
      attemptedBuyerAccessCodeRef.current = null
      return
    }

    const code = routeBuyerAccessCode?.trim() || ''
    if (!code) return

    const accessAttemptKey = `table:${code}`
    const isKnownCodeForCurrentBuyer =
      effectiveIsLoggedIn
      && typeof authenticatedEntidadeId === 'number'
      && readPersistedBuyerPriceTableAccessCode(authenticatedEntidadeId) === code

    if (isKnownCodeForCurrentBuyer) {
      attemptedBuyerAccessCodeRef.current = accessAttemptKey
      return
    }

    if (attemptedBuyerAccessCodeRef.current === accessAttemptKey) return
    attemptedBuyerAccessCodeRef.current = accessAttemptKey

    let cancelled = false

    void consumeBuyerAccessShortCode(code)
      .then((response) => {
        if (cancelled) return

        if (!response.authenticated || !response.entidadeId) {
          toast.error(response.reason || 'Link de acesso inválido ou expirado.')
          navigateToView('login')
          return
        }

        persistBuyerPriceTableAccessCode(response.entidadeId, code)
        applyAuthenticatedBuyerSession(response)
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to consume buyer table access code:', error)
        toast.error('Não foi possível validar o link de acesso.')
        navigateToView('login')
      })

    return () => {
      cancelled = true
    }
  }, [
    applyAuthenticatedBuyerSession,
    authenticatedEntidadeId,
    currentView,
    effectiveIsLoggedIn,
    navigateToView,
    routeBuyerAccessCode,
  ])

  useEffect(() => {
    if (currentView !== 'redirect-checkout') {
      attemptedLegacyCheckoutRedirectRef.current = null
      return
    }

    const payload = parseLegacyCheckoutRedirectLink(routeLegacyCheckoutLink)
    if (!payload) {
      toast.error('Link de checkout inválido ou expirado.')
      navigateToView('login')
      return
    }

    const redirectAttemptKey = `${payload.uid}:${payload.compraHash}`
    if (attemptedLegacyCheckoutRedirectRef.current === redirectAttemptKey) return
    attemptedLegacyCheckoutRedirectRef.current = redirectAttemptKey

    let cancelled = false

    void loginLegacyCheckout({
      uid: payload.uid,
      auth: payload.auth,
      compra: payload.compraHash,
    })
      .then((response) => {
        if (cancelled) return

        if (!response.authenticated || !response.entidadeId) {
          toast.error(response.reason || 'Link de checkout inválido ou expirado.')
          navigateToView('login')
          return
        }

        applyAuthenticatedBuyerSession(response, String(payload.uid))
        if (typeof window !== 'undefined') {
          const target = response.legacyCheckout
          if (target?.redirectPath) {
            try {
              const redirectUrl = new URL(target.redirectPath, window.location.origin)
              redirectUrl.searchParams.set('legacy_uid', String(payload.uid))
              redirectUrl.searchParams.set('legacy_redirect', '1')
              window.location.replace(`${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`)
            } catch {
              window.location.replace(target.redirectPath)
            }
            return
          }

          if (target?.type === 'quote' && target.id > 0) {
            const params = new URLSearchParams({
              source: 'quote',
              quote_id: String(target.id),
              legacy_uid: String(payload.uid),
              legacy_redirect: '1',
            })
            window.location.replace(`${buildCheckoutPath('cart')}?${params.toString()}`)
            return
          }

          if (target?.type === 'order' && target.id > 0) {
            const params = new URLSearchParams({
              pedido_id: String(target.id),
              legacy_uid: String(payload.uid),
              legacy_redirect: '1',
            })
            window.location.replace(`${buildCheckoutPath('success')}?${params.toString()}`)
            return
          }

          const params = new URLSearchParams({
            legacy_uid: String(payload.uid),
            legacy_compra: payload.compraHash,
            legacy_redirect: '1',
          })
          window.location.replace(`${buildCheckoutPath('success')}?${params.toString()}`)
        }
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to authenticate legacy checkout buyer session:', error)
        toast.error('Não foi possível validar o acesso automático deste link.')
        navigateToView('login')
      })

    return () => {
      cancelled = true
    }
  }, [
    applyAuthenticatedBuyerSession,
    currentView,
    navigateToView,
    routeLegacyCheckoutLink,
  ])

  const handlePasswordRecoveryDestinations = useCallback(async (
    identifier: string,
    options?: { suppressErrorToast?: boolean; documento?: string }
  ) => {
    const shouldToast = !options?.suppressErrorToast
    let alreadyNotified = false
    try {
      const response = await getPasswordRecoveryDestinations(identifier, options?.documento)
      if (response.requiresDocument) {
        return response
      }
      if (!response.loaded) {
        alreadyNotified = shouldToast
        if (shouldToast) {
          toast.error(response.reason || 'Não foi possível consultar os números disponíveis.')
        }
        throw new Error(response.reason || 'password_recovery_destinations_failed')
      }
      return response
    } catch (error) {
      console.error('Failed to load password recovery destinations:', error)
      if (!alreadyNotified && shouldToast) {
        const message = error instanceof Error
          ? error.message
            .replace(/^API error:\s*\d+\s*-\s*/i, '')
            .replace(/^API error:\s*\d+\s*/i, '')
            .trim()
          : ''
        toast.error(message || 'Não foi possível consultar os números disponíveis.')
      }
      throw error
    }
  }, [])

  const handlePasswordRecoveryRequest = useCallback(async (
    identifier: string,
    channel: PasswordlessLoginChannel,
    selection?: {
      destinationType?: 'entidade' | 'contato'
      contactId?: number | null
    },
    documento?: string
  ) => {
    let alreadyNotified = false
    try {
      const response = await requestPasswordRecoveryCode(identifier, channel, selection, documento)
      if (response.requiresDocument) {
        return response
      }
      if (!response.sent) {
        alreadyNotified = true
        toast.error(response.reason || 'Não foi possível enviar o código de recuperação.')
        throw new Error(response.reason || 'password_recovery_request_failed')
      }
      toast.success(response.reason || (response.channel === 'email'
        ? 'Código de recuperação enviado por e-mail.'
        : 'Código de recuperação enviado pelo WhatsApp.'))
      return response
    } catch (error) {
      console.error('Failed to request password recovery code:', error)
      if (!alreadyNotified) {
        const message = error instanceof Error
          ? error.message
            .replace(/^API error:\s*\d+\s*-\s*/i, '')
            .replace(/^API error:\s*\d+\s*/i, '')
            .trim()
          : ''
        toast.error(message || 'Não foi possível enviar o código de recuperação.')
      }
      throw error
    }
  }, [])

  const handlePasswordRecoveryVerify = useCallback(async (input: {
    identifier: string
    documento?: string
    code: string
    channel: PasswordlessLoginChannel
    requestId?: string | number | null
  }) => {
    try {
      const response = await verifyPasswordRecoveryCode(input)
      if (response.requiresDocument) {
        return response
      }
      if (!response.verified || !response.resetToken) {
        toast.error(response.reason || 'Não foi possível validar o código de recuperação.')
        return null
      }
      return response
    } catch (error) {
      console.error('Failed to verify password recovery code:', error)
      const message = error instanceof Error
        ? error.message
          .replace(/^API error:\s*\d+\s*-\s*/i, '')
          .replace(/^API error:\s*\d+\s*/i, '')
          .trim()
        : ''
      toast.error(message || 'Não foi possível validar o código de recuperação.')
      return null
    }
  }, [])

  const handlePasswordRecoveryReset = useCallback(async (input: {
    identifier: string
    documento?: string
    resetToken: string
    password: string
  }) => {
    try {
      const response = await resetPasswordWithRecoveryToken(input)
      if (!response.reset) {
        toast.error(response.reason || 'Não foi possível alterar a senha.')
        return false
      }
      toast.success(response.message || response.reason || 'Senha alterada com sucesso. Entre usando a nova senha.')
      return true
    } catch (error) {
      console.error('Failed to reset buyer password:', error)
      const message = error instanceof Error
        ? error.message
          .replace(/^API error:\s*\d+\s*-\s*/i, '')
          .replace(/^API error:\s*\d+\s*/i, '')
          .trim()
        : ''
      toast.error(message || 'Não foi possível alterar a senha.')
      return false
    }
  }, [])

  const handleRegister = async (
    credentials: { loginValue: string; fallbackLoginValue?: string; senha: string },
    options?: { postLoginRedirectOverride?: PostLoginRedirect | null }
  ) => {
    const loginCandidates = [
      credentials.loginValue.trim(),
      credentials.fallbackLoginValue?.trim() || '',
    ].filter((value, index, values) => value && values.indexOf(value) === index)

    let loggedIn = false

    for (const candidate of loginCandidates) {
      loggedIn = await performLogin(candidate, credentials.senha, undefined, {
        suppressSuccessToast: true,
        suppressErrorToast: true,
        postLoginRedirectOverride: options?.postLoginRedirectOverride,
      })

      if (loggedIn) {
        break
      }
    }

    if (!loggedIn) {
      toast.error(t('toasts.login_failed'))
      navigateToView('login')
      return false
    }

    return true
  }

  const handleCheckoutBusinessRegister = async (
    credentials: {
      loginValue: string
      fallbackLoginValue?: string
      senha: string
    },
    context?: RegisterSuccessContext
  ) => {
    const sourceEntidadeId = typeof authenticatedEntidadeId === 'number' && authenticatedEntidadeId > 0
      ? authenticatedEntidadeId
      : effectiveAccountProfile?.entidadeId

    if (!sourceEntidadeId || !Number.isFinite(sourceEntidadeId) || sourceEntidadeId <= 0) {
      throw new Error('Não foi possível identificar o cadastro atual para transferir o carrinho.')
    }

    const targetDocument = normalizeDocumentDigits(context?.documentValue || credentials.loginValue)
    if (targetDocument.length !== 14) {
      throw new Error('Não foi possível identificar o CNPJ do novo cadastro.')
    }

    let targetEntidadeId = typeof context?.createdCustomerId === 'number' && context.createdCustomerId > 0
      ? Math.trunc(context.createdCustomerId)
      : 0

    if (targetEntidadeId <= 0) {
      const createdClient = await checkClienteExists({ cpfCnpj: targetDocument })
      const resolvedCreatedId = Number.parseInt(createdClient.id || '', 10)
      if (!createdClient.exists || !Number.isFinite(resolvedCreatedId) || resolvedCreatedId <= 0) {
        throw new Error('A empresa foi cadastrada, mas não foi possível localizar o novo cadastro para concluir a migração.')
      }
      targetEntidadeId = Math.trunc(resolvedCreatedId)
    }

    if (targetEntidadeId === sourceEntidadeId) {
      throw new Error('O novo cadastro empresarial retornou o mesmo identificador do cadastro atual.')
    }

    await relateClientes({
      id: sourceEntidadeId,
      empresa1Id: sourceEntidadeId,
      empresa2Id: targetEntidadeId,
    })

    await copyCarrinhoBetweenCompradores({
      entidadeId: targetEntidadeId,
      empresaOrigemId: sourceEntidadeId,
      empresaDestinoId: targetEntidadeId,
    })

    const loggedIn = await handleRegister(credentials, {
      postLoginRedirectOverride: { view: 'checkout' },
    })
    if (loggedIn) {
      setShowCheckoutBusinessRegisterDialog(false)
      navigateToCheckout('cart')
      return
    }

    throw new Error('A empresa foi cadastrada, mas não foi possível acessar a nova conta empresarial.')
  }

  const handleCheckoutCustomerProfileUpdate = useCallback((profile: CheckoutCustomerProfile | null) => {
    if (!profile) return

    checkoutCustomerProfileFetchRef.current = { entidadeId: profile.id, status: 'loaded' }

    setAccountProfile((current) => {
      const normalizedDocument = normalizeDocumentDigits(profile.document)
      const nextProfile: AccountProfile = {
        entidadeId: profile.id,
        corporateName: current?.corporateName || profile.corporateName || profile.name || undefined,
        name: current?.name || profile.name || profile.corporateName || undefined,
        email: current?.email || profile.email || undefined,
        cnpj: normalizedDocument || current?.cnpj,
        document: normalizedDocument || current?.document || current?.cnpj,
        activity: current?.activity || profile.activity || undefined,
        stateRegistration: profile.stateRegistration || undefined,
        tipoPessoa: profile.tipoPessoa ?? current?.tipoPessoa ?? null,
        buyerSessionToken: current?.buyerSessionToken,
        buyerUser: current?.buyerUser,
      }

      const didChange =
        current?.entidadeId !== nextProfile.entidadeId
        || current?.corporateName !== nextProfile.corporateName
        || current?.name !== nextProfile.name
        || current?.email !== nextProfile.email
        || current?.cnpj !== nextProfile.cnpj
        || current?.document !== nextProfile.document
        || current?.activity !== nextProfile.activity
        || current?.stateRegistration !== nextProfile.stateRegistration
        || (current?.tipoPessoa ?? null) !== (nextProfile.tipoPessoa ?? null)
        || current?.buyerSessionToken !== nextProfile.buyerSessionToken
        || JSON.stringify(current?.buyerUser ?? null) !== JSON.stringify(nextProfile.buyerUser ?? null)

      return didChange ? nextProfile : current
    })
  }, [setAccountProfile])

  const handleCheckoutComplete = (result?: CheckoutOrderResult | null, completedItemsArg?: CartItem[]) => {
    const completedItems = completedItemsArg ?? currentCheckoutItemsRef.current
    const purchasePayload = {
      pedido_id: result?.pedidoId || undefined,
      numero_pedido: result?.numeroPedido || undefined,
      items_count: completedItems.length,
      quantity_total: completedItems.reduce((sum, item) => sum + item.quantity, 0),
    }
    queueStorefrontAnalyticsEvent({
      eventType: 'purchase',
      pageKind: 'checkout',
      pageKey: 'success',
      pagePath: `${VIEW_PATHS.checkout}/success`,
      entidadeId: getAuthenticatedEntidadeId(),
      payload: routeCheckoutSource === 'cart'
        ? withStorefrontCartAnalyticsPayload(purchasePayload, completedItems)
        : purchasePayload,
    })
    if (routeCheckoutSource === 'cart') {
      trackStorefrontCartState([], 'checkout_complete')
      setCartItems([])
      setCartQuoteOrigin(null)
      cartItemsRef.current = []
      cartItemKeysByProductIdRef.current = new Map()
    }
    if (routeCheckoutSource === 'cart' && effectiveIsLoggedIn && getAuthenticatedEntidadeId()) {
      broadcastCartChange('checkout-complete')
    }
    toast.success(t('toasts.order_success'))
  }

  const handleQuoteCheckoutEmpty = useCallback((quoteId: number) => {
    navigateToQuoteDetails({
      quoteId,
      quoteToken: buildQuoteDetailToken(quoteId),
    })
  }, [navigateToQuoteDetails])

  const handleOrderCheckoutEmpty = useCallback((orderId: number) => {
    navigateToOrderDetails({
      orderId,
      orderToken: buildOrderDetailToken(orderId, String(orderId)),
    })
  }, [navigateToOrderDetails])

  const clearCartContents = useCallback(async (options?: { skipSuccessToast?: boolean }) => {
    if (!isCartAuthenticated) {
      if (isAnonymousQuickCheckoutAvailable) {
        clearQuickCheckoutCartItems(quickCheckoutLojaId)
        setQuickCheckoutCartItems([])
        quickCheckoutCartItemsRef.current = []
        trackStorefrontCartState([], 'cart_cleared')
        if (!options?.skipSuccessToast) {
          toast.success(t('toasts.cart_cleared'))
        }
        return
      }
      redirectToLoginForCart()
      throw new Error('Faça login para limpar o carrinho.')
    }

    const currentItems = cartItemsRef.current
    if (currentItems.length === 0) {
      setCartQuoteOrigin(null)
      return
    }

    try {
      await runCartMutation(async () => {
        await clearCarrinho()
        setCartItems([])
        setCartQuoteOrigin(null)
        cartItemsRef.current = []
        cartItemKeysByProductIdRef.current = new Map()
      })
    } catch (error) {
      console.error('Failed to clear cart:', error)
      throw (
        error instanceof Error
          ? error
          : new Error(t('toasts.cart_clear_error'))
      )
    }
    broadcastCartChange('cleared')
    trackStorefrontCartState([], 'cart_cleared')
    if (!options?.skipSuccessToast) {
      toast.success(t('toasts.cart_cleared'))
    }
  }, [
    broadcastCartChange,
    isAnonymousQuickCheckoutAvailable,
    isCartAuthenticated,
    quickCheckoutLojaId,
    redirectToLoginForCart,
    runCartMutation,
    t,
  ])

  const handleClearCart = async () => {
    try {
      await clearCartContents()
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t('toasts.cart_clear_error')
      )
    }
  }

  const handleCartDrawerOpenChange = (open: boolean) => {
    if (!open) {
      // Ao fechar o drawer, envia imediatamente qualquer sync pendente em vez de
      // esperar o debounce.
      void flushPendingCartSync()
      setCartDrawerSource('default')
    }
    setShowCartDrawer(open)
  }

  const handleProceedToCheckout = async (
    freightSelection: CartFreightSelection | null = null,
    financialSummary: CheckoutSimulationResult | null = null
  ) => {
    if (isInternationalCommerce) {
      toast.info('Para exportación, guarda el carrito como cotización EXW.')
      return
    }

    if (!isCartAuthenticated) {
      if (isAnonymousQuickCheckoutAvailable) {
        handleCartDrawerOpenChange(false)
        navigateToView('quick-checkout')
        return
      }
      redirectToLoginForCart()
      return
    }

    await flushPendingCartSync()
    await hydrateCartFromApi()

    queueStorefrontAnalyticsEvent({
      eventType: 'begin_checkout',
      pageKind: 'checkout',
      pageKey: 'cart',
      pagePath: `${VIEW_PATHS.checkout}/cart`,
      entidadeId: getAuthenticatedEntidadeId(),
      payload: withStorefrontCartAnalyticsPayload({
        items_count: cartItemsRef.current.length,
        quantity_total: cartItemsRef.current.reduce((sum, item) => sum + item.quantity, 0),
      }, cartItemsRef.current),
    })
    // Garante que qualquer alteração de quantidade pendente (debounced) seja
    // enviada antes de o checkout ler o carrinho do servidor.
    handleCartDrawerOpenChange(false)
    navigateToCheckout('cart', {
      cartShippingPrefill: freightSelection,
      cartFinancialPrefill: financialSummary,
    })
  }

  const handleLogout = () => {
    clearAuthenticatedSessionStorage()
    clearAuthenticatedEntidadeId()
    buyerRotatingSearchTermsFetchRef.current = { entidadeId: null, status: 'idle' }
    flushSync(() => {
      setIsLogoutRequested(true)
      setIsLoggedIn(false)
      setAccountProfile(null)
      setAuthenticatedSearchHighlights([])
      setCartItems([])
      setCartQuoteOrigin(null)
      setShowCartDrawer(false)
      setCartDrawerSource('default')
    })
    cartItemsRef.current = []
    cartItemKeysByProductIdRef.current = new Map()
    clearAccountProfileCookies()
    navigateHome()
    toast.info(t('toasts.logged_out'))
  }

  const renderAccountLoginPrompt = (title: string, subtitle: string, targetView: View) => {
    if (!isAuthBootstrapResolved && !isLogoutRequested) {
      return (
        <div className="container mx-auto px-4 py-10">
          <div className="mx-auto max-w-2xl rounded-2xl border border-border/70 bg-white p-6 shadow-sm">
            <Skeleton className="mx-auto h-7 w-40" />
            <Skeleton className="mx-auto mt-4 h-4 w-72 max-w-full" />
            <Skeleton className="mx-auto mt-6 h-10 w-36" />
          </div>
        </div>
      )
    }

    return (
      <div className="container mx-auto px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-border/70 bg-white p-6 text-center shadow-sm">
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
          <p className="mt-5 text-sm text-muted-foreground">{t('cart.login_to_checkout')}</p>
          <Button className="mt-6" onClick={() => openLogin({ view: targetView })}>
            {t('actions.access_account')}
          </Button>
        </div>
      </div>
    )
  }

  const handleAddToCompare = (product: Product) => {
    setCompareProducts((current) => {
      const existing = current || []
      if (existing.find(p => p.id === product.id)) {
        toast.info(t('toasts.compare_already'))
        return existing
      }
      if (existing.length >= 4) {
        toast.error(t('toasts.compare_limit'))
        return existing
      }
      recommendationTracking.trackInteraction(product.id, 'compare', product.category, product.brand)
      toast.success(t('toasts.compare_added'), {
        description: product.name,
      })
      return [...existing, product]
    })
  }

  const handleRemoveFromCompare = (productId: string) => {
    setCompareProducts((current) => {
      const existing = current || []
      return existing.filter(p => p.id !== productId)
    })
    toast.info(t('toasts.compare_removed'))
  }

  const handleClearCompare = () => {
    setCompareProducts([])
    toast.info(t('toasts.compare_cleared'))
  }

  const handleViewCompare = () => {
    navigateToView('compare')
  }

  const totalCartItems = activeCartItems.length

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = i18n.language || 'pt-BR'
    }

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/'
    const currentLanguage = i18n.language || 'pt-BR'
    const locale = resolveSeoLocale(currentLanguage)
    const isSearchMetadataPending = isSearchResultsActive
      && normalizedSearchQuery.length >= 2
      && (isLoadingProducts || productsDataKey !== activeSearchProductsKey)

    if (isBackendUnavailable) {
      applySeoMetadata({
        title: `${t('maintenance.title')} | ${SEO_SITE_NAME}`,
        description: t('maintenance.description'),
        siteName: SEO_SITE_NAME,
        canonicalUrl: currentPath,
        locale,
        ogType: 'website',
        robots: 'noindex,nofollow',
        jsonLd: null,
      })
      return
    }

    const buildProductDescription = (product: Product) => {
      const attributes = [product.brand, product.category, product.subcategory].filter(Boolean)
      const summary = attributes.length > 0
        ? `Produto ${attributes.join(' | ')}.`
        : 'Produto industrial para aplicação profissional.'

      return `${summary} Referência ${product.code || product.manufacturerCode || product.id}. Compre na ${SEO_SITE_NAME}.`
    }

    const resolveProductPrice = (product: Product) => {
      const candidate = product.discountedPrice && product.discountedPrice > 0
        ? product.discountedPrice
        : product.originalPrice
      return typeof candidate === 'number' && candidate > 0 ? candidate : null
    }

    const buildProductsItemList = (
      items: Product[],
      listName: string
    ): Record<string, unknown> | null => {
      if (items.length === 0) return null
      return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: listName,
        itemListElement: items.slice(0, 12).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: toAbsoluteUrl(`/produto/${item.id}/${slugify(item.name || '') || 'produto'}`),
          name: item.name,
        })),
      }
    }

    const organizationJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteConfig?.empresa_nome?.trim() || SEO_SITE_NAME,
      url: toAbsoluteUrl('/'),
      logo: toAbsoluteUrl(SEO_DEFAULT_IMAGE),
      description: SEO_DEFAULT_DESCRIPTION,
      email: siteConfig?.email?.trim() || undefined,
      telephone: siteConfig?.telefone?.trim() || siteConfig?.whatsapp?.trim() || undefined,
      address: siteConfig?.endereco?.trim()
        ? {
            '@type': 'PostalAddress',
            streetAddress: siteConfig.endereco.trim(),
            addressCountry: 'BR',
          }
        : {
            '@type': 'PostalAddress',
            addressLocality: 'Blumenau',
            addressRegion: 'SC',
            addressCountry: 'BR',
          },
    }

    const buildBreadcrumbJsonLd = (
      items: Array<{ name: string; path: string }>
    ): Record<string, unknown> => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: toAbsoluteUrl(item.path),
      })),
    })

    const buildInstitutionalJsonLd = (
      pageType: 'AboutPage' | 'WebPage' | 'CollectionPage',
      name: string,
      path: string,
      description: string
    ): Array<Record<string, unknown>> => ([
      organizationJsonLd,
      buildBreadcrumbJsonLd([
        { name: t('nav.home'), path: '/' },
        { name, path },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': pageType,
        name,
        description,
        url: toAbsoluteUrl(path),
        isPartOf: {
          '@type': 'WebSite',
          name: SEO_SITE_NAME,
          url: toAbsoluteUrl('/'),
        },
      },
    ])

    let metadata: SeoMetadata = {
      title: SEO_DEFAULT_TITLE,
      description: SEO_DEFAULT_DESCRIPTION,
      siteName: SEO_SITE_NAME,
      canonicalUrl: currentPath,
      imageUrl: SEO_DEFAULT_IMAGE,
      imageAlt: SEO_DEFAULT_IMAGE_ALT,
      locale,
      ogType: 'website',
      robots: 'index,follow',
      jsonLd: [organizationJsonLd],
    }

    switch (currentView) {
      case 'home':
        metadata = {
          ...metadata,
          title: SEO_DEFAULT_TITLE,
          description: SEO_DEFAULT_DESCRIPTION,
          canonicalUrl: '/',
          robots: 'index,follow',
          jsonLd: [
            organizationJsonLd,
            {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: SEO_SITE_NAME,
              url: toAbsoluteUrl('/'),
              potentialAction: {
                '@type': 'SearchAction',
                target: `${toAbsoluteUrl('/buscar')}?q={search_term_string}`,
                'query-input': 'required name=search_term_string',
              },
            },
          ],
        }
        break
      case 'products': {
        if (isSearchResultsActive && normalizedSearchQuery.length >= 2) {
          metadata = {
            ...metadata,
            title: isSearchMetadataPending
              ? `Resultados para "${normalizedSearchQuery}" | ${SEO_SITE_NAME}`
              : `${allProducts.length} resultados para "${normalizedSearchQuery}" | ${SEO_SITE_NAME}`,
            description: `Resultados de busca para "${normalizedSearchQuery}" na ${SEO_SITE_NAME}. Encontre produtos industriais e materiais elétricos para sua empresa.`,
            canonicalUrl: `${VIEW_PATHS.products}?q=${encodeURIComponent(normalizedSearchQuery)}`,
            robots: 'noindex,follow',
            jsonLd: null,
          }
        } else if (selectedCategoryName) {
          metadata = {
            ...metadata,
            title: `${selectedCategoryName} | ${SEO_SITE_NAME}`,
            description: `Compre ${selectedCategoryName.toLocaleLowerCase('pt-BR')} para uso profissional e industrial na ${SEO_SITE_NAME}.`,
            canonicalUrl: currentPath,
            robots: 'index,follow',
            jsonLd: buildProductsItemList(allProducts, selectedCategoryName),
          }
        } else {
          metadata = {
            ...metadata,
            title: selectedSingleManufacturerBrandName
              ? `${selectedSingleManufacturerBrandName} | ${SEO_SITE_NAME}`
              : `Resultado da busca | ${SEO_SITE_NAME}`,
            description: selectedSingleManufacturerBrandName
              ? `Confira produtos da marca ${selectedSingleManufacturerBrandName} na ${SEO_SITE_NAME}.`
              : `Catálogo completo de produtos industriais, automação e materiais elétricos para empresas na ${SEO_SITE_NAME}.`,
            canonicalUrl: currentPath,
            robots: 'index,follow',
            jsonLd: buildProductsItemList(
              allProducts,
              selectedSingleManufacturerBrandName || 'Resultado da busca'
            ),
          }
        }
        break
      }
      case 'launches':
        metadata = {
          ...metadata,
          title: `${t('header.quick_links.launches')} | ${SEO_SITE_NAME}`,
          description: `Confira os ${t('header.quick_links.launches').toLocaleLowerCase('pt-BR')} em materiais elétricos e automação para empresas na ${SEO_SITE_NAME}.`,
          canonicalUrl: VIEW_PATHS.launches,
          robots: 'index,follow',
          jsonLd: buildProductsItemList(allProducts, t('header.quick_links.launches')),
        }
        break
      case 'just_arrived':
        metadata = {
          ...metadata,
          title: `${t('header.quick_links.just_arrived')} | ${SEO_SITE_NAME}`,
          description: `Confira o que ${t('header.quick_links.just_arrived').toLocaleLowerCase('pt-BR')} em materiais elétricos e automação para empresas na ${SEO_SITE_NAME}.`,
          canonicalUrl: VIEW_PATHS.just_arrived,
          robots: 'index,follow',
          jsonLd: buildProductsItemList(allProducts, t('header.quick_links.just_arrived')),
        }
        break
      case 'product-detail': {
        const seoProduct = displayedProductDetail ?? selectedProduct
        if (seoProduct) {
          const price = resolveProductPrice(seoProduct)
          const canonicalPath = typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : buildProductHref(
                {
                  id: routeProductId ?? seoProduct.id,
                  name: seoProduct.name,
                },
                {
                  parentContextId: routeProductParentContextId,
                },
              )
          const productImages = (seoProduct.images && seoProduct.images.length > 0
            ? seoProduct.images
            : [seoProduct.image]).filter(Boolean)

          metadata = {
            ...metadata,
            title: `${seoProduct.name} | ${SEO_SITE_NAME}`,
            description: buildProductDescription(seoProduct),
            canonicalUrl: canonicalPath,
            imageUrl: productImages[0],
            imageAlt: seoProduct.name,
            ogType: 'product',
            robots: 'index,follow',
            jsonLd: {
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: seoProduct.name,
              image: productImages.map((image) => toAbsoluteUrl(image)),
              sku: seoProduct.code || seoProduct.id,
              mpn: seoProduct.manufacturerCode || undefined,
              brand: seoProduct.brand
                ? {
                    '@type': 'Brand',
                    name: seoProduct.brand,
                  }
                : undefined,
              category: seoProduct.category || undefined,
              offers: price
                ? {
                    '@type': 'Offer',
                    priceCurrency: 'BRL',
                    price: price.toFixed(2),
                    availability: seoProduct.inStock
                      ? 'https://schema.org/InStock'
                      : 'https://schema.org/OutOfStock',
                    url: toAbsoluteUrl(canonicalPath),
                  }
                : undefined,
            },
          }
        } else {
          metadata = {
            ...metadata,
            title: `Produto | ${SEO_SITE_NAME}`,
            description: 'Detalhes do produto industrial.',
            canonicalUrl: currentPath,
            robots: 'noindex,follow',
            jsonLd: null,
          }
        }
        break
      }
      case 'checkout':
        metadata = {
          ...metadata,
          title: `Checkout | ${SEO_SITE_NAME}`,
          description: 'Finalize seu pedido com segurança.',
          canonicalUrl: buildCheckoutPath(checkoutRouteStep),
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'checkout-tracking':
        metadata = {
          ...metadata,
          title: `Acompanhamento do pedido | ${SEO_SITE_NAME}`,
          description: 'Acompanhe as etapas e o rastreamento do seu pedido.',
          canonicalUrl: currentPath,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'quick-checkout':
        metadata = {
          ...metadata,
          title: `Finalizar compra | ${SEO_SITE_NAME}`,
          description: 'Finalize seu pedido com segurança.',
          canonicalUrl: VIEW_PATHS['quick-checkout'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'access':
        metadata = {
          ...metadata,
          title: `Acessando conta | ${SEO_SITE_NAME}`,
          description: 'Validando acesso automático à conta.',
          canonicalUrl: currentPath,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'redirect-checkout':
        metadata = {
          ...metadata,
          title: `Redirecionando checkout | ${SEO_SITE_NAME}`,
          description: 'Validando acesso automático e preparando a confirmação da compra.',
          canonicalUrl: VIEW_PATHS['redirect-checkout'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'qrcode':
        metadata = {
          ...metadata,
          title: `Dados | ${SEO_SITE_NAME}`,
          description: 'Resultado decodificado.',
          canonicalUrl: VIEW_PATHS.qrcode,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'nfe-qr':
        metadata = {
          ...metadata,
          title: `Documentos da nota fiscal | ${SEO_SITE_NAME}`,
          description: 'Envio seguro dos documentos da nota fiscal para o e-mail cadastrado.',
          canonicalUrl: VIEW_PATHS['nfe-qr'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'login':
        metadata = {
          ...metadata,
          title: `Entrar | ${SEO_SITE_NAME}`,
          description: 'Acesse sua conta B2B.',
          canonicalUrl: VIEW_PATHS.login,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'register':
        metadata = {
          ...metadata,
          title: `Criar conta | ${SEO_SITE_NAME}`,
          description: 'Cadastre sua empresa e comece a comprar no atacado.',
          canonicalUrl: VIEW_PATHS.register,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'quotes':
        metadata = {
          ...metadata,
          title: routeQuoteDetailId ? `Detalhes do orçamento | ${SEO_SITE_NAME}` : `Orçamentos | ${SEO_SITE_NAME}`,
          description: routeQuoteDetailId ? 'Consulte todos os itens e condições do orçamento.' : 'Área de orçamentos.',
          canonicalUrl: routeQuoteDetailToken ? buildQuoteDetailPathFromToken(routeQuoteDetailToken) : VIEW_PATHS.quotes,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'public-ticket':
        metadata = {
          ...metadata,
          title: `Atendimento | ${SEO_SITE_NAME}`,
          description: 'Acompanhe e responda seu atendimento.',
          canonicalUrl: routePublicTicketCode
            ? `${VIEW_PATHS['public-ticket']}/${encodeURIComponent(routePublicTicketCode)}`
            : VIEW_PATHS['public-ticket'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'attendances':
        metadata = {
          ...metadata,
          title: routeAttendanceDetailId ? `Detalhes do atendimento | ${SEO_SITE_NAME}` : `Atendimentos | ${SEO_SITE_NAME}`,
          description: routeAttendanceDetailId ? 'Consulte o histórico completo do atendimento e os anexos vinculados.' : 'Acompanhe protocolos, histórico e anexos do atendimento da sua conta.',
          canonicalUrl: routeAttendanceDetailToken ? buildAttendanceDetailPathFromToken(routeAttendanceDetailToken) : VIEW_PATHS.attendances,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'dashboard':
        metadata = {
          ...metadata,
          title: `Minha conta | ${SEO_SITE_NAME}`,
          description: 'Área do cliente.',
          canonicalUrl: VIEW_PATHS.dashboard,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'profile':
        metadata = {
          ...metadata,
          title: `Dados cadastrais | ${SEO_SITE_NAME}`,
          description: 'Atualize as informações da empresa e da conta.',
          canonicalUrl: VIEW_PATHS.profile,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'change-password':
        metadata = {
          ...metadata,
          title: `Alterar senha | ${SEO_SITE_NAME}`,
          description: 'Atualize a senha da sua conta.',
          canonicalUrl: VIEW_PATHS['change-password'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'business':
      case 'clients':
        metadata = {
          ...metadata,
          title: `Meu Negócio | ${SEO_SITE_NAME}`,
          description: 'Gerencie seus clientes e acompanhe suas comissões.',
          canonicalUrl: VIEW_PATHS.business,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'finance':
        metadata = {
          ...metadata,
          title: `Financeiro | ${SEO_SITE_NAME}`,
          description: 'Boletos e cobranças da sua conta.',
          canonicalUrl: VIEW_PATHS.finance,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'benefits':
        metadata = {
          ...metadata,
          title: `Meus benefícios | ${SEO_SITE_NAME}`,
          description: 'Condições comerciais e recursos liberados para a sua conta.',
          canonicalUrl: VIEW_PATHS.benefits,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'cashback':
        metadata = {
          ...metadata,
          title: `Cashback | ${SEO_SITE_NAME}`,
          description: 'Consulte o saldo, validade e histórico do seu cashback.',
          canonicalUrl: VIEW_PATHS.cashback,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'addresses':
        metadata = {
          ...metadata,
          title: `Meus endereços | ${SEO_SITE_NAME}`,
          description: 'Consulte os endereços de entrega e faturamento vinculados à sua conta.',
          canonicalUrl: VIEW_PATHS.addresses,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'favorites':
        metadata = {
          ...metadata,
          title: `Favoritos | ${SEO_SITE_NAME}`,
          description: 'Produtos salvos para consulta e recompra.',
          canonicalUrl: VIEW_PATHS.favorites,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'orders':
        metadata = {
          ...metadata,
          title: routeOrderDetailId ? `Detalhes do pedido | ${SEO_SITE_NAME}` : `Pedidos | ${SEO_SITE_NAME}`,
          description: routeOrderDetailId ? 'Consulte todos os itens e dados do pedido.' : 'Acompanhe seus pedidos.',
          canonicalUrl: routeOrderDetailToken ? buildOrderDetailPathFromToken(routeOrderDetailToken) : VIEW_PATHS.orders,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'tracking':
        metadata = {
          ...metadata,
          title: `Rastreamento de pedido | ${SEO_SITE_NAME}`,
          description: 'Consulte o andamento do pedido informando número e CPF/CNPJ.',
          canonicalUrl: VIEW_PATHS.tracking,
          robots: 'index,follow',
          jsonLd: null,
        }
        break
      case 'price-table':
        metadata = {
          ...metadata,
          title: `${effectiveIsLoggedIn ? 'Tabela de Preços' : 'Catálogo de Produtos'} | ${SEO_SITE_NAME}`,
          description: effectiveIsLoggedIn
            ? 'Consulte a tabela de preços da sua conta com carregamento contínuo por grupos e páginas.'
            : 'Navegue pelo catálogo de produtos com carregamento contínuo e sem exibição de preços.',
          canonicalUrl: VIEW_PATHS['price-table'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'price-table-margin':
        metadata = {
          ...metadata,
          title: `Tabela de Preços com Margem | ${SEO_SITE_NAME}`,
          description: 'Aplique uma margem personalizada e consulte a tabela de preços pronta para impressão.',
          canonicalUrl: VIEW_PATHS['price-table-margin'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'store-network-marketplace':
      case 'store-network-access':
        metadata = {
          ...metadata,
          title: `Marketplace | ${SEO_SITE_NAME}`,
          description: 'Catálogo das lojas conectadas.',
          canonicalUrl: currentView === 'store-network-access'
            ? VIEW_PATHS['store-network-access']
            : VIEW_PATHS['store-network-marketplace'],
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'affiliates':
        metadata = {
          ...metadata,
          title: `Afiliados | ${SEO_SITE_NAME}`,
          description: 'Acompanhe sua participação no programa de afiliados.',
          canonicalUrl: VIEW_PATHS.affiliates,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'indicator':
        metadata = {
          ...metadata,
          title: `Portal do Indicador | ${SEO_SITE_NAME}`,
          description: 'Acompanhe clientes, orçamentos, performance e comissões do portal de indicador.',
          canonicalUrl: VIEW_PATHS.indicator,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'returns':
        metadata = {
          ...metadata,
          title: `Devoluções | ${SEO_SITE_NAME}`,
          description: 'Solicitações de troca e devolução.',
          canonicalUrl: VIEW_PATHS.returns,
          robots: 'noindex,nofollow',
          jsonLd: null,
        }
        break
      case 'return-request': {
        const returnRequestCanonicalUrl = buildReturnRequestPath(
          returnRequestRouteStep,
          returnRequestRouteFlowType
        )
        metadata = {
          ...metadata,
          title: `Devolução e Conserto | ${SEO_SITE_NAME}`,
          description: 'Abra solicitações de devolução e remessa para conserto com validação por pedido ou nota fiscal.',
          canonicalUrl: returnRequestCanonicalUrl,
          robots: 'index,follow',
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `Portal de Devolução ${SEO_SITE_NAME}`,
            url: toAbsoluteUrl(returnRequestCanonicalUrl),
          },
        }
        break
      }
      case 'support': {
        const supportCanonicalUrl = routeSupportSubjectSlug
          ? `${VIEW_PATHS.support}/${encodeURIComponent(routeSupportSubjectSlug)}`
          : VIEW_PATHS.support
        metadata = {
          ...metadata,
          title: `Atendimento | ${SEO_SITE_NAME}`,
          description: 'Central de atendimento para clientes B2B.',
          canonicalUrl: supportCanonicalUrl,
          robots: 'index,follow',
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: `Atendimento ${SEO_SITE_NAME}`,
            url: toAbsoluteUrl(supportCanonicalUrl),
          },
        }
        break
      }
      case 'service-evaluation':
        metadata = {
          ...metadata,
          title: `${t('serviceEvaluation.title')} | ${SEO_SITE_NAME}`,
          description: t('serviceEvaluation.description'),
          canonicalUrl: VIEW_PATHS['service-evaluation'],
          robots: 'index,follow',
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: t('serviceEvaluation.title'),
            description: t('serviceEvaluation.description'),
            url: toAbsoluteUrl(VIEW_PATHS['service-evaluation']),
          },
        }
        break
      case 'whatsapp':
        metadata = {
          ...metadata,
          title: `Atendimento | ${SEO_SITE_NAME}`,
          description: 'Escolha o melhor canal de atendimento para compra, suporte técnico ou financeiro.',
          canonicalUrl: VIEW_PATHS.whatsapp,
          robots: 'index,follow',
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: `Atendimento ${SEO_SITE_NAME}`,
            url: toAbsoluteUrl(VIEW_PATHS.whatsapp),
          },
        }
        break
      case 'about':
        metadata = {
          ...metadata,
          title: `Quem somos | ${SEO_SITE_NAME}`,
          description: t('institutional.about.seo_description'),
          canonicalUrl: VIEW_PATHS.about,
          imageAlt: t('institutional.about.title'),
          robots: 'index,follow',
          jsonLd: buildInstitutionalJsonLd(
            'AboutPage',
            t('institutional.about.title'),
            VIEW_PATHS.about,
            t('institutional.about.seo_description')
          ),
        }
        break
      case 'privacy':
        metadata = {
          ...metadata,
          title: `Política de privacidade | ${SEO_SITE_NAME}`,
          description: institutionalSeoDescriptions.privacy,
          canonicalUrl: VIEW_PATHS.privacy,
          imageAlt: t('institutional.privacy.title'),
          robots: 'index,follow',
          jsonLd: buildInstitutionalJsonLd(
            'WebPage',
            t('institutional.privacy.title'),
            VIEW_PATHS.privacy,
            institutionalSeoDescriptions.privacy
          ),
        }
        break
      case 'terms':
        metadata = {
          ...metadata,
          title: `Termos de uso | ${SEO_SITE_NAME}`,
          description: t('institutional.terms.seo_description'),
          canonicalUrl: VIEW_PATHS.terms,
          imageAlt: t('institutional.terms.title'),
          robots: 'index,follow',
          jsonLd: buildInstitutionalJsonLd(
            'WebPage',
            t('institutional.terms.title'),
            VIEW_PATHS.terms,
            t('institutional.terms.seo_description')
          ),
        }
        break
      case 'careers':
        metadata = {
          ...metadata,
          title: `Trabalhe conosco | ${SEO_SITE_NAME}`,
          description: institutionalSeoDescriptions.careers,
          canonicalUrl: VIEW_PATHS.careers,
          imageAlt: t('institutional.careers.title'),
          robots: 'index,follow',
          jsonLd: buildInstitutionalJsonLd(
            'CollectionPage',
            t('institutional.careers.title'),
            VIEW_PATHS.careers,
            institutionalSeoDescriptions.careers
          ),
        }
        break
      case 'returns-policy':
        metadata = {
          ...metadata,
          title: `Políticas de devoluções | ${SEO_SITE_NAME}`,
          description: institutionalSeoDescriptions.returnsPolicy,
          canonicalUrl: VIEW_PATHS['returns-policy'],
          imageAlt: t('institutional.returnsPolicy.title'),
          robots: 'index,follow',
          jsonLd: buildInstitutionalJsonLd(
            'WebPage',
            t('institutional.returnsPolicy.title'),
            VIEW_PATHS['returns-policy'],
            institutionalSeoDescriptions.returnsPolicy
          ),
        }
        break
      case 'blog':
        metadata = {
          ...metadata,
          title: `Comunidade da Brasiltec | ${SEO_SITE_NAME}`,
          description: 'Portal editorial com artigos técnicos, tendências e ferramentas para engenharia elétrica.',
          canonicalUrl: VIEW_PATHS.blog,
          imageAlt: 'Comunidade da Brasiltec',
          robots: 'index,follow',
          jsonLd: buildInstitutionalJsonLd(
            'Blog',
            `Comunidade da Brasiltec | ${SEO_SITE_NAME}`,
            VIEW_PATHS.blog,
            'Portal editorial com artigos técnicos, tendências e ferramentas para engenharia elétrica.'
          ),
        }
        break
      case 'compare':
        metadata = {
          ...metadata,
          title: `Comparar produtos | ${SEO_SITE_NAME}`,
          description: 'Compare produtos selecionados para decisão de compra.',
          canonicalUrl: VIEW_PATHS.compare,
          robots: 'noindex,follow',
          jsonLd: null,
        }
        break
      default:
        break
    }

    applySeoMetadata(metadata)
  }, [
    SEO_DEFAULT_DESCRIPTION,
    SEO_DEFAULT_IMAGE,
    SEO_DEFAULT_IMAGE_ALT,
    SEO_DEFAULT_TITLE,
    SEO_SITE_NAME,
    allProducts,
    activeSearchProductsKey,
    isBackendUnavailable,
    isLoadingProducts,
    currentView,
    checkoutRouteStep,
    i18n.language,
    isSearchResultsActive,
    productsDataKey,
    routeOrderDetailId,
    routeOrderDetailToken,
    routePublicTicketCode,
    routeQuoteDetailId,
    routeQuoteDetailToken,
    returnRequestRouteStep,
    returnRequestRouteFlowType,
    routeSupportSubjectSlug,
    searchQuery,
    selectedCategoryName,
    selectedSingleManufacturerBrandName,
    displayedProductDetail,
    selectedProduct,
    siteConfig?.email,
    siteConfig?.empresa_nome,
    siteConfig?.endereco,
    siteConfig?.telefone,
    siteConfig?.whatsapp,
    t,
  ])

  const isStandalonePriceTable = currentView === 'price-table' || currentView === 'price-table-margin'
  const isPublicTicketView = currentView === 'public-ticket'
  const priceTableCartPanelViewKey = isStandalonePriceTable
    ? `${currentView}:${authenticatedEntidadeId ?? effectiveAccountProfile?.entidadeId ?? 0}`
    : ''

  useEffect(() => {
    if (priceTableCartPanelViewKeyRef.current === priceTableCartPanelViewKey) return

    priceTableCartPanelViewKeyRef.current = priceTableCartPanelViewKey
    setShowPriceTableCartPanel(false)
    setIsPriceTableCartPanelCollapsed(true)
    setIsPriceTableCatalogReady(false)
  }, [priceTableCartPanelViewKey])

  useEffect(() => {
    if (!priceTableCartPanelViewKey || !effectiveIsLoggedIn || !isPriceTableCatalogReady) {
      setShowPriceTableCartPanel(false)
      setIsPriceTableCartPanelCollapsed(true)
      return
    }

    if (isMobileViewport) {
      setShowPriceTableCartPanel(false)
      setIsPriceTableCartPanelCollapsed(true)
      return
    }

    if (showPriceTableCartPanel) return

    const timerId = window.setTimeout(() => {
      setCartDrawerSource('price-table')
      setShowPriceTableCartPanel(true)
      setIsPriceTableCartPanelCollapsed(true)
    }, PRICE_TABLE_CART_PANEL_REVEAL_DELAY_MS)

    return () => window.clearTimeout(timerId)
  }, [
    effectiveIsLoggedIn,
    isMobileViewport,
    isPriceTableCatalogReady,
    priceTableCartPanelViewKey,
    showPriceTableCartPanel,
  ])

  const handlePriceTableCatalogReadyChange = useCallback((ready: boolean) => {
    setIsPriceTableCatalogReady(ready)
  }, [])

  const shouldShowWideProductCartPanel =
    currentView === 'product-detail'
    && hasWideProductCartSpace

  useEffect(() => {
    if (shouldShowWideProductCartPanel && showCartDrawer) {
      setShowCartDrawer(false)
    }
  }, [shouldShowWideProductCartPanel, showCartDrawer])

  if (isBackendUnavailable && !hasPendingCheckoutAttempt && currentView !== 'qrcode') {
    return (
      <MaintenanceView
        onRetry={() => window.location.reload()}
      />
    )
  }

  if (currentView === 'qrcode') {
    return <QrCodeDataResultView />
  }

  if (currentView === 'nfe-qr') {
    return (
      <NfeQrEmailView
        companyName={siteConfig?.empresa_nome || siteConfig?.nome}
        logoUrl={siteConfig?.logo_url}
        lojaId={siteConfig?.loja_id}
      />
    )
  }

  const shouldShowPriceTableCartPanel =
    isStandalonePriceTable && effectiveIsLoggedIn && isPriceTableCatalogReady && showPriceTableCartPanel && !isMobileViewport
  const shouldExpandPriceTableCartPanel =
    shouldShowPriceTableCartPanel && !isPriceTableCartPanelCollapsed
  const shouldShowCartSheet = showCartDrawer
    && !isPublicTicketView
    && (!isStandalonePriceTable || isMobileViewport)
    && !shouldShowWideProductCartPanel
  const isCartDrawerPriceTableMode = cartDrawerSource === 'price-table'
  const shouldShowPriceTableMobileCartTrigger =
    isStandalonePriceTable && effectiveIsLoggedIn && isPriceTableCatalogReady && isMobileViewport
  const shouldUseCheckoutHeader =
    currentView === 'checkout' || currentView === 'quick-checkout' || currentView === 'checkout-tracking'
  const shouldWrapPrimaryContentInMain =
    !isStandalonePriceTable &&
    !isPublicTicketView &&
    currentView !== 'support' &&
    currentView !== 'tracking' &&
    currentView !== 'blog'

  return (
    <PricingProvider value={pricingContextValue}>
      <div id="spark-app" className="min-h-screen bg-background flex flex-col">
        {shouldUseCheckoutHeader ? (
          <Suspense fallback={<CheckoutHeaderSkeleton />}>
            <CheckoutHeader
              onHomeClick={navigateHome}
              stepInfo={currentView === 'checkout-tracking' ? null : checkoutStepInfo}
            />
          </Suspense>
        ) : !isStandalonePriceTable && !isPublicTicketView ? (
          <Header
            cartCount={totalCartItems}
            isLoggedIn={effectiveIsLoggedIn}
            suppressGuestSearchHighlights={shouldSuppressGuestSearchHighlights}
            priceTableHref={resolvedPriceTableHref}
            canAccessIndicatorPortal={canAccessIndicatorPortal && !indicatorDelegation}
            canAccessBusiness={canShowBusiness}
            canAccessAffiliates={canAccessAffiliates}
            canAccessMarketplace={hasMarketplaceBenefit}
            storeNetworkMarketplaceEnabled={Boolean(siteConfig?.rede_lojas_menu_ativo)}
            storeNetworkOriginGlobalStoreId={siteConfig?.global_store_id}
            accountNotifications={accountNotifications}
            authenticatedSearchHighlights={authenticatedSearchHighlights}
            accountProfile={effectiveAccountProfile}
            indicatorDelegation={indicatorDelegation ? {
              indicatorId: indicatorDelegation.principalProfile.entidadeId,
              indicatorName: indicatorDelegation.principalProfile.corporateName || indicatorDelegation.principalProfile.name || 'Indicador',
              indicatorDocument: indicatorDelegation.principalProfile.document || indicatorDelegation.principalProfile.cnpj || '',
              clientName: indicatorDelegation.client.name,
              clientDocument: indicatorDelegation.client.document,
              authorized: indicatorDelegation.authorized,
            } : null}
            activeSearchQuery={currentView === 'products' && !isImageSearchActive ? searchQuery : ''}
            activeVehicleFilter={activeVehicleFilter}
            currentView={currentView}
            navigationKey={headerNavigationKey}
            categories={mainMenuCategories}
            isMenuLoading={isMenuLoading}
            isImageSearchLoading={isImageSearchLoading}
            isImageSearchActive={isImageSearchActive}
            onSearchChange={handleSearchChange}
            onVehicleSearch={handleVehicleSearch}
            onImageSearch={handleImageSearch}
            aiSearchLojaId={siteConfig?.loja_id ?? null}
            onAiAddToCart={(product, searchResultContext) => {
              void handleAddToCart(product, 1, searchResultContext)
            }}
            onProductClick={handleProductClick}
            onLoginClick={handleLoginClick}
            onCartClick={handleCartDrawerOpen}
            onCartPrefetch={prefetchCartDrawer}
            onCategoriesClick={() => setShowCategorySidebar(true)}
            onCategoriesPrefetch={() => {
              void ensureMenuLoaded()
            }}
            onCategorySelect={handleCategorySelect}
            onViewChange={handleRequestedViewChange}
            onLogout={handleLogout}
            onEndIndicatorDelegation={() => endIndicatorClientSession('business')}
            initialVehicleSearchMode={vehicleSearchModeFromRoute ?? 'plate'}
          />
        ) : null}

        <AppPrimaryContent enabled={shouldWrapPrimaryContentInMain}>
        {currentView === 'public-ticket' && (
          <Suspense fallback={<PageGridSkeleton cards={2} />}>
            <PublicTicketPage ticketCode={routePublicTicketCode} />
          </Suspense>
        )}

        {currentView === 'home' && (
          <HomeView
            categories={visibleHomeCategories}
            featuredProducts={homeProducts}
            featuredProductsView={homeFeaturedProductsView}
            justArrivedProducts={homeJustArrivedProducts}
            onCategoryClick={handleCategorySelect}
            onProductClick={handleProductClick}
            onAddToCart={(product) => handleAddToCart(product, 1)}
            onManufacturerBrandClick={(brand) => handleHomeManufacturerBrandSelect({
              id: brand.id,
              name: brand.name,
              logoUrl: brand.logoUrl,
            })}
            onViewChange={(view) => handleRequestedViewChange(view)}
            isLoggedIn={effectiveIsLoggedIn}
            allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
            recommendationEntidadeId={authenticatedEntidadeId}
            recommendationRefreshKey={homeRecommendationRefreshKey}
            isProductsLoading={isLoadingProducts}
            isCategoriesLoading={visibleHomeCategories.length === 0 && (isHomeMenuLoading || (isMenuLoading && !hasLoadedMenuOnce && mainMenuCategories.length === 0))}
            deferredSectionsReady={isHomeDeferredContentReady}
          />
        )}

        {currentView === 'products' && (
          <Suspense fallback={null}>
            {isAnySearchResultsActive ? (
              <SearchResultsView
                products={allProducts}
                totalResults={isTextSearchServerPaginated ? serverPaginatedProductsTotal : undefined}
                selectedCategory={selectedCategory}
                categories={mainMenuCategories}
                searchQuery={isImageSearchActive ? '' : searchQuery}
                listingTitleOverride={isImageSearchActive ? t('header.search.image_results_title') : undefined}
                listingMessage={isImageSearchActive ? imageSearchState?.message : null}
                viewStateKey={currentProductsViewStateKey}
                restoreViewState={shouldRestoreCurrentProductsViewState}
                breadcrumbSegments={buildProductsBreadcrumbSegments()}
                onProductClick={handleProductClick}
                onAddToCart={(product, searchResultContext) => handleAddToCart(product, 1, searchResultContext)}
                onClearCategory={() => {
                  setImageSearchState(null)
                  setSelectedCategory(undefined)
                  clearCategoryBreadcrumb()
                  setCategoryRouteSlugs([])
                }}
                onHomeClick={navigateHome}
                isLoggedIn={effectiveIsLoggedIn}
                allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
                isLoading={isSearchResultsLoading}
                hasMoreProducts={isTextSearchServerPaginated ? hasMoreProducts : false}
                isLoadingMoreProducts={isTextSearchServerPaginated ? isLoadingMoreProducts : false}
                onLoadMoreProducts={isTextSearchServerPaginated ? loadMoreServerPaginatedProducts : undefined}
                onRestoreComplete={handleProductsViewStateRestoreComplete}
                manufacturerBrandFilterEnabled={showManufacturerBrandFilter && !isImageSearchActive}
                selectedManufacturerBrands={selectedManufacturerBrands}
                onManufacturerBrandSelect={handleManufacturerBrandFilterSelect}
                onManufacturerBrandClear={handleManufacturerBrandFilterClear}
                onlyAvailableFilter={onlyAvailableFilter}
                onOnlyAvailableFilterChange={isImageSearchActive ? undefined : handleOnlyAvailableFilterChange}
                activeVehicleFilter={activeVehicleFilter}
                onVehicleSearch={handleVehicleSearch}
                onVehicleFilterClear={handleVehicleFilterClear}
                vehicleSearchEnabled={!isImageSearchActive}
                initialVehicleSearchMode={vehicleSearchModeFromRoute ?? 'plate'}
                searchAnalyticsContext={activeSearchAnalyticsContext}
              />
            ) : (
              <ProductsView
                products={allProducts}
                totalResults={isCatalogServerPaginated ? serverPaginatedProductsTotal : undefined}
                selectedCategory={selectedCategory}
                categories={mainMenuCategories}
                disableGrouping={Boolean(selectedCategory)}
                viewStateKey={currentProductsViewStateKey}
                restoreViewState={shouldRestoreCurrentProductsViewState}
                breadcrumbSegments={buildProductsBreadcrumbSegments()}
                onProductClick={handleProductClick}
                onAddToCart={(product) => handleAddToCart(product, 1)}
                onClearCategory={() => {
                  setImageSearchState(null)
                  setSelectedCategory(undefined)
                  clearCategoryBreadcrumb()
                  setCategoryRouteSlugs([])
                }}
                onHomeClick={navigateHome}
                isLoggedIn={effectiveIsLoggedIn}
                allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
                isLoading={isLoadingProducts}
                prioritizeAvailableProducts
                hasMoreProducts={isCatalogServerPaginated ? hasMoreProducts : false}
                isLoadingMoreProducts={isCatalogServerPaginated ? isLoadingMoreProducts : false}
                onLoadMoreProducts={isCatalogServerPaginated ? loadMoreServerPaginatedProducts : undefined}
                onRestoreComplete={handleProductsViewStateRestoreComplete}
                manufacturerBrandFilterEnabled={showManufacturerBrandFilter}
                selectedManufacturerBrands={selectedManufacturerBrands}
                onManufacturerBrandSelect={handleManufacturerBrandFilterSelect}
                onManufacturerBrandClear={handleManufacturerBrandFilterClear}
                onlyAvailableFilter={onlyAvailableFilter}
                onOnlyAvailableFilterChange={handleOnlyAvailableFilterChange}
                activeVehicleFilter={activeVehicleFilter}
                onVehicleFilterClear={handleVehicleFilterClear}
              />
            )}
          </Suspense>
        )}

        {currentView === 'launches' && (
          <Suspense fallback={null}>
            <ProductsView
              products={allProducts}
              totalResults={serverPaginatedProductsTotal}
              categories={mainMenuCategories}
              viewStateKey={currentProductsViewStateKey}
              restoreViewState={shouldRestoreCurrentProductsViewState}
              prioritizeAvailableProducts
              breadcrumbSegments={buildLaunchesBreadcrumbSegments()}
              hasMoreProducts={hasMoreProducts}
              isLoadingMoreProducts={isLoadingMoreProducts}
              onProductClick={handleProductClick}
              onAddToCart={(product) => handleAddToCart(product, 1)}
              onClearCategory={() => {
                setSelectedCategory(undefined)
                clearCategoryBreadcrumb()
                setCategoryRouteSlugs([])
              }}
              onLoadMoreProducts={loadMoreServerPaginatedProducts}
              onHomeClick={navigateHome}
              isLoggedIn={effectiveIsLoggedIn}
              allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
              isLoading={isLoadingProducts}
              onRestoreComplete={handleProductsViewStateRestoreComplete}
            />
          </Suspense>
        )}

        {currentView === 'just_arrived' && (
          <Suspense fallback={null}>
            <ProductsView
              products={allProducts}
              totalResults={serverPaginatedProductsTotal}
              categories={mainMenuCategories}
              viewStateKey={currentProductsViewStateKey}
              restoreViewState={shouldRestoreCurrentProductsViewState}
              prioritizeAvailableProducts
              breadcrumbSegments={buildJustArrivedBreadcrumbSegments()}
              hasMoreProducts={hasMoreProducts}
              isLoadingMoreProducts={isLoadingMoreProducts}
              onProductClick={handleProductClick}
              onAddToCart={(product) => handleAddToCart(product, 1)}
              onClearCategory={() => {
                setSelectedCategory(undefined)
                clearCategoryBreadcrumb()
                setCategoryRouteSlugs([])
              }}
              onLoadMoreProducts={loadMoreServerPaginatedProducts}
              onHomeClick={navigateHome}
              isLoggedIn={effectiveIsLoggedIn}
              allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
              isLoading={isLoadingProducts}
              onRestoreComplete={handleProductsViewStateRestoreComplete}
            />
          </Suspense>
        )}

        {currentView === 'marketplace-listing-detail' && routeMarketplaceListingId && (
          <Suspense fallback={<ProductDetailPageSkeleton />}>
            <MarketplaceListingDetailPage
              anuncioId={routeMarketplaceListingId}
              isLoggedIn={effectiveIsLoggedIn}
              entidadeId={authenticatedEntidadeId ?? effectiveAccountProfile?.entidadeId ?? null}
              allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
              onHomeClick={navigateHome}
              onProductsClick={() => navigateToView('products')}
              onProductClick={handleProductClick}
              onAddToCart={(product) => handleAddToCart(product, 1)}
              onLoginRequest={() => openLogin({ view: 'marketplace-listing-detail' })}
              onRegisterRequest={() => navigateToView('register')}
              onSellerClick={handleMarketplaceSellerClick}
            />
          </Suspense>
        )}

        {currentView === 'marketplace-seller' && routeMarketplaceSellerId && (
          <Suspense fallback={<PageGridSkeleton cards={6} />}>
            <MarketplaceSellerPage
              sellerId={routeMarketplaceSellerId}
              isLoggedIn={effectiveIsLoggedIn}
              allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
              onHomeClick={navigateHome}
              onProductsClick={() => navigateToView('products')}
              onProductClick={handleProductClick}
              onAddToCart={(product) => handleAddToCart(product, 1)}
            />
          </Suspense>
        )}

        {currentView === 'product-detail'
          && selectedProduct
          && productMatchesDetailRoute(selectedProduct, routeProductId, routeProductParentContextId) && (
          <Suspense fallback={<ProductDetailPageSkeleton />}>
            <ProductDetailView
              key={selectedProduct.id}
              product={selectedProduct}
              routeProductId={routeProductId ?? selectedProduct.id}
              routeProductParentContextId={routeProductParentContextId}
              categoryModel={categoryModel}
              relatedProductsClassId={resolveProductClassIdForRelatedProducts(selectedProduct)}
              breadcrumbSegments={buildProductBreadcrumbSegments(selectedProduct)}
              onBack={handleBackFromProduct}
              onProductClick={handleProductClick}
              onAddToCart={handleAddToCart}
              onAddMultipleToCart={handleAddMultipleToCart}
              onUpdateCartQuantity={handleUpdateQuantity}
              onRemoveFromCart={handleRemoveFromCart}
              onBuyNow={async (product, quantity) => {
                const currentItems = isCartAuthenticated
                  ? cartItemsRef.current
                  : quickCheckoutCartItemsRef.current
                const hadItemsInCart = currentItems.length > 0
                const currentProductItem = currentItems.find((item) => item.product.id === product.id)
                const normalizedQuantity = Math.max(
                  product.minOrder ?? 1,
                  Math.min(CART_ITEM_QUANTITY_MAX, Math.trunc(quantity || 0))
                )

                // Na página do produto, o seletor de quantidade já mantém o
                // carrinho sincronizado. Comprar novamente o mesmo item aqui
                // duplicava a quantidade e invalidava impostos e frete já
                // calculados justamente no momento de abrir o drawer.
                if (currentProductItem) {
                  if (currentProductItem.quantity !== normalizedQuantity) {
                    await handleUpdateQuantity(product.id, normalizedQuantity)
                  }
                  if (isCartAuthenticated) {
                    await flushPendingCartSync()
                    prefetchCartCalculations(cartItemsRef.current)
                  }
                  handleCartDrawerOpen()
                  return
                }

                if (!isCartAuthenticated) {
                  if (!isAnonymousQuickCheckoutAvailable) {
                    redirectToLoginForCart()
                    return
                  }
                  const didAddToCart = await addItemsToCart(
                    [{ product, quantity }],
                    { skipSuccessToast: true }
                  )
                  if (!didAddToCart) return
                  if (hadItemsInCart) {
                    handleCartDrawerOpen()
                    return
                  }
                  navigateToView('quick-checkout')
                  return
                }

                const didAddToCart = await addItemsToCart(
                  [{ product, quantity }],
                  { skipSuccessToast: true }
                )
                if (!didAddToCart) return
                if (isInternationalCommerce) {
                  handleCartDrawerOpen()
                  return
                }
                if (hadItemsInCart) {
                  handleCartDrawerOpen()
                  return
                }
                queueStorefrontAnalyticsEvent({
                  eventType: 'begin_checkout',
                  pageKind: 'checkout',
                  pageKey: 'cart',
                  pagePath: `${VIEW_PATHS.checkout}/cart`,
                  entidadeId: getAuthenticatedEntidadeId(),
                  payload: withStorefrontCartAnalyticsPayload({
                    items_count: cartItemsRef.current.length,
                    quantity_total: cartItemsRef.current.reduce((sum, item) => sum + item.quantity, 0),
                    source_view: 'product-detail',
                  }, cartItemsRef.current),
                })
                navigateToCheckout()
              }}
              onGoToCart={() => {
                if (isInternationalCommerce) {
                  handleCartDrawerOpen()
                  return
                }
                if (isAnonymousQuickCheckoutAvailable) {
                  navigateToView('quick-checkout')
                  return
                }
                navigateToCheckout('cart')
              }}
              onAddToCompare={handleAddToCompare}
              isInCompare={(compareProducts || []).some(p => p.id === selectedProduct.id)}
              isLoggedIn={effectiveIsLoggedIn}
              questionContact={{
                name: effectiveAccountProfile?.name || effectiveAccountProfile?.corporateName || null,
                email: effectiveAccountProfile?.email || null,
                whatsapp: effectiveAccountProfile?.phone || null,
              }}
              allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
              isCartAuthenticated={isCartAuthenticated}
              cartQuantityByProductId={cartQuantityByProductId}
              onHomeClick={navigateHome}
              onDisplayedProductChange={setDisplayedProductDetail}
              priceRefreshVersion={storefrontPriceRefreshVersion}
              wideCartPanelWidth={WIDE_PRODUCT_CART_PANEL_WIDTH}
              onWideCartSpaceChange={setHasWideProductCartSpace}
              onLoginRequest={() => openLogin({
                view: 'product-detail',
                productId: routeProductId ?? selectedProduct.id,
                parentContextId: routeProductParentContextId,
              })}
              onRegisterRequest={() => navigateToView('register')}
            />
          </Suspense>
        )}

        {currentView === 'product-detail'
          && (!selectedProduct || !productMatchesDetailRoute(selectedProduct, routeProductId, routeProductParentContextId)) && (
          <div className="flex-1 bg-background">
            {isLoadingProduct ? (
              <ProductDetailPageSkeleton />
            ) : (
              <div className="container mx-auto px-4 py-12">
                <div className="rounded-lg border border-border/60 bg-white p-6 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {productLoadError
                      ? t(productLoadError)
                      : t('product.not_found')}
                  </p>
                  <Button variant="outline" asChild>
                    <a
                      href={VIEW_PATHS.products}
                      onClick={(event) => handleLinkClick(event, handleBackFromProduct)}
                    >
                      {t('actions.back_to_products')}
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'quotes' && (
          effectiveIsLoggedIn
            ? (
              routeQuoteDetailId ? (
                <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                  <QuoteDetailPage
                    quoteId={routeQuoteDetailId}
                    quoteToken={routeQuoteDetailToken}
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateQuotes={() => navigateToView('quotes')}
                    onNavigateToQuoteDetails={navigateToQuoteDetails}
                    onStartCheckout={navigateToQuoteCheckout}
                    onQuoteSentToCart={handleQuoteSentToCart}
                    onQuoteCartSnapshotUpdated={handleQuoteCartSnapshotUpdated}
                    cartItemCount={visibleCartItems.length}
                    onClearCartBeforeQuoteSend={() => clearCartContents({ skipSuccessToast: true })}
                    onSaveCurrentCartAsQuoteBeforeQuoteSend={handleSaveCurrentCartAsQuoteBeforeQuoteSend}
                    canAccessIndicatorPortal={canAccessIndicatorPortal}
                    canSendQuoteToCart={!isSellerAccount}
                    modernWorkspace
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<AccountPageSkeleton variant="list" />}>
                  <QuotesListPage
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateIndicator={() => navigateToView('indicator')}
                    onNavigateToQuoteDetails={navigateToQuoteDetails}
                    onStartCheckout={navigateToQuoteCheckout}
                    onQuoteSentToCart={handleQuoteSentToCart}
                    canAccessIndicatorPortal={canAccessIndicatorPortal}
                    canSendQuoteToCart={!isSellerAccount}
                  />
                </Suspense>
              )
            )
            : renderAccountLoginPrompt(t('account.quotes.title'), t('account.quotes.subtitle'), 'quotes')
        )}

        {currentView === 'attendances' && (
          effectiveIsLoggedIn
            ? (
              routeAttendanceDetailId ? (
                <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                  <AttendanceDetailPage
                    attendanceId={routeAttendanceDetailId}
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateAttendances={() => navigateToView('attendances')}
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<AccountPageSkeleton variant="list" />}>
                  <AttendancesPage
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateToAttendanceDetails={navigateToAttendanceDetails}
                    onNavigateToSupport={() => navigateToView('support')}
                  />
                </Suspense>
              )
            )
            : renderAccountLoginPrompt(t('account.attendances.title'), t('account.attendances.subtitle'), 'attendances')
        )}

        {currentView === 'login' && (
          <Suspense fallback={<AccountPageSkeleton />}>
            <LoginView
              onLogin={handleLogin}
              storefrontWhatsappConfigured={Boolean(siteConfig?.whatsapp?.trim())}
              onPasswordRecoveryDestinations={handlePasswordRecoveryDestinations}
              onPasswordRecoveryRequest={handlePasswordRecoveryRequest}
              onPasswordRecoveryVerify={handlePasswordRecoveryVerify}
              onPasswordRecoveryReset={handlePasswordRecoveryReset}
              onPasswordlessDestinations={handlePasswordlessDestinations}
              onPasswordlessRequest={handlePasswordlessRequest}
              onPasswordlessContactRequest={handlePasswordlessContactRequest}
              onPasswordlessVerify={handlePasswordlessVerify}
              onSwitchToRegister={() => navigateToView('register')}
            />
          </Suspense>
        )}

        {currentView === 'access' && (
          <div className="container mx-auto px-4 py-16">
            <div className="mx-auto max-w-lg rounded-2xl border border-border/70 bg-white p-8 text-center shadow-sm">
              <h1 className="text-2xl font-semibold text-foreground">Validando acesso</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Estamos autenticando sua conta e preparando o redirecionamento.
              </p>
            </div>
          </div>
        )}

        {currentView === 'redirect-checkout' && (
          <div className="container mx-auto px-4 py-16">
            <div className="mx-auto max-w-lg rounded-2xl border border-border/70 bg-white p-8 text-center shadow-sm">
              <h1 className="text-2xl font-semibold text-foreground">Preparando sua compra</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Estamos validando o acesso e abrindo o checkout.
              </p>
            </div>
          </div>
        )}

        {currentView === 'register' && (
          <Suspense fallback={<AccountPageSkeleton />}>
            <RegisterView
              onRegister={handleRegister}
              onSwitchToLogin={() => navigateToView('login')}
              retomadaCode={routePreCadastroRetomadaCode}
              lojaId={siteConfig?.loja_id}
              emailCodeFallbackEnabled={Boolean(siteConfig?.cadastro_codigo_email_habilitado)}
            />
          </Suspense>
        )}

        {currentView === 'checkout' && (
          isInternationalCommerce ? (
            <div className="container mx-auto px-4 py-10">
              <div className="mx-auto max-w-xl rounded-lg border border-sky-200 bg-sky-50 p-6 text-sky-950">
                <h1 className="text-lg font-semibold">Cotización internacional</h1>
                <p className="mt-2 text-sm text-sky-800">
                  Las compras de exportación se guardan como cotización en USD, con entrega EXW y pago anticipado.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button onClick={handleCartDrawerOpen}>Revisar carrito</Button>
                  <Button variant="outline" onClick={() => handleOpenSaveQuoteDialog()}>
                    Guardar cotización
                  </Button>
                </div>
              </div>
            </div>
          ) : isSellerAccount ? (
            <div className="container mx-auto px-4 py-10 text-sm text-slate-600">
              Usuários vendedores podem montar carrinhos e gerar orçamentos, mas não têm acesso ao checkout.
            </div>
          ) : (
            <CheckoutProvider>
              <Suspense fallback={<CheckoutPageSkeleton />}>
                <CheckoutView
                  items={checkoutItems}
                  isCartLoading={
                    routeCheckoutSource === 'quote'
                      ? checkoutQuoteStatus === 'loading'
                      : routeCheckoutSource === 'order'
                        ? checkoutOrderStatus === 'loading'
                        : (isCheckoutSessionAuthenticated && isCartAuthenticated && (isCartInitializing || !hasHydratedCartOnce))
                  }
                  accountProfileSummary={effectiveAccountProfile}
                  isLoggedIn={isCheckoutSessionAuthenticated}
                  onComplete={handleCheckoutComplete}
                  onRequireLogin={() => openLogin({ view: 'checkout' })}
                  onOpenBusinessRegistration={() => setShowCheckoutBusinessRegisterDialog(true)}
                  onCustomerProfileUpdate={handleCheckoutCustomerProfileUpdate}
                  onHomeClick={navigateHome}
                  onNavigateOrders={() => navigateToView('orders')}
                  onQuoteCheckoutEmpty={handleQuoteCheckoutEmpty}
                  onOrderCheckoutEmpty={handleOrderCheckoutEmpty}
                  requestedStep={checkoutRouteStep}
                  onProductClick={handleProductClick}
                  onUpdateQuantity={routeCheckoutSource === 'cart' ? handleUpdateQuantity : undefined}
                  onRemoveItem={routeCheckoutSource === 'cart' ? handleRemoveFromCart : undefined}
                  onAddToCart={routeCheckoutSource === 'cart' ? ((product) => handleAddToCart(product, 1)) : undefined}
                  onSaveAsQuote={routeCheckoutSource === 'cart' ? (() => handleOpenSaveQuoteDialog()) : undefined}
                  onClearCart={routeCheckoutSource === 'cart' ? handleClearCart : undefined}
                  onPriceModeChanged={routeCheckoutSource === 'cart' ? refreshStorefrontPricesAfterTaxModeChange : undefined}
                  checkoutSource={routeCheckoutSource}
                  checkoutQuoteId={routeCheckoutQuoteId}
                  checkoutQuoteMode={routeCheckoutQuoteMode}
                  checkoutQuoteDetail={checkoutQuoteDetail}
                  checkoutOrderId={routeCheckoutOrderId}
                  checkoutOrderDetail={checkoutOrderDetail}
                  checkoutOrderMode={routeCheckoutOrderMode}
                  checkoutOrderUnavailableCount={checkoutOrderUnavailableCount}
                  checkoutStorageScope={checkoutStorageScope}
                  cartShippingPrefill={routeCheckoutSource === 'cart' ? cartCheckoutShippingPrefill : null}
                  cartFinancialPrefill={routeCheckoutSource === 'cart' ? cartCheckoutFinancialPrefill : null}
                  onStepChange={(info) => {
                    setCheckoutStepInfo(info.stepInfo)
                    setCheckoutRouteStep(info.currentStepId)
                  }}
                />
              </Suspense>
            </CheckoutProvider>
          )
        )}

        {currentView === 'quick-checkout' && (
          isQuickCheckoutFlowAvailable ? (
            <Suspense fallback={<CheckoutPageSkeleton />}>
              <QuickCheckoutView
                items={quickCheckoutCartItems}
                affiliateCode={quickCheckoutAffiliateCode}
                isPreparingDirectProduct={
                  Boolean(routeQuickCheckoutProductId)
                  || (isProductCheckoutQuickFlowActive && quickCheckoutCartItems.length === 0)
                }
                onUpdateQuantity={handleQuickCheckoutUpdateQuantity}
                onHomeClick={navigateHome}
                onClearCart={() => {
                  clearQuickCheckoutCartItems(quickCheckoutLojaId)
                  if (quickCheckoutLojaId) {
                    clearQuickCheckoutCartItems(null)
                  }
                  setQuickCheckoutCartItems([])
                  quickCheckoutCartItemsRef.current = []
                  trackStorefrontCartState([], 'cart_cleared')
                  updateProductCheckoutQuickFlow(false)
                }}
                onStepChange={(info) => {
                  setCheckoutStepInfo(info.stepInfo)
                }}
              />
            </Suspense>
          ) : renderAccountLoginPrompt('Entre para finalizar', 'Acesse sua conta para concluir a compra.', 'checkout')
        )}

        {currentView === 'dashboard' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="dashboard" />}>
                <DashboardPage
                  canAccessIndicatorPortal={canAccessIndicatorPortal && !indicatorDelegation}
                  canAccessAffiliates={canAccessAffiliates}
                  canAccessMarketplace={hasMarketplaceBenefit}
                  showBusiness={canShowBusiness}
                  onNavigate={navigateToView}
                  onHomeClick={navigateHome}
                  accountNotifications={accountNotifications}
                  accountProfile={effectiveAccountProfile}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(t('account.dashboard.title'), t('account.dashboard.subtitle'), 'dashboard')
        )}

        {currentView === 'finance' && (
          effectiveIsLoggedIn
            ? (
              isSellerAccount
                ? (
                  <div className="container mx-auto px-4 py-10 text-sm text-slate-600">
                    Usuários vendedores não têm acesso ao financeiro da conta.
                  </div>
                )
                : (
                  <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                    <FinancePage
                      onHomeClick={navigateHome}
                      onNavigateDashboard={() => navigateToView('dashboard')}
                    />
                  </Suspense>
                )
            )
            : renderAccountLoginPrompt(t('account.finance.title'), t('account.finance.subtitle'), 'finance')
        )}

        {currentView === 'profile' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <ProfilePage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                  onNavigateUsers={() => navigateToView('users')}
                  accountProfile={effectiveAccountProfile}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(t('account.profile.title'), t('account.profile.subtitle'), 'profile')
        )}

        {currentView === 'change-password' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <AccountPasswordChangePage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                  accountProfile={effectiveAccountProfile}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(t('account.changePassword.title'), t('account.changePassword.subtitle'), 'change-password')
        )}

        {currentView === 'users' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <UsersPage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                  accountProfile={effectiveAccountProfile}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt('Meus usuários', 'Gerencie os contatos que acessam a loja.', 'users')
        )}

        {(currentView === 'business' || currentView === 'clients') && canShowBusiness && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                {currentView === 'business' && routeOrderDetailId ? (
                  <OrderDetailPage
                    orderId={routeOrderDetailId}
                    orderLoader={(orderId) => getIndicadorPedidoDetalheAsAccountOrder(orderId, siteConfig?.loja_id ?? undefined)}
                    readOnly
                    businessContext
                    showCommissionColumn
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateOrders={() => navigateToView('business')}
                    onReorderItems={(items) => addItemsToCart(items)}
                  />
                ) : currentView === 'business' && routeQuoteDetailId ? (
                  <QuoteDetailPage
                    quoteId={routeQuoteDetailId}
          quoteToken={routeQuoteDetailToken}
          businessContext
          canAccessIndicatorPortal={canAccessIndicatorPortal}
          canSendQuoteToCart
          onStartCheckout={navigateToBusinessQuoteCheckout}
          onQuoteSentToCart={handleQuoteSentToCart}
          onQuoteCartSnapshotUpdated={handleQuoteCartSnapshotUpdated}
              cartItemCount={visibleCartItems.length}
              onClearCartBeforeQuoteSend={() => clearCartContents({ skipSuccessToast: true })}
              onSaveCurrentCartAsQuoteBeforeQuoteSend={handleSaveCurrentCartAsQuoteBeforeQuoteSend}
              onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateQuotes={() => navigateToView('business')}
                    onNavigateToQuoteDetails={navigateToBusinessQuoteDetails}
                  />
                ) : (
                  <BusinessPage
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    lojaId={siteConfig?.loja_id ?? undefined}
                    onReorderItems={(items) => addItemsToCart(items)}
                    onNavigateToOrderDetails={navigateToBusinessOrderDetails}
                    onNavigateToQuoteDetails={navigateToBusinessQuoteTarget}
                    onStartClientSession={startIndicatorClientSession}
                  />
                )}
              </Suspense>
            )
            : renderAccountLoginPrompt('Meu Negócio', 'Gerencie seus clientes e acompanhe suas comissões.', 'business')
        )}

        {currentView === 'marketplace-listings' && (
          effectiveIsLoggedIn
          && hasMarketplaceBenefit
          && effectiveAccountProfile?.buyerUser?.acessoMarketplaceAnuncios
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <MarketplaceListingsPage
                  onHomeClick={navigateHome}
                  onNavigate={navigateToView}
                  canAnswerQuestions={Boolean(effectiveAccountProfile?.buyerUser?.acessoMarketplacePerguntas)}
                  customerId={effectiveAccountProfile?.entidadeId ?? null}
                  customerName={effectiveAccountProfile?.corporateName || effectiveAccountProfile?.name || null}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt('Marketplace · Anúncios', 'Acesse uma conta habilitada para gerenciar anúncios.', 'marketplace-listings')
        )}

        {currentView === 'marketplace' && (
          effectiveIsLoggedIn
          && hasMarketplaceBenefit
          && effectiveAccountProfile?.buyerUser?.acessoMarketplaceAnuncios
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <MarketplaceProfilePage
                  onHomeClick={navigateHome}
                  onNavigate={navigateToView}
                  canAnswerQuestions={Boolean(effectiveAccountProfile?.buyerUser?.acessoMarketplacePerguntas)}
                  customerId={effectiveAccountProfile?.entidadeId ?? null}
                  customerName={effectiveAccountProfile?.corporateName || effectiveAccountProfile?.name || null}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt('Marketplace', 'Acesse uma conta habilitada para gerenciar o perfil da vitrine.', 'marketplace')
        )}

        {currentView === 'marketplace-questions' && (
          effectiveIsLoggedIn
          && hasMarketplaceBenefit
          && effectiveAccountProfile?.buyerUser?.acessoMarketplacePerguntas
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <MarketplaceQuestionsPage
                  onHomeClick={navigateHome}
                  onNavigate={navigateToView}
                  canManageListings={Boolean(effectiveAccountProfile?.buyerUser?.acessoMarketplaceAnuncios)}
                  customerId={effectiveAccountProfile?.entidadeId ?? null}
                  customerName={effectiveAccountProfile?.corporateName || effectiveAccountProfile?.name || null}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt('Marketplace · Perguntas', 'Acesse uma conta habilitada para responder perguntas.', 'marketplace-questions')
        )}

        {currentView === 'marketplace-performance' && (
          effectiveIsLoggedIn
          && hasMarketplaceBenefit
          && effectiveAccountProfile?.buyerUser?.acessoMarketplaceAnuncios
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <MarketplacePerformancePage
                  onHomeClick={navigateHome}
                  onNavigate={navigateToView}
                  canAnswerQuestions={Boolean(effectiveAccountProfile?.buyerUser?.acessoMarketplacePerguntas)}
                  customerId={effectiveAccountProfile?.entidadeId ?? null}
                  customerName={effectiveAccountProfile?.corporateName || effectiveAccountProfile?.name || null}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt('Marketplace · Desempenho', 'Acesse uma conta habilitada para acompanhar o desempenho dos anúncios.', 'marketplace-performance')
        )}

        {currentView === 'benefits' && (
          effectiveIsLoggedIn
            ? (
              isSellerAccount ? (
                <div className="container mx-auto px-4 py-10 text-sm text-slate-600">
                  Usuários vendedores não têm acesso aos benefícios da conta.
                </div>
              ) : (
                <Suspense fallback={<AccountPageSkeleton variant="benefits" />}>
                  <BenefitsPage
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateCashback={() => navigateToView('cashback')}
                  />
                </Suspense>
              )
            )
            : renderAccountLoginPrompt(t('account.benefits.title'), t('account.benefits.subtitle'), 'benefits')
        )}

        {currentView === 'cashback' && (
          effectiveIsLoggedIn
            ? (
              isSellerAccount
                ? (
                  <div className="container mx-auto px-4 py-10 text-sm text-slate-600">
                    Usuários vendedores não têm acesso ao cashback da empresa.
                  </div>
                )
                : (
                  <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                    <CashbackPage
                      onHomeClick={navigateHome}
                      onNavigateDashboard={() => navigateToView('dashboard')}
                      onNavigateProducts={() => navigateToView('products')}
                    />
                  </Suspense>
                )
            )
            : renderAccountLoginPrompt(t('account.cashback.title'), t('account.cashback.subtitle'), 'cashback')
        )}

        {currentView === 'addresses' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <AddressesPage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(t('account.addresses.title'), t('account.addresses.subtitle'), 'addresses')
        )}

        {currentView === 'favorites' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="favorites" />}>
                <FavoritesPage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                  onProductClick={handleProductClick}
                  onAddToCart={(product) => handleAddToCart(product, 1)}
                  isLoggedIn={effectiveIsLoggedIn}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(t('account.favorites.title'), t('account.favorites.subtitle'), 'favorites')
        )}

        {currentView === 'orders' && (
          effectiveIsLoggedIn
            ? (
              isSellerAccount ? (
                <div className="container mx-auto px-4 py-10 text-sm text-slate-600">
                  Usuários vendedores não têm acesso aos pedidos da conta.
                </div>
              ) : (
                routeOrderDetailId ? (
                  <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                    <OrderDetailPage
                      orderId={routeOrderDetailId}
                      onHomeClick={navigateHome}
                      onNavigateDashboard={() => navigateToView('dashboard')}
                      onNavigateOrders={() => navigateToView('orders')}
                      onNavigateToPayment={(detail) => navigateToPendingOrderPayment(detail.orderId)}
                      onRepurchaseOrder={navigateToOrderCheckout}
                      onReorderItems={(items) => addItemsToCart(items)}
                    />
                  </Suspense>
                ) : (
                  <Suspense fallback={<AccountPageSkeleton variant="list" />}>
                    <OrdersListPage
                      onHomeClick={navigateHome}
                      onNavigateDashboard={() => navigateToView('dashboard')}
                      onNavigateToPayment={(detail) => navigateToPendingOrderPayment(detail.orderId)}
                      onNavigateToOrderDetails={navigateToOrderDetails}
                      onRepurchaseOrder={navigateToOrderCheckout}
                      onReorderItems={(items) => addItemsToCart(items)}
                    />
                  </Suspense>
                )
              )
            )
            : renderAccountLoginPrompt(t('account.orders.title'), t('account.orders.subtitle'), 'orders')
        )}

        {currentView === 'tracking' && (
          <Suspense fallback={<AccountPageSkeleton variant="tracking" />}>
              <OrderTrackingView
                onHomeClick={navigateHome}
                onNavigateDashboard={() => navigateToView('dashboard')}
                isLoggedIn={effectiveIsLoggedIn}
                defaultDocument={effectiveAccountProfile?.document || effectiveAccountProfile?.cnpj}
              />
          </Suspense>
        )}

        {currentView === 'checkout-tracking' && routeOrderTrackingCode && (
          <Suspense fallback={<AccountPageSkeleton variant="tracking" />}>
            <CheckoutOrderTrackingView
              code={routeOrderTrackingCode}
              onHomeClick={navigateHome}
              whatsappNotificationsEnabled={Boolean(
                siteConfig?.whatsapp_notificacoes_pedido?.trim()
                && siteConfig.whatsapp_notificacoes_pedido_habilitado
              )}
            />
          </Suspense>
        )}

        {currentView === 'price-table' && (
          (
            <Suspense fallback={<AccountPageSkeleton variant="price-table" />}>
              <PriceTablePage
                onHomeClick={navigateHome}
                onNavigateDashboard={() => navigateToView('dashboard')}
                isLoggedIn={effectiveIsLoggedIn}
                authenticatedEntidadeId={authenticatedEntidadeId ?? effectiveAccountProfile?.entidadeId ?? null}
                accountProfile={effectiveAccountProfile}
                onAddToCart={handlePriceTableAddToCart}
                onSetCartQuantity={handlePriceTableSetCartQuantity}
                cartQuantityByProductId={cartQuantityByProductId}
                cartPanelOpen={shouldExpandPriceTableCartPanel}
                onCatalogReadyChange={handlePriceTableCatalogReadyChange}
              />
            </Suspense>
          )
        )}

        {currentView === 'store-network-marketplace' && (
          isSiteConfigLoading && !siteConfig
            ? <PageGridSkeleton cards={8} />
            : Boolean(siteConfig?.rede_lojas_menu_ativo)
            ? (
                <Suspense fallback={<PageGridSkeleton cards={8} />}>
                  <StoreNetworkMarketplacePage
                    lojaId={siteConfig?.loja_id ?? 0}
                    enabled
                    isLoggedIn={effectiveIsLoggedIn}
                    originGlobalStoreId={siteConfig?.global_store_id}
                  />
                </Suspense>
              )
            : <div className="container mx-auto px-4 py-16 text-center"><h1 className="text-2xl font-semibold">Página não encontrada</h1></div>
        )}

        {currentView === 'store-network-access' && (
          isSiteConfigLoading && !siteConfig
            ? <PageGridSkeleton cards={1} />
            : Boolean(siteConfig?.rede_lojas_menu_ativo)
            ? <Suspense fallback={<PageGridSkeleton cards={1} />}><StoreNetworkAccessPage lojaId={siteConfig?.loja_id ?? 0} originGlobalStoreId={siteConfig?.global_store_id} /></Suspense>
            : <div className="container mx-auto px-4 py-16 text-center"><h1 className="text-2xl font-semibold">Página não encontrada</h1></div>
        )}

        {currentView === 'price-table-margin' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="price-table" />}>
                <PriceTableMarginPage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                  isLoggedIn={effectiveIsLoggedIn}
                  authenticatedEntidadeId={authenticatedEntidadeId ?? effectiveAccountProfile?.entidadeId ?? null}
                  accountProfile={effectiveAccountProfile}
                  onAddToCart={handlePriceTableAddToCart}
                  onSetCartQuantity={handlePriceTableSetCartQuantity}
                  cartQuantityByProductId={cartQuantityByProductId}
                  cartPanelOpen={shouldExpandPriceTableCartPanel}
                  onCatalogReadyChange={handlePriceTableCatalogReadyChange}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(
              t('account.priceTable.margin.title'),
              t('account.priceTable.margin.subtitle'),
              'price-table-margin'
            )
        )}

        {currentView === 'affiliates' && (
          effectiveIsLoggedIn
            ? (
              canAccessAffiliates ? (
                <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                  <AffiliatesPage
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                  />
                </Suspense>
              ) : null
            )
            : renderAccountLoginPrompt(t('account.affiliates.title'), t('account.affiliates.subtitle'), 'affiliates')
        )}

        {currentView === 'indicator' && (
          effectiveIsLoggedIn
            ? (
              buyerBeneficiosStatus === 'loading' ? (
                <AccountPageSkeleton variant="simple" />
              ) : canAccessIndicatorPortal ? (
                <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                  <IndicatorPortalPage
                    onHomeClick={navigateHome}
                    onNavigateDashboard={() => navigateToView('dashboard')}
                    onNavigateQuotes={() => navigateToView('quotes')}
                  />
                </Suspense>
              ) : null
            )
            : renderAccountLoginPrompt(t('account.indicator.title'), t('account.indicator.subtitle'), 'indicator')
        )}

        {currentView === 'returns' && (
          effectiveIsLoggedIn
            ? (
              <Suspense fallback={<AccountPageSkeleton variant="simple" />}>
                <ReturnsPage
                  onHomeClick={navigateHome}
                  onNavigateDashboard={() => navigateToView('dashboard')}
                />
              </Suspense>
            )
            : renderAccountLoginPrompt(t('account.returns.title'), t('account.returns.subtitle'), 'returns')
        )}

        {currentView === 'return-request' && (
          <Suspense fallback={<PageGridSkeleton cards={4} />}>
            <ReturnRequestView
              onHomeClick={navigateHome}
              onViewChange={(view) => {
                handleRequestedViewChange(view)
              }}
              isLoggedIn={effectiveIsLoggedIn}
              defaultDocument={effectiveAccountProfile?.document || effectiveAccountProfile?.cnpj}
              defaultName={effectiveAccountProfile?.name}
              defaultEmail={effectiveAccountProfile?.email}
              requestedStep={returnRequestRouteStep}
              requestedFlowType={returnRequestRouteFlowType}
              onRouteStepChange={handleReturnRequestRouteStepChange}
            />
          </Suspense>
        )}

        {currentView === 'whatsapp' && (
          <Suspense fallback={<PageGridSkeleton cards={4} />}>
            <WhatsappSupportView
              onViewChange={(view) => {
                handleRequestedViewChange(view)
              }}
              onTechnicalSupportClick={() => navigateToSupport('2401')}
              isLoggedIn={effectiveIsLoggedIn}
            />
          </Suspense>
        )}

        {currentView === 'support' && (
          <Suspense fallback={<AccountPageSkeleton variant="support" />}>
            <SupportView
              onHomeClick={navigateHome}
              onViewChange={(view) => {
                handleRequestedViewChange(view)
              }}
              onLoginClick={handleLoginClick}
              subjectSlug={routeSupportSubjectSlug}
              onSubjectSlugChange={navigateToSupport}
              isLoggedIn={effectiveIsLoggedIn}
              authenticatedEntidadeId={authenticatedEntidadeId}
              defaultEmail={effectiveAccountProfile?.email}
            />
          </Suspense>
        )}

        {currentView === 'service-evaluation' && (
          <Suspense fallback={<PageGridSkeleton cards={2} />}>
            <ServiceEvaluationPage
              isLoggedIn={effectiveIsLoggedIn}
              lojaId={siteConfig?.loja_id ?? getLojaVirtualDefaultLojaId() ?? 0}
            />
          </Suspense>
        )}

        {currentView === 'blog' && (
          <Suspense fallback={<PageGridSkeleton cards={4} />}>
            <BlogExperience
              isLoggedIn={effectiveIsLoggedIn}
              accountProfile={effectiveAccountProfile}
              onLoginClick={handleLoginClick}
              onLogoutClick={handleLogout}
              onNavigateView={(view) => {
                if (view === 'blog') {
                  navigateToBlog()
                  return
                }
                handleRequestedViewChange(view)
              }}
            />
          </Suspense>
        )}

        {isInstitutionalView(currentView) && (
          <Suspense fallback={<PageGridSkeleton cards={4} />}>
            <InstitutionalView
              onHomeClick={navigateHome}
              onSectionChange={navigateToView}
              section={currentView}
            />
          </Suspense>
        )}

        {currentView === 'compare' && (
          <Suspense fallback={<PageGridSkeleton cards={4} />}>
            <CompareView
              products={compareProducts || []}
              onRemoveProduct={handleRemoveFromCompare}
              onClearAll={handleClearCompare}
              onBack={() => navigateToView('products')}
              onHomeClick={navigateHome}
              isLoggedIn={effectiveIsLoggedIn}
            />
          </Suspense>
        )}
        </AppPrimaryContent>

        {currentView !== 'blog' && currentView !== 'quick-checkout' && !isStandalonePriceTable && !isPublicTicketView && (
          <Suspense fallback={null}>
            <Footer onViewChange={handleRequestedViewChange} />
          </Suspense>
        )}

        {currentView === 'dashboard' && effectiveIsLoggedIn && !isStandalonePriceTable && (
          <Suspense fallback={null}>
            <FloatingHelpMenu onAction={handleFloatingHelpAction} />
          </Suspense>
        )}

        {shouldShowWideProductCartPanel && (
          <aside className="fixed inset-y-0 right-0 z-[55] flex w-[26rem] flex-col border-l border-slate-200 bg-white pt-1 print:hidden">
            <Suspense
              fallback={(
                <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-4">
                  <Skeleton className="h-7 w-32" />
                  <Skeleton className="h-24 rounded-2xl" />
                  <Skeleton className="h-24 rounded-2xl" />
                  <div className="mt-auto space-y-3 border-t border-slate-100 pt-4">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-11 rounded-xl" />
                  </div>
                </div>
              )}
            >
              <CartDrawer
                renderWithinSheet
                renderHeaderAsDialogTitle={false}
                suppressMissingProductPhotoLookup
                hideEmptyAction
                open
                onOpenChange={() => undefined}
                items={activeCartItems}
                isLoggedIn={effectiveIsLoggedIn}
                allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
                onUpdateQuantity={handleUpdateQuantity}
                onEnsureCartSynchronized={flushPendingCartSync}
                onPriceModeChanged={refreshStorefrontPricesAfterTaxModeChange}
                onRemoveItem={handleRemoveFromCart}
                onAddToCart={(product) => handleAddToCart(product, 1)}
                onProductClick={handleProductClick}
                freightPrefill={cartQuoteFreightPrefill ?? cartQuoteOrigin?.freightPrefill ?? null}
                onSaveAsQuote={handleOpenSaveQuoteDialog}
                onCheckout={handleProceedToCheckout}
                onClearCart={handleClearCart}
              />
            </Suspense>
          </aside>
        )}

        {shouldShowPriceTableCartPanel && (
          <>
            <aside
              aria-hidden={isPriceTableCartPanelCollapsed}
              className={`fixed inset-y-0 right-0 z-[90] hidden w-[420px] flex-col border-l border-slate-200 bg-white shadow-[-22px_0_58px_-36px_rgba(15,23,42,0.68)] transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform print:hidden xl:flex ${
                isPriceTableCartPanelCollapsed ? 'pointer-events-none translate-x-full shadow-none' : 'translate-x-0'
              }`}
            >
              <button
                type="button"
                className="absolute left-0 top-1/2 z-10 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-[0_14px_34px_-18px_rgba(15,23,42,0.75)] transition duration-200 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2"
                onClick={() => setIsPriceTableCartPanelCollapsed(true)}
                title="Recolher carrinho"
                aria-label="Recolher carrinho da tabela"
              >
                <CaretRight className="h-4 w-4" weight="bold" />
              </button>
              <Suspense
                fallback={(
                  <>
                    <div className="shrink-0 border-b bg-white px-4 py-3">
                      <h2 className="text-base font-semibold">
                        Carrinho da tabela ({totalCartItems})
                      </h2>
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-4">
                      <Skeleton className="h-24 rounded-2xl" />
                      <Skeleton className="h-24 rounded-2xl" />
                      <Skeleton className="h-24 rounded-2xl" />
                      <div className="mt-auto space-y-3 border-t border-slate-100 pt-4">
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="h-11 rounded-xl" />
                      </div>
                    </div>
                  </>
                )}
              >
                <CartDrawer
                  renderWithinSheet
                  renderHeaderAsDialogTitle={false}
                  open
                  onOpenChange={(open) => {
                    if (!open) setIsPriceTableCartPanelCollapsed(true)
                  }}
                  items={activeCartItems}
                  isLoggedIn={effectiveIsLoggedIn}
                  allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
                  onUpdateQuantity={handleUpdateQuantity}
                  onEnsureCartSynchronized={flushPendingCartSync}
                  onPriceModeChanged={refreshStorefrontPricesAfterTaxModeChange}
                  onRemoveItem={handleRemoveFromCart}
                  onAddToCart={(product) => handleAddToCart(product, 1)}
                  onProductClick={handleProductClick}
                  contextLabel="Tabela de preços"
                  freightPrefill={cartQuoteFreightPrefill ?? cartQuoteOrigin?.freightPrefill ?? null}
                  emptyActionLabel="Continuar na tabela"
                  onSaveAsQuote={handleOpenSaveQuoteDialog}
                  onCheckout={handleProceedToCheckout}
                  onClearCart={handleClearCart}
                />
              </Suspense>
            </aside>
            {isPriceTableCartPanelCollapsed && (
              <button
                type="button"
                className="group fixed right-0 top-1/2 z-[91] hidden h-24 w-12 -translate-y-1/2 flex-col items-center justify-center gap-1.5 overflow-visible rounded-l-[1.35rem] border border-r-0 border-slate-200 bg-white text-slate-700 shadow-[-16px_0_36px_-24px_rgba(15,23,42,0.55)] transition-[width,background-color,border-color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:w-14 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 hover:shadow-[-18px_0_42px_-22px_rgba(15,23,42,0.62)] focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 print:hidden xl:flex"
                onClick={() => setIsPriceTableCartPanelCollapsed(false)}
                title="Abrir carrinho"
                aria-label={`Abrir carrinho da tabela com ${totalCartItems} itens`}
              >
                <span className="absolute inset-y-4 left-0 w-1 rounded-r-full bg-amber-400 shadow-[0_0_14px_rgba(251,191,36,0.32)]" aria-hidden="true" />
                {totalCartItems > 0 && (
                  <span className="absolute -left-2 -top-2 flex min-w-6 items-center justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-bold leading-none text-white shadow-sm">
                    {totalCartItems}
                  </span>
                )}
                <span className="flex size-9 items-center justify-center rounded-full bg-amber-50 text-amber-600 transition group-hover:bg-white group-hover:text-amber-700">
                  <ShoppingCart className="h-5 w-5" weight="fill" />
                </span>
                <CaretLeft className="h-3.5 w-3.5 text-slate-400 transition group-hover:text-amber-600" weight="bold" />
              </button>
              )}
          </>
        )}

        {shouldShowPriceTableMobileCartTrigger && !showCartDrawer && (
          <button
            type="button"
            className="fixed bottom-4 right-4 z-[56] inline-flex h-12 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-[0_24px_45px_-28px_rgba(15,23,42,0.55)] transition-colors hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 xl:hidden"
            onClick={() => {
              setCartDrawerSource('price-table')
              setShowCartDrawer(true)
              void loadCartDrawer()
            }}
            aria-label={`Abrir carrinho da tabela com ${totalCartItems} itens`}
          >
            <span className="inline-flex size-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
              <ShoppingCart className="h-4 w-4" weight="fill" />
            </span>
            <span>Carrinho</span>
            <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-bold text-white">
              {totalCartItems}
            </span>
          </button>
        )}

        {shouldShowCartSheet && (
          <Sheet open={showCartDrawer} onOpenChange={handleCartDrawerOpenChange}>
            <SheetContent
              className={`flex h-full min-h-0 w-full flex-col overflow-hidden p-0 ${
                isCartDrawerPriceTableMode
                  ? 'max-w-[460px] sm:max-w-[520px] xl:max-w-[560px]'
                  : 'max-w-[420px] sm:max-w-md'
              }`}
            >
              <Suspense
                fallback={(
                  <>
                    <SheetHeader className="shrink-0 border-b bg-white px-4 py-3">
                      <SheetTitle className="text-base font-semibold">
                        {isCartDrawerPriceTableMode
                          ? `Carrinho da tabela (${totalCartItems})`
                          : t('cart.drawer_title', { count: totalCartItems })}
                      </SheetTitle>
                    </SheetHeader>
                    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-4">
                      <Skeleton className="h-24 rounded-2xl" />
                      <Skeleton className="h-24 rounded-2xl" />
                      <Skeleton className="h-24 rounded-2xl" />
                      <div className="mt-auto space-y-3 border-t border-slate-100 pt-4">
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="h-11 rounded-xl" />
                      </div>
                    </div>
                  </>
                )}
              >
                <CartDrawer
                  renderWithinSheet
                  open={showCartDrawer}
                  onOpenChange={handleCartDrawerOpenChange}
                  items={activeCartItems}
                  isLoggedIn={effectiveIsLoggedIn}
                  allowGuestCheckout={isAnonymousQuickCheckoutAvailable}
                  onUpdateQuantity={handleUpdateQuantity}
                  onEnsureCartSynchronized={flushPendingCartSync}
                  onPriceModeChanged={refreshStorefrontPricesAfterTaxModeChange}
                  onRemoveItem={handleRemoveFromCart}
                  onAddToCart={(product) => handleAddToCart(product, 1)}
                  onProductClick={handleProductClick}
                  contextLabel={isCartDrawerPriceTableMode ? 'Tabela de preços' : undefined}
                  freightPrefill={cartQuoteFreightPrefill ?? cartQuoteOrigin?.freightPrefill ?? null}
                  emptyActionLabel={isCartDrawerPriceTableMode ? 'Voltar para a tabela' : undefined}
                  onSaveAsQuote={(freightSelection, financialSummary) => {
                    handleCartDrawerOpenChange(false)
                    handleOpenSaveQuoteDialog(freightSelection, financialSummary)
                  }}
                  onCheckout={handleProceedToCheckout}
                  onClearCart={handleClearCart}
                />
              </Suspense>
            </SheetContent>
          </Sheet>
        )}

        {showCategorySidebar && (
          <Dialog open={showCategorySidebar} onOpenChange={setShowCategorySidebar}>
            <Suspense
              fallback={(
                <DialogContent className="max-w-2xl">
                  <CategorySidebarSkeleton />
                </DialogContent>
              )}
            >
              <DialogContent className="max-w-2xl">
                <CategorySidebar
                  categories={mainMenuCategories}
                  selectedCategory={selectedCategory}
                  onCategorySelect={handleCategorySelect}
                />
              </DialogContent>
            </Suspense>
          </Dialog>
        )}

        <Dialog open={showSaveQuoteDialog} onOpenChange={setShowSaveQuoteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('quotes.save_title')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {cartQuoteOrigin ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-5 text-blue-950">
                  Você escolheu criar um novo orçamento. O orçamento #{cartQuoteOrigin.quoteNumber} permanecerá sem alterações.
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="quote-name">{t('quotes.name_label')}</Label>
                <Input
                  id="quote-name"
                  placeholder={t('quotes.name_placeholder')}
                  value={quoteName}
                  onChange={(e) => setQuoteName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveQuoteDialog(false)
                  setCartQuoteFreightSelection(null)
                  setCartQuoteFinancialSummary(null)
                }}
              >
                {t('actions.cancel')}
              </Button>
              <Button onClick={handleSaveQuote} disabled={isSavingQuote}>
                {t('actions.save_quote')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showSaveQuoteDestinationDialog}
          onOpenChange={(open) => {
            if (isSavingQuote) return
            setShowSaveQuoteDestinationDialog(open)
            if (!open) {
              setCartQuoteFreightSelection(null)
              setCartQuoteFinancialSummary(null)
            }
          }}
        >
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-xl">Como deseja salvar este carrinho?</DialogTitle>
            </DialogHeader>

            <div className="space-y-3 py-3">
              <p className="text-base leading-6 text-slate-600">
                Este carrinho começou no orçamento abaixo. Escolha se deseja atualizar esse orçamento ou criar um novo:
              </p>

              {cartQuoteOrigin && (
                <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-sm font-semibold text-slate-600">
                    Orçamento #{cartQuoteOrigin.quoteNumber}
                  </p>
                  <p className="text-base font-bold text-slate-950">
                    {cartQuoteOrigin.quoteName || 'Orçamento atual'}
                  </p>
                </div>
              )}

              <button
                type="button"
                className="flex min-h-24 w-full items-center gap-4 rounded-lg border-2 border-emerald-700 bg-emerald-100 px-4 py-4 text-left shadow-[0_0_0_3px_rgba(5,150,105,0.16)] transition hover:bg-emerald-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
                onClick={handleUpdateOriginQuote}
                disabled={isSavingQuote || !cartQuoteOrigin}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-white">
                  <ArrowsClockwise className="h-6 w-6" weight="bold" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-base font-bold text-emerald-950">
                    <span>{isSavingQuote ? 'Atualizando orçamento...' : 'Atualizar o orçamento atual'}</span>
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-700 px-2.5 py-1 text-xs font-bold text-white">
                      <CheckCircle className="h-4 w-4" weight="fill" />
                      Selecionada
                    </span>
                  </span>
                  <span className="mt-1 block text-sm leading-5 text-emerald-900">
                    Substitui os itens do orçamento acima. Não cria outro orçamento.
                  </span>
                </span>
              </button>

              <button
                type="button"
                className="flex min-h-24 w-full items-center gap-4 rounded-lg border border-slate-300 bg-white px-4 py-4 text-left transition hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:opacity-60"
                onClick={handleChooseNewQuote}
                disabled={isSavingQuote}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                  <FilePlus className="h-6 w-6" weight="bold" />
                </span>
                <span>
                  <span className="block text-base font-bold text-slate-950">
                    Criar um novo orçamento
                  </span>
                  <span className="mt-1 block text-sm leading-5 text-slate-600">
                    Mantém o orçamento atual sem alterações e salva este carrinho no seu cadastro.
                  </span>
                </span>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {showCheckoutBusinessRegisterDialog && (
          <Dialog open={showCheckoutBusinessRegisterDialog} onOpenChange={setShowCheckoutBusinessRegisterDialog}>
            <Suspense
              fallback={(
                <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] rounded-[28px] border-0 bg-transparent p-0 shadow-none sm:max-w-[min(920px,calc(100vw-2rem))] [&_[data-slot=dialog-close]]:right-4 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:border [&_[data-slot=dialog-close]]:border-zinc-200 [&_[data-slot=dialog-close]]:bg-white [&_[data-slot=dialog-close]]:p-1.5 [&_[data-slot=dialog-close]]:opacity-100">
                  <div className="rounded-[22px] border border-zinc-200/90 bg-white p-6 shadow-[0_30px_80px_-48px_rgba(15,23,42,0.35)]">
                    <div className="space-y-4">
                      <Skeleton className="h-7 w-56" />
                      <Skeleton className="h-2 w-full rounded-full" />
                      <Skeleton className="h-24 w-full rounded-2xl" />
                      <Skeleton className="h-12 w-full rounded-xl" />
                      <Skeleton className="h-12 w-full rounded-xl" />
                    </div>
                  </div>
                </DialogContent>
              )}
            >
                <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-[28px] border-0 bg-transparent p-0 shadow-none sm:max-w-[min(920px,calc(100vw-2rem))] [&_[data-slot=dialog-close]]:right-4 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:border [&_[data-slot=dialog-close]]:border-zinc-200 [&_[data-slot=dialog-close]]:bg-white [&_[data-slot=dialog-close]]:p-1.5 [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:shadow-[0_8px_18px_-14px_rgba(15,23,42,0.35)]">
                <DialogHeader className="sr-only">
                  <DialogTitle>Cadastrar minha empresa</DialogTitle>
                </DialogHeader>
                <RegisterView
                  onRegister={handleCheckoutBusinessRegister}
                  onSwitchToLogin={() => {
                    setShowCheckoutBusinessRegisterDialog(false)
                    navigateToView('login')
                  }}
                  initialDocumentType="cnpj"
                  lockDocumentType
                  presentation="embedded"
                  hideLoginPrompt
                  titleOverride="Cadastrar minha empresa"
                  lojaId={siteConfig?.loja_id}
                  emailCodeFallbackEnabled={Boolean(siteConfig?.cadastro_codigo_email_habilitado)}
                />
              </DialogContent>
            </Suspense>
          </Dialog>
        )}

        <Suspense fallback={null}>
          <CookieConsentBanner />
        </Suspense>

        {!isStandalonePriceTable && <Toaster position={isPublicTicketView ? 'top-right' : 'bottom-left'} />}
      </div>
    </PricingProvider>
  )
}

export default App
